// src/analysis/MusicAnalyzer.ts
var MusicAnalyzer = class {
  /**returns a list of measure indexes indicating the start measures of any
   * regions that match the region described by your [regionStartIndex] and
   * [regionEndIndex]. Time signatures will always be checked if [checkNotes]
   * is true, regardless of [checkTimeSigs].*/
  findRegionMatches(measures, regionStartIndex, regionEndIndex, {
    checkNotes = true,
    checkTempi = true,
    checkTimeSigs = true,
    allowRegionOverlap = false
  }) {
    let matches = [];
    let regLength = regionEndIndex - regionStartIndex + 1;
    for (let i = 0; i + regLength <= regionStartIndex; i++) {
      if (this.regionsMatch(
        measures,
        regionStartIndex,
        regionEndIndex,
        i,
        {
          checkNotes,
          checkTempi,
          checkTimeSigs
        }
      )) {
        matches.push(i);
        if (!allowRegionOverlap) {
          i += regLength - 1;
        }
      }
    }
    for (let i = regionEndIndex + 1; i + regLength <= measures.length; i++) {
      if (this.regionsMatch(
        measures,
        regionStartIndex,
        regionEndIndex,
        i,
        {
          checkNotes,
          checkTempi,
          checkTimeSigs
        }
      )) {
        matches.push(i);
        if (!allowRegionOverlap) {
          i += regLength - 1;
        }
      }
    }
    return matches;
  }
  /**returns true if the measures in two regions match. Specify the indexes
   * of the start and end measures in the first region and the index of the
   * start measure in the second region. Time signatures will always be 
   * checked if [checkNotes] is true, regardless of [checkTimeSigs].*/
  regionsMatch(measures, region1StartIndex, region1EndIndex, region2StartIndex, {
    checkNotes = true,
    checkTempi = true,
    checkTimeSigs = true
  }) {
    if (checkNotes && !this._notesMatch(
      measures,
      region1StartIndex,
      region1EndIndex,
      region2StartIndex
    )) {
      return false;
    }
    if (checkTempi && !this._tempiMatch(
      measures,
      region1StartIndex,
      region1EndIndex,
      region2StartIndex
    )) {
      return false;
    }
    if (!checkNotes && checkTimeSigs && !this._timeSigsMatch(
      measures,
      region1StartIndex,
      region1EndIndex,
      region2StartIndex
    )) {
      return false;
    }
    return true;
  }
  _notesMatch(measures, region1StartIndex, region1EndIndex, region2StartIndex) {
    for (let i = region1StartIndex; i <= region1EndIndex; i++) {
      let measure1 = measures[i];
      let measure2 = measures[region2StartIndex + i - region1StartIndex];
      if (measure1.stack.qNoteLength !== measure2.stack.qNoteLength) {
        return false;
      }
      if (measure1.voices.length !== measure2.voices.length) {
        return false;
      }
      for (let j = 0; j < measure1.voices.length; j++) {
        let ngList1 = measure1.voices[j].noteGroups;
        let ngList2 = measure2.voices[j].noteGroups;
        if (ngList1.length !== ngList2.length) {
          return false;
        }
        for (let k = 0; k < ngList1.length; k++) {
          let ng1 = ngList1[k];
          let ng2 = ngList2[k];
          if (ng1.duration !== ng2.duration || ng1.numDots !== ng2.numDots) {
            return false;
          }
          let notes1 = ng1.notes;
          let notes2 = ng2.notes;
          if (notes1.length !== notes2.length) {
            return false;
          }
          for (let l = 0; l < notes1.length; l++) {
            if (notes1[l].displayCents !== notes2[l].displayCents) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }
  _tempiMatch(measures, region1StartIndex, region1EndIndex, region2StartIndex) {
    let stack1 = measures[region1StartIndex].stack;
    let stack2 = measures[region2StartIndex].stack;
    let currentTM1 = stack1.getInheritedTempoMarker();
    let currentTM2 = stack2.getInheritedTempoMarker();
    if (currentTM1.tempo !== currentTM2.tempo) {
      return false;
    }
    for (let i = region1StartIndex; i <= region1EndIndex; i++) {
      stack1 = measures[i].stack;
      stack2 = measures[region2StartIndex + i - region1StartIndex].stack;
      if ((stack1.tempoMarkers == null || stack1.tempoMarkers.length === 0) && (stack2.tempoMarkers == null || stack2.tempoMarkers.length === 0)) {
        continue;
      }
      let map1 = /* @__PURE__ */ new Map([[0, currentTM1]]);
      if (stack1.tempoMarkers != null) {
        for (let tm of stack1.tempoMarkers) {
          map1.set(tm.qNoteTime - stack1.startTime, tm);
        }
      }
      let map2 = /* @__PURE__ */ new Map([[0, currentTM2]]);
      if (stack2.tempoMarkers != null) {
        for (let tm of stack2.tempoMarkers) {
          map2.set(tm.qNoteTime - stack2.startTime, tm);
        }
      }
      for (let e of map1.entries()) {
        if (map2.get(e[0])?.tempo !== e[1].tempo) {
          return false;
        }
      }
      for (let e of map2.entries()) {
        if (map1.get(e[0])?.tempo !== e[1].tempo) {
          return false;
        }
      }
    }
    return true;
  }
  _timeSigsMatch(measures, region1StartIndex, region1EndIndex, region2StartIndex) {
    for (let i = region1StartIndex; i <= region1EndIndex; i++) {
      let measure1 = measures[i];
      let measure2 = measures[region2StartIndex + i - region1StartIndex];
      if (measure1.numBeats !== measure2.numBeats || measure1.beatType !== measure2.beatType) {
        return false;
      }
    }
    return true;
  }
};

// src/data/constants/ClefType.ts
var _ClefType = class _ClefType {
  static getClefType(sign, line) {
    switch (sign) {
      case "G":
        return _ClefType.TREBLE;
      case "F":
        return _ClefType.BASS;
      case "C":
        return line == 4 ? _ClefType.TENOR : _ClefType.ALTO;
      default:
        return _ClefType.TREBLE;
    }
  }
};
_ClefType.TREBLE = "treble";
_ClefType.BASS = "bass";
_ClefType.ALTO = "alto";
_ClefType.TENOR = "tenor";
var ClefType = _ClefType;

// src/editing/transposition/TranspositionDO.ts
var TranspositionDO = class {
  constructor(diatonic, chromatic) {
    this.diatonic = diatonic;
    this.chromatic = chromatic;
  }
};

// src/utils/PitchUtils.ts
var _PitchUtils = class _PitchUtils {
  /**
   * takes a pitch name (such as "A4" or "C5" - never with an accidental and 
   * always uppercase) and converts it into a integer value useful for 
   * locating the pitch on a staff
   * @param	pitchName A two character string - an uppercase letter followed
   * by a single digit number. 
   * The system depends on these as sequential: A3, B3, C4, D4, E4, F4, G4
   * @return	an integer representation of the pitch that can be used for math
   * based placement of objects on staff lines/spaces
   */
  static getStepValueOfNote(pitchName) {
    let letter = pitchName[0];
    let number = letter !== "A" && letter !== "B" ? Number.parseInt(pitchName.substring(1)) : Number.parseInt(pitchName.substring(1)) + 1;
    let stepValue = 7 * number + (letter.charCodeAt(0) - 65);
    return stepValue;
  }
  /**
   * gets the number of steps (lines and spaces) the requested pitch lies 
   * below the top staff line of the requested clef
   * @param	pitchName A two character string - an uppercase letter (A-G) 
   * followed by a single digit number (0-9)
   * @param	clefType A string matching a constant from the 
   * nEngine.data.symbols.ClefType class
   * @return	the number of steps below. A negative value means the note is 
   * above the staff.
   */
  static getStepsFromTopStaffLine(pitchName, clefType) {
    let stepValue = _PitchUtils.getStepValueOfNote(pitchName);
    let topStaffLineStepValue = 40;
    switch (clefType) {
      case ClefType.TREBLE:
        topStaffLineStepValue = 40;
        break;
      case ClefType.BASS:
        topStaffLineStepValue = 28;
        break;
      case ClefType.TENOR:
        topStaffLineStepValue = 32;
        break;
      case ClefType.ALTO:
        topStaffLineStepValue = 34;
        break;
      default:
        console.log("unsupported clef! nEngine.utils.PitchUtils.getStepsFromTopStaffLine: " + clefType);
    }
    return topStaffLineStepValue - stepValue;
  }
  /**
   * returns the name of the note (not including accidental) that matches the 
   * staff line position on the specified clef
   * @param stepsFromTopStaffLine each line and space is a step - negative 
   * values mean above the staff
   * @param clefType a string matching a constant from the ClefType class
   * @return the name of the pitch (ex. A4, B5, etc.)
   */
  static getPitchNameFromStaffLinePosition(stepsFromTopStaffLine, clefType) {
    let topStaffLineNoteIndex;
    let topStaffLineNoteOctave;
    switch (clefType) {
      case ClefType.TREBLE:
        topStaffLineNoteIndex = 3;
        topStaffLineNoteOctave = 5;
        break;
      case ClefType.BASS:
        topStaffLineNoteIndex = 5;
        topStaffLineNoteOctave = 3;
        break;
      case ClefType.TENOR:
        topStaffLineNoteIndex = 2;
        topStaffLineNoteOctave = 4;
        break;
      case ClefType.ALTO:
        topStaffLineNoteIndex = 4;
        topStaffLineNoteOctave = 4;
        break;
      default:
        console.log("unsupported clef! nEngine.utils.PitchUtils.getPitchNameFromStaffLinePosition: $clefType");
        topStaffLineNoteIndex = 3;
        topStaffLineNoteOctave = 5;
    }
    let noteIndex = topStaffLineNoteIndex - stepsFromTopStaffLine;
    let noteOctave = topStaffLineNoteOctave;
    while (noteIndex < 0) {
      noteIndex += 7;
      noteOctave--;
    }
    while (noteIndex > 6) {
      noteIndex -= 7;
      noteOctave++;
    }
    let noteName = _PitchUtils.pitchNames[noteIndex] + noteOctave.toString();
    return noteName;
  }
  static getCommonPitchNameFromCents(cents) {
    let idx = cents / 100 % 12;
    if (idx < 0) {
      idx += 12;
    }
    let letter = _PitchUtils.centPitchNames[idx * 100];
    letter += (~~(cents / 1200) - 1).toString();
    return letter;
  }
  /**
   * returns the number of leger lines for the requested line/space position
   * @param stepsFromTopStaffLine negative values indicate steps above the 
   * staff, positive below
   * @return negative values for leger lines above, positive for below
   */
  static getNumberOfLegerLines(stepsFromTopStaffLine) {
    let legerLines = 0;
    if (stepsFromTopStaffLine < -1) {
      legerLines = Math.trunc(stepsFromTopStaffLine / 2);
    } else if (stepsFromTopStaffLine > 9) {
      legerLines = Math.trunc((stepsFromTopStaffLine - 8) / 2);
    }
    return legerLines;
  }
  /**
   * returns a reasonable diatonic transposition for a given chromatic
   * transposition
   * @param	chromaticSteps the number of chromatic steps the transposition
   * encompasses
   * @return	a corresponding number of diatonic steps that's typically
   * associated with this chromatic transposition
   */
  static getCommonDiatonicTranspositionFromChromaticSteps(chromaticSteps) {
    let numOctaves = Math.trunc(chromaticSteps / 12);
    let index = chromaticSteps < 0 ? -1 * (chromaticSteps % 12) : chromaticSteps % 12;
    let diatonicSteps = chromaticSteps < 0 ? -1 * _PitchUtils.transpositions[index][0] : _PitchUtils.transpositions[index][0];
    diatonicSteps += 7 * numOctaves;
    return diatonicSteps;
  }
  ///returns the pitch name, including octave, of the requested MIDI pitch.
  ///[midiPitch] is a value from 0 to 127, with 60 as C4 / middle C. The
  ///[accidentalPreference] can be 0 for showing both #'s and bs (C#4/Db4),
  ///1 for just showing sharps (C#4), or 2 for showing flats (Db4)
  static getPitchStringFromMidiPitch(midiPitch, accidentalPreference = 0) {
    let octave = Math.trunc((midiPitch - 12) / 12).toString();
    let pitchNames;
    if (accidentalPreference === 0) {
      pitchNames = [
        "C",
        "C#/Db",
        "D",
        "D#/Eb",
        "E",
        "F",
        "F#/Gb",
        "G",
        "G#/Ab",
        "A",
        "A#/Bb",
        "B"
      ];
    } else if (accidentalPreference === 1) {
      pitchNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    } else if (accidentalPreference === 2) {
      pitchNames = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
    } else {
      throw "unsupported accidentalPreference: $accidentalPreference";
    }
    return pitchNames[midiPitch % 12] + octave;
  }
  /**
   * calculates the alteration for a given note as indicated by the key 
   * signature
   * @param pitchLetterName the uppercase letter name of the pitch (A, B, C, 
   * D, E, or F)
   * @param keySig the number of sharps or flats in the key sig - positive 
   * values for sharps, negative for flats
   * @return negative integers refer to the number of flats, positive values 
   * indicate sharps, 0 indicates no alteration (natural)
   */
  static getKeySigAlterationForNote(pitchLetterName, keySig) {
    let keySigAlteration = 0;
    if (keySig > 0) {
      let pitchIndex = _PitchUtils.sharps.indexOf(pitchLetterName);
      keySigAlteration = Math.trunc(keySig / 7) + (pitchIndex < keySig % 7 ? 1 : 0);
    } else if (keySig < 0) {
      let pitchIndex = _PitchUtils.flats.indexOf(pitchLetterName);
      keySigAlteration = 1 * Math.trunc(keySig / 7) - (pitchIndex < -1 * keySig % 7 ? 1 : 0);
    } else {
      keySigAlteration = 0;
    }
    return keySigAlteration;
  }
  /** 
   * Returns the absolute alteration of a note, relative to the key of C, and 
   * regardless of the key signature. Ex. An F# (6600) in the key of D will 
   * return 1 even though F# is in the key signature.
   * @displayCents = the chromatic pitch displayed (so a Trumpet's D4 and a 
   * Flute's D4 are both 6200)
   * @diatonicStepsFromTopStaffLine = the number of lines and spaces the 
   * displayed pitch is from the top staff line
   * @clefType = the clef of the note*/
  static getAlterationForCents(displayCents, pitchName) {
    let naturalCents = _PitchUtils.pitchNameToDiatonicCents(pitchName);
    return Math.trunc((displayCents - naturalCents) / 100);
  }
  static pitchNameToDiatonicCents(pitchName) {
    let cents = _PitchUtils.pitchNameCentVals[pitchName[0]];
    let octave = Number.parseInt(pitchName.substring(1));
    cents += 1200 + octave * 1200;
    return cents;
  }
  /**
   * transposes a pitch name (minus any octave indication) by a number of 
   * chromatic and diatonic steps. 
   * [pitchName] is a character, "A", "B", "C", "D", "E", "F" or "G"
   * [alteration] is the absolute accidental alteration of the note (not 
   * considering key signature).
   * The pitch will be transposed by the number of [diatonicSteps] and 
   * [chromaticSteps], and a Record<string, number> will be returned where the 
   * String represents the new pitchName and number the new alteration.*/
  static transposePitchName(pitchName, alteration, diatonicSteps, chromaticSteps) {
    let cents = _PitchUtils.pitchNameCentVals[pitchName] + alteration * 100;
    let newCents = cents + chromaticSteps * 100;
    while (newCents < 0) {
      newCents += 1200;
    }
    newCents %= 1200;
    let index = _PitchUtils.pitchNames.indexOf(pitchName);
    index += diatonicSteps;
    while (index < 0) {
      index += 7;
    }
    let newPitchName = _PitchUtils.pitchNames[index % 7];
    let newAlteration = _PitchUtils.getAlterationForCents(
      newCents,
      newPitchName + "-1"
    );
    return {
      newPitchName,
      newAlteration
    };
  }
  /**
   * returns the integer value of the display key for a corresponding concert 
   * key given an instrument transposition. Negative values indicate flats and
   * positive values sharps. The [chromaticTransposition] value is the number 
   * of chromatic steps the written display notes appear above the concert 
   * sounding pitch (example: 2 for Bb Trumpet, -12 for soprano recorder). */
  static getDisplayKeyForConcertKey(concertKey, chromaticTransposition, diatonicTransposition) {
    if (diatonicTransposition == null) {
      diatonicTransposition = _PitchUtils.getCommonDiatonicTranspositionFromChromaticSteps(
        chromaticTransposition
      );
    }
    let delta = _PitchUtils.getKeySigAlterationForTransposition(
      diatonicTransposition,
      chromaticTransposition
    );
    return concertKey + delta;
  }
  /**
   * returns the integer value of the concert key for a corresponding display 
   * key given an instrument transposition. Negative values indicate flats and
   * positive values sharps. The [chromaticTransposition] value is the number 
   * of chromatic steps the written display notes appear above the concert 
   * sounding pitch (example: 2 for Bb Trumpet, -12 for soprano recorder).*/
  static getConcertKeyForDisplayKey(displayKey, chromaticTransposition, diatonicTransposition) {
    if (diatonicTransposition == null) {
      diatonicTransposition = _PitchUtils.getCommonDiatonicTranspositionFromChromaticSteps(
        chromaticTransposition
      );
    }
    let delta = _PitchUtils.getKeySigAlterationForTransposition(
      diatonicTransposition,
      chromaticTransposition
    );
    return displayKey - delta;
  }
  /**
   * gets the key signature alteration amount for the passed in transposition 
   * values
   * @param diatonicSteps the number of diatonic steps the transposed note is 
   * above the concert pitch note (Bb trumpet would be 1)
   * @param chromaticSteps the number of chromatic steps the transposed note 
   * is above the concert pitch note (Bb trumpet would be 2)
   * @return negative integer for an alteration in the flats direction, 
   * positive for an alteration in the sharps direction
   */
  static getKeySigAlterationForTransposition(diatonicSteps, chromaticSteps) {
    return 2 * diatonicSteps + 7 * (chromaticSteps - 2 * diatonicSteps);
  }
  /**
   * returns the key signature value (-7 to 7) for the requested key
   * @param diatonicStepsFromC the diatonic steps from C of the new key
   * @param chromaticStepsFromC the chromatic steps from C of the new key
   * @param major true if the key is Major, false if it's minor
   * @return a negative integer indicates a key with that number of flats, 
   * positive with sharps, and 0 is no sharps or flats
   */
  static getKeySignature(diatonicStepsFromC, chromaticStepsFromC, major) {
    return major ? _PitchUtils.getKeySigAlterationForTransposition(
      diatonicStepsFromC,
      chromaticStepsFromC
    ) : _PitchUtils.getKeySigAlterationForTransposition(
      diatonicStepsFromC,
      chromaticStepsFromC
    ) - 3;
  }
  static getTranspositionForKeyChange(oldKey, newKey, oldKeyIsMajor, newKeyIsMajor, transposeUp = true) {
    if (!oldKeyIsMajor)
      oldKey += 3;
    if (!newKeyIsMajor)
      newKey += 3;
    let keyDelta = newKey - oldKey;
    let chromDelta = keyDelta * 7 % 12;
    let diatDelta = keyDelta * 4 % 7;
    if (transposeUp && chromDelta < 0) {
      chromDelta += 12;
      diatDelta += 7;
    } else if (!transposeUp && chromDelta > 0) {
      chromDelta -= 12;
      diatDelta -= 7;
    }
    return new TranspositionDO(diatDelta, chromDelta);
  }
  /**
   * determines the nearest transposition direction from an old key to a new 
   * one -1 means down is closer, 1 means up is closer, 0 means they're equal
   */
  static getNearestTranspositionDirection(oldKey, newKey, oldKeyIsMajor, newKeyIsMajor) {
    if (!oldKeyIsMajor)
      oldKey += 3;
    if (!newKeyIsMajor)
      newKey += 3;
    let keyDelta = newKey - oldKey;
    let chromDelta = keyDelta * 7 % 12;
    if (chromDelta > 6 || chromDelta > -6 && chromDelta < 0) {
      return -1;
    } else if (chromDelta < -6 || chromDelta < 6 && chromDelta > 0) {
      return 1;
    } else {
      return 0;
    }
  }
  /**
   * gets the total key size, combining the outgoing key with the new key by 
   * figuring out how many outgoing naturals are needed
   * @param newKey the new key signature that will be displayed
   * @param outgoingKey the key signature from the previous measure
   * @return the total number of accidentals that must be displayed (always 
   * positive)
   */
  static getTotalKeySize(newKey, outgoingKey) {
    if (newKey < 0) {
      if (outgoingKey < newKey) {
        return outgoingKey * -1;
      } else if (outgoingKey <= 0) {
        return newKey * -1;
      } else {
        return newKey * -1 + outgoingKey;
      }
    } else {
      if (outgoingKey > newKey) {
        return outgoingKey;
      } else if (outgoingKey >= 0) {
        return newKey;
      } else {
        return newKey - outgoingKey;
      }
    }
  }
  /**
   * figures out which accidentals (naturals, sharps and flats) should be 
   * shown for the combination of new and outgoing keys
   * @param newKey the new key signature (from -7 to 7)
   * @param outgoingKey the old key signature (from -7 to 7)
   * @return creates an array that contains 4 sets of 7 numbers. Each set 
   * corresponds to an accidental category - naturalized flats, naturalized 
   * sharps, flats, and sharps. Each set goes in order for flats or sharps 
   * (BEADGCF or FCGDAEB). A 1 indicates that an accidental should be drawn 
   * for that position. A 0 indicates it should not.
   * [NAT. FLATS,    NAT. SHARPS,   FLATS,         SHARPS       ]
   * [0,0,0,1,1,0,0, 0,0,0,0,0,0,0, 1,1,1,0,0,0,0, 0,0,0,0,0,0,0]
   */
  static getAccidentalsForNewKeySignature(newKey, outgoingKey) {
    let numNaturals = 0;
    let numFlats = 0;
    let numSharps = 0;
    let numDoubleFlats = 0;
    let numDoubleSharps = 0;
    if (newKey < -7) {
      numDoubleFlats = -7 - newKey;
      newKey = -7;
    } else if (newKey > 7) {
      numDoubleSharps = newKey - 7;
      newKey = 7;
    }
    if (outgoingKey < -7) {
      outgoingKey = -7;
    } else if (outgoingKey > 7) {
      outgoingKey = 7;
    }
    let cancelStartIndex = 0;
    let cancelSharps = false;
    if (newKey < 0) {
      numFlats = -1 * newKey;
      if (outgoingKey < newKey) {
        numNaturals = newKey - outgoingKey;
        cancelStartIndex = newKey * -1;
        cancelSharps = false;
      } else if (outgoingKey <= 0) {
      } else {
        numNaturals = outgoingKey;
        cancelStartIndex = 0;
        cancelSharps = true;
      }
    } else {
      numSharps = newKey;
      if (outgoingKey > newKey) {
        numNaturals = outgoingKey - newKey;
        cancelStartIndex = newKey;
        cancelSharps = true;
      } else if (outgoingKey >= 0) {
      } else {
        numNaturals = -1 * outgoingKey;
        cancelStartIndex = 0;
        cancelSharps = false;
      }
    }
    let accidentals = new Array(28);
    let i = 0;
    for (i; i < 7; i++) {
      if (!cancelSharps) {
        if (numNaturals > 0 && i >= cancelStartIndex) {
          accidentals[i] = 1;
          numNaturals--;
          continue;
        }
      }
      accidentals[i] = 0;
    }
    for (i; i < 14; i++) {
      if (cancelSharps) {
        if (numNaturals > 0 && i - 7 >= cancelStartIndex) {
          accidentals[i] = 1;
          numNaturals--;
          continue;
        }
      }
      accidentals[i] = 0;
    }
    for (i; i < 21; i++) {
      if (i - 14 < numDoubleFlats) {
        accidentals[i] = 2;
      } else if (i - 14 < numFlats) {
        accidentals[i] = 1;
      } else {
        accidentals[i] = 0;
      }
    }
    for (i; i < 28; i++) {
      if (i - 21 < numDoubleSharps) {
        accidentals[i] = 2;
      } else if (i - 21 < numSharps) {
        accidentals[i] = 1;
      } else {
        accidentals[i] = 0;
      }
    }
    return accidentals;
  }
};
//mapping of chromatic to diatonic transpositions
_PitchUtils.transpositions = [
  [0, 0],
  [1, 1],
  [1, 2],
  [2, 3],
  [2, 4],
  [3, 5],
  [4, 6],
  [4, 7],
  [5, 8],
  [5, 9],
  [6, 10],
  [6, 11],
  [7, 12]
];
//diatonic base cent values of pitch names
_PitchUtils.pitchNameCentVals = {
  "C": 0,
  "D": 200,
  "E": 400,
  "F": 500,
  "G": 700,
  "A": 900,
  "B": 1100
};
_PitchUtils.centPitchNames = {
  0: "C",
  100: "C",
  200: "D",
  300: "E",
  400: "E",
  500: "F",
  600: "F",
  700: "G",
  800: "A",
  900: "A",
  1e3: "B",
  1100: "B"
};
_PitchUtils.pitchNames = ["C", "D", "E", "F", "G", "A", "B"];
_PitchUtils.sharps = ["F", "C", "G", "D", "A", "E", "B"];
_PitchUtils.flats = ["B", "E", "A", "D", "G", "C", "F"];
var PitchUtils = _PitchUtils;

// src/analysis/TheoryTools.ts
var sharpFifths = ["C", "G", "D", "A", "E", "B", "F", "C", "G", "D", "A", "E", "B"];
var flatFifths = ["C", "F", "B", "E", "A", "D", "G", "C", "F", "B", "E", "A", "D"];
var sharpFifthsWithAccidentals = [
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "F#",
  "C#",
  "G#",
  "D#",
  "A#",
  "E#",
  "B#"
];
var flatFifthsWithAccidentals = [
  "C",
  "F",
  "Bb",
  "Eb",
  "Ab",
  "Db",
  "Gb",
  "Cb",
  "Fb",
  "Bbb",
  "Ebb",
  "Abb",
  "Dbb"
];
function getAccidentalString(accidental) {
  switch (accidental) {
    case 0:
      return "";
    case -1:
      return "b";
    case 1:
      return "#";
    case -2:
      return "bb";
    case 2:
      return "x";
  }
}
var TheoryTools = class {
  /**returns a ScaleInfo object with info pertaining to the passed in key.
   * @param key A value indicating the key signature. 0 is no sharps/flats,
   * negative value indicates flats, positive value indicates sharps. Ex.
   * 3 = 3 sharps, -2 = 2 flats. */
  getScaleInfo(key) {
    let info = new ScaleInfo();
    info.key = key;
    let rootMajor;
    let rootMinor;
    if (key >= 0) {
      info.numSharps = key;
      info.numFlats = 0;
      rootMajor = sharpFifths[key];
      rootMinor = key >= 4 ? sharpFifths[key - 4] : sharpFifths[key + 3];
    } else {
      info.numSharps = 0;
      info.numFlats = key * -1;
      rootMajor = flatFifths[key * -1];
      rootMinor = key > -3 ? flatFifths[key * -1 + 4] : flatFifths[key * -1 - 3];
    }
    info.accidentals = [
      PitchUtils.getKeySigAlterationForNote("A", key),
      PitchUtils.getKeySigAlterationForNote("B", key),
      PitchUtils.getKeySigAlterationForNote("C", key),
      PitchUtils.getKeySigAlterationForNote("D", key),
      PitchUtils.getKeySigAlterationForNote("E", key),
      PitchUtils.getKeySigAlterationForNote("F", key),
      PitchUtils.getKeySigAlterationForNote("G", key)
    ];
    const letters = ["A", "B", "C", "D", "E", "F", "G"];
    info.pitchNames = letters.slice();
    for (let i = 0; i < 7; i++) {
      let accidental = info.accidentals[i];
      for (let j = 0; j < Math.abs(accidental); j++) {
        accidental > 0 ? info.pitchNames[i] += "#" : info.pitchNames[i] += "b";
      }
    }
    info.majorScalePitchNames = [];
    let startIdx = letters.indexOf(rootMajor);
    info.majorScaleRootIndex = startIdx;
    for (let i = 0; i < 7; i++) {
      info.majorScalePitchNames[i] = info.pitchNames[(startIdx + i) % 7];
    }
    info.minorScalePitchNames = [];
    startIdx = letters.indexOf(rootMinor);
    info.minorScaleRootIndex = startIdx;
    for (let i = 0; i < 7; i++) {
      info.minorScalePitchNames[i] = info.pitchNames[(startIdx + i) % 7];
    }
    return info;
  }
  /**returns the pitch names with accidentals that make up the requested
   * chord. Not all chords are supported. If not supported, returns null.*/
  getPitchNamesInChord(chord) {
    let rootName = chord.rootName;
    let key;
    let idx = sharpFifthsWithAccidentals.indexOf(rootName);
    if (idx !== -1) {
      key = idx;
    } else {
      idx = flatFifthsWithAccidentals.indexOf(rootName);
      if (idx !== -1) {
        key = -1 * idx;
      } else {
        return null;
      }
    }
    let scaleInfo = this.getScaleInfo(key);
    let chordPitches = [];
    let letters = ["A", "B", "C", "D", "E", "F", "G"];
    function addNote(rootIdx, scaleDegree, accidentalOffset) {
      let noteIdx = rootIdx + scaleDegree - 1;
      let accidental = scaleInfo.accidentals[noteIdx] + accidentalOffset;
      let pitchName = letters[noteIdx] + getAccidentalString(accidental);
      chordPitches.push(pitchName);
    }
    switch (chord.type) {
      case "major": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        break;
      }
      case "minor": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, 0);
        break;
      }
      case "augmented": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 1);
        break;
      }
      case "diminished": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, -1);
        break;
      }
      case "dominant": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        addNote(scaleInfo.majorScaleRootIndex, 7, -1);
        break;
      }
      case "major-seventh": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        addNote(scaleInfo.majorScaleRootIndex, 7, 0);
        break;
      }
      case "minor-seventh": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, 0);
        addNote(scaleInfo.minorScaleRootIndex, 7, 0);
        break;
      }
      case "diminished-seventh": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, -1);
        addNote(scaleInfo.minorScaleRootIndex, 7, 0);
        break;
      }
      case "half-diminished": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, -1);
        addNote(scaleInfo.minorScaleRootIndex, 7, -1);
        break;
      }
      case "major-sixth": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        addNote(scaleInfo.majorScaleRootIndex, 6, 0);
        break;
      }
      case "major-ninth": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 3, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        addNote(scaleInfo.majorScaleRootIndex, 7, 0);
        addNote(scaleInfo.majorScaleRootIndex, 9, 0);
        break;
      }
      case "minor-ninth": {
        addNote(scaleInfo.minorScaleRootIndex, 1, 0);
        addNote(scaleInfo.minorScaleRootIndex, 3, 0);
        addNote(scaleInfo.minorScaleRootIndex, 5, 0);
        addNote(scaleInfo.minorScaleRootIndex, 7, 0);
        addNote(scaleInfo.minorScaleRootIndex, 9, 0);
        break;
      }
      case "suspended-fourth": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 4, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        break;
      }
      case "suspended-second": {
        addNote(scaleInfo.majorScaleRootIndex, 1, 0);
        addNote(scaleInfo.majorScaleRootIndex, 2, 0);
        addNote(scaleInfo.majorScaleRootIndex, 5, 0);
        break;
      }
    }
    return chordPitches;
  }
};
var ScaleInfo = class {
};

// src/data/BaseDataObject.ts
import { v4 as uuidv4 } from "uuid";
var _BaseDataObject = class _BaseDataObject {
  constructor() {
    this._dataID = 0;
  }
  static resetIDCounter() {
    this._nextID = 0;
  }
  /**
   * Gets the unique dataID for this object in the score.
   */
  get dataID() {
    if (this._dataID == 0) {
      this._dataID = _BaseDataObject._nextID;
      _BaseDataObject._nextID++;
    }
    return this._dataID;
  }
  set dataID(value) {
    this._dataID = value;
  }
  /**
   * Gets a string that should be unique across calls and sessions (uses
   * uuid lib)
   */
  get uniqueID() {
    if (this._uniqueID == null) {
      this._uniqueID = uuidv4();
    }
    return this._uniqueID;
  }
  set uniqueID(value) {
    this._uniqueID = value;
  }
};
_BaseDataObject._nextID = 1;
var BaseDataObject = _BaseDataObject;

// src/data/constants/StaffGroupingStyle.ts
var StaffGroupingStyle = /* @__PURE__ */ ((StaffGroupingStyle2) => {
  StaffGroupingStyle2[StaffGroupingStyle2["part"] = 0] = "part";
  StaffGroupingStyle2[StaffGroupingStyle2["score"] = 1] = "score";
  return StaffGroupingStyle2;
})(StaffGroupingStyle || {});

// src/data/ScoreProperties.ts
var _ScoreProperties = class _ScoreProperties {
  constructor() {
    /**number of pixels between two staff lines on the same staff*/
    this._pixelsPerSpace = _ScoreProperties.DEFAULT_PIXELS_PER_SPACE;
  }
  ScoreProperties() {
  }
  clone() {
    let scoreProps = new _ScoreProperties();
    scoreProps.rights = this._rights;
    scoreProps.pageHeight = this._pageHeight;
    scoreProps.pageWidth = this._pageWidth;
    scoreProps.leftPageMargin = this._leftPageMargin;
    scoreProps.rightPageMargin = this._rightPageMargin;
    scoreProps.topPageMargin = this._topPageMargin;
    scoreProps.bottomPageMargin = this._bottomPageMargin;
    scoreProps.firstPageVerticalOffset = this._firstPageVerticalOffset;
    scoreProps.systemSpacing = this._systemSpacing;
    scoreProps.topSystemDistance = this._topSystemDistance;
    scoreProps.leftSystemMargin = this._leftSystemMargin;
    scoreProps.rightSystemMargin = this._rightSystemMargin;
    scoreProps.staffSpacing = this._staffSpacing;
    scoreProps._pixelsPerSpace = this._pixelsPerSpace;
    scoreProps.stemWidth = this._stemWidth;
    scoreProps.beamWidth = this._beamWidth;
    scoreProps.staffLineWidth = this._staffLineWidth;
    scoreProps.lightBarlineWidth = this._lightBarlineWidth;
    scoreProps.heavyBarlineWidth = this._heavyBarlineWidth;
    scoreProps.legerLineWidth = this._legerLineWidth;
    scoreProps.repeatEndingWidth = this._repeatEndingWidth;
    scoreProps.hairpinLineWidth = this._hairpinLineWidth;
    scoreProps.enclosureWidth = this._enclosureWidth;
    scoreProps.tupletBracketWidth = this._tupletBracketWidth;
    scoreProps.graceNoteSize = this._graceNoteSize;
    scoreProps.cueNoteSize = this._cueNoteSize;
    scoreProps.noteheadWidth = this._noteheadWidth;
    scoreProps.clefDistanceFromBarline = this._clefDistanceFromBarline;
    scoreProps.chordSymbolFontHeight = this._chordSymbolFontHeight;
    scoreProps.staffGroupingStyle = this._staffGroupingStyle;
    return scoreProps;
  }
  /**
   * creates a new ScoreProperties object with default values and returns it
   * @return the new ScoreProperties object
   */
  static getNewScoreProperties() {
    let scoreProps = new _ScoreProperties();
    scoreProps.rights = "";
    scoreProps.pageHeight = 130;
    scoreProps.pageWidth = _ScoreProperties.DEFAULT_PAGE_WIDTH;
    scoreProps.leftPageMargin = 0;
    scoreProps.rightPageMargin = 0;
    scoreProps.topPageMargin = 7.4;
    scoreProps.bottomPageMargin = 7.4;
    scoreProps.firstPageVerticalOffset = 5;
    scoreProps.systemSpacing = 7.1;
    scoreProps.topSystemDistance = 4.3;
    scoreProps.leftSystemMargin = 0;
    scoreProps.rightSystemMargin = 0;
    scoreProps.staffSpacing = 8;
    scoreProps.stemWidth = 0.07487;
    scoreProps.beamWidth = 0.5;
    scoreProps.staffLineWidth = 0.07487;
    scoreProps.lightBarlineWidth = 0.07487;
    scoreProps.heavyBarlineWidth = 0.5;
    scoreProps.legerLineWidth = 0.07487;
    scoreProps.repeatEndingWidth = 0.14583;
    scoreProps.hairpinLineWidth = 0.07487;
    scoreProps.enclosureWidth = 0.07487;
    scoreProps.tupletBracketWidth = 0.07487;
    scoreProps.graceNoteSize = 0.6;
    scoreProps.cueNoteSize = 0.6;
    scoreProps.noteheadWidth = 1.2;
    scoreProps.clefDistanceFromBarline = 1;
    scoreProps.chordSymbolFontHeight = 2.5;
    scoreProps.qNoteWidth = 4.44;
    scoreProps.keySigWidth = 1.2;
    scoreProps.timeSigWidth = 2.4;
    scoreProps.clefWidth = 2.82;
    scoreProps.measureLeadIn = 1.8;
    scoreProps.minMeasureWidth = 0;
    scoreProps.staffGroupingStyle = 0 /* part */;
    return scoreProps;
  }
  get cueNoteSize() {
    return this._cueNoteSize;
  }
  set cueNoteSize(value) {
    this._cueNoteSize = value > 0 ? value : 0.6;
  }
  get graceNoteSize() {
    return this._graceNoteSize;
  }
  set graceNoteSize(value) {
    this._graceNoteSize = value > 0 ? value : 0.6;
  }
  get noteheadWidth() {
    return this._noteheadWidth;
  }
  set noteheadWidth(value) {
    this._noteheadWidth = value;
  }
  get tupletBracketWidth() {
    return this._tupletBracketWidth;
  }
  set tupletBracketWidth(value) {
    this._tupletBracketWidth = value > 0 ? value : 0.1;
  }
  get enclosureWidth() {
    return this._enclosureWidth;
  }
  set enclosureWidth(value) {
    this._enclosureWidth = value > 0 ? value : 0.1;
  }
  get hairpinLineWidth() {
    return this._hairpinLineWidth;
  }
  set hairpinLineWidth(value) {
    this._hairpinLineWidth = value > 0 ? value : 0.1;
  }
  get repeatEndingWidth() {
    return this._repeatEndingWidth;
  }
  set repeatEndingWidth(value) {
    this._repeatEndingWidth = value > 0 ? value : 0.1;
  }
  get legerLineWidth() {
    return this._legerLineWidth;
  }
  set legerLineWidth(value) {
    this._legerLineWidth = value > 0 ? value : 0.1875;
  }
  get heavyBarlineWidth() {
    return this._heavyBarlineWidth;
  }
  set heavyBarlineWidth(value) {
    this._heavyBarlineWidth = value > 0 ? value : 0.5;
  }
  get lightBarlineWidth() {
    return this._lightBarlineWidth;
  }
  set lightBarlineWidth(value) {
    this._lightBarlineWidth = value > 0 ? value : 0.1875;
  }
  get staffLineWidth() {
    return this._staffLineWidth;
  }
  set staffLineWidth(value) {
    this._staffLineWidth = value > 0 ? value : 0.1;
  }
  get beamWidth() {
    return this._beamWidth;
  }
  set beamWidth(value) {
    this._beamWidth = value > 0 ? value : 0.5;
  }
  get stemWidth() {
    return this._stemWidth;
  }
  set stemWidth(value) {
    this._stemWidth = value > 0 ? value : 0.1;
  }
  get staffSpacing() {
    return this._staffSpacing;
  }
  set staffSpacing(value) {
    this._staffSpacing = value;
  }
  get pixelsPerSpace() {
    return this._pixelsPerSpace;
  }
  set pixelsPerSpace(value) {
    this._pixelsPerSpace = value;
  }
  get rightSystemMargin() {
    return this._rightSystemMargin;
  }
  set rightSystemMargin(value) {
    this._rightSystemMargin = value;
  }
  get leftSystemMargin() {
    return this._leftSystemMargin;
  }
  set leftSystemMargin(value) {
    this._leftSystemMargin = value;
  }
  get topSystemDistance() {
    return this._topSystemDistance;
  }
  set topSystemDistance(value) {
    this._topSystemDistance = value;
  }
  get systemSpacing() {
    return this._systemSpacing;
  }
  set systemSpacing(value) {
    this._systemSpacing = value;
  }
  get bottomPageMargin() {
    return this._bottomPageMargin;
  }
  set bottomPageMargin(value) {
    this._bottomPageMargin = value;
  }
  get topPageMargin() {
    return this._topPageMargin;
  }
  set topPageMargin(value) {
    this._topPageMargin = value;
  }
  get rightPageMargin() {
    return this._rightPageMargin;
  }
  set rightPageMargin(value) {
    this._rightPageMargin = value;
  }
  get leftPageMargin() {
    return this._leftPageMargin;
  }
  set leftPageMargin(value) {
    this._leftPageMargin = value;
  }
  get firstPageVerticalOffset() {
    return this._firstPageVerticalOffset;
  }
  set firstPageVerticalOffset(value) {
    this._firstPageVerticalOffset = value;
  }
  get pageWidth() {
    return this._pageWidth;
  }
  set pageWidth(value) {
    this._pageWidth = value;
  }
  get pageHeight() {
    return this._pageHeight;
  }
  set pageHeight(value) {
    this._pageHeight = value;
  }
  get rights() {
    return this._rights;
  }
  set rights(value) {
    this._rights = value;
  }
  get software() {
    return this._software;
  }
  set software(value) {
    this._software = value;
  }
  get clefDistanceFromBarline() {
    return this._clefDistanceFromBarline;
  }
  set clefDistanceFromBarline(value) {
    this._clefDistanceFromBarline = value;
  }
  get chordSymbolFontHeight() {
    return this._chordSymbolFontHeight;
  }
  set chordSymbolFontHeight(value) {
    this._chordSymbolFontHeight = value;
  }
  /**
   * width granted for a quarter note in music spacing
   */
  get qNoteWidth() {
    return this._qNoteWidth;
  }
  set qNoteWidth(value) {
    this._qNoteWidth = value;
  }
  /**
   * width granted for a single accidental in a key signature for music 
   * spacing
   */
  get keySigWidth() {
    return this._keySigWidth;
  }
  set keySigWidth(value) {
    this._keySigWidth = value;
  }
  /**
   * width granted for a time signature in music spacing
   */
  get timeSigWidth() {
    return this._timeSigWidth;
  }
  set timeSigWidth(value) {
    this._timeSigWidth = value;
  }
  /**
   * width granted for a normal sized clef in music spacing
   */
  get clefWidth() {
    return this._clefWidth;
  }
  set clefWidth(value) {
    this._clefWidth = value;
  }
  /**
   * width of space before notes begin in a measure for music spacing
   */
  get measureLeadIn() {
    return this._measureLeadIn;
  }
  set measureLeadIn(value) {
    this._measureLeadIn = value;
  }
  /**
   * minimum allowed measure width for music spacing
   */
  get minMeasureWidth() {
    return this._minMeasureWidth;
  }
  set minMeasureWidth(value) {
    this._minMeasureWidth = value;
  }
  get staffGroupingStyle() {
    return this._staffGroupingStyle;
  }
  set staffGroupingStyle(value) {
    this._staffGroupingStyle = value;
  }
};
_ScoreProperties.DEFAULT_PAGE_WIDTH = 100;
_ScoreProperties.DEFAULT_PIXELS_PER_SPACE = 10;
_ScoreProperties.DEFAULT_TEMPO = 120;
var ScoreProperties = _ScoreProperties;

// src/data/constants/AccidentalType.ts
var _AccidentalType = class _AccidentalType {
  /**
   * returns the constant from the AccidentalType class that matches the 
   * passed in alteration
   * @param alteration a value from -2 to 2 (negative for flats, positive for 
   * sharps, 0 for natural)
   * @return a number matching a constant from the AccidentalType class
   */
  static getAccidentalType(alteration) {
    switch (alteration) {
      case 0:
        return _AccidentalType.NATURAL;
      case 1:
        return _AccidentalType.SHARP;
      case 2:
        return _AccidentalType.DOUBLE_SHARP;
      case 3:
        return _AccidentalType.TRIPLE_SHARP;
      case -1:
        return _AccidentalType.FLAT;
      case -2:
        return _AccidentalType.DOUBLE_FLAT;
      case -3:
        return _AccidentalType.TRIPLE_FLAT;
      default:
        console.log("unsupported accidental alteration: $alteration");
        return _AccidentalType.NONE;
    }
  }
  static getAccidentalFromString(name) {
    switch (name) {
      case "none":
        return _AccidentalType.NONE;
      case "natural":
        return _AccidentalType.NATURAL;
      case "sharp":
        return _AccidentalType.SHARP;
      case "flat":
        return _AccidentalType.FLAT;
      case "double-sharp":
        return _AccidentalType.DOUBLE_SHARP;
      case "sharp-sharp":
        return _AccidentalType.DOUBLE_SHARP;
      case "flat-flat":
        return _AccidentalType.DOUBLE_FLAT;
      case "triple-sharp":
        return _AccidentalType.TRIPLE_SHARP;
      case "triplet-flat":
        return _AccidentalType.TRIPLE_FLAT;
      default:
        return _AccidentalType.NONE;
    }
  }
};
_AccidentalType.NONE = 100;
_AccidentalType.NATURAL = 0;
_AccidentalType.SHARP = 1;
_AccidentalType.FLAT = -1;
_AccidentalType.DOUBLE_SHARP = 2;
_AccidentalType.DOUBLE_FLAT = -2;
_AccidentalType.TRIPLE_SHARP = 3;
_AccidentalType.TRIPLE_FLAT = -3;
var AccidentalType = _AccidentalType;

// src/data/constants/ArticulationType.ts
var _ArticulationType = class _ArticulationType {
  static getTypeFromName(name) {
    switch (name) {
      case "staccato":
        return _ArticulationType.STACCATO;
      case "accent":
        return _ArticulationType.ACCENT;
      case "tenuto":
        return _ArticulationType.LEGATO;
      default:
        return _ArticulationType.UNKNOWN;
    }
  }
};
_ArticulationType.UNKNOWN = -1;
_ArticulationType.STACCATO = 0;
_ArticulationType.ACCENT = 1;
_ArticulationType.LEGATO = 2;
var ArticulationType = _ArticulationType;

// src/data/constants/BarlineLocation.ts
var _BarlineLocation = class _BarlineLocation {
  static getLocation(location) {
    if (location == null)
      return _BarlineLocation.RIGHT;
    switch (location) {
      case "left":
        return _BarlineLocation.LEFT;
      case "middle":
        return _BarlineLocation.MIDDLE;
      case "right":
        return _BarlineLocation.RIGHT;
      default:
        return _BarlineLocation.RIGHT;
    }
  }
};
_BarlineLocation.LEFT = 0;
_BarlineLocation.MIDDLE = 1;
_BarlineLocation.RIGHT = 2;
var BarlineLocation = _BarlineLocation;

// src/data/constants/BarlineStyle.ts
var _BarlineStyle = class _BarlineStyle {
  static getStyle(type) {
    if (type == null)
      return _BarlineStyle.REGULAR;
    switch (type) {
      case "regular":
        return _BarlineStyle.REGULAR;
      case "dotted":
        return _BarlineStyle.DOTTED;
      case "dashed":
        return _BarlineStyle.DASHED;
      case "heavy":
        return _BarlineStyle.HEAVY;
      case "light-light":
        return _BarlineStyle.LIGHT_LIGHT;
      case "light-heavy":
        return _BarlineStyle.LIGHT_HEAVY;
      case "heavy-light":
        return _BarlineStyle.HEAVY_LIGHT;
      case "heavy-heavy":
        return _BarlineStyle.HEAVY_HEAVY;
      case "tick":
        return _BarlineStyle.TICK;
      case "short":
        return _BarlineStyle.SHORT;
      case "none":
        return _BarlineStyle.NONE;
      default:
        return _BarlineStyle.REGULAR;
    }
  }
};
_BarlineStyle.REGULAR = 0;
_BarlineStyle.DOTTED = 1;
_BarlineStyle.DASHED = 2;
_BarlineStyle.HEAVY = 3;
_BarlineStyle.LIGHT_LIGHT = 4;
_BarlineStyle.LIGHT_HEAVY = 5;
_BarlineStyle.HEAVY_LIGHT = 6;
_BarlineStyle.HEAVY_HEAVY = 7;
_BarlineStyle.TICK = 8;
_BarlineStyle.SHORT = 9;
_BarlineStyle.NONE = 10;
var BarlineStyle = _BarlineStyle;

// src/data/constants/BeamState.ts
var BeamState = class {
};
BeamState.NONE = "none";
BeamState.BEGIN = "begin";
BeamState.CONTINUE = "continue";
BeamState.END = "end";
BeamState.FORWARD_HOOK = "forward hook";
BeamState.BACKWARD_HOOK = "backward hook";

// src/data/constants/DurationType.ts
var _DurationType = class _DurationType {
  /**
   * returns the duration unit duration of the durationType:requested, 
   * factoring in the number of augmentation dots
   * @param durationType a String matching a constant from this class
   * @param numDots the number of augmentation dots
   * @return the duration of the note in Duration Units
   */
  static getDurationValue(durationType, numDots) {
    let duration = durationType;
    let dotVal = ~~(duration / 2);
    for (let i = 0; i < numDots; i++) {
      duration += dotVal;
      dotVal = ~~(dotVal / 2);
    }
    return duration;
  }
  /**
   * returns the String DurationType value for the requested duration
   * @param duration a duration that is one of the supported powers of 2
   * @return a String matching one of the String constants from this 
   * class
   */
  static getDurationType(durationName) {
    switch (durationName) {
      case "whole":
        return _DurationType.WHOLE;
      case "half":
        return _DurationType.HALF;
      case "quarter":
        return _DurationType.QUARTER;
      case "eighth":
        return _DurationType.EIGHTH;
      case "16th":
        return _DurationType.SIXTEENTH;
      case "32nd":
        return _DurationType.THIRTY_SECOND;
      case "64th":
        return _DurationType.SIXTY_FOURTH;
      case "breve":
        return _DurationType.BREVE;
      case "long":
        return _DurationType.LONG;
      case "maxima":
        return _DurationType.MAXIMA;
      case "128th":
        return _DurationType.ONE_TWO_EIGHTH;
      case "256th":
        return _DurationType.TWO_FIVE_SIXTH;
      case "512th":
        return _DurationType.FIVE_TWELFTH;
      case "1024th":
        return _DurationType.ONE_ZERO_TWO_FOURTH;
      default:
        console.log("unsupported base duration! ScoreManager.getDurationType: $durationName");
    }
    return _DurationType.UNSUPPORTED;
  }
  /**
   * calculates the dots portion of the duration
   * @param duration a duration matching one of the powers of 2 durations
   * @param numDots number of augmentation dots
   * @return the duration value of the dots themselves (not combined with the 
   * duration:supplied)
   */
  static calculateDotDuration(duration, numDots) {
    let dotVal = ~~(duration / 2);
    let dotTotal = 0;
    for (let i = 0; i < numDots; i++) {
      dotTotal += dotVal;
      dotVal = ~~(dotVal / 2);
    }
    return dotTotal;
  }
  /**
   * gets the base duration and the number of dots needed to create the 
   * requested total duration
   * @param totalDuration the duration you want to match
   * @return an array with 2 elements - the base first:duration, then the 
   * number of dots
   */
  static getBaseDurationAndDots(totalDuration) {
    let baseDuration = 2;
    while (baseDuration * 2 < totalDuration) {
      baseDuration *= 2;
    }
    let numDots = 0;
    let combinedDuration = baseDuration;
    let dotValue = ~~(baseDuration / 2);
    while (combinedDuration < totalDuration) {
      combinedDuration += dotValue;
      dotValue = ~~(dotValue / 2);
      numDots++;
    }
    if (combinedDuration !== totalDuration) {
    }
    return [baseDuration, numDots];
  }
};
_DurationType.MAXIMA = 32768;
_DurationType.LONG = 16384;
_DurationType.BREVE = 8192;
_DurationType.WHOLE = 4096;
_DurationType.HALF = 2048;
_DurationType.QUARTER = 1024;
_DurationType.EIGHTH = 512;
_DurationType.SIXTEENTH = 256;
_DurationType.THIRTY_SECOND = 128;
_DurationType.SIXTY_FOURTH = 64;
_DurationType.ONE_TWO_EIGHTH = 32;
_DurationType.TWO_FIVE_SIXTH = 16;
_DurationType.FIVE_TWELFTH = 8;
_DurationType.ONE_ZERO_TWO_FOURTH = 4;
_DurationType.UNSUPPORTED = -1;
var DurationType = _DurationType;

// src/data/constants/DynamicVolumeMap.ts
var DynamicVolumeMap = class {
  constructor() {
    this.FFFF = 1;
    this.FFF = 0.95;
    this.FF = 0.9;
    this.F = 0.8;
    this.MF = 0.65;
    this.MP = 0.5;
    this.P = 0.35;
    this.PP = 0.25;
    this.PPP = 0.15;
    this.PPPP = 0.1;
    this.DEFAULT = 0.6;
  }
};

// src/data/constants/DynamicType.ts
var _DynamicType = class _DynamicType {
  static getVolumeByType(type) {
    switch (type) {
      case _DynamicType.F:
        return 0.8;
      case _DynamicType.P:
        return 0.35;
      case _DynamicType.FF:
        return 0.9;
      case _DynamicType.PP:
        return 0.25;
      case _DynamicType.MF:
        return 0.65;
      case _DynamicType.MP:
        return 0.5;
      case _DynamicType.FFF:
        return 0.95;
      case _DynamicType.PPP:
        return 0.15;
      case _DynamicType.FFFF:
        return 1;
      case _DynamicType.PPPP:
        return 0.1;
      default:
        return 0.6;
    }
  }
};
_DynamicType.FFFF = "ffff";
_DynamicType.FFF = "fff";
_DynamicType.FF = "ff";
_DynamicType.F = "f";
_DynamicType.MF = "mf";
_DynamicType.MP = "mp";
_DynamicType.P = "p";
_DynamicType.PP = "pp";
_DynamicType.PPP = "ppp";
_DynamicType.PPPP = "pppp";
_DynamicType.volumeMap = new DynamicVolumeMap();
var DynamicType = _DynamicType;

// src/data/constants/EndingType.ts
var _EndingType = class _EndingType {
  static getType(type) {
    if (type == null)
      return _EndingType.STOP;
    switch (type) {
      case "start":
        return _EndingType.START;
      case "stop":
        return _EndingType.STOP;
      case "discontinue":
        return _EndingType.DISCONTINUE;
      default:
        return _EndingType.STOP;
    }
  }
};
_EndingType.START = 0;
_EndingType.STOP = 1;
_EndingType.DISCONTINUE = 2;
var EndingType = _EndingType;

// src/data/constants/LyricsMode.ts
var LyricsMode = /* @__PURE__ */ ((LyricsMode2) => {
  LyricsMode2[LyricsMode2["SHOW_LYRICS"] = 0] = "SHOW_LYRICS";
  LyricsMode2[LyricsMode2["SHOW_NOTE_NAMES"] = 1] = "SHOW_NOTE_NAMES";
  LyricsMode2[LyricsMode2["HIDE_LYRICS"] = 2] = "HIDE_LYRICS";
  return LyricsMode2;
})(LyricsMode || {});

// src/data/constants/MeasureAttachmentType.ts
var MeasureAttachmentType = /* @__PURE__ */ ((MeasureAttachmentType2) => {
  MeasureAttachmentType2[MeasureAttachmentType2["dynamic"] = 0] = "dynamic";
  MeasureAttachmentType2[MeasureAttachmentType2["chordSymbol"] = 1] = "chordSymbol";
  MeasureAttachmentType2[MeasureAttachmentType2["measureInstruction"] = 2] = "measureInstruction";
  return MeasureAttachmentType2;
})(MeasureAttachmentType || {});

// src/data/constants/OrnamentType.ts
var _OrnamentType = class _OrnamentType {
  static getTypeFromName(name) {
    switch (name) {
      case "inverted-mordent":
        return _OrnamentType.INVERTED_MORDENT;
      case "mordent":
        return _OrnamentType.MORDENT;
      case "trill-mark":
        return _OrnamentType.TRILL_MARK;
      default:
        return _OrnamentType.UNKNOWN;
    }
  }
  static getTypeFromMusicXmlName(name) {
    let type = _OrnamentType.getTypeFromName(name);
    if (type == _OrnamentType.INVERTED_MORDENT) {
      type = _OrnamentType.MORDENT;
    } else if (type == _OrnamentType.MORDENT) {
      type = _OrnamentType.INVERTED_MORDENT;
    }
    return type;
  }
};
_OrnamentType.UNKNOWN = -1;
_OrnamentType.INVERTED_MORDENT = 0;
_OrnamentType.MORDENT = 1;
_OrnamentType.TRILL_MARK = 2;
var OrnamentType = _OrnamentType;

// src/data/constants/RepeatDirection.ts
var _RepeatDirection = class _RepeatDirection {
  static getDirection(direction) {
    if (direction == null)
      return null;
    switch (direction) {
      case "forward":
        return _RepeatDirection.FORWARD;
      case "backward":
        return _RepeatDirection.BACKWARD;
      default:
        return null;
    }
  }
};
_RepeatDirection.FORWARD = 0;
_RepeatDirection.BACKWARD = 1;
var RepeatDirection = _RepeatDirection;

// src/data/constants/StemDirection.ts
var StemDirection = class {
};
StemDirection.UP = "up";
StemDirection.DOWN = "down";
StemDirection.NO_STEM = "noStem";

// src/data/constants/SyllablePosition.ts
var SyllablePosition = class {
};
SyllablePosition.BEGIN = "begin";
SyllablePosition.MIDDLE = "middle";
SyllablePosition.END = "end";

// src/data/constants/TieState.ts
var TieState = class {
};
TieState.NONE = "none";
TieState.START = "start";
TieState.STOP = "stop";
TieState.CONTINUE = "continue";

// src/data/io/MeasureData.ts
var MeasureData = class {
};

// src/data/io/VoiceData.ts
var VoiceData = class {
};

// src/data/containers/Voice.ts
var Voice = class extends BaseDataObject {
  constructor(number) {
    super();
    this._noteGroups = [];
    //the voice number (voice 1, voice 2, etc.)
    this._number = 0;
    this._number = number;
  }
  toVoiceData() {
    let data = new VoiceData();
    data.noteGroups = this._noteGroups.map((ng) => ng.toNoteGroupData());
    data.number = this._number;
    return data;
  }
  addNoteGroup(noteGroup) {
    noteGroup.voice = this;
    this._noteGroups.push(noteGroup);
    if (noteGroup.tuplets != null) {
    }
  }
  /**
   * removes the NoteGroupContainer and all child NoteGroups and 
   * NoteGroupContainers. If the parent NGC of this NGC is empty, it too is 
   * removed. This check is done recursively in both directions (for children
   * and parents)
   * @param ngc the NoteGroupContainer you want to remove
   * @param removeParentIfEmpty if true, if removing the ngc leaves its parent
   * with an empty ngcList, the parentNGC will be removed
   * @return returns a list of NoteGroup objects that were removed
   */
  removeNoteGroupContainer(ngc, removeParentIfEmpty = true) {
    let removedNoteGroups = [];
    if (ngc.noteGroup != null) {
      removedNoteGroups.push(ngc.noteGroup);
      this._noteGroups.splice(this._noteGroups.indexOf(ngc.noteGroup), 1);
    } else {
      let ngcList = ngc.ngcList;
      for (let i = ngcList.length - 1; i >= 0; i--) {
        removedNoteGroups.push(...this.removeNoteGroupContainer(
          ngcList[i],
          false
        ));
      }
    }
    if (ngc.parentNGC != null) {
      let parentNGCList = ngc.parentNGC.ngcList;
      parentNGCList.splice(parentNGCList.indexOf(ngc), 1);
      if (parentNGCList.length === 0 && removeParentIfEmpty) {
        this.removeNoteGroupContainer(ngc.parentNGC);
      }
    } else {
      this._noteGroupContainers.splice(
        this._noteGroupContainers.indexOf(ngc),
        1
      );
    }
    return removedNoteGroups;
  }
  /*public function clone():Voice {
  	let voice:Voice = new Voice(_number);
  	
  	return voice;
  }*/
  get noteGroups() {
    return this._noteGroups;
  }
  get noteGroupContainers() {
    return this._noteGroupContainers;
  }
  set noteGroupContainers(value) {
    this._noteGroupContainers = value;
  }
  get number() {
    return this._number;
  }
  get measure() {
    return this._measure;
  }
  set measure(value) {
    this._measure = value;
  }
  get isEmptyOrSingleRest() {
    if (this._noteGroups.length > 1 || !this._noteGroups[0].isRest) {
      return false;
    }
    return true;
  }
};

// src/data/containers/Measure.ts
var Measure = class extends BaseDataObject {
  constructor() {
    super();
    //voices/layers
    this._voices = [];
    ////////////KEY SIG////////////
    //the number of diatonic steps the transposed pitches appear above concert 
    //pitch
    this._diatonicTransposition = 0;
    //the number of chromatic steps the transposed pitches appear above concert 
    //pitch
    this._chromaticTransposition = 0;
    //the untransposed key for this measure
    this._concertKey = 0;
    //key value that is displayed (includes transposition) - 0 = C, -1 = 1 flat,
    //1 = 1 sharp
    this._displayKey = 0;
    //the display key signature from the previous measure - only present if the 
    //measure follows a measure that was in a different key
    this._outgoingKey = 0;
    //true if the key signature is major, false if it's minor
    this._isMajorKey = false;
    //true if the key signature should be rendered for this measure
    this._showKey = false;
    ////////////TIME SIG////////////
    //time sig number beats
    this._numBeats = 4;
    //time sig beat type
    this._beatType = 4;
    /**ex. [0, 1, 2, 3] for 4/4 or [0, 1.5] for 6/8. Lists the counts (in 
     * qNotes) that the main beats in the measure fall on*/
    this._beatGroups = [0, 1, 2, 3];
    /**ex. [0.5, 1.5, 2.5, 3.5] for 4/4 or [0.5, 1.0, 2.0, 2.5] for 6/8. Lists
    * 	 *the counts (in qNotes that the sub beats in the measure fall on)*/
    this._subBeatGroups = [];
    //true if the time signature should be rendered for this measure
    this._showTime = false;
    ///////////CLEFS/////////
    //holds Clef objects for this measure
    this._clefs = [];
    //a list of the MeasureAttachment objects attached to this measure
    this._attachments = [];
    //if true, the NoteGroup objects should be re-rendered for this measure 
    //during the next render
    this._notesNeedRendering = true;
  }
  toMeasureData() {
    let data = new MeasureData();
    data.voices = this._voices.map((v) => v.toVoiceData());
    data.diatonicTransposition = this._diatonicTransposition;
    data.chromaticTransposition = this._chromaticTransposition;
    data.concertKey = this._concertKey;
    data.displayKey = this._displayKey;
    data.outgoingKey = this._outgoingKey;
    data.isMajorKey = this._isMajorKey;
    data.showKey = this._showKey;
    data.numBeats = this._numBeats;
    data.beatType = this._beatType;
    data.displayNumBeats = this._displayNumBeats;
    data.displayBeatType = this._displayBeatType;
    data.beatGroups = this._beatGroups.slice();
    data.subBeatGroups = this._subBeatGroups.slice();
    data.showTime = this._showTime;
    data.clefs = this._clefs.map((c) => c.toClefData());
    data.attachments = this._attachments.map((a) => a.toMeasureAttachmentData());
    return data;
  }
  /**
   * gets the Voice object with the number matching the value passed in. If it
   * doesn't exist, a new Voice object is created and returned
   * @param	voiceNumber the number of the voice, typically taken from the 
   * musicXML
   * @return	a Voice object
   */
  getVoiceByNumber(voiceNumber) {
    for (let voice of this._voices) {
      if (voice.number === voiceNumber) {
        return voice;
      }
    }
    let newVoice = new Voice(voiceNumber);
    newVoice.measure = this;
    this._voices.push(newVoice);
    return newVoice;
  }
  /**
   * the untransposed key for this measure - positive value indicates sharps, 
   * negative indicates flats
   */
  get concertKey() {
    return this._concertKey;
  }
  set concertKey(value) {
    this._concertKey = value;
  }
  /**
   * indicates the number of accidentals displayed for the key signature 
   * (includes transposition) - positive value indicates sharps, negative 
   * indicates flats
   */
  get displayKey() {
    return this._displayKey;
  }
  set displayKey(value) {
    this._displayKey = value;
  }
  /**
   * the display key signature from the previous measure - only present if the
   * measure follows a measure that was in a different key
   */
  get outgoingKey() {
    return this._outgoingKey;
  }
  set outgoingKey(value) {
    this._outgoingKey = value;
  }
  /**
   * the number of diatonic steps the transposed pitches appear above concert 
   * pitch
   */
  get diatonicTransposition() {
    return this._diatonicTransposition;
  }
  set diatonicTransposition(value) {
    this._diatonicTransposition = value;
  }
  /**
   * the number of chromatic steps the transposed pitches appear above concert
   * pitch
   */
  get chromaticTransposition() {
    return this._chromaticTransposition;
  }
  set chromaticTransposition(value) {
    this._chromaticTransposition = value;
  }
  /**
   * true if the key signature is major, false if it's minor
   */
  get isMajorKey() {
    return this._isMajorKey;
  }
  set isMajorKey(value) {
    this._isMajorKey = value;
  }
  get numBeats() {
    return this._numBeats;
  }
  set numBeats(value) {
    this._numBeats = value;
  }
  get beatType() {
    return this._beatType;
  }
  set beatType(value) {
    this._beatType = value;
  }
  /**if not null, this value will be used for rendering display*/
  get displayNumBeats() {
    return this._displayNumBeats;
  }
  set displayNumBeats(value) {
    this._displayNumBeats = value;
  }
  /**if not null, this value will be used for rendering diplay */
  get displayBeatType() {
    return this._displayBeatType;
  }
  set displayBeatType(value) {
    this._displayBeatType = value;
  }
  /**
   * ex. [0, 1, 2, 3] for 4/4 or [0, 1.5] for 6/8. Lists the counts (in 
   * qNotes) that the main beats in the measure fall on
   */
  get beatGroups() {
    return this._beatGroups;
  }
  set beatGroups(value) {
    this._beatGroups = value;
  }
  /**ex. [0.5, 1.5, 2.5, 3.5] for 4/4 or [0.5, 1.0, 2.0, 2.5] for 6/8. Lists
  *the counts (in qNotes that the sub beats in the measure fall on)*/
  get subBeatGroups() {
    return this._subBeatGroups;
  }
  set subBeatGroups(value) {
    this._subBeatGroups = value;
  }
  /**gets the duration of a beat in quarter notes*/
  get beatQnoteDuration() {
    return this._beatType % 8 == 0 ? 4 / this._beatType * 3 : 4 / this._beatType;
  }
  /**gets the duration of a sub beat in quarter notes*/
  get subBeatQnoteDuration() {
    return this._beatType % 8 == 0 ? 4 / this._beatType : 2 / this._beatType;
  }
  /**
   * holds Clef objects for this measure
   */
  get clefs() {
    return this._clefs;
  }
  set clefs(value) {
    this._clefs = value;
  }
  get voices() {
    return this._voices;
  }
  /**if true, the time sig will be rendered and will affect spacing*/
  get showTime() {
    return this._showTime;
  }
  set showTime(value) {
    this._showTime = value;
  }
  get showKey() {
    return this._showKey;
  }
  set showKey(value) {
    this._showKey = value;
  }
  get stack() {
    return this._stack;
  }
  set stack(value) {
    this._stack = value;
  }
  get staff() {
    return this._staff;
  }
  set staff(value) {
    this._staff = value;
  }
  /**
   * a list of the MeasureAttachment objects attached to this measure
   */
  get attachments() {
    return this._attachments;
  }
  set attachments(value) {
    this._attachments = value;
  }
  /**
   * calculates the total note duration (how full the measure is). 
   * A full bar would be 4 * (numBeats / beatType) QNotes.
   */
  get totalNoteDuration() {
    let lastNG = this._voices[0].noteGroups[this._voices[0].noteGroups.length - 1];
    return lastNG.qNoteTime + lastNG.qNoteDuration;
  }
  /**
   * if true, the NoteGroup objects should be re-rendered for this measure 
   * during the next render
   */
  get notesNeedRendering() {
    return this._notesNeedRendering;
  }
  set notesNeedRendering(value) {
    this._notesNeedRendering = value;
    if (value == true) {
      if (this.stack != null) {
        this.stack.needsRendering = true;
      }
    }
  }
  /**
   * returns the measure that follows this one - not stored as a property, but
   * rather searched for dynamically
   */
  get next() {
    let measures = this._staff.measures;
    let numMeasures = measures.length;
    for (let i = 0; i < numMeasures; i++) {
      if (measures[i] == this) {
        return i < numMeasures - 1 ? measures[i + 1] : null;
      }
    }
    return null;
  }
  /**
   * returns the measure that precedes this one - not stored as a property, 
   * but rather searched for dynamically
   */
  get prev() {
    let measures = this._staff.measures;
    let numMeasures = measures.length;
    for (let i = numMeasures - 1; i >= 0; i--) {
      if (measures[i] == this) {
        return i > 0 ? measures[i - 1] : null;
      }
    }
    return null;
  }
  /**
   * returns the measure that is below this one in the stack - not stored as a
   * property, but rather searched for dynamically
   */
  get below() {
    let measures = this._stack.measures;
    let numMeasures = measures.length;
    for (let i = 0; i < numMeasures; i++) {
      if (measures[i] == this) {
        return i < numMeasures - 1 ? measures[i + 1] : null;
      }
    }
    return null;
  }
  /**
   * returns the measure that is above this one in the stack - not stored as a
   * property, but rather searched for dynamically
   */
  get above() {
    let measures = this._stack.measures;
    let numMeasures = measures.length;
    for (let i = numMeasures - 1; i >= 0; i--) {
      if (measures[i] == this) {
        return i > 0 ? measures[i - 1] : null;
      }
    }
    return null;
  }
};

// src/data/io/MeasureStackData.ts
var MeasureStackData = class {
};

// src/data/containers/MeasureStack.ts
var MeasureStack = class extends BaseDataObject {
  constructor() {
    super();
    //all of the NoteGroup objects in the stack
    this._noteGroups = [];
    //all of the Measure objects in the stack
    this._measures = [];
    ////////////QNOTE TIME MARKERS//////////
    //the start time of the measure stack, in qNoteTime
    this._startTime = 0;
    //the end time of the measure stack, in qNoteTime
    this._endTime = 0;
    //the length of the measure stack, in qNotes
    this._qNoteLength = 0;
    //begins a new system
    this._newSystem = false;
    //actual width of the measure
    this._width = 200;
    //the indentation before the music - for keys, clefs, time sig, and standard
    //offset. This doesn't scale with measure width when calculating actual 
    //positions.
    this._indent = 0;
    //the indentation before the music if this stack happens to be the first 
    //stack in its system (clef and key are required)
    this._indentAsSystemLeader = 0;
    //distance from the left edge of the system to the beginning of this measure
    this._startPosition = 0;
    //the measure number
    this._number = 1;
    //////////SPACING CONSIDERATION VALUES//////////////
    //the amount of horizontal space the stack would consume if no other stacks 
    //or factors had to be considered
    this._idealWidth = 0;
    //the highest number of sharps or flats any measure within the stack has (to
    //account for transposing staves with differing key sigs)
    this._maxKeySize = 0;
    //set to true if there is a new key in the bar that must be shown, 
    //regardless of whether this measure is first in a system.
    this._newKey = false;
    //set to true if there is a new time sig in the bar that must be shown, 
    //regardless of whether this measure is first in a system.
    //private _newTime:boolean = false; 
    //set to true if there is a new clef in the bar that must be shown, 
    //regardless of whether this measure is first in a system.
    this._newClef = false;
    //true if the stack is a pickup measure stack
    this._isPickup = false;
    //true if there is at least one Measure in the stack that needs to be
    //rendered again
    this._needsRendering = true;
  }
  toMeasureStackData() {
    let data = new MeasureStackData();
    data.startTime = this._startTime;
    data.endTime = this._endTime;
    data.qNoteLength = this._qNoteLength;
    data.number = this._number;
    data.newKey = this._newKey;
    data.newClef = this._newClef;
    data.tempoMarkers = this._tempoMarkers?.map((tm) => tm.toTempoMarkerData());
    data.barline = this.barline?.toBarlineData();
    data.repeatDOs = this.repeatDOs?.map((r) => r.toRepeatDOData());
    data.isPickup = this._isPickup;
    return data;
  }
  /**
   * adds the NoteGroup object to the stack in order by its time
   * @param	noteGroup the NoteGroup to add - must have its startTime 
   * property set.
   */
  addNoteGroup(noteGroup) {
    let numNotes = this._noteGroups.length;
    let i = 0;
    for (i = 0; i < numNotes; i++) {
      let cNoteGroup = this._noteGroups[i];
      if (noteGroup.qNoteTime < cNoteGroup.qNoteTime) {
        this._noteGroups.splice(i, 0, noteGroup);
        return;
      }
    }
    this._noteGroups.push(noteGroup);
  }
  /**
   * adds the Measure object to the stack - this does NOT add the NoteGroups 
   * to the noteGroups list - use addNoteGroupsFromMeasure() for that
   * @param	measure
   */
  addMeasure(measure) {
    this._measures.push(measure);
  }
  /**
   * adds all of the NoteGroup objects in the passed in measure. NoteGroups 
   * are sorted by time against pre-existing NoteGroups
   * @param	measure the Measure object to scan for NoteGroup objects
   */
  addNoteGroupsFromMeasure(measure) {
    for (let voice of measure.voices) {
      for (let noteGroup of voice.noteGroups) {
        this.addNoteGroup(noteGroup);
      }
    }
  }
  /**
   * removes the passed in NoteGroup from the MeasureStack
   * @param noteGroup the NoteGroup to remove
   */
  removeNoteGroup(noteGroup) {
    let numNoteGroups = this._noteGroups.length;
    for (let i = 0; i < numNoteGroups; i++) {
      if (this._noteGroups[i] === noteGroup) {
        this._noteGroups.splice(i, 1);
        break;
      }
    }
  }
  /**
   * clears the NoteGroups (usually used prior to adding NoteGroup objects for
   * the current set of parts)
   */
  clearNoteGroups() {
    this._noteGroups = [];
  }
  /**
   * clears the Measures (usually used prior to adding Measure objects for the current set of parts)
   */
  clearMeasures() {
    this._measures = [];
  }
  ///Retrieves the most recent [TempoMarker] that indicates the tempo at the
  ///start time of this [MeasureStack]. If this stack has a marker at the
  ///beginning of it, that marker will be returned. Otherwise it will look
  ///backwards through previous stacks until it finds the most recent marker.
  ///If no marker is found, null is returned.
  getInheritedTempoMarker() {
    if (this._tempoMarkers != null && this._tempoMarkers.length > 0 && this._tempoMarkers[0].qNoteTime == this._startTime) {
      return this._tempoMarkers[0];
    }
    let cStack = this._previous;
    while (cStack != null) {
      if (cStack._tempoMarkers != null && cStack._tempoMarkers.length > 0) {
        return cStack._tempoMarkers[cStack._tempoMarkers.length - 1];
      }
      cStack = cStack.previous;
    }
    return null;
  }
  ///Creates and returns a list of all the [MeasureAttachment] objects
  ///associated with [Measure]s in this stack
  ///@param sortByQNoteTime if true, sorts attachments by ascending qNoteTime
  getMeasureAttachments(sortByQNoteTime) {
    let attachments = [];
    for (let measure of this._measures) {
      attachments.push(...measure.attachments);
    }
    if (sortByQNoteTime) {
      attachments.sort((a1, a2) => a1.qNoteTime <= a2.qNoteTime ? -1 : 1);
    }
    return attachments;
  }
  get noteGroups() {
    return this._noteGroups;
  }
  get measures() {
    return this._measures;
  }
  get qNoteLength() {
    return this._qNoteLength;
  }
  set qNoteLength(value) {
    this._qNoteLength = value;
  }
  get startTime() {
    return this._startTime;
  }
  set startTime(value) {
    this._startTime = value;
    this._qNoteLength = this._endTime - this._startTime;
  }
  get endTime() {
    return this._endTime;
  }
  set endTime(value) {
    this._endTime = value;
    this._qNoteLength = this._endTime - this._startTime;
  }
  get beatQNotePositions() {
    return this._beatQNotePositions;
  }
  set beatQNotePositions(value) {
    this._beatQNotePositions = value;
  }
  get newSystem() {
    return this._newSystem;
  }
  set newSystem(value) {
    this._newSystem = value;
  }
  get width() {
    return this._width;
  }
  set width(value) {
    this._width = value;
  }
  /**
   * The normal indent for this stack if it is not the first stack in a 
   * system. Clefs and Keys are counted only if they are new (and thus must be
   * shown)
   */
  get indent() {
    return this._indent;
  }
  set indent(value) {
    this._indent = value;
  }
  /**
   * the indentation before the music if this stack happens to be the first 
   * stack in its system (clef and key are required)
   */
  get indentAsSystemLeader() {
    return this._indentAsSystemLeader;
  }
  set indentAsSystemLeader(value) {
    this._indentAsSystemLeader = value;
  }
  get startPosition() {
    return this._startPosition;
  }
  set startPosition(value) {
    this._startPosition = value;
  }
  get number() {
    return this._number;
  }
  set number(value) {
    this._number = value;
  }
  get idealWidth() {
    return this._idealWidth;
  }
  set idealWidth(value) {
    this._idealWidth = value;
  }
  get maxKeySize() {
    return this._maxKeySize;
  }
  set maxKeySize(value) {
    this._maxKeySize = value;
  }
  get newKey() {
    return this._newKey;
  }
  set newKey(value) {
    this._newKey = value;
  }
  //bool get newTime { return _newTime; }		
  //void set newTime(bool value) { _newTime = value;	}
  get newClef() {
    return this._newClef;
  }
  set newClef(value) {
    this._newClef = value;
  }
  /**
   * //holds list of TempoMarker objects that take effect starting with this 
   * stack
   */
  get tempoMarkers() {
    return this._tempoMarkers;
  }
  set tempoMarkers(value) {
    this._tempoMarkers = value;
  }
  get previous() {
    return this._previous;
  }
  set previous(value) {
    this._previous = value;
  }
  get next() {
    return this._next;
  }
  set next(value) {
    this._next = value;
  }
  get systemRef() {
    return this._systemRef;
  }
  set systemRef(value) {
    this._systemRef = value;
  }
  /**
   * true if the stack is a pickup measure stack
   */
  get isPickup() {
    return this._isPickup;
  }
  set isPickup(value) {
    this._isPickup = value;
  }
  /**
   * true if there is at least one Measure in the stack that needs to be 
   * rendered again
   */
  get needsRendering() {
    return this._needsRendering;
  }
  set needsRendering(value) {
    this._needsRendering = value;
    if (value == true && this.systemRef != null) {
      this.systemRef.needsRendering = true;
    }
  }
};

// src/data/io/NoteGroupData.ts
var NoteGroupData = class {
};

// src/data/containers/NoteGroup.ts
var NoteGroup = class extends BaseDataObject {
  constructor() {
    super();
    //the notes that make up this NoteGroup
    this._notes = [];
    //horizontal position of this NoteGroup. Variations of individual notes 
    //within this group are defined in the Note class
    this._hPos = 0;
    //"noStem", "up", or "down"
    this._stemDirection = StemDirection.NO_STEM;
    //the vertical position of the end of the stem measured from the top staff 
    //line. Negative values are above the top staff line.
    this._stemEndPos = 0;
    //the horizontal position of the stem
    this._stemHPos = 0;
    //duration in duration unit values - 1024 is a quarter note. This value DOES
    //NOT have the tuplet ratio applied to it, so it must be converted for 
    //actual length
    this._duration = 1024;
    //duration in terms of qNotes.
    this._qNoteDuration = 1;
    //the start time of this note expressed in qNotes
    this._qNoteTime = 0;
    //"quarter", "eighth", etc. - the shape of the note - does NOT differentiate
    //things like "triplet"
    this._durationType = DurationType.QUARTER;
    //number of augmentation dots
    this._numDots = 0;
    //true if it's a grace note
    this._isGrace = false;
    //an array of strings conforming to constants from 
    //nEngine.data.symbols.BeamState - the order should match outer beam, second
    //beam, third beam, etc.
    this._beamStates = [];
    //the number of beams this note could potentially support (0 for a quarter 
    //or bigger, 1 for an eighth, 2 for a 16th note, etc.)
    this._maxBeams = 0;
    //the number of accidental level positions needed for this chord (for 
    //spacing purposes)
    this._numAccidentalLevels = 0;
    //true if it's a rest
    this._isRest = false;
    //the vertical position of the rest (if it's a rest)
    this._restVPos = 0;
    //true if this note starts a clip
    this._clipStart = false;
    //true if this note ends a clip
    this._clipEnd = false;
    //the position this note would occupy within its measure if the measure was
    //given its ideal amount of spacing
    this._idealHPos = 0;
    //the playback start time for this note, in seconds, from the beginning of
    //the piece.
    this._playbackStartTime = 0;
    //true if this noteGroup should be displayed
    this._visible = true;
    //stores the previous visibility state of the NoteGroup
    this._prevVisible = null;
  }
  toNoteGroupData() {
    let data = new NoteGroupData();
    data.uniqueID = this.uniqueID;
    data.notes = this.notes.map((n) => n.toNoteData());
    data.stemDirection = this._stemDirection;
    data.duration = this._duration;
    data.qNoteDuration = this._qNoteDuration;
    data.qNoteTime = this._qNoteTime;
    data.durationType = this._durationType;
    data.numDots = this._numDots;
    data.isGrace = this._isGrace;
    data.beamStates = this._beamStates.slice(0);
    data.isRest = this._isRest;
    data.tuplets = this._tuplets?.map((t) => t.toTupletData());
    data.slurs = this._slurs?.map((s) => s.toSlurData());
    data.clefUniqueID = this._clef?.uniqueID;
    data.lyric = this._lyric?.toLyricData();
    data.clipStart = this._clipStart;
    data.clipEnd = this._clipEnd;
    data.articulations = this._articulations?.map((a) => a.toArticulationData());
    data.ornaments = this._ornaments?.map((o) => o.toOrnamentData());
    data.visible = this._visible;
    data.trackId = this._trackId;
    return data;
  }
  /**
   * adds the Note to the NoteGroup at the last position - this does NOT sort
   *  the Note by pitch (use insertNote() for that).
   * @param note the Note object to add
   */
  addNote(note) {
    this._notes.push(note);
    note.noteGroup = this;
  }
  /**
   * adds the Note to the NoteGroup in its order by pitch - lowest to highest
   * @param note the Note object to add/insert
   */
  insertNote(note) {
    let numNotes = this._notes.length;
    let i = 0;
    for (i = 0; i < numNotes; i++) {
      if (this._notes[i].stepsFromTopStaffLine < note.stepsFromTopStaffLine) {
        break;
      }
    }
    this._notes.splice(i, 0, note);
    note.noteGroup = this;
  }
  /**sorts the Notes for this NoteGroup from lowest to highest */
  sortNotes() {
    this._notes.sort((n1, n2) => n1.stepsFromTopStaffLine >= n2.stepsFromTopStaffLine ? -1 : 1);
  }
  /**
   * removes the Note from the NoteGroup
   * @param note the Note object to remove
   */
  removeNote(note) {
    let numNotes = this._notes.length;
    for (let i = 0; i < numNotes; i++) {
      if (this._notes[i] == note) {
        this._notes.splice(i, 1);
        if (note.noteGroup == this) {
          note.noteGroup = null;
        }
        break;
      }
    }
  }
  /**
   * sorts the notes by pitch so that the lowest note is first in the list
   */
  sortNotesByPitch() {
    this._notes.sort((n1, n2) => {
      return n1.displayCents <= n2.displayCents ? -1 : 1;
    });
  }
  clearNextPrevNGCaches() {
    this._cachedNextNG = null;
    this._cachedPrevNG = null;
  }
  clearVisibleNotesCache() {
    this._visibleNotesCache = null;
  }
  /**reverts the visibility of the NG to its last state. Calling multiple
   * times will have no additional effect (not a complete undo history).*/
  revertVisibility() {
    if (this._prevVisible != null) {
      this._visible = this._prevVisible;
      this._prevVisible = null;
    }
  }
  get notes() {
    return this._notes;
  }
  /**Gets the list of notes, in bottom to top order, that are currently 
   * visible. When changing notes' visibility property, be sure to call 
   * clearVisibleNotesCache();*/
  get visibleNotes() {
    if (this._visibleNotesCache != null) {
      return this._visibleNotesCache;
    }
    if (this._isRest) {
      this._visibleNotesCache = null;
      return null;
    }
    this._visibleNotesCache = [];
    for (let note of this._notes) {
      if (note.visible) {
        this._visibleNotesCache.push(note);
      }
    }
    return this._visibleNotesCache;
  }
  get isRest() {
    return this._isRest;
  }
  set isRest(value) {
    this._isRest = value;
  }
  get restVPos() {
    return this._restVPos;
  }
  set restVPos(value) {
    this._restVPos = value;
  }
  get numDots() {
    return this._numDots;
  }
  set numDots(value) {
    this._numDots = value;
  }
  get durationType() {
    return this._durationType;
  }
  set durationType(value) {
    this._durationType = value;
    if (value === DurationType.EIGHTH) {
      this._maxBeams = 1;
    } else if (value === DurationType.SIXTEENTH) {
      this._maxBeams = 2;
    } else if (value === DurationType.THIRTY_SECOND) {
      this._maxBeams = 3;
    } else if (value === DurationType.SIXTY_FOURTH) {
      this._maxBeams = 4;
    } else {
      this._maxBeams = 0;
    }
  }
  /**
   * duration in duration unit values - 1024 is a quarter note. This value 
   * DOES NOT have the tuplet ratio applied to it, so it must be converted for
   * actual length
   */
  get duration() {
    return this._duration;
  }
  set duration(value) {
    this._duration = value;
  }
  /**
   * /duration in terms of qNotes.
   */
  get qNoteDuration() {
    return this._qNoteDuration;
  }
  set qNoteDuration(value) {
    this._qNoteDuration = value;
  }
  get qNoteTime() {
    return this._qNoteTime;
  }
  set qNoteTime(value) {
    this._qNoteTime = value;
  }
  get isGrace() {
    return this._isGrace;
  }
  set isGrace(value) {
    this._isGrace = value;
  }
  get stem() {
    return this._stem;
  }
  set stem(value) {
    this._stem = value;
  }
  /**
   * the vertical position of the start of the stem (at the notehead) measured
   * from the top staff line - negative values are below the top staff line
   */
  get stemStartPos() {
    return this._stemStartPos;
  }
  set stemStartPos(value) {
    this._stemStartPos = value;
  }
  get stemEndPos() {
    return this._stemEndPos;
  }
  set stemEndPos(value) {
    this._stemEndPos = value;
  }
  /**
   * the horizontal position of the stem
   */
  get stemHPos() {
    return this._stemHPos;
  }
  set stemHPos(value) {
    this._stemHPos = value;
  }
  get stemDirection() {
    return this._stemDirection;
  }
  set stemDirection(value) {
    this._stemDirection = value;
  }
  get beamStates() {
    return this._beamStates;
  }
  set beamStates(value) {
    this._beamStates = value;
  }
  /**
   * the number of beams this note could potentially support (0 for a quarter
   * or bigger, 1 for an eighth, 2 for a 16th note, etc.)
   */
  get maxBeams() {
    return this._maxBeams;
  }
  set maxBeams(value) {
    this._maxBeams = value;
  }
  /**
   * the number of accidental level positions needed for this chord (for 
   * spacing purposes)
   */
  get numAccidentalLevels() {
    return this._numAccidentalLevels;
  }
  set numAccidentalLevels(value) {
    this._numAccidentalLevels = value;
  }
  get hPos() {
    return this._hPos;
  }
  set hPos(value) {
    this._hPos = value;
  }
  get tuplets() {
    return this._tuplets;
  }
  set tuplets(value) {
    this._tuplets = value;
  }
  /**
   * contains all Slur objects that start or stop on this note
   */
  get slurs() {
    return this._slurs;
  }
  set slurs(value) {
    this._slurs = value;
  }
  /**
   * if this note is the first note after the start of a mid-measure clef, 
   * this holds a reference to that Clef - needed for spacing calculation
   */
  get clef() {
    return this._clef;
  }
  set clef(value) {
    this._clef = value;
  }
  get lyric() {
    return this._lyric;
  }
  set lyric(value) {
    this._lyric = value;
  }
  get clipStart() {
    return this._clipStart;
  }
  set clipStart(value) {
    this._clipStart = value;
  }
  get clipEnd() {
    return this._clipEnd;
  }
  set clipEnd(value) {
    this._clipEnd = value;
  }
  get idealHPos() {
    return this._idealHPos;
  }
  set idealHPos(value) {
    this._idealHPos = value;
  }
  get voice() {
    return this._voice;
  }
  set voice(value) {
    this._voice = value;
  }
  /**
   * the NoteGroupContainer this NoteGroup belongs to
   */
  get ngcRef() {
    return this._ngcRef;
  }
  set ngcRef(value) {
    this._ngcRef = value;
  }
  /**
   * returns the NoteGroup that follows this one in the same voice - not 
   * stored as a property, but rather searched for dynamically
   */
  get next() {
    if (this._cachedNextNG != null) {
      return this._cachedNextNG;
    }
    let noteGroups = this._voice.noteGroups;
    let numNoteGroups = noteGroups.length;
    for (let i = 0; i < numNoteGroups; i++) {
      if (noteGroups[i] === this) {
        if (i < numNoteGroups - 1) {
          this._cachedNextNG = noteGroups[i + 1];
          return this._cachedNextNG;
        }
      }
    }
    let voiceNum = this._voice.number;
    let nextMeasure = this._voice.measure.next;
    while (nextMeasure != null) {
      let voices = nextMeasure.voices;
      for (let nextVoice of voices) {
        if (nextVoice.number === voiceNum) {
          if (nextVoice.noteGroups.length > 0) {
            this._cachedNextNG = nextVoice.noteGroups[0];
            return this._cachedNextNG;
          }
        }
      }
      nextMeasure = nextMeasure.next;
    }
    return null;
  }
  /**
   * returns the NoteGroup that precedes this one in the same voice - not 
   * stored as a property, but rather searched for dynamically
   */
  get prev() {
    if (this._cachedPrevNG != null) {
      return this._cachedPrevNG;
    }
    let noteGroups = this._voice.noteGroups;
    let numNoteGroups = noteGroups.length;
    for (let i = numNoteGroups - 1; i >= 0; i--) {
      if (noteGroups[i] === this) {
        if (i > 0) {
          this._cachedPrevNG = noteGroups[i - 1];
          return this._cachedPrevNG;
        }
      }
    }
    let voiceNum = this._voice.number;
    let prevMeasure = this._voice.measure.prev;
    while (prevMeasure != null) {
      let voices = prevMeasure.voices;
      for (let prevVoice of voices) {
        if (prevVoice.number === voiceNum) {
          if (prevVoice.noteGroups.length > 0) {
            this._cachedPrevNG = prevVoice.noteGroups[prevVoice.noteGroups.length - 1];
            return this._cachedPrevNG;
          }
        }
      }
      prevMeasure = this._voice.measure.prev;
    }
    return null;
  }
  /**
   * the playback start time for this note, in seconds, from the beginning of
   * the piece.
   */
  get playbackStartTime() {
    return this._playbackStartTime;
  }
  set playbackStartTime(value) {
    this._playbackStartTime = value;
  }
  /**
   * the current tempo at the time of this note
   */
  get playbackTempo() {
    return this._playbackTempo;
  }
  set playbackTempo(value) {
    this._playbackTempo = value;
  }
  /**
   * a list of Articulation objects attached to this NoteGroup. Null if no 
   * articulations are attached.
   */
  get articulations() {
    return this._articulations;
  }
  set articulations(value) {
    this._articulations = value;
  }
  ///a list of [Ornament] objects attached to this [NoteGroup]. Is Null if no
  ///ornaments are attached.
  get ornaments() {
    return this._ornaments;
  }
  set ornaments(value) {
    this._ornaments = value;
  }
  /**
   * true if this notegroup should be displayed and accounted for in 
   * formatting
   */
  get visible() {
    return this._visible;
  }
  set visible(value) {
    this._prevVisible = this._visible;
    this._visible = value;
  }
  /**if not null, playback notes created from this note should be assigned
   * this track ID for volume control*/
  get trackId() {
    return this._trackId;
  }
  set trackId(value) {
    this._trackId = value;
  }
};

// src/data/containers/NoteGroupContainer.ts
var NoteGroupContainer = class extends BaseDataObject {
  constructor() {
    super();
    //start duration unit time of the container within the containing measure or
    //tuplet
    this._durUnitStartTime = 0;
    //end duration unit time of the container within the containing measure or 
    //tuplet
    this._durUnitEndTime = 0;
  }
  get qNoteStartTime() {
    return this._qNoteStartTime;
  }
  set qNoteStartTime(value) {
    this._qNoteStartTime = value;
  }
  get durUnitStartTime() {
    return this._durUnitStartTime;
  }
  set durUnitStartTime(value) {
    this._durUnitStartTime = value;
  }
  get durUnitEndTime() {
    return this._durUnitEndTime;
  }
  set durUnitEndTime(value) {
    this._durUnitEndTime = value;
  }
  get parentNGC() {
    return this._parentNGC;
  }
  set parentNGC(value) {
    this._parentNGC = value;
  }
  get noteGroup() {
    return this._noteGroup;
  }
  set noteGroup(value) {
    this._noteGroup = value;
  }
  get ngcList() {
    return this._ngcList;
  }
  set ngcList(value) {
    this._ngcList = value;
  }
  get tuplet() {
    return this._tuplet;
  }
  set tuplet(value) {
    this._tuplet = value;
  }
};

// src/data/containers/Page.ts
var Page = class extends BaseDataObject {
  constructor() {
    super();
    this._systems = [];
  }
  addSystem(system) {
    if (this._systems.length > 0) {
      let prevSystem = this._systems[this._systems.length - 1];
      prevSystem.next = system;
      system.previous = prevSystem;
    }
    system.pageRef = this;
    this._systems.push(system);
  }
  /**
   * removes the System from the Page. The System MUST have no remaining 
   * MeasureStacks for this to go through
   * @param cSystem the empty System to remove
   */
  removeSystem(system) {
    if (system.measureStacks.length === 0) {
      for (let i = this._systems.length - 1; i >= 0; i--) {
        if (this._systems[i] === system) {
          this._systems.splice(i, 1);
          if (system.previous != null) {
            if (system.next != null) {
              system.previous.next = system.next;
            } else {
              system.previous.next = null;
            }
          }
          if (system.next != null) {
            if (system.previous != null) {
              system.next.previous = system.previous;
            } else {
              system.next.previous = null;
            }
          }
          return;
        }
      }
    } else {
      console.log("Attempted to remove a System that still contains MeasureStacks!");
    }
  }
  get systems() {
    return this._systems;
  }
  get previous() {
    return this._previous;
  }
  set previous(value) {
    this._previous = value;
  }
  get next() {
    return this._next;
  }
  set next(value) {
    this._next = value;
  }
};

// src/data/io/PartData.ts
var PartData = class {
  static fromJsonString(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (err) {
      console.log(err);
    }
  }
};

// src/data/containers/Part.ts
var Part = class extends BaseDataObject {
  constructor(partID, name, abbreviation) {
    super();
    //private let _divisionsPerQuarterNote:int;
    this._staves = [];
    //number of diatonic steps that this part appears above concert pitch (- 
    //values for appearing below)
    this._diatonicTransposition = 0;
    //number of chromatic steps that this part appears above concert pitch (- 
    //values for appearing below)
    this._chromaticTransposition = 0;
    //true if this part is being rendered - affects whether NoteGroups are 
    //included in MeasureStacks
    this._visible = false;
    //playback settings
    this._midiBank = 0;
    this._midiPreset = 0;
    this._midiChannel = 0;
    this._muted = false;
    this._volume = 0.15;
    this._pan = 0.5;
    //list of assessment instruments assigned to this part
    this._assessmentInstruments = [];
    this._preventTransposition = false;
    ///This is an optional override for controlling the playback pitch of all
    ///notes in the Part. When transposing parts there are times we want the
    ///changes in displayed pitch and part transposition to have less of an
    ///effect on playback. This value can be used by the playback engine to
    ///compensate the playback.
    this._playbackOctaveDelta = 0;
    this._partID = partID;
    this._name = name;
    this._abbreviation = abbreviation;
  }
  toPartData() {
    let data = new PartData();
    data.partID = this._partID;
    data.name = this._name;
    data.abbreviation = this._abbreviation;
    data.staves = this._staves.map((s) => s.toStaffData());
    data.diatonicTransposition = this._diatonicTransposition;
    data.chromaticTransposition = this._chromaticTransposition;
    data.midiBank = this._midiBank;
    data.midiPreset = this._midiPreset;
    data.midiChannel = this._midiChannel;
    data.percussionMap = this._percussionMap?.slice();
    return data;
  }
  addStaff(staff) {
    this._staves.push(staff);
    staff.partRef = this;
  }
  /**
   * gets all of the Measure objects in this Part containing entries between 
   * the start and end times
   */
  getMeasuresByTime(qNoteStartTime, qNoteEndTime = null) {
    let allMeasures = [];
    if (qNoteEndTime == null) {
      let measures = this._staves[0].measures;
      qNoteEndTime = measures[measures.length - 1].stack.endTime;
    }
    let numStaves = this._staves.length;
    for (let i = 0; i < numStaves; i++) {
      let measures = this._staves[i].measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        let measure = measures[j];
        if (measure.stack.endTime <= qNoteStartTime) {
          continue;
        } else if (measure.stack.startTime > qNoteEndTime) {
          break;
        }
        allMeasures.push(measure);
      }
    }
    return allMeasures;
  }
  /**
   * gets all of the NoteGroup objects in this Part between the start and end
   * times
   */
  getNoteGroupsByTime(qNoteStartTime, qNoteEndTime = null) {
    if (qNoteEndTime == null) {
      let measures2 = this._staves[0].measures;
      qNoteEndTime = measures2[measures2.length - 1].stack.endTime;
    }
    let measures = this.getMeasuresByTime(qNoteStartTime, qNoteEndTime);
    let allNoteGroups = [];
    let numMeasures = measures.length;
    for (let i = 0; i < numMeasures; i++) {
      let voices = measures[i].voices;
      for (let j = 0; j < voices.length; j++) {
        let noteGroups = voices[j].noteGroups;
        let numNoteGroups = noteGroups.length;
        for (let k = 0; k < numNoteGroups; k++) {
          if (i > 0 && i < numMeasures - 1 || noteGroups[k].qNoteTime >= qNoteStartTime && noteGroups[k].qNoteTime < qNoteEndTime) {
            allNoteGroups.push(noteGroups[k]);
          }
        }
      }
    }
    return allNoteGroups;
  }
  /**
   * returns a Array<NoteGroup> with all NoteGroup objects that are not rests
   * from a single staff and voice (noteGroupsExcludingRests property differs
   * as it returns all NoteGroups without rests for all staves and voices)
   */
  getNoteGroupsWithoutRests(staffIndex = 0, voiceIndex = 0) {
    let ngList = [];
    let measures = this._staves[staffIndex].measures;
    for (let measure of measures) {
      let noteGroups = measure.voices[voiceIndex].noteGroups;
      for (let ng of noteGroups) {
        if (!ng.isRest) {
          ngList.push(ng);
        }
      }
    }
    return ngList;
  }
  /**
   * returns a Array<NoteGroup> with all NoteGroup objects that are neither
   * rests nor the target of ties (NG's that begin ties are included)
   */
  getNoteGroupsWithoutRestsOrTieIntos(staffIndex = 0, voiceIndex = 0) {
    let ngList = [];
    let measures = this._staves[staffIndex].measures;
    for (let measure of measures) {
      let noteGroups = measure.voices[voiceIndex].noteGroups;
      for (let ng of noteGroups) {
        if (!ng.isRest && ng.visibleNotes[0].tieState !== TieState.CONTINUE && ng.visibleNotes[0].tieState !== TieState.STOP) {
          ngList.push(ng);
        }
      }
    }
    return ngList;
  }
  getFirstNote(staffIndex = 0, voiceIndex = 0) {
    return this._staves[staffIndex].measures[0].voices[voiceIndex].noteGroups[0];
  }
  /**
   * sets number of diatonic and chromatic steps that this part appears above
   * concert pitch (- values for appearing below)
   * for sounding pitch, the effect is reversed (appearing above concert pitch
   * means sounding below)
   * @updateMeasures - Measure.diatonicTransposition should usually be kept in
   * sync with Part - this does it.
   */
  setTransposition(diatonic, chromatic, updateMeasures = true) {
    this._diatonicTransposition = diatonic;
    this._chromaticTransposition = chromatic;
    if (updateMeasures) {
      let numStaves = this._staves.length;
      for (let i = 0; i < numStaves; i++) {
        let measures = this._staves[i].measures;
        let numMeasures = measures.length;
        for (let j = 0; j < numMeasures; j++) {
          let measure = measures[j];
          measure.diatonicTransposition = diatonic;
          measure.chromaticTransposition = chromatic;
        }
      }
    }
  }
  get staves() {
    return this._staves;
  }
  /**
   * ID for this Part
   */
  get partID() {
    return this._partID;
  }
  get name() {
    return this._name;
  }
  set name(value) {
    this._name = value;
  }
  get abbreviation() {
    return this._abbreviation;
  }
  set abbreviation(value) {
    this._abbreviation = value;
  }
  /**
   * number of diatonic steps that this part appears above concert pitch 
   * (- values for appearing below)
   * for sounding pitch, the effect is reversed (appearing above concert pitch
   * means sounding below)
   */
  get diatonicTransposition() {
    return this._diatonicTransposition;
  }
  //void set diatonicTransposition(number value) { 
  //_diatonicTransposition = value; 
  //}
  /**
   * number of chromatic steps that this part appears above concert pitch 
   * (- values for appearing below)
   * for sounding pitch, the effect is reversed (appearing above concert pitch
   * means sounding below)
   */
  get chromaticTransposition() {
    return this._chromaticTransposition;
  }
  //void set chromaticTransposition(number value) { 
  //	_chromaticTransposition = value; 
  //}
  /**
   * true if this part is being rendered - affects whether NoteGroups and
   * measures are included in MeasureStacks
   */
  get visible() {
    return this._visible;
  }
  set visible(value) {
    this._visible = value;
  }
  /**
   * gets all of the Measure objects in this Part
   */
  get measures() {
    let allMeasures = [];
    let numStaves = this._staves.length;
    for (let i = 0; i < numStaves; i++) {
      let measures = this._staves[i].measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        allMeasures.push(measures[j]);
      }
    }
    return allMeasures;
  }
  /**
   * gets all of the NoteGroup objects in this Part
   */
  get noteGroups() {
    let allNoteGroups = [];
    let numStaves = this._staves.length;
    for (let i = 0; i < numStaves; i++) {
      let measures = this._staves[i].measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        let voices = measures[j].voices;
        for (let k = 0; k < voices.length; k++) {
          let noteGroups = voices[k].noteGroups;
          let numNoteGroups = noteGroups.length;
          for (let m = 0; m < numNoteGroups; m++) {
            allNoteGroups.push(noteGroups[m]);
          }
        }
      }
    }
    return allNoteGroups;
  }
  /**
   * gets all of the NoteGroup objects in this Part
   */
  get noteGroupsExcludingRests() {
    let allNoteGroups = [];
    let numStaves = this._staves.length;
    for (let i = 0; i < numStaves; i++) {
      let measures = this._staves[i].measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        let voices = measures[j].voices;
        for (let k = 0; k < voices.length; k++) {
          let noteGroups = voices[k].noteGroups;
          let numNoteGroups = noteGroups.length;
          for (let m = 0; m < numNoteGroups; m++) {
            if (!noteGroups[m].isRest) {
              allNoteGroups.push(noteGroups[m]);
            }
          }
        }
      }
    }
    return allNoteGroups;
  }
  /**
   * MIDI Bank number for the playback sound
   */
  get midiBank() {
    return this._midiBank;
  }
  set midiBank(value) {
    this._midiBank = value;
  }
  /**
   * MIDI Preset number for the playback sound
   */
  get midiPreset() {
    return this._midiPreset;
  }
  set midiPreset(value) {
    this._midiPreset = value;
  }
  /**
   * MIDI Channel number for the playback sound
   */
  get midiChannel() {
    return this._midiChannel;
  }
  set midiChannel(value) {
    this._midiChannel = value;
  }
  /**
   * An array of playback notes for a non-pitched percussion part (will be 
   * null if part isn't non-pitched percussion)
   * The index serves as the display pitch, and the value at that index is the
   * note that should be played instead. Indexes for unmapped display notes 
   * have null values associated with them. The 0 index can be used to serve 
   * as a default value.
   */
  get percussionMap() {
    return this._percussionMap;
  }
  set percussionMap(value) {
    this._percussionMap = value;
  }
  /**
   * true if playback is muted for this staff
   */
  get muted() {
    return this._muted;
  }
  set muted(value) {
    this._muted = value;
  }
  /**
   * a number between 0 and 1 representing the maximum volume the part can 
   * play at (assuming max dynamic)
   */
  get volume() {
    return this._volume;
  }
  set volume(value) {
    this._volume = value;
  }
  /**
   * a number between 0 (left) and 1 (right) for the pan position of the 
   * playback
   */
  get pan() {
    return this._pan;
  }
  set pan(value) {
    this._pan = value;
  }
  /**
   * list of assessment instruments assigned to this part
   */
  get assessmentInstruments() {
    return this._assessmentInstruments;
  }
  set assessmentInstruments(value) {
    this._assessmentInstruments = value;
  }
  get preventTransposition() {
    return this._preventTransposition;
  }
  set preventTransposition(value) {
    this._preventTransposition = value;
  }
  /**This is an optional override for controlling the playback pitch of all
   * notes in the Part. When transposing parts there are times we want the
   * changes in displayed pitch and part transposition to have less of an
   * effect on playback. This value can be used by the playback engine to
   * compensate the playback.*/
  get playbackOctaveDelta() {
    return this._playbackOctaveDelta;
  }
  set playbackOctaveDelta(value) {
    this._playbackOctaveDelta = value;
  }
};

// src/data/io/ScoreData.ts
var ScoreData = class {
};

// src/data/playback/PlaybackRegionDataType.ts
var PlaybackRegionDataType = /* @__PURE__ */ ((PlaybackRegionDataType2) => {
  PlaybackRegionDataType2[PlaybackRegionDataType2["MEASURE_STACK"] = 0] = "MEASURE_STACK";
  PlaybackRegionDataType2[PlaybackRegionDataType2["TEMPO"] = 1] = "TEMPO";
  PlaybackRegionDataType2[PlaybackRegionDataType2["MEASURE_ATTACHMENT"] = 2] = "MEASURE_ATTACHMENT";
  PlaybackRegionDataType2[PlaybackRegionDataType2["BEAT"] = 3] = "BEAT";
  PlaybackRegionDataType2[PlaybackRegionDataType2["SUB_BEAT"] = 4] = "SUB_BEAT";
  PlaybackRegionDataType2[PlaybackRegionDataType2["NOTE_GROUP"] = 5] = "NOTE_GROUP";
  return PlaybackRegionDataType2;
})(PlaybackRegionDataType || {});

// src/data/playback/TempoTimeStampDO.ts
var TempoTimeStampDO = class {
  constructor(time, msTime, qNoteTime, tempo) {
    this.time = time;
    this.msTime = msTime;
    this.qNoteTime = qNoteTime;
    this.tempo = tempo;
  }
};

// src/data/playback/PlaybackRegion.ts
var PlaybackRegion = class _PlaybackRegion {
  constructor(qNoteStartTime, qNoteEndTime) {
    /**list of [PlaybackRegionData] objects that describe events in the region*/
    this.data = [];
    this.qNoteStartTime = qNoteStartTime;
    this.qNoteEndTime = qNoteEndTime;
  }
  /**Creates and returns TempoTimeStampDO objects for the region.*/
  createTempoTimeStamps() {
    let timeStamps = [];
    let timeStamp = new TempoTimeStampDO(
      this.startTime,
      Math.floor(this.startTime * 1e3),
      this.qNoteStartTime,
      this.startTempo
    );
    timeStamps.push(timeStamp);
    let tdatas = this.data.filter((d) => d.type === 1 /* TEMPO */);
    for (let tdata of tdatas) {
      if (tdata.qNoteTime === this.qNoteEndTime) {
        continue;
      }
      if (tdata.qNoteTime > timeStamp.qNoteTime) {
        timeStamp.qNoteEndTime = tdata.qNoteTime;
        timeStamp = new TempoTimeStampDO(
          tdata.time,
          Math.floor(tdata.time * 1e3),
          tdata.qNoteTime,
          tdata.tempo
        );
        timeStamps[timeStamps.length - 1].nextSampleTimeStampDO = timeStamp;
        timeStamps.push(timeStamp);
      }
    }
    timeStamp.qNoteEndTime = this.qNoteEndTime;
    return timeStamps;
  }
  /**clones the [PlaybackRegion], copying all data elements. Data elements
   * are not cloned themselves, just referenced.
   * @startQNT the start QNoteTime to truncate the new region to. If null, the
   * original region's qNoteStartTime is used.
   * @endQNT the end QNoteTime to truncate the new region to. If null, the
   * original region's qNoteEndTime is used;*/
  clone(startQNT = null, endQNT = null) {
    if (startQNT == null)
      startQNT = this.qNoteStartTime;
    if (endQNT == null)
      endQNT = this.qNoteEndTime;
    if (startQNT < this.qNoteStartTime || endQNT > this.qNoteEndTime)
      throw "startQNT or endQNT outside of range of this region";
    let reg = new _PlaybackRegion(startQNT, endQNT);
    let dList = this.data.filter((d) => d.qNoteTime >= startQNT && d.qNoteTime <= endQNT);
    if (dList[0].qNoteTime !== startQNT) {
      throw "The requested startQNT does not match any PlaybackRegionData";
      " objects in this region";
    }
    reg.startTime = dList[0].time;
    reg.startTempo = dList[0].tempo;
    if (dList[dList.length - 1].qNoteTime !== endQNT && endQNT !== this.qNoteEndTime) {
      throw "The requested endQNT does not match any PlaybackRegionData";
      " objects in this region and does not match the end time of the";
      " region.";
    }
    if (dList[dList.length - 1].qNoteTime === endQNT) {
      reg.endTime = dList[dList.length - 1].time;
      dList.pop();
    } else {
      reg.endTime = this.endTime;
    }
    reg.data = dList;
    return reg;
  }
  /**returns a [PlaybackRegionData] object with a type of MEASURE_STACK that 
   * contains the startQNoteTime and repeatPass. If repeatPass is null, any 
   * pass matches. Returns null if a matching stack data isn't found. The 
   * startQNT has to fall between the beginning of the stack (inclusive) and
   * end (exclusive).*/
  stackDataWithStartQNT(startQNT, repeatPass = null) {
    if (startQNT >= this.qNoteStartTime && startQNT < this.qNoteEndTime) {
      let match = this.data.find((d) => d.type == 0 /* MEASURE_STACK */ && d.stack.startTime <= startQNT && d.stack.endTime > startQNT && (repeatPass != null ? d.stackRepeatPass == repeatPass : true));
      return match;
    }
    return null;
  }
  /**returns a [PlaybackRegionData] object with a type of MEASURE_STACK that 
   * contains the endQNoteTime and repeatPass. If repeatPass is null, any pass
   * matches. Returns null if a matching stack data isn't found. The endQNT
   * has to fall between the beginning of the stack (exclusive) and the end
   * (inclusive).*/
  stackDataWithEndQNT(endQNT, repeatPass = null) {
    if (endQNT > this.qNoteStartTime && endQNT <= this.qNoteEndTime) {
      let match = this.data.find((d) => d.type == 0 /* MEASURE_STACK */ && d.stack.startTime < endQNT && d.stack.endTime >= endQNT && (repeatPass != null ? d.stackRepeatPass == repeatPass : true));
      return match;
    }
    return null;
  }
};

// src/data/playback/PlaybackRegionData.ts
var PlaybackRegionData = class {
};

// src/data/playback/_PlaybackRegionManager.ts
var _PlaybackRegionManager = class {
  constructor(score, stacks, repeatAnalyzer) {
    this._score = score;
    this._stacks = stacks;
    this._repeatAnalyzer = repeatAnalyzer;
  }
  /**Creates a list of [PlaybackRegion] objects that match the requested
   * parameters.
   * @param startQNT - the start time in QNotes. 0 is beginning of piece. This
   * value must match the QNote value of a MeasureStack, beat or note in the
   * score. This value is inclusive (the first event at this QNote time will
   * be included.)
   * @param endQNT - the end time in QNotes. This value must match the QNote
   * value of a MeasureStack beginning or ending, beat or note in the score.
   * This value is exclusive (the first event at this QNote time will be
   * excluded.) If left null, the end time of the final [MeasureStack] will
   * be used (end of the piece).
   * @param startRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass.
   * @param endRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass. If left null, the final pass for
   * the endQNT will be used (matching end of song if endQNT is also null).
   * @param includeRepeats - if false, a single PlaybackRegion will be created
   * even if the score includes repeats. Both the startRepeatPass and
   * endRepeatPass should be 1.*/
  getPlaybackRegions(startQNT = 0, endQNT = null, startRepeatPass = 1, endRepeatPass = null, includeRepeats = true) {
    if (includeRepeats && this._regions == null) {
      this._regions = this._createRegions(
        this._repeatAnalyzer.playbackPath
      );
    } else if (!includeRepeats && this._regionNoRepeats == null) {
      this._regionNoRepeats = this._createRegions([
        0,
        this._stacks[this._stacks.length - 1].endTime
      ])[0];
    }
    let regions = includeRepeats ? this._regions : [this._regionNoRepeats];
    if (startQNT === 0 && endQNT == null && startRepeatPass === 1 && endRepeatPass == null) {
      return regions;
    }
    if (startQNT == null)
      startQNT = 0;
    if (startRepeatPass == null)
      startRepeatPass = 1;
    if (endQNT == null)
      endQNT = this._stacks[this._stacks.length - 1].endTime;
    if (endRepeatPass == null) {
      for (let i = regions.length - 1; i >= 0; i--) {
        let stackData = regions[i].stackDataWithEndQNT(endQNT);
        if (stackData != null) {
          endRepeatPass = stackData.stackRepeatPass;
          break;
        }
      }
    }
    let firstSourceRegion = regions.find((r) => r.stackDataWithStartQNT(startQNT, startRepeatPass) != null);
    if (!firstSourceRegion) {
      throw "startQNT not found for requested pass";
    }
    let lastSourceRegion = regions.find((r) => r.stackDataWithEndQNT(endQNT, endRepeatPass) != null);
    if (!lastSourceRegion) {
      throw "endQNT not found for requested pass";
    }
    if (firstSourceRegion === lastSourceRegion) {
      let clone = firstSourceRegion.clone(startQNT, endQNT);
      return [clone];
    }
    let clones = [];
    for (let reg of regions) {
      if (reg === firstSourceRegion) {
        clones.push(reg.clone(startQNT));
      } else if (reg === lastSourceRegion) {
        clones.push(reg.clone(null, endQNT));
        break;
      } else if (clones.length > 0) {
        clones.push(reg.clone());
      }
    }
    return clones;
  }
  /**Creates and returns TempoTimeStampDO objects for passed in regions.*/
  createTempoTimeStamps(regions) {
    let allTimeStamps = [];
    for (let i = regions.length - 1; i >= 0; i--) {
      let regionStamps = regions[i].createTempoTimeStamps();
      if (allTimeStamps.length > 0) {
        regionStamps[regionStamps.length - 1].nextSampleTimeStampDO = allTimeStamps[0];
      }
      allTimeStamps.splice(0, 0, ...regionStamps);
    }
    let reg = regions[regions.length - 1];
    let lastStack = this._stacks[this._stacks.length - 1];
    let lastTM = lastStack.tempoMarkers[lastStack.tempoMarkers.length - 1];
    let stamp = new TempoTimeStampDO(
      reg.endTime,
      Math.floor(reg.endTime * 1e3),
      reg.qNoteEndTime,
      lastTM.tempo
    );
    stamp.qNoteEndTime = reg.qNoteEndTime;
    allTimeStamps[allTimeStamps.length - 1].nextSampleTimeStampDO = stamp;
    allTimeStamps.push(stamp);
    return allTimeStamps;
  }
  /**Gets the time in seconds for a given qNoteTime
   * @param targetQNoteTime - the qNote start time to match
   * @param repeatPass - the repeat pass to match - 1 is the first pass.*/
  convertQNoteTimeToSeconds(targetQNoteTime, repeatPass, includeRepeats) {
    if (this._regions == null && includeRepeats) {
      this._regions = this._createRegions(
        this._repeatAnalyzer.playbackPath
      );
    } else if (this._regionNoRepeats == null && !includeRepeats) {
      this._regionNoRepeats = this._createRegions(
        [0, this._stacks[this._stacks.length - 1].endTime]
      )[0];
    }
    let regions = includeRepeats ? this._regions : [this._regionNoRepeats];
    let lastRegion = regions[regions.length - 1];
    if (targetQNoteTime === lastRegion.qNoteEndTime) {
      return lastRegion.endTime;
    }
    let passNum;
    for (let reg of regions) {
      for (let data of reg.data) {
        if (data.stack != null) {
          passNum = data.stackRepeatPass;
        }
        if (data.qNoteTime === targetQNoteTime && passNum === repeatPass) {
          return Number.parseFloat(data.time.toFixed(4));
        }
      }
    }
    throw "targetQNoteTime and repeatPass combo not found in song";
  }
  /**Creates PlaybackRegion objects which contain a list of 
   * PlaybackSequenceEvents. There will be one region for each pair of qNote
   * values in the playbackPath*/
  _createRegions(playbackPath) {
    if (this._stackNoteGroupsMap == null)
      this._createStackMaps();
    let cTempo;
    let refTempoTime = 0;
    let refQNoteTime = 0;
    let stackPassMap = /* @__PURE__ */ new Map();
    let regions = [];
    for (let i = 0; i < playbackPath.length; i += 2) {
      let startQNT = playbackPath[i];
      let endQNT = playbackPath[i + 1];
      let region = new PlaybackRegion(startQNT, endQNT);
      region.startTime = regions.length > 0 ? regions[regions.length - 1].endTime : 0;
      let regionStacks = this._stacks.filter((s) => s.startTime >= startQNT && s.endTime <= endQNT);
      cTempo = regionStacks[0].getInheritedTempoMarker().tempo;
      refTempoTime = region.startTime;
      refQNoteTime = startQNT;
      region.startTempo = cTempo;
      for (let stack of regionStacks) {
        let stackQNT = stack.startTime;
        let cStackData = new PlaybackRegionData();
        cStackData.qNoteTime = stack.startTime;
        let qNoteDelta2 = cStackData.qNoteTime - refQNoteTime;
        cStackData.time = 60 / cTempo * qNoteDelta2 + refTempoTime;
        cStackData.tempo = cTempo;
        cStackData.stack = stack;
        cStackData.type = 0 /* MEASURE_STACK */;
        if (stackPassMap.has(stack)) {
          stackPassMap.set(stack, stackPassMap.get(stack) + 1);
        } else {
          stackPassMap.set(stack, 1);
        }
        cStackData.stackRepeatPass = stackPassMap.get(stack);
        region.data.push(cStackData);
        let tms = stack.tempoMarkers;
        let attachments = this._stackAttachmentsMap.get(stack);
        let measure = stack.measures[0];
        let beats = measure.beatGroups;
        let subBeats = measure.subBeatGroups;
        let beatQnoteDur = measure.beatQnoteDuration;
        let subBeatQnoteDur = measure.subBeatQnoteDuration;
        let ngs = this._stackNoteGroupsMap.get(stack);
        let nextTmIndex = 0;
        let nextAttIndex = 0;
        let nextBeatIndex = 0;
        let nextSubBeatIndex = 0;
        let nextNgIndex = 0;
        let eventFound;
        do {
          eventFound = false;
          let testVals = [];
          if (tms != null && tms.length > nextTmIndex) {
            testVals.push(tms[nextTmIndex].qNoteTime);
            eventFound = true;
          } else
            testVals.push(Number.MAX_SAFE_INTEGER);
          if (attachments.length > nextAttIndex) {
            testVals.push(attachments[nextAttIndex].qNoteTime);
            eventFound = true;
          } else
            testVals.push(Number.MAX_SAFE_INTEGER);
          if (beats.length > nextBeatIndex) {
            testVals.push(stackQNT + beats[nextBeatIndex]);
            eventFound = true;
          } else
            testVals.push(Number.MAX_SAFE_INTEGER);
          if (subBeats.length > nextSubBeatIndex) {
            testVals.push(stackQNT + subBeats[nextSubBeatIndex]);
            eventFound = true;
          } else
            testVals.push(Number.MAX_SAFE_INTEGER);
          if (ngs.length > nextNgIndex) {
            testVals.push(ngs[nextNgIndex].qNoteTime);
            eventFound = true;
          } else
            testVals.push(Number.MAX_SAFE_INTEGER);
          if (eventFound) {
            let prData = new PlaybackRegionData();
            let resultIndex = this._getIndexOfMinValue(testVals);
            prData.qNoteTime = testVals[resultIndex];
            let qNoteDelta3 = prData.qNoteTime - refQNoteTime;
            prData.time = 60 / cTempo * qNoteDelta3 + refTempoTime;
            switch (resultIndex) {
              case 0:
                prData.type = 1 /* TEMPO */;
                cTempo = tms[nextTmIndex].tempo;
                refQNoteTime = prData.qNoteTime;
                refTempoTime = prData.time;
                if (cStackData.qNoteTime == refQNoteTime) {
                  cStackData.tempo = cTempo;
                }
                nextTmIndex++;
                break;
              case 1:
                prData.type = 2 /* MEASURE_ATTACHMENT */;
                prData.measureAttachment = attachments[nextAttIndex];
                nextAttIndex++;
                break;
              case 2:
                prData.type = 3 /* BEAT */;
                prData.qNoteDuration = beatQnoteDur;
                nextBeatIndex++;
                break;
              case 3:
                prData.type = 4 /* SUB_BEAT */;
                prData.qNoteDuration = subBeatQnoteDur;
                nextSubBeatIndex++;
                break;
              case 4:
                prData.type = 5 /* NOTE_GROUP */;
                prData.noteGroup = ngs[nextNgIndex];
                nextNgIndex++;
                break;
            }
            prData.tempo = cTempo;
            region.data.push(prData);
          }
        } while (eventFound);
      }
      let qNoteDelta = endQNT - refQNoteTime;
      region.endTime = 60 / cTempo * qNoteDelta + refTempoTime;
      regions.push(region);
    }
    return regions;
  }
  _getIndexOfMinValue(values) {
    let minValue = Number.MAX_SAFE_INTEGER;
    let minIndex;
    for (let i = 0; i < values.length; i++) {
      if (values[i] < minValue) {
        minValue = values[i];
        minIndex = i;
      }
    }
    return minIndex;
  }
  _createStackMaps() {
    this._stackNoteGroupsMap = /* @__PURE__ */ new Map();
    this._stackAttachmentsMap = /* @__PURE__ */ new Map();
    for (let i = 0; i < this._stacks.length; i++) {
      let ngList = [];
      let attList = [];
      for (let part of this._score.parts) {
        for (let staff of part.staves) {
          let measure = staff.measures[i];
          for (let voice of measure.voices) {
            ngList.push(...voice.noteGroups);
          }
          attList.push(...measure.attachments);
        }
      }
      ngList.sort((ng1, ng2) => ng1.qNoteTime <= ng2.qNoteTime ? -1 : 1);
      this._stackNoteGroupsMap.set(this._stacks[i], ngList);
      attList.sort((a1, a2) => a1.qNoteTime <= a2.qNoteTime ? -1 : 1);
      this._stackAttachmentsMap.set(this._stacks[i], attList);
    }
  }
};

// src/data/playback/_RepeatAnalyzer.ts
var _RepeatAnalyzer = class {
  constructor(measureStacks) {
    this._measureStacks = measureStacks;
  }
  updatePlaybackPath() {
    let passMap = /* @__PURE__ */ new Map();
    for (let stack of this._measureStacks) {
      if (stack.repeatDOs != null) {
        passMap.set(stack, 0);
      }
    }
    this._playbackPath = [];
    this._createPlaybackPathPair(this._measureStacks, passMap, 0);
    return this._playbackPath;
  }
  _createPlaybackPathPair(stacks, passMap, startIndex) {
    let lastForwardRepeatIndex;
    for (let i = startIndex; i < stacks.length; i++) {
      let stack = stacks[i];
      if (stack.repeatDOs != null) {
        passMap.set(stack, passMap.get(stack) + 1);
        for (let rdo of stack.repeatDOs) {
          if (rdo.repeatDirection === RepeatDirection.FORWARD) {
            lastForwardRepeatIndex = i;
          }
          if (rdo.repeatDirection == RepeatDirection.BACKWARD && passMap.get(stack) <= rdo.repeatTimes) {
            if (lastForwardRepeatIndex == null) {
              lastForwardRepeatIndex = this._findPrecedingForwardRepeat(
                stacks,
                startIndex - 1
              );
            }
            this._playbackPath.push(stacks[startIndex].startTime);
            this._playbackPath.push(stack.endTime);
            this._createPlaybackPathPair(
              stacks,
              passMap,
              lastForwardRepeatIndex
            );
            return;
          }
          if (rdo.endingType == EndingType.START && rdo.endingNumber.indexOf("1") !== -1) {
            let endingTargetIndex = this._chooseEnding(stacks, passMap, i);
            if (endingTargetIndex !== i) {
              this._playbackPath.push(
                stacks[startIndex].startTime
              );
              this._playbackPath.push(stack.startTime);
              this._createPlaybackPathPair(
                stacks,
                passMap,
                endingTargetIndex
              );
              return;
            }
          }
        }
      }
    }
    this._playbackPath.push(stacks[startIndex].startTime);
    this._playbackPath.push(stacks[stacks.length - 1].endTime);
  }
  _findPrecedingForwardRepeat(stacks, startIndex) {
    for (let i = startIndex; i >= 0; i--) {
      let stack = stacks[i];
      if (stack.repeatDOs != null) {
        for (let rdo of stack.repeatDOs) {
          if (rdo.repeatDirection == RepeatDirection.FORWARD) {
            return i;
          }
        }
      }
    }
    return 0;
  }
  _chooseEnding(stacks, passMap, startIndex) {
    let endingTargetNum = passMap.get(stacks[startIndex]).toString();
    let lastEndingCloseIndex;
    for (let i = startIndex; i < stacks.length; i++) {
      let stack = stacks[i];
      if (stack.repeatDOs != null) {
        for (let rdo of stack.repeatDOs) {
          if (rdo.endingType === EndingType.START) {
            if (rdo.endingNumber != null && rdo.endingNumber.indexOf(endingTargetNum) !== -1) {
              return i;
            }
            lastEndingCloseIndex = null;
          } else if (rdo.endingType === EndingType.DISCONTINUE || rdo.endingType === EndingType.STOP) {
            lastEndingCloseIndex = i;
          }
        }
        if (lastEndingCloseIndex != null && lastEndingCloseIndex < i) {
          return i;
        }
      }
    }
    return startIndex;
  }
  /**contains a list of qNoteTimes in from/to pairings. If no repeats exist, 
   * this will be from 0 to the qNoteTime of the end of the song.
   * Values are presented as pairs of startQNoteTimes (inclusive) and 
   * stopQNoteTimes (exclusive). */
  get playbackPath() {
    return this._playbackPath ?? this.updatePlaybackPath();
  }
};

// src/data/playback/ScorePlaybackInfo.ts
var ScorePlaybackInfo = class {
  constructor(score) {
    this._score = score;
    this.reset();
  }
  /**clears cached data - use if Score changes in a way that can affect
   * playback (added measures, changed tempos, etc.).*/
  reset() {
    this._stacks = this._score.getMeasureStacks();
    this._repeatAnalyzer = new _RepeatAnalyzer(this._stacks);
    this._regionManager = new _PlaybackRegionManager(
      this._score,
      this._stacks,
      this._repeatAnalyzer
    );
  }
  /**Gets the time in seconds for a given qNoteTime at song's default tempo.
   * Considers repeats and TempoMarkers.
   * @param targetQNoteTime - the qNote start time to match
   * @param repeatPass - the repeat pass to match - 1 is the first pass.*/
  convertQNoteTimeToSeconds(targetQNoteTime, repeatPass = 1, includeRepeats = true) {
    return this._regionManager.convertQNoteTimeToSeconds(
      targetQNoteTime,
      repeatPass,
      includeRepeats
    );
  }
  /**Creates a list of [PlaybackRegion] objects that match the requested
   * parameters.
   * @param startQNT - the start time in QNotes. 0 is beginning of piece. This
   * value must match the QNote value of a MeasureStack, beat or note in the
   * score. This value is inclusive (the first event at this QNote time will
   * be included.)
   * @param endQNT - the end time in QNotes. This value must match the QNote
   * value of a MeasureStack beginning or ending, beat or note in the score.
   * This value is exclusive (the first event at this QNote time will be
   * excluded.) If left null, the end time of the final [MeasureStack] will
   * be used (end of the piece).
   * @param startRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass.
   * @param endRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass. If left null, the final pass for
   * the endQNT will be used (matching end of song if endQNT is also null).
   * @param includeRepeats - if false, a single PlaybackRegion will be created
   * even if the score includes repeats. Both the startRepeatPass and
   * endRepeatPass should be 1.*/
  getPlaybackRegions(startQNT = null, endQNT = null, startRepeatPass = null, endRepeatPass = null, includeRepeats = true) {
    return this._regionManager.getPlaybackRegions(
      startQNT,
      endQNT,
      startRepeatPass,
      endRepeatPass,
      includeRepeats
    );
  }
  /**contains a list of qNoteTimes in from/to pairings. If no repeats exist, 
   * this will be from 0 to the qNoteTime of the end of the song.
   * Values are presented as pairs of startQNoteTimes (inclusive) and 
   * stopQNoteTimes (exclusive). */
  get playbackPath() {
    return this._repeatAnalyzer.playbackPath;
  }
  /**Creates and returns TempoTimeStampDO objects for passed in regions.*/
  createTempoTimeStamps(regions) {
    return this._regionManager.createTempoTimeStamps(regions);
  }
};

// src/data/containers/Score.ts
var Score = class extends BaseDataObject {
  constructor() {
    super();
    //contains Part objects (container hierarchy: Parts >> Staves >> Measures >>
    //Voices >> NoteGroups >> Notes)
    this._parts = [];
    //contains the Page objects (container hierarchy: Pages >> Systems >> 
    //MeasureStacks)
    this._pages = [];
    //true when grace notes have not been offset properly (MusicXML files don't 
    //have them positioned)
    this._graceNotesNeedFixing = false;
  }
  toScoreData() {
    let stacks = this.getMeasureStacks();
    let data = new ScoreData();
    data.graceNotesNeedFixing = this._graceNotesNeedFixing;
    data.measureStacks = stacks.map((s) => s.toMeasureStackData());
    return data;
  }
  /**
   * adds the Part
   * @param	part a Part object
   */
  addPart(part) {
    this._parts.push(part);
  }
  /**
   * adds the Page
   * @param	page a Page object
   */
  addPage(page) {
    if (this._pages.length > 0) {
      let prevPage = this._pages[this._pages.length - 1];
      prevPage.next = page;
      page.previous = prevPage;
    }
    this._pages.push(page);
  }
  /**
   * gets all of the System objects found in the score (looks through each 
   * page)
   * @return a list of System objects
   */
  getSystems() {
    let systems = [];
    for (let page of this._pages) {
      for (let system of page.systems) {
        systems.push(system);
      }
    }
    return systems;
  }
  /**
   * gets all of the MeasureStack objects found in the score (looks through 
   * each page and system
   * @return a list of MeasureStack objects
   */
  getMeasureStacks() {
    let stacks = [];
    for (let page of this._pages) {
      for (let system of page.systems) {
        for (let stack of system.measureStacks) {
          stacks.push(stack);
        }
      }
    }
    return stacks;
  }
  /**
   * gets the qnote position for every beat in the score, returning them in an 
   * array and also writing them to the MeasureStack objects
   * @return a list of the qnote positions
   */
  getBeatQNotePositions() {
    let allBeatQNotePositions = [];
    let totalBeats = 0;
    let stacks = this.getMeasureStacks();
    for (let stack of stacks) {
      let measure = stack.measures[0];
      let numBeats = measure.numBeats;
      let numBigBeats = 0;
      let beatQNotePositions = [];
      for (let i = 0; i < numBeats; i++) {
        if (measure.beatType === 8 || measure.beatType === 16) {
          if (i % 3 === 0) {
            let position = i * (4 / measure.beatType) + stack.startTime;
            beatQNotePositions.push(position);
            allBeatQNotePositions.push(position);
          }
        } else {
          let position = i * (4 / measure.beatType) + stack.startTime;
          beatQNotePositions.push(position);
          allBeatQNotePositions.push(position);
        }
      }
      stack.beatQNotePositions = beatQNotePositions;
    }
    return allBeatQNotePositions;
  }
  /**
   * removes the Page from the Score. The Page MUST have no remaining Systems 
   * for this to go through
   * @param page the empty Page to remove
   */
  removePage(page) {
    if (page.systems.length == 0) {
      for (let i = this._pages.length - 1; i >= 0; i--) {
        if (this._pages[i] === page) {
          if (i > 0 && this._pages[i - 1].next === page) {
            this._pages[i - 1].next = i < this._pages.length - 1 ? this._pages[i + 1] : null;
          }
          if (i < this._pages.length - 1 && this._pages[i + 1].previous === page) {
            this._pages[i + 1].previous = i > 0 ? this._pages[i - 1] : null;
          }
          this._pages.splice(i, 1);
          return;
        }
      }
    } else {
      console.log("Attempted to remove a Page that still contains Systems!");
    }
  }
  /**adjusts the scale the score is formatted and rendered at. The number of
   * pixels that fit between two staff lines (within the same staff). Default
   * is specified in [ScoreProperties._pixelsPerSpace] (10). If
   * [preservePageSize] is true, the page width and height in spaces is 
   * changed to preserve the original pixel width/height.*/
  setPixelsPerSpace(pixelsPerSpace, preservePageSize = true) {
    if (preservePageSize) {
      let scale = this._scoreProperties.pixelsPerSpace / pixelsPerSpace;
      this._scoreProperties.pageWidth *= scale;
      this._scoreProperties.pageHeight *= scale;
      this._scoreProperties.topPageMargin *= scale;
      this._scoreProperties.leftPageMargin *= scale;
      this._scoreProperties.rightPageMargin *= scale;
      this._scoreProperties.bottomPageMargin *= scale;
    }
    this._scoreProperties.pixelsPerSpace = pixelsPerSpace;
    let systems = this.getSystems();
    for (let sys of systems) {
      sys.getSettingsFromScoreProperties();
    }
  }
  /**adjusts the width and height of the score without changing the
   * scaling (staff height) of the notation. If [valuesArePixels] is true,
   * then the values are interpreted as pixels instead of spaces.*/
  setPageSize(pageWidth, pageHeight, valuesArePixels = true) {
    if (valuesArePixels) {
      let px = this._scoreProperties.pixelsPerSpace;
      this._scoreProperties.pageWidth = pageWidth / px;
      this._scoreProperties.pageHeight = pageHeight / px;
    } else {
      this._scoreProperties.pageWidth = pageWidth;
      this._scoreProperties.pageHeight = pageHeight;
    }
    let systems = this.getSystems();
    for (let sys of systems) {
      sys.getSettingsFromScoreProperties();
    }
  }
  /**utility method to mark all measures (and thus stacks and systems as
   * needing a re-render. Ordinarily measures are marked for rendering as part
   * of the formatting process, but this can be used to force a clean redraw*/
  markAllMeasuresNeedRendering() {
    let stacks = this.getMeasureStacks();
    for (let i = 0; i < stacks.length; i++) {
      let stack = stacks[i];
      let measures = stack.measures;
      for (let j = 0; j < measures.length; j++) {
        measures[j].notesNeedRendering = true;
      }
    }
  }
  get playbackInfo() {
    if (this._playbackInfo == null)
      this._playbackInfo = new ScorePlaybackInfo(this);
    return this._playbackInfo;
  }
  get scoreProperties() {
    return this._scoreProperties;
  }
  set scoreProperties(value) {
    this._scoreProperties = value;
  }
  get parts() {
    return this._parts;
  }
  get pages() {
    return this._pages;
  }
  /**
   * true when grace notes have not been offset properly (MusicXML files don't
   * have them positioned)
   */
  get graceNotesNeedFixing() {
    return this._graceNotesNeedFixing;
  }
  set graceNotesNeedFixing(value) {
    this._graceNotesNeedFixing = value;
  }
};

// src/data/io/StaffData.ts
var StaffData = class {
};

// src/data/containers/Staff.ts
var Staff = class extends BaseDataObject {
  constructor() {
    super();
    this._measures = [];
    this._lyricsMode = 0 /* SHOW_LYRICS */;
    ///determines whether staff is rendered
    this._visible = true;
  }
  toStaffData() {
    let data = new StaffData();
    data.measures = this._measures.map((m) => m.toMeasureData());
    data.visible = this._visible;
    return data;
  }
  addMeasure(measure) {
    this._measures.push(measure);
    measure.staff = this;
  }
  /**
   * removes the measure at the requested index
   * @param index the index of the measure with the measures list
   */
  removeMeasureAt(index) {
    this._measures.splice(index, 1);
  }
  /**
   * inserts the Measure at the requested index
   * @param	measure the Measure to insert
   * @param	insertionIndex the position to insert the Measure (if invalid, 
   * the Measure is not added)
   */
  insertMeasure(measure, insertionIndex) {
    if (insertionIndex <= this._measures.length && insertionIndex >= 0) {
      this._measures.splice(insertionIndex, 0, measure);
      measure.staff = this;
    } else {
      console.log("requested index out of range!");
    }
  }
  /**returns the first NoteGroup that isn't a rest or null if none is found.*/
  getFirstNonRest() {
    for (let measure of this._measures) {
      for (let voice of measure.voices) {
        for (let ng of voice.noteGroups) {
          if (!ng.isRest) {
            return ng;
          }
        }
      }
    }
    return null;
  }
  get measures() {
    return this._measures;
  }
  set measures(value) {
    this._measures = value;
  }
  get partRef() {
    return this._partRef;
  }
  set partRef(value) {
    this._partRef = value;
  }
  get originalLyrics() {
    return this._originalLyrics;
  }
  set originalLyrics(value) {
    this._originalLyrics = value;
  }
  get lyricsMode() {
    return this._lyricsMode;
  }
  set lyricsMode(value) {
    this._lyricsMode = value;
  }
  /**
   * true if this staff is being rendered - affects whether NoteGroups and
   * measures are included in MeasureStacks. Note, at least one staff must
   * be visible for any given part. To hide an entire part, don't include it
   * in the list of Parts sent for render.
   */
  get visible() {
    return this._visible;
  }
  set visible(value) {
    if (value == false) {
      let atLeastOneStaffVisible = false;
      for (let staff of this._partRef.staves) {
        if (staff !== this && staff._visible) {
          atLeastOneStaffVisible = true;
        }
      }
      if (!atLeastOneStaffVisible) {
        throw "Part must have at least one visible Staff";
      }
    }
    this._visible = value;
  }
};

// src/data/containers/System.ts
var System = class extends BaseDataObject {
  constructor(scoreProps) {
    super();
    //the amount to indent the system, in tenths.
    this._indent = 0;
    //default space between staff lines
    // number _staffLineSpacing; 
    //vertical offset positions of the staves in the system (in tenths) (0 is 
    //default position)
    this._staffVPosOffsets = [];
    this._measureStacks = [];
    //set to true if this system starts a new page
    this._newPage = false;
    //set to true in order to force re-rendering of system on next render
    this._needsRendering = true;
    this._scoreProps = scoreProps;
    this._init();
  }
  _init() {
    this.getSettingsFromScoreProperties();
  }
  getSettingsFromScoreProperties() {
    this._systemWidth = this._scoreProps.pageWidth - this._scoreProps.leftPageMargin - this._scoreProps.rightPageMargin - this._scoreProps.leftSystemMargin - this._scoreProps.rightSystemMargin;
    this._staffSpacing = this._scoreProps.staffSpacing;
  }
  /**
   * adds a single MeasureStack
   * @param	stack the MeasureStack object to add
   * @param	linkStacksBetweenSystems if true, the stacks in this system will
   * be linked with the stacks in the surrounding systems (prev and next 
   * properties)
   */
  addMeasureStack(stack, linkStacksBetweenSystems = true) {
    stack.systemRef = this;
    if (this._measureStacks.length > 0) {
      let prevStack = this._measureStacks[this._measureStacks.length - 1];
      prevStack.next = stack;
      stack.previous = prevStack;
    }
    this._measureStacks.push(stack);
    if (linkStacksBetweenSystems) {
      this.linkStacksInSurroundingSystems();
    }
  }
  /**
   * adds one or more MeasureStack objects
   * @param	stacks a list of MeasureStack objects
   * @param	linkStacksBetweenSystems if true, the stacks in this system will
   * be linked with the stacks in the surrounding systems (prev and next 
   * properties)
   */
  addMeasureStacks(stacks, linkStacksBetweenSystems = true) {
    let prevStack;
    if (this._measureStacks.length > 0) {
      prevStack = this._measureStacks[this._measureStacks.length - 1];
    }
    for (let stack of stacks) {
      stack.systemRef = this;
      prevStack.next = stack;
      stack.previous = prevStack;
      this._measureStacks.push(stack);
      prevStack = stack;
    }
    if (linkStacksBetweenSystems) {
      this.linkStacksInSurroundingSystems();
    }
  }
  /**
   * inserts a single MeasureStack at the beginning of the system
   * @param	stack the MeasureStack to insert
   * @param	linkStacksBetweenSystems if true, the stacks in this system will
   * be linked with the stacks in the surrounding systems (prev and next 
   * properties)
   */
  insertStack(stack, linkStacksBetweenSystems = true) {
    stack.systemRef = this;
    this._measureStacks.splice(0, 0, stack);
    if (this._measureStacks.length > 1) {
      let nextStack = this._measureStacks[1];
      nextStack.previous = stack;
      stack.next = nextStack;
    }
    if (linkStacksBetweenSystems) {
      this.linkStacksInSurroundingSystems();
    }
  }
  /**
   * inserts one or more MeasureStack objects at the beginning of the system,
   * retaining their order
   * @param	stacks a list of MeasureStack objects
   * @param	linkStacksBetweenSystems if true, the stacks in this system will
   * be linked with the stacks in the surrounding systems (prev and next 
   * properties)
   */
  insertMeasureStacks(stacks, linkStacksBetweenSystems = true) {
    let index = 0;
    for (let stack of stacks) {
      stack.systemRef = this;
      this._measureStacks.splice(index, 0, stack);
      if (index > 0) {
        let prevStack = this._measureStacks[index - 1];
        prevStack.next = stack;
        stack.previous = prevStack;
      }
      if (index < this._measureStacks.length - 1) {
        let nextStack = this._measureStacks[index + 1];
        nextStack.previous = stack;
        stack.next = nextStack;
      }
      index++;
    }
    if (linkStacksBetweenSystems) {
      this.linkStacksInSurroundingSystems();
    }
  }
  /**
   * removes the specified number of stacks from the front of the system and
   * returns them as a list
   * @param	count the number of stacks to remove. If less than or equal to
   * 0, all of the stacks are removed.
   * @return	the list of MeasureStacks that was removed
   */
  removeStacksFromBeginning(count) {
    if (count >= this._measureStacks.length || count <= 0) {
      count = this._measureStacks.length;
    }
    let removedStacks = this._measureStacks.slice(0, count);
    this._measureStacks.splice(0, count);
    return removedStacks;
  }
  /**
   * removes the specified number of stacks from the end of the system and
   * returns them as a list
   * @param	count the number of stacks to remove. If less than or equal to
   * 0, all of the stacks are removed.
   * @return	the list of MeasureStacks that was removed
   */
  removeStacksFromEnd(count) {
    if (count >= this._measureStacks.length || count <= 0) {
      count = this._measureStacks.length;
    }
    let removedStacks = this._measureStacks.slice(
      this._measureStacks.length - count,
      this._measureStacks.length
    );
    this._measureStacks.splice(
      this._measureStacks.length - count,
      this._measureStacks.length
    );
    return removedStacks;
  }
  /**
   * adds the new stack directly after an existing stack (used to insert the
   * stack in the middle of other stacks)
   * @param	newStack the Stack to insert
   * @param	stackToFollow the Stack to find and insert the new Stack after
   * @param	linkStacksBetweenSystems if true, the stacks in this system will
   * be linked with the stacks in the surrounding systems (prev and next
   * properties)
   * @return	returns true if the stackToFollow is found and the newStack is
   * added
   */
  addStackAfterExistingStack(newStack, stackToFollow, linkStacksBetweenSystems = true) {
    let numStacks = this._measureStacks.length;
    for (let i = 0; i < numStacks; i++) {
      if (this._measureStacks[i] === stackToFollow) {
        stackToFollow.next = newStack;
        newStack.previous = stackToFollow;
        newStack.systemRef = this;
        if (i + 1 < numStacks) {
          let nextStack = this._measureStacks[i + 1];
          nextStack.previous = newStack;
          newStack.next = nextStack;
        }
        this._measureStacks.splice(i + 1, 0, newStack);
        if (linkStacksBetweenSystems) {
          this.linkStacksInSurroundingSystems();
        }
        return true;
      }
    }
    return false;
  }
  /**
   * removes the MeasureStack at the requested index and recreates the
   * MeasureStack links
   * @param index the index of the stack to remove
   */
  removeStack(stack) {
    let index = this._measureStacks.indexOf(stack);
    if (index === -1) {
      return;
    }
    this._measureStacks.splice(index, 1);
    if (index === 0 || index === this._measureStacks.length) {
      this.linkStacksInSurroundingSystems();
    } else {
      this._measureStacks[index - 1].next = this._measureStacks[index];
      this._measureStacks[index].previous = this._measureStacks[index - 1];
    }
  }
  attemptStackDonation() {
    return false;
  }
  /**
   * clears all MeasureStacks objects from the System. It does NOT modify the
   * references in the MeasureStack objects, so they will still refer to this
   * System as their owner and refer to surrounding Stacks as next and 
   * previous
   */
  clearAllStacks() {
    this._measureStacks = [];
  }
  /**
   * links the first stack of this system with the last stack of the previous
   * system and links the last stack in this system with the first stack in 
   * the next system (assuming the surrounding systems exist)
   */
  linkStacksInSurroundingSystems() {
    if (this._measureStacks.length <= 0) {
      return;
    }
    let firstStack = this._measureStacks[0];
    let lastStack = this._measureStacks[this._measureStacks.length - 1];
    let prevSystem = this.previous;
    if (prevSystem == null) {
      firstStack.previous = null;
    }
    while (prevSystem != null) {
      if (prevSystem.measureStacks.length > 0) {
        let prevStack = prevSystem.measureStacks[prevSystem.measureStacks.length - 1];
        prevStack.next = firstStack;
        firstStack.previous = prevStack;
        break;
      }
      prevSystem = prevSystem.previous;
    }
    let nextSystem = this.next;
    if (nextSystem == null) {
      lastStack.next = null;
    }
    while (nextSystem != null) {
      if (nextSystem.measureStacks.length > 0) {
        let nextStack = nextSystem.measureStacks[0];
        nextStack.previous = lastStack;
        lastStack.next = nextStack;
        break;
      }
      nextSystem = nextSystem.next;
    }
  }
  /**
   * creates a list of the staff barline groupings according to the number of
   * staves in each part. Ex. [1, 1, 1, 2] for flute, oboe, bassoon, piano
   * Only creates groupings for visible parts and staves.
   */
  _createPartStaffGroupings() {
    if (this._measureStacks.length === 0) {
      return [];
    }
    let groupings = [];
    let stack = this._measureStacks[0];
    let measures = stack.measures;
    let numMeasures = measures.length;
    let measureIndex = 0;
    while (measureIndex < numMeasures) {
      let measure = measures[measureIndex];
      let numVisibleStaves = 0;
      for (let staff of measure.staff.partRef.staves) {
        if (staff.visible)
          numVisibleStaves++;
      }
      let numStaves = measure.staff.partRef.staves.length;
      if (this._visibleParts.indexOf(measure.staff.partRef) !== -1) {
        groupings.push(numVisibleStaves);
      }
      measureIndex += numStaves;
    }
    return groupings;
  }
  /**creates staff barline groupings with all parts/staves in a single group*/
  _createScoreStaffGroupings() {
    if (this._measureStacks.length === 0) {
      return [];
    }
    let stack = this._measureStacks[0];
    let measures = stack.measures;
    let numMeasures = measures.length;
    let measureIndex = 0;
    let totalStaves = 0;
    while (measureIndex < numMeasures) {
      let measure = measures[measureIndex];
      let numVisibleStaves = 0;
      for (let staff of measure.staff.partRef.staves) {
        if (staff.visible)
          numVisibleStaves++;
      }
      let numStaves = measure.staff.partRef.staves.length;
      if (this._visibleParts.indexOf(measure.staff.partRef) !== -1) {
        totalStaves += numVisibleStaves;
      }
      measureIndex += numStaves;
    }
    return [totalStaves];
  }
  /**
   * returns the sum of the idealWidths of the measure stacks currently in the
   * system (regardless of what the system's width is)
   */
  get requestedStackWidth() {
    if (this._measureStacks.length === 0) {
      return 0;
    }
    let totalWidth = 0;
    for (let stack of this._measureStacks) {
      totalWidth += stack.idealWidth;
    }
    return totalWidth;
  }
  get measureStacks() {
    return this._measureStacks;
  }
  get indent() {
    return this._indent;
  }
  set indent(value) {
    this._indent = value;
  }
  /**
   * width of the system, in tenths
   */
  get systemWidth() {
    return this._systemWidth;
  }
  set systemWidth(value) {
    this._systemWidth = value;
  }
  /**
   * default space between staves, in tenths
   */
  get staffSpacing() {
    return this._staffSpacing;
  }
  set staffSpacing(value) {
    this._staffSpacing = value;
  }
  /**
   * default space between staff lines, in tenths
   */
  // number get staffLineSpacing { return _staffLineSpacing; }
  // void set staffLineSpacing(number value) { _staffLineSpacing = value; }
  /**
   * returns a list of the vertical offsets from default positions (in tenths)
   * of all staves in the system - for any that haven't previously been set,
   * it sets the offset to 0. We use this system instead of returning exact 
   * values to account for scores being rendered with only some parts present
   */
  get staffVPosOffsets() {
    if (this._measureStacks.length === 0) {
      return [];
    }
    let staffIndex = this._staffVPosOffsets.length;
    let numStaves = this._measureStacks[0].measures.length;
    if (staffIndex > numStaves) {
      this._staffVPosOffsets.splice(numStaves, staffIndex - numStaves);
      staffIndex = this._staffVPosOffsets.length;
    }
    while (staffIndex < numStaves) {
      if (staffIndex >= this._staffVPosOffsets.length) {
        this._staffVPosOffsets.push(0);
      } else {
        this._staffVPosOffsets[staffIndex] = 0;
      }
      staffIndex++;
    }
    return this._staffVPosOffsets;
  }
  set staffVPosOffsets(value) {
    this._staffVPosOffsets = value;
  }
  /**
   * returns a list of the staff barline groupings according to the number of
   * staves in each part. Ex. [1, 1, 1, 2] for flute, oboe, bassoon, piano
   * Only returns groupings for visible parts and staves.
   */
  get staffGroupings() {
    let staffGroupings;
    switch (this._scoreProps.staffGroupingStyle) {
      case 0 /* part */:
        staffGroupings = this._createPartStaffGroupings();
        break;
      case 1 /* score */:
        staffGroupings = this._createScoreStaffGroupings();
        break;
    }
    return staffGroupings;
  }
  /**
   * the parts that are visible in this system
   */
  get visibleParts() {
    return this._visibleParts;
  }
  set visibleParts(value) {
    this._visibleParts = value;
  }
  get previous() {
    return this._previous;
  }
  set previous(value) {
    this._previous = value;
  }
  get next() {
    return this._next;
  }
  set next(value) {
    this._next = value;
  }
  get pageRef() {
    return this._pageRef;
  }
  set pageRef(value) {
    this._pageRef = value;
  }
  /**
   * set to true if this system starts a new page
   */
  get newPage() {
    return this._newPage;
  }
  set newPage(value) {
    this._newPage = value;
  }
  /**
   * set to true in order to force re-rendering of system on next render
   */
  get needsRendering() {
    return this._needsRendering;
  }
  set needsRendering(value) {
    this._needsRendering = value;
  }
  /**
   * list of SlurSegments that should be rendered for this system
   */
  get slurSegments() {
    return this._slurSegments;
  }
  set slurSegments(value) {
    this._slurSegments = value;
  }
};

// src/data/io/RepeatDOData.ts
var RepeatDOData = class {
};

// src/data/playback/RepeatDO.ts
var RepeatDO = class extends BaseDataObject {
  toRepeatDOData() {
    let data = new RepeatDOData();
    data.repeatDirection = this.repeatDirection;
    data.repeatTimes = this.repeatTimes;
    data.endingNumber = this.endingNumber;
    data.endingType = this.endingType;
    data.endingVPos = this.endingVPos;
    return data;
  }
};

// src/data/io/NotationObjectData.ts
var NotationObjectData = class {
};

// src/data/io/NoteAttachmentData.ts
var NoteAttachmentData = class extends NotationObjectData {
};

// src/data/io/ArticulationData.ts
var ArticulationData = class extends NoteAttachmentData {
};

// src/data/symbols/NotationObject.ts
var NotationObject = class extends BaseDataObject {
  constructor() {
    super();
    this._visible = false;
  }
  addNotationObjectData(data) {
    data.visible = this._visible;
    data.hPos = this._hPos;
    data.vPos = this._vPos;
  }
  get visible() {
    return this._visible;
  }
  set visible(value) {
    this._visible = value;
  }
  get hPos() {
    return this._hPos;
  }
  set hPos(value) {
    this._hPos = value;
  }
  get vPos() {
    return this._vPos;
  }
  set vPos(value) {
    this._vPos = value;
  }
};

// src/data/symbols/NoteAttachment.ts
var NoteAttachment = class extends NotationObject {
  constructor() {
    super();
    this._isAbove = false;
    this._height = 0;
    this._width = 0;
    this._outsideStaff = false;
  }
  addNoteAttachmentData(data) {
    super.addNotationObjectData(data);
    data.noteGroupUniqueID = this._noteGroup.uniqueID;
    data.isAbove = this._isAbove;
    data.height = this._height;
    data.width = this._width;
    data.outsideStaff = this._outsideStaff;
  }
  get noteGroup() {
    return this._noteGroup;
  }
  set noteGroup(value) {
    this._noteGroup = value;
  }
  get isAbove() {
    return this._isAbove;
  }
  set isAbove(value) {
    this._isAbove = value;
  }
  /**
   * the height of the attachment, in staffline spaces
   */
  get height() {
    return this._height;
  }
  set height(value) {
    this._height = value;
  }
  /**
   * the width of the attachment, in staffline spaces
   */
  get width() {
    return this._width;
  }
  set width(value) {
    this._width = value;
  }
  get outsideStaff() {
    return this._outsideStaff;
  }
  set outsideStaff(value) {
    this._outsideStaff = value;
  }
};

// src/data/symbols/Articulation.ts
var Articulation = class extends NoteAttachment {
  constructor() {
    super();
    this._type = 0;
  }
  toArticulationData() {
    let data = new ArticulationData();
    super.addNoteAttachmentData(data);
    data.type = this._type;
    return data;
  }
  getWidthHeight() {
    switch (this._type) {
      case ArticulationType.STACCATO:
        this.width = 0.4;
        this.height = 0.4;
        break;
      case ArticulationType.LEGATO:
        this.width = 1;
        this.height = 0.25;
        break;
      case ArticulationType.ACCENT:
        this.width = 1.3;
        this.height = 1;
        break;
      default:
        this.width = 1;
        this.height = 1;
    }
  }
  /**an integer matching a constant from the ArticulationType class*/
  get type() {
    return this._type;
  }
  set type(value) {
    this._type = value;
  }
};

// src/data/io/BarlineData.ts
var BarlineData = class extends NotationObjectData {
};

// src/data/symbols/Barline.ts
var Barline = class extends NotationObject {
  constructor() {
    super();
  }
  toBarlineData() {
    let data = new BarlineData();
    super.addNotationObjectData(data);
    data.style = this.style;
    data.location = this.location;
    return data;
  }
};

// src/data/io/MeasureAttachmentData.ts
var MeasureAttachmentData = class extends NotationObjectData {
};

// src/data/io/ChordSymbolData.ts
var ChordSymbolData = class extends MeasureAttachmentData {
};

// src/data/symbols/MeasureAttachment.ts
var MeasureAttachment = class extends NotationObject {
  constructor() {
    super();
    this._isAbove = false;
  }
  addMeasureAttachmentData(data) {
    super.addNotationObjectData(data);
    data.qNoteTime = this._qNoteTime;
    data.isAbove = this._isAbove;
    data.height = this._height;
    data.width = this._width;
    data.baseline = this._baseline;
    data.measureAttachmentType = this.measureAttachmentType;
  }
  /**
   * the time in the score that this object is attached (and potentially takes 
   * effect)
   */
  get qNoteTime() {
    return this._qNoteTime;
  }
  set qNoteTime(value) {
    this._qNoteTime = value;
  }
  /**
   * the Measure this object is attached to
   */
  get measure() {
    return this._measure;
  }
  set measure(value) {
    this._measure = value;
  }
  /**
   * true if this attachment goes above the measure
   */
  get isAbove() {
    return this._isAbove;
  }
  set isAbove(value) {
    this._isAbove = value;
  }
  /**
   * the height of the attachment - used for spacing calculations
   */
  get height() {
    return this._height;
  }
  set height(value) {
    this._height = value;
  }
  /**
   * the width of the attachment - used for spacing calculations
   */
  get width() {
    return this._width;
  }
  set width(value) {
    this._width = value;
  }
  /**the default position, in spaces, relative to the top staff line. Negative
   * values indicate a position above the staff. If null, no baseline is used.
   **/
  get baseline() {
    return this._baseline;
  }
  set baseline(value) {
    this._baseline = value;
  }
};

// src/data/symbols/ChordSymbol.ts
var ChordSymbol = class extends MeasureAttachment {
  constructor() {
    super();
    this.degrees = [];
    this.measureAttachmentType = 1 /* chordSymbol */;
  }
  toMeasureAttachmentData() {
    let data = new ChordSymbolData();
    super.addMeasureAttachmentData(data);
    data.rootName = this.rootName;
    data.rootAlteration = this.rootAlteration;
    data.bassName = this.bassName;
    data.bassAlteration = this.bassAlteration;
    data.type = this.type;
    data.displayType = this.displayType;
    data.degrees = this.degrees.map((d) => d.toChordSymbolDegreeData());
    return data;
  }
  /**calculates the height of the chord symbol. Pass in the [textHeight] in
   * spaces (available from [ScoreProperties]).*/
  calculateHeight(textHeight) {
    this.height = this.bassName != null ? textHeight + 0.5 : textHeight;
  }
};

// src/data/io/ChordSymbolDegreeData.ts
var ChordSymbolDegreeData = class {
};

// src/data/symbols/ChordSymbolDegree.ts
var ChordSymbolDegree = class {
  toChordSymbolDegreeData() {
    let data = new ChordSymbolDegreeData();
    data.value = this.value;
    data.alteration = this.alteration;
    data.type = this.type;
    data.displayText = this.displayText;
    return data;
  }
};

// src/data/io/ClefData.ts
var ClefData = class extends NotationObjectData {
};

// src/data/symbols/Clef.ts
var Clef = class extends NotationObject {
  constructor() {
    super();
    this._isNew = false;
    this._show = false;
    this._smallSize = true;
  }
  toClefData() {
    let data = new ClefData();
    super.addNotationObjectData(data);
    data.uniqueID = this.uniqueID;
    data.type = this._type;
    data.qNoteTime = this._qNoteTime;
    data.isNew = this._isNew;
    data.show = this._show;
    data.smallSize = this._smallSize;
    return data;
  }
  /**
   * a string matching a constant from the ClefType class
   */
  get type() {
    return this._type;
  }
  set type(value) {
    this._type = value;
  }
  /**
   * the time that this clef starts taking effect, from the beginning of the 
   * piece in terms of quarter notes
   */
  get qNoteTime() {
    return this._qNoteTime;
  }
  set qNoteTime(value) {
    this._qNoteTime = value;
  }
  /**
   * a reference to the NoteGroup this clef should precede, if any. If this is
   * null, the clef is placed at the beginning of the measure.
   */
  /*public function get ngRef():NoteGroup { return _ngRef; }
  public function set ngRef(value:NoteGroup):void { _ngRef = value; }*/
  /**
   * true if the clef is new (previous music was in a different clef, or there
   * was no previous music
   */
  get isNew() {
    return this._isNew;
  }
  set isNew(value) {
    this._isNew = value;
  }
  /**
   * true if the clef should be rendered and counted in spacing
   */
  get show() {
    return this._show;
  }
  set show(value) {
    this._show = value;
  }
  /**
   * true if the clef should be displayed at reduced size (system start clefs 
   * are full size, but others are usually reduced)
   */
  get smallSize() {
    return this._smallSize;
  }
  set smallSize(value) {
    this._smallSize = value;
  }
};

// src/data/io/DynamicData.ts
var DynamicData = class extends MeasureAttachmentData {
};

// src/data/symbols/Dynamic.ts
var Dynamic = class extends MeasureAttachment {
  constructor() {
    super();
    this._volume = 1;
    this.measureAttachmentType = 0 /* dynamic */;
  }
  toMeasureAttachmentData() {
    let data = new DynamicData();
    super.addMeasureAttachmentData(data);
    data.volume = this._volume;
    data.type = this._type;
    return data;
  }
  /**
   * A value between 0 and 1 indicating how this marking should affect the 
   * volume
   */
  get volume() {
    return this._volume;
  }
  set volume(value) {
    this._volume = value;
  }
  /**
   * Matches a constant from the DynamicType class
   */
  get type() {
    return this._type;
  }
  set type(value) {
    this._type = value;
  }
};

// src/data/io/LyricData.ts
var LyricData = class extends NotationObjectData {
};

// src/data/symbols/Lyric.ts
var Lyric = class extends NotationObject {
  constructor(syllablePosition, text, verse, hPos, vPos) {
    super();
    this._verse = 0;
    this._syllablePosition = syllablePosition;
    this._text = text;
    this._verse = verse;
    this.hPos = hPos;
    this.vPos = vPos;
  }
  toLyricData() {
    let data = new LyricData();
    super.addNotationObjectData(data);
    data.syllablePosition = this._syllablePosition;
    data.text = this._text;
    data.verse = this._verse;
    return data;
  }
  get syllablePosition() {
    return this._syllablePosition;
  }
  set syllablePosition(value) {
    this._syllablePosition = value;
  }
  get text() {
    return this._text;
  }
  set text(value) {
    this._text = value;
  }
  get verse() {
    return this._verse;
  }
  set verse(value) {
    this._verse = value;
  }
};

// src/data/io/MeasureInstructionData.ts
var MeasureInstructionData = class extends MeasureAttachmentData {
};

// src/data/symbols/MeasureInstruction.ts
var MeasureInstruction = class extends MeasureAttachment {
  constructor() {
    super();
    this.measureAttachmentType = 2 /* measureInstruction */;
  }
  toMeasureAttachmentData() {
    let data = new MeasureInstructionData();
    super.addMeasureAttachmentData(data);
    data.text = this.text;
    return data;
  }
};

// src/data/io/NoteData.ts
var NoteData = class extends NotationObjectData {
};

// src/data/symbols/Note.ts
var Note = class extends NotationObject {
  constructor() {
    super();
    //doesn't include alteration
    this._pitchName = "C4";
    //0 is no alteration, 1 is a half step up, -1 a half step down, etc. 
    //Absolute (not relative to display key)
    this._alteration = 0;
    //includes alteration - displayed pitch, not concert pitch (So trumpet D4 is
    //6200)
    this._displayCents = 0;
    //used for drum channels
    this._playbackCents = 0;
    //absolute accidental, regardless of key signature (aligns with alteration)
    this._accidental = AccidentalType.NONE;
    this._showAccidental = false;
    this._tieState = TieState.NONE;
    //the number of leger lines - negative numbers mean lines above, positive 
    //numbers mean lines below, 0 means no leger lines
    this._legerLines = 0;
    //the number of steps from the top staff line - each line or space is a 
    //step - negative values imply above the staff
    this._stepsFromTopStaffLine = 0;
    //stores previous value of _visible
    this._prevVisible = null;
    this._visible = true;
  }
  toNoteData() {
    let data = new NoteData();
    super.addNotationObjectData(data);
    data.pitchName = this._pitchName;
    data.alteration = this._alteration;
    data.displayCents = this._displayCents;
    data.playbackCents = this._playbackCents;
    data.accidental = this._accidental;
    data.showAccidental = this._showAccidental;
    data.tieState = this._tieState;
    data.legerLines = this._legerLines;
    data.stepsFromTopStaffLine = this._stepsFromTopStaffLine;
    return data;
  }
  /**reverts the visibility of the NG to its last state. Calling multiple
   * times will have no additional effect (not a complete undo history).*/
  revertVisibility() {
    if (this._prevVisible != null) {
      this._visible = this._prevVisible;
      this._prevVisible = null;
    }
  }
  get tieState() {
    return this._tieState;
  }
  set tieState(value) {
    this._tieState = value;
  }
  get isTieTarget() {
    return this._tieState == TieState.CONTINUE || this._tieState == TieState.STOP;
  }
  get accidental() {
    return this._accidental;
  }
  set accidental(value) {
    this._accidental = value;
  }
  get showAccidental() {
    return this._showAccidental;
  }
  set showAccidental(value) {
    this._showAccidental = value;
  }
  /**
   * the horizontal position of the accidental in respect to the NoteGroup 
   * (not Note)
   */
  get accidentalPos() {
    return this._accidentalPos;
  }
  set accidentalPos(value) {
    this._accidentalPos = value;
  }
  /**pitch name, including letter and number, but no alteration (C4) */
  get pitchName() {
    return this._pitchName;
  }
  set pitchName(value) {
    this._pitchName = value;
  }
  /**the letter portion of the pitch (C in C4) - doesn't include alteration */
  get pitchLetter() {
    if (this._pitchLetter == null) {
      this._pitchLetter = this._pitchName[0];
    }
    return this._pitchLetter;
  }
  /**the octave portion of the pitch (4 in C4) */
  get pitchOctave() {
    if (this._pitchOctave == null) {
      this._pitchOctave = Number.parseInt(this._pitchName.slice(1));
    }
    return this._pitchOctave;
  }
  get alteration() {
    return this._alteration;
  }
  set alteration(value) {
    this._alteration = value;
  }
  get displayCents() {
    return this._displayCents;
  }
  set displayCents(value) {
    this._displayCents = value;
  }
  /**
   * used for drum channels to denote a different playback pitch than 
   * displayed pitch
   */
  get playbackCents() {
    return this._playbackCents;
  }
  set playbackCents(value) {
    this._playbackCents = value;
  }
  get legerLines() {
    return this._legerLines;
  }
  set legerLines(value) {
    this._legerLines = value;
  }
  get stepsFromTopStaffLine() {
    return this._stepsFromTopStaffLine;
  }
  set stepsFromTopStaffLine(value) {
    this._stepsFromTopStaffLine = value;
  }
  get visible() {
    return this._visible;
  }
  set visible(value) {
    this._prevVisible = value;
    this._visible = value;
  }
  /**
   * the parent NoteGroup for this Note
   */
  get noteGroup() {
    return this._noteGroup;
  }
  set noteGroup(value) {
    this._noteGroup = value;
  }
};

// src/data/io/OrnamentData.ts
var OrnamentData = class extends NoteAttachmentData {
};

// src/data/symbols/Ornament.ts
var Ornament = class extends NoteAttachment {
  constructor() {
    super();
    this._type = 0;
  }
  toOrnamentData() {
    let data = new OrnamentData();
    super.addNoteAttachmentData(data);
    data.type = this._type;
    return data;
  }
  getWidthHeight() {
    switch (this._type) {
      case OrnamentType.INVERTED_MORDENT:
        this.width = 2;
        this.height = 1.5;
        break;
      case OrnamentType.MORDENT:
        this.width = 2;
        this.height = 1;
        break;
      case OrnamentType.TRILL_MARK:
        this.width = 2;
        this.height = 1.6;
        break;
      default:
        this.width = 1;
        this.height = 1;
    }
  }
  /** an integer matching a constant from the ArticulationType class*/
  get type() {
    return this._type;
  }
  set type(value) {
    this._type = value;
  }
};

// src/data/io/SlurData.ts
var SlurData = class {
};

// src/data/symbols/Slur.ts
var Slur = class extends BaseDataObject {
  constructor() {
    super();
    //used to identify the slur with MusicXML - NOT UNIQUE
    this._slurID = 0;
    //true if the slur should appear above the notes
    this._above = false;
  }
  toSlurData() {
    let sd = new SlurData();
    sd.uniqueID = this.uniqueID;
    sd.firstNoteUniqueID = this._firstNote.uniqueID;
    sd.endNoteUniqueID = this._endNote.uniqueID;
    return sd;
  }
  /**
   * used to identify the slur with MusicXML - NOT UNIQUE
   */
  get slurID() {
    return this._slurID;
  }
  set slurID(value) {
    this._slurID = value;
  }
  /**
   * true if the slur should appear above the notes
   */
  get above() {
    return this._above;
  }
  set above(value) {
    this._above = value;
  }
  /**
   * the note where the slur starts
   */
  get firstNote() {
    return this._firstNote;
  }
  set firstNote(value) {
    this._firstNote = value;
  }
  /**
   * the note where the slur ends
   */
  get endNote() {
    return this._endNote;
  }
  set endNote(value) {
    this._endNote = value;
  }
  /**
   * the SlurSegment objects that are drawn for this slur
   */
  get segments() {
    return this._segments;
  }
  set segments(value) {
    this._segments = value;
  }
};

// src/data/io/TempoMarkerData.ts
var TempoMarkerData = class {
};

// src/data/symbols/TempoMarker.ts
var TempoMarker = class extends BaseDataObject {
  constructor() {
    super();
  }
  toTempoMarkerData() {
    let data = new TempoMarkerData();
    data.tempo = this._tempo;
    data.qNoteTime = this._qNoteTime;
    return data;
  }
  /**
   * a bpm tempo value
   */
  get tempo() {
    return this._tempo;
  }
  set tempo(value) {
    this._tempo = value;
  }
  /**
   * the time that this tempo takes effect (in terms of quarter notes from 
   * song beginning)
   */
  get qNoteTime() {
    return this._qNoteTime;
  }
  set qNoteTime(value) {
    this._qNoteTime = value;
  }
};

// src/data/io/TupletData.ts
var TupletData = class {
};

// src/data/symbols/Tuplet.ts
var Tuplet = class extends BaseDataObject {
  constructor() {
    super();
    //an identifier value for the tuplet - useful when dealing with nested 
    //tuplets
    this._tupletID = 0;
    //the number of notes private _numeratorDuration:of value that fit in the 
    //tuplet (for 3 in the space of 2, this is the 3)
    this._numerator = 0;
    //the number of notes private _denominatorDuration:of value that represent 
    //the real duration the tuplet takes up (for 3 in the space of 2, this is 
    //the 2)
    this._denominator = 0;
    //the duration of the type of note X as in X in the space of Y
    this._numeratorDuration = 0;
    //the duration of the type of note Y as in X in the space of Y
    this._denominatorDuration = 0;
    //the tuplet should be placed above
    this._above = false;
    this._showBracket = false;
    this._vPos = 0;
  }
  toTupletData() {
    let td = new TupletData();
    td.uniqueID = this.uniqueID;
    td.tupletID = this._tupletID;
    td.firstNoteUniqueId = this._firstNote.uniqueID;
    td.endNoteUniqueId = this._endNote.uniqueID;
    td.numerator = this._numerator;
    td.denominator = this._denominator;
    td.numeratorDuration = this.numeratorDuration;
    td.denominatorDuration = this.denominatorDuration;
    return td;
  }
  get tupletID() {
    return this._tupletID;
  }
  set tupletID(value) {
    this._tupletID = value;
  }
  get firstNote() {
    return this._firstNote;
  }
  set firstNote(value) {
    this._firstNote = value;
  }
  get endNote() {
    return this._endNote;
  }
  set endNote(value) {
    this._endNote = value;
  }
  get numerator() {
    return this._numerator;
  }
  set numerator(value) {
    this._numerator = value;
  }
  get denominator() {
    return this._denominator;
  }
  set denominator(value) {
    this._denominator = value;
  }
  get numeratorDuration() {
    return this._numeratorDuration;
  }
  set numeratorDuration(value) {
    this._numeratorDuration = value;
  }
  get denominatorDuration() {
    return this._denominatorDuration;
  }
  set denominatorDuration(value) {
    this._denominatorDuration = value;
  }
  get above() {
    return this._above;
  }
  set above(value) {
    this._above = value;
  }
  get showBracket() {
    return this._showBracket;
  }
  set showBracket(value) {
    this._showBracket = value;
  }
  get vPos() {
    return this._vPos;
  }
  set vPos(value) {
    this._vPos = value;
  }
};

// src/data/io/ScoreIO.ts
var ScoreIO = class {
  //////////SCORE/MEASURE STACKS/////////
  createScoreData(score) {
    return score.toScoreData();
  }
  createScoreDataJsonString(scoreData) {
    return JSON.stringify(scoreData);
  }
  parseScoreDataJsonString(jsonString) {
    return JSON.parse(jsonString);
  }
  /**
   * @param scoreData - the full ScoreData from a createScoreData() call, not
   * just the stacks intended for inclusion
   * @param scoreProperties optional ScoreProperties settings
   * @param startMeasureIndex - the index of the first stack in the scoreData
   * that should be included.
   * @param endMeasureIndex - the index of the last stack in the scoreData
   * that should be included.
   */
  createScoreFromScoreData(scoreData, scoreProperties = null, startMeasureIndex = 0, endMeasureIndex = null) {
    if (scoreProperties == null) {
      scoreProperties = ScoreProperties.getNewScoreProperties();
    }
    let score = new Score();
    score.scoreProperties = scoreProperties;
    score.graceNotesNeedFixing = scoreData.graceNotesNeedFixing;
    let page = new Page();
    score.addPage(page);
    let system = new System(scoreProperties);
    system.newPage = true;
    page.addSystem(system);
    if (endMeasureIndex == null) {
      endMeasureIndex = scoreData.measureStacks.length - 1;
    }
    let qNoteOffset = scoreData.measureStacks[startMeasureIndex].startTime;
    for (let i = startMeasureIndex; i <= endMeasureIndex; i++) {
      let stackData = scoreData.measureStacks[i];
      let stack = this._createMeasureStack(
        stackData,
        qNoteOffset,
        startMeasureIndex,
        startMeasureIndex === i
      );
      system.addMeasureStack(stack);
    }
    this._addStartAndEndTempoMarkers(
      system.measureStacks[0],
      system.measureStacks[system.measureStacks.length - 1],
      scoreData,
      startMeasureIndex,
      endMeasureIndex
    );
    return score;
  }
  //////////PARTS//////////
  createPartData(part) {
    return part.toPartData();
  }
  createPartDataJsonString(partData) {
    return JSON.stringify(partData);
  }
  parsePartDataJsonString(jsonString) {
    return JSON.parse(jsonString);
  }
  /**
   * @param score - the Score object to add the parts to. This should contain
   * only the stacks that should be filled, which is potentially a subset of
   * the stacks in the ScoreData object
   * @param partData - the full PartData from a createPartData() call, not
   * just the measures intended for inclusion
   * @param scoreData - the full ScoreData from a createScoreData() call, not
   * just the stacks intended for inclusion
   * @param startMeasureIndex - the index of the first measure in the part
   * that measures should be pulled from. The endIndex is determined by the
   * number of stacks in the passed in score.
   */
  addPartDataToScore(score, partData, scoreData, startMeasureIndex = 0) {
    let stacks = score.getMeasureStacks();
    let qNoteOffset = scoreData.measureStacks[startMeasureIndex].startTime;
    let numMeasures = stacks.length;
    let part = this._createPart(
      partData,
      score,
      stacks,
      startMeasureIndex,
      numMeasures,
      qNoteOffset
    );
    score.addPart(part);
  }
  //////////////score/stack parsing//////////////
  _createMeasureStack(data, qNoteOffset, measureOffset, isFirstStack) {
    let stack = new MeasureStack();
    stack.startTime = data.startTime - qNoteOffset;
    stack.endTime = data.endTime - qNoteOffset;
    stack.qNoteLength = data.qNoteLength;
    stack.number = data.number - measureOffset;
    stack.newKey = data.newKey || isFirstStack;
    stack.newClef = data.newClef || isFirstStack;
    if (data.tempoMarkers != null) {
      this._processStackTempoMarkers(stack, data, qNoteOffset);
    }
    if (data.barline != null) {
      stack.barline = this._createBarline(data.barline);
    }
    if (data.repeatDOs != null) {
      this._processStackRepeatDOs(stack, data);
    }
    stack.isPickup = data.isPickup;
    return stack;
  }
  _processStackTempoMarkers(stack, stackData, qNoteOffset) {
    stack.tempoMarkers = [];
    for (let tmData of stackData.tempoMarkers) {
      let tm = new TempoMarker();
      tm.tempo = tmData.tempo;
      tm.qNoteTime = tmData.qNoteTime - qNoteOffset;
      stack.tempoMarkers.push(tm);
    }
  }
  _createBarline(data) {
    let barline = new Barline();
    barline.hPos = data.hPos;
    barline.vPos = data.vPos;
    barline.visible = data.visible;
    barline.style = data.style;
    barline.location = data.location;
    return barline;
  }
  _processStackRepeatDOs(stack, stackData) {
    stack.repeatDOs = [];
    for (let rdoData of stackData.repeatDOs) {
      let rdo = new RepeatDO();
      rdo.repeatDirection = rdoData.repeatDirection;
      rdo.repeatTimes = rdoData.repeatTimes;
      rdo.endingNumber = rdoData.endingNumber;
      rdo.endingType = rdoData.endingType;
      rdo.endingVPos = rdoData.endingVPos;
      stack.repeatDOs.push(rdo);
    }
  }
  _addStartAndEndTempoMarkers(startStack, endStack, scoreData, startMeasureIndex, endMeasureIndex) {
    if (startStack.tempoMarkers == null || startStack.tempoMarkers[0].qNoteTime > startStack.startTime) {
      for (let i = startMeasureIndex - 1; i >= 0; i--) {
        let data = scoreData.measureStacks[i];
        if (data.tempoMarkers != null) {
          let tmd = data.tempoMarkers[data.tempoMarkers.length - 1];
          let tm = new TempoMarker();
          tm.tempo = tmd.tempo;
          tm.qNoteTime = startStack.startTime;
          if (startStack.tempoMarkers != null) {
            startStack.tempoMarkers.splice(0, 0, tm);
          } else {
            startStack.tempoMarkers = [tm];
          }
          break;
        }
      }
    }
    if (endStack.tempoMarkers == null || endStack.tempoMarkers[endStack.tempoMarkers.length - 1].qNoteTime !== endStack.endTime) {
      for (let i = endMeasureIndex; i >= 0; i--) {
        let data = scoreData.measureStacks[i];
        if (data.tempoMarkers != null) {
          let tmd = data.tempoMarkers[data.tempoMarkers.length - 1];
          let tm = new TempoMarker();
          tm.tempo = tmd.tempo;
          tm.qNoteTime = endStack.endTime;
          if (endStack.tempoMarkers != null) {
            endStack.tempoMarkers.push(tm);
          } else {
            endStack.tempoMarkers = [tm];
          }
          break;
        }
      }
    }
  }
  //////////////part parsing//////////////////
  _createPart(data, score, stacks, startMeasureIndex, numMeasures, qNoteOffset) {
    let partId = `P${score.parts.length + 1}`;
    let part = new Part(partId, data.name, data.abbreviation);
    part.volume = 1;
    part.midiChannel = data.midiChannel;
    part.midiBank = data.midiBank;
    part.midiPreset = data.midiPreset;
    part.setTransposition(
      data.diatonicTransposition,
      data.chromaticTransposition
    );
    part.percussionMap = data.percussionMap?.slice();
    this._slursInPart = [];
    for (let staffData of data.staves) {
      part.addStaff(this._createStaff(
        staffData,
        stacks,
        startMeasureIndex,
        numMeasures,
        qNoteOffset
      ));
    }
    this._slursInPart = null;
    return part;
  }
  _createStaff(data, stacks, startMeasureIndex, numMeasures, qNoteOffset) {
    let staff = new Staff();
    staff.visible = data.visible;
    let endMeasureIndex = startMeasureIndex + numMeasures - 1;
    for (let i = startMeasureIndex; i <= endMeasureIndex; i++) {
      let stack = stacks[i - startMeasureIndex];
      let measureData = data.measures[i];
      let measure = this._createMeasure(
        measureData,
        stack,
        qNoteOffset,
        i === startMeasureIndex
      );
      staff.addMeasure(measure);
      stack.addMeasure(measure);
      measure.stack = stack;
    }
    return staff;
  }
  _createMeasure(data, stack, qNoteOffset, isFirstMeasure) {
    let measure = new Measure();
    measure.diatonicTransposition = data.diatonicTransposition;
    measure.chromaticTransposition = data.chromaticTransposition;
    measure.concertKey = data.concertKey;
    measure.displayKey = data.displayKey;
    measure.outgoingKey = data.outgoingKey;
    measure.isMajorKey = data.isMajorKey;
    measure.showKey = data.showKey || isFirstMeasure;
    measure.numBeats = data.numBeats;
    measure.beatType = data.beatType;
    measure.displayNumBeats = data.displayNumBeats;
    measure.displayBeatType = data.displayBeatType;
    measure.beatGroups = data.beatGroups.slice();
    measure.subBeatGroups = data.subBeatGroups.slice();
    measure.showTime = data.showTime || isFirstMeasure;
    for (let clefData of data.clefs) {
      measure.clefs.push(this._createClef(clefData, qNoteOffset));
    }
    for (let attachmentData of data.attachments) {
      measure.attachments.push(this._createMeasureAttachment(
        attachmentData,
        qNoteOffset,
        measure
      ));
    }
    for (let voiceData of data.voices) {
      let voice = measure.getVoiceByNumber(voiceData.number);
      this._processVoiceData(voiceData, voice, qNoteOffset);
    }
    return measure;
  }
  _createClef(data, qNoteOffset) {
    let clef = new Clef();
    clef.hPos = data.hPos;
    clef.vPos = data.vPos;
    clef.visible = data.visible;
    clef.uniqueID = data.uniqueID;
    clef.type = data.type;
    clef.qNoteTime = data.qNoteTime - qNoteOffset;
    clef.isNew = data.isNew;
    clef.show = data.show;
    clef.smallSize = data.smallSize;
    return clef;
  }
  _createMeasureAttachment(data, qNoteOffset, measure) {
    let attachment;
    switch (data.measureAttachmentType) {
      case 0 /* dynamic */: {
        let dynamic = new Dynamic();
        let dynamicData = data;
        dynamic.volume = dynamicData.volume;
        dynamic.type = dynamicData.type;
        attachment = dynamic;
        break;
      }
      case 1 /* chordSymbol */: {
        let cs = new ChordSymbol();
        let csData = data;
        cs.rootName = csData.rootName;
        cs.rootAlteration = csData.rootAlteration;
        cs.bassName = csData.bassName;
        cs.bassAlteration = csData.bassAlteration;
        cs.type = csData.type;
        cs.displayType = csData.displayType;
        for (let csdData of csData.degrees) {
          let csd = new ChordSymbolDegree();
          csd.value = csdData.value;
          csd.alteration = csdData.alteration;
          csd.type = csdData.type;
          csd.displayText = csdData.displayText;
          cs.degrees.push(csd);
        }
        attachment = cs;
        break;
      }
      case 2 /* measureInstruction */: {
        let instruction = new MeasureInstruction();
        let instructionData = data;
        instruction.text = instructionData.text;
        attachment = instruction;
      }
    }
    attachment.hPos = data.hPos;
    attachment.vPos = data.vPos;
    attachment.visible = data.visible;
    attachment.qNoteTime = data.qNoteTime - qNoteOffset;
    attachment.measure = measure;
    attachment.isAbove = data.isAbove;
    attachment.height = data.height;
    attachment.width = data.width;
    attachment.baseline = data.baseline;
    attachment.measureAttachmentType = data.measureAttachmentType;
    return attachment;
  }
  _processVoiceData(data, voice, qNoteOffset) {
    let measure = voice.measure;
    let tupletsInVoice = [];
    for (let ngData of data.noteGroups) {
      let ng = new NoteGroup();
      ng.uniqueID = ngData.uniqueID;
      for (let noteData of ngData.notes) {
        ng.addNote(this._createNote(noteData));
      }
      ng.stemDirection = ngData.stemDirection;
      ng.duration = ngData.duration;
      ng.qNoteDuration = ngData.qNoteDuration;
      ng.qNoteTime = ngData.qNoteTime - qNoteOffset;
      ng.durationType = ngData.durationType;
      ng.numDots = ngData.numDots;
      ng.isGrace = ngData.isGrace;
      ng.beamStates = ngData.beamStates.slice();
      ng.isRest = ngData.isRest;
      if (ngData.tuplets != null) {
        this._processTupletsForNoteGroup(ng, ngData, tupletsInVoice);
      }
      if (ngData.slurs != null) {
        this._processSlursForNoteGroup(ng, ngData);
      }
      if (ngData.clefUniqueID != null) {
        ng.clef = measure.clefs.find((c) => c.uniqueID === ngData.clefUniqueID);
      }
      if (ngData.lyric != null) {
        ng.lyric = this._createLyric(ngData.lyric);
      }
      ng.clipStart = ngData.clipStart;
      ng.clipEnd = ngData.clipEnd;
      if (ngData.articulations != null) {
        this._processArticulations(ng, ngData);
      }
      if (ngData.ornaments != null) {
        this._processOrnaments(ng, ngData);
      }
      ng.visible = ngData.visible;
      ng.trackId = ngData.trackId;
      voice.addNoteGroup(ng);
    }
  }
  _createLyric(data) {
    let lyric = new Lyric(
      data.syllablePosition,
      data.text,
      data.verse,
      data.hPos,
      data.vPos
    );
    lyric.visible = data.visible;
    return lyric;
  }
  _createNote(data) {
    let note = new Note();
    note.hPos = data.hPos;
    note.vPos = data.vPos;
    note.visible = data.visible;
    note.pitchName = data.pitchName;
    note.alteration = data.alteration;
    note.displayCents = data.displayCents;
    note.playbackCents = data.playbackCents;
    note.accidental = data.accidental;
    note.showAccidental = data.showAccidental;
    note.tieState = data.tieState;
    note.legerLines = data.legerLines;
    note.stepsFromTopStaffLine = data.stepsFromTopStaffLine;
    return note;
  }
  _processTupletsForNoteGroup(ng, ngData, tupletsInVoice) {
    ng.tuplets = [];
    for (let tupletData of ngData.tuplets) {
      let tuplet = tupletsInVoice.find((t) => t.uniqueID === tupletData.uniqueID);
      if (tuplet == null) {
        tuplet = this._createTuplet(tupletData);
        tupletsInVoice.push(tuplet);
      }
      if (ng.uniqueID === tupletData.firstNoteUniqueId) {
        tuplet.firstNote = ng;
      }
      if (ng.uniqueID === tupletData.endNoteUniqueId) {
        tuplet.endNote = ng;
      }
      ng.tuplets.push(tuplet);
    }
  }
  _createTuplet(data) {
    let tuplet = new Tuplet();
    tuplet.uniqueID = data.uniqueID;
    tuplet.tupletID = data.tupletID;
    tuplet.numerator = data.numerator;
    tuplet.denominator = data.denominator;
    tuplet.numeratorDuration = data.numeratorDuration;
    tuplet.denominatorDuration = data.denominatorDuration;
    return tuplet;
  }
  _processSlursForNoteGroup(ng, ngData) {
    ng.slurs = [];
    for (let slurData of ngData.slurs) {
      let slur = this._slursInPart.find((s) => s.uniqueID === slurData.uniqueID);
      if (slur == null) {
        slur = this._createSlur(slurData);
        this._slursInPart.push(slur);
      }
      if (ng.uniqueID === slurData.firstNoteUniqueID) {
        slur.firstNote = ng;
      }
      if (ng.uniqueID === slurData.endNoteUniqueID) {
        slur.endNote = ng;
      }
      ng.slurs.push(slur);
    }
  }
  _createSlur(data) {
    let slur = new Slur();
    slur.uniqueID = data.uniqueID;
    return slur;
  }
  _processArticulations(ng, ngData) {
    ng.articulations = [];
    for (let articulationData of ngData.articulations) {
      let articulation = new Articulation();
      articulation.hPos = articulationData.hPos;
      articulation.vPos = articulationData.vPos;
      articulation.visible = articulationData.visible;
      articulation.noteGroup = ng;
      articulation.isAbove = articulationData.isAbove;
      articulation.height = articulationData.height;
      articulation.width = articulationData.width;
      articulation.outsideStaff = articulationData.outsideStaff;
      articulation.type = articulationData.type;
      ng.articulations.push(articulation);
    }
  }
  _processOrnaments(ng, ngData) {
    ng.ornaments = [];
    for (let ornamentData of ngData.ornaments) {
      let ornament = new Ornament();
      ornament.hPos = ornamentData.hPos;
      ornament.vPos = ornamentData.vPos;
      ornament.visible = ornamentData.visible;
      ornament.noteGroup = ng;
      ornament.isAbove = ornamentData.isAbove;
      ornament.height = ornamentData.height;
      ornament.width = ornamentData.width;
      ornament.outsideStaff = ornamentData.outsideStaff;
      ornament.type = ornamentData.type;
      ng.ornaments.push(ornament);
    }
  }
};

// src/data/symbols/SlurSegment.ts
var SlurSegment = class extends BaseDataObject {
  constructor() {
    super();
    this._points = new Array(4);
  }
  /**
   * the Slur that this SlurSegment corresponds to
   */
  get slur() {
    return this._slur;
  }
  set slur(value) {
    this._slur = value;
  }
  /**
   * the System this segment belongs to
   */
  get systemRef() {
    return this._systemRef;
  }
  set systemRef(value) {
    this._systemRef = value;
  }
  /**
   * the positions of the (up to) five points of this slur segment - index 0 
   * is beginning, index 4 is end, 2 is the middle
   */
  get points() {
    return this._points;
  }
  /**
   * contains a list of X/Y pairs of coordinates for the top and bottom points
   * of each NoteGroup that falls in the segment's region
   */
  get ngCoordinates() {
    return this._ngCoordinates;
  }
  set ngCoordinates(value) {
    this._ngCoordinates = value;
  }
};

// src/data/symbols/Stem.ts
var Stem = class extends NotationObject {
  constructor() {
    super();
    //"noStem", "up", or "down"
    this._direction = StemDirection.NO_STEM;
  }
  get endPosition() {
    return this._endPosition;
  }
  set endPosition(value) {
    this._endPosition = value;
  }
  get direction() {
    return this._direction;
  }
  set direction(value) {
    this._direction = value;
  }
};

// src/editing/Arranger.ts
var Arranger = class {
  constructor(scoreManager) {
    this._debug = false;
    this._scoreManager = scoreManager;
  }
  //////////////////////////////NOTE/STAFF HIDING//////////////////////////
  /** hides all but the indicated staff for the given part*/
  makePartSingleStaff(part, staffIndex = 0) {
    for (let i = 0; i < part.staves.length; i++) {
      part.staves[i].visible = i === staffIndex;
    }
  }
  /**sets all staves to visible (reverts makePartSingleStaff())*/
  showAllStaves() {
    for (let part of this._scoreManager.score.parts) {
      for (let staff of part.staves) {
        staff.visible = true;
      }
    }
  }
  /**hides all the notes and rests on a staff */
  hideAllNotes(staff, exceptions = null) {
    if (exceptions == null) {
      for (let measure of staff.measures) {
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            ng.visible = false;
          }
        }
      }
    } else {
      for (let measure of staff.measures) {
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            if (exceptions.indexOf(ng) === -1) {
              ng.visible = false;
            }
          }
        }
      }
    }
  }
  /**shows only the top notes in the top voice*/
  showTopNotesOnly(staff) {
    for (let measure of staff.measures) {
      let voices = measure.voices;
      for (let i = 0; i < voices.length; i++) {
        let voice = voices[i];
        if (voices.length > 0 && voice.number > 1) {
          for (let ng of voice.noteGroups) {
            ng.visible = false;
          }
        } else {
          for (let ng of voice.noteGroups) {
            if (!ng.isRest) {
              let notes = ng.notes;
              if (notes.length > 1) {
                for (let j = notes.length - 2; j >= 0; j--) {
                  notes[j].visible = false;
                }
                ng.clearVisibleNotesCache();
              }
            }
          }
        }
      }
    }
  }
  /**designed to undo showTopNotes only, reverts all note visibility to
   * previous state*/
  revertNoteHiding(staff) {
    for (let measure of staff.measures) {
      for (let voice of measure.voices) {
        for (let ng of voice.noteGroups) {
          ng.revertVisibility();
          for (let note of ng.notes) {
            note.revertVisibility();
          }
          ng.clearVisibleNotesCache();
        }
      }
    }
  }
  ////////////////////////////SMART TRANSPOSITION///////////////////////////
  /**Transposes song to fit the specified range as comfortably as possible.
   * All pitches are assumed to be display pitches (not concert pitch), and
   * you should call scoreManager.setPartTransposition() before this if you
   * need to accommodate a written range.
   * 
   * If you intend to use fixOctaveProblems() after this to enforce an 
   * instrument range, you should specify a maxAllowedPitch here, as it will 
   * attempt to choose the key that will result in the fewest required octave 
   * correction points. If you don't intend to call fixOctaveProblems(), you 
   * might want to set enforceMinPitch to true and leave maxAllowedPitch null,
   * as this will use a simpler algorithm that picks the key and transposition
   * that best fits the comfort note and key range while not allowing notes to
   * go below the minAllowedPitch.
   * 
   * comfortPitch = the comfort center of the written range (soprano recorder
   * 		low C is 60, same as flute)
   * minAllowedPitch = the min allowed written pitch for the instrument (60 
   * 		for sop recorder).
   * 		minAllowedPitch is used in conjunction with enforceMinPitch. If 
   * 		enforceMinPitch is set to true,	the resulting transposition will 
   * 		honor the minAllowedPitch. If enfoceMinPitch is false, the result 
   * 		may go below the minAllowedPitch to allow for range octave 
   * 		correction to handle the task.
   * minKey = desired minimum key sig (negative values for flats, positive for
   * 		sharps)
   * maxKey = desired maximum key sig (negative values for flats, positive for
   * 		sharps)
   * parts = the part to base the song transposition on
   * limitPlaybackOctaveChange = if true, each part's playbackOctaveDelta prop
   * will be adjusted if necessary to keep the playback octave from changing
   * too drastically.
   * maxAllowedPitch = if set, this value will be used as a non-binding 
   * 		consideration for choosing the best key. The result may still go 
   * 		above this value. With this set to null, a different algorithm is 
   * 		run for choosing the best key to transpose to.*/
  fitMusicToRange(comfortPitch, minAllowedPitch, minKey, maxKey, part = null, limitPlaybackOctaveChange = true, maxAllowedPitch = null, enforceMinPitch = true) {
    let minPitch = 200;
    let maxPitch = 0;
    let pitches = [];
    if (part == null) {
      part = this._scoreManager.score.parts[0];
    }
    let noteGroups = part.getNoteGroupsWithoutRests(0, 0);
    for (let ng of noteGroups) {
      if (!ng.isRest && ng.visible) {
        let pitch = ~~(ng.visibleNotes[0].displayCents / 100);
        pitches.push(pitch);
        if (pitch > maxPitch) {
          maxPitch = pitch;
        }
        if (pitch < minPitch) {
          minPitch = pitch;
        }
      }
    }
    let adjustInterval = comfortPitch - Math.floor((minPitch + maxPitch) / 2);
    if (enforceMinPitch && minPitch + adjustInterval < minAllowedPitch) {
      adjustInterval = minAllowedPitch - minPitch;
    }
    let firstMeasure = part.staves[0].measures[0];
    let origKey = firstMeasure.displayKey;
    let isMajor = firstMeasure.isMajorKey;
    let validTranspositions = /* @__PURE__ */ new Map();
    for (let newKey = minKey; newKey <= maxKey; newKey++) {
      let upTrans = PitchUtils.getTranspositionForKeyChange(
        origKey,
        newKey,
        isMajor,
        isMajor,
        true
      );
      while (upTrans.chromatic < adjustInterval) {
        upTrans.chromatic += 12;
        upTrans.diatonic += 7;
      }
      while (upTrans.chromatic > adjustInterval + 12) {
        upTrans.chromatic -= 12;
        upTrans.diatonic -= 7;
      }
      validTranspositions.set(upTrans, newKey);
      let downTrans = new TranspositionDO(
        upTrans.diatonic - 7,
        upTrans.chromatic - 12
      );
      if (!enforceMinPitch || downTrans.chromatic + minPitch >= minAllowedPitch) {
        validTranspositions.set(downTrans, newKey);
      }
    }
    let bestTrans;
    if (maxAllowedPitch == null) {
      bestTrans = this._chooseTranspositionByKeyAndDistanceFromIdeal(
        validTranspositions,
        minKey,
        maxKey,
        adjustInterval
      );
    } else {
      bestTrans = this._chooseTranspositionByFewestOctaveShifts(
        validTranspositions,
        pitches,
        minAllowedPitch,
        maxAllowedPitch,
        adjustInterval,
        minKey,
        maxKey
      );
    }
    let pboDelta = 0;
    if (limitPlaybackOctaveChange) {
      while (bestTrans.chromatic + pboDelta * 12 > 7) {
        pboDelta--;
      }
      while (bestTrans.chromatic + pboDelta * 12 <= -5) {
        pboDelta++;
      }
    }
    this._scoreManager.transposeSong(
      bestTrans.diatonic,
      bestTrans.chromatic,
      false,
      pboDelta
    );
  }
  /**scores potential transpositions based on how far they are from the ideal 
   * chromatic transposition interval and how much they deviate from the ideal
   * key - returns the winner*/
  _chooseTranspositionByKeyAndDistanceFromIdeal(validTranspositions, minKey, maxKey, idealChromatic) {
    let idealKey = ~~((maxKey + minKey) / 2);
    let adjustmentOffset;
    let bestScore = 0;
    let bestTrans;
    let points;
    for (let trans of validTranspositions.keys()) {
      adjustmentOffset = Math.abs(trans.chromatic - idealChromatic);
      let newKey = validTranspositions.get(trans);
      points = ~~(100 - adjustmentOffset - 1.5 * Math.abs(newKey - idealKey));
      if (points > bestScore) {
        bestScore = points;
        bestTrans = trans;
      }
    }
    return bestTrans;
  }
  /**scores potential transpositions based on how many octave shifts would be 
   * necessary to make the notes fit between the min and max allowed pitches*/
  _chooseTranspositionByFewestOctaveShifts(validTranspositions, pitches, minAllowedPitch, maxAllowedPitch, idealChromatic, minKey, maxKey) {
    let idealKey = ~~((maxKey + minKey) / 2);
    let adjustmentOffset;
    let bestScore = 999999;
    let bestNonZeroOffsets = 999999;
    let bestTrans;
    let points;
    for (let trans of validTranspositions.keys()) {
      adjustmentOffset = Math.abs(trans.chromatic - idealChromatic);
      if (adjustmentOffset > 6) {
      }
      let chromaticTrans = trans.chromatic;
      let numShifts = 0;
      let currentOffset = 0;
      let numNonZeroOffsetNotes = 0;
      for (let pitch of pitches) {
        let changeMade = false;
        while (pitch + currentOffset + chromaticTrans > maxAllowedPitch) {
          currentOffset -= 12;
          changeMade = true;
        }
        while (pitch + currentOffset + chromaticTrans < minAllowedPitch) {
          currentOffset += 12;
          changeMade = true;
        }
        if (changeMade) {
          numShifts++;
        }
        if (currentOffset !== 0) {
          numNonZeroOffsetNotes++;
        }
      }
      points = numShifts;
      if (points < bestScore) {
        bestScore = points;
        bestTrans = trans;
        bestNonZeroOffsets = numNonZeroOffsetNotes;
      } else if (points === bestScore && numNonZeroOffsetNotes < bestNonZeroOffsets) {
        bestScore = points;
        bestTrans = trans;
        bestNonZeroOffsets = numNonZeroOffsetNotes;
      } else if (points === bestScore) {
        let newKeyDelta = Math.abs(idealKey - validTranspositions.get(trans));
        let newPoints = 100 - adjustmentOffset - Math.round(1.5 * newKeyDelta);
        let bestKeyDelta = Math.abs(idealKey - validTranspositions.get(bestTrans));
        let bestAdjustmentOffset = Math.abs(bestTrans.chromatic - idealChromatic);
        let bestPoints = 100 - bestAdjustmentOffset - Math.round(1.5 * bestKeyDelta);
        if (newPoints > bestPoints) {
          bestScore = points;
          bestTrans = trans;
          bestNonZeroOffsets = numNonZeroOffsetNotes;
        }
      }
    }
    return bestTrans;
  }
  /**/
  ////////////////OCTAVE RANGE CHECKING ADJUSTMENT////////////////////*/
  fixOctaveProblems(part, minPitch, maxPitch, comfortPitch) {
    let ngList = part.getNoteGroupsWithoutRestsOrTieIntos();
    if (ngList.length === 0) {
      return;
    }
    let _isInRange = (index, transposition) => {
      let pitch = ~~(ngList[index].visibleNotes[0].displayCents / 100 + 12 * transposition);
      return pitch >= minPitch && pitch <= maxPitch;
    };
    let points = this._getOctaveShiftMarkers(
      ngList,
      minPitch,
      maxPitch
    );
    let leftIndex = 0;
    let rightIndex;
    let leftTrans = points[0] == null ? 0 : points[0];
    let rightTrans;
    for (let i = 0; i < points.length; i++) {
      if (points[i] != null || i === points.length - 1) {
        if (i === points.length - 1) {
        }
        rightIndex = i;
        rightTrans = points[i] == null ? 0 : points[i];
        let leftTransReachIndex = leftIndex;
        while (leftTransReachIndex + 1 <= rightIndex && _isInRange(leftTransReachIndex + 1, leftTrans)) {
          leftTransReachIndex++;
        }
        let rightTransReachIndex = rightIndex;
        while (rightTransReachIndex - 1 >= leftIndex && _isInRange(rightTransReachIndex - 1, rightTrans)) {
          rightTransReachIndex--;
        }
        if (leftIndex === 0 && leftTrans === 0 && rightIndex === points.length - 1 && rightTrans === 0) {
          points[0] = 0;
          points[points.length - 1] = 0;
          this._createTransitionTranspositions(
            ngList,
            points,
            leftIndex,
            rightIndex,
            minPitch,
            maxPitch,
            comfortPitch
          );
        } else if (rightIndex - leftIndex <= 1) {
          if (leftIndex === 0 && leftTrans === 0) {
            points[leftIndex] = _isInRange(leftIndex, rightTrans) ? rightTrans : 0;
          }
          if (rightIndex === points.length - 1 && rightTrans === 0) {
            points[points.length - 1] = _isInRange(rightIndex, leftTrans) ? leftTrans : 0;
          }
        } else if (leftTransReachIndex >= rightTransReachIndex - 1) {
          if (leftIndex === 0 && leftTrans === 0) {
            points[0] = rightTransReachIndex <= leftIndex ? points[rightIndex] : 0;
          } else if (rightIndex === points.length - 1 && rightTrans === 0) {
            points[points.length - 1] = leftTransReachIndex >= rightIndex ? points[leftIndex] : 0;
          }
          this._createTransitionTranspositions(
            ngList,
            points,
            leftIndex,
            rightIndex,
            minPitch,
            maxPitch,
            comfortPitch
          );
        } else {
          let m1Index = leftIndex === 0 && leftTrans === 0 ? leftIndex : leftTransReachIndex + 1;
          let m2Index = rightIndex === points.length - 1 && rightTrans === 0 ? rightIndex : rightTransReachIndex - 1;
          let averageTrans = (leftTrans + rightTrans) / 2;
          let testTrans = averageTrans > 0 ? Math.ceil(averageTrans) : Math.floor(averageTrans);
          let foundSuitable = false;
          while (!foundSuitable) {
            if (testTrans === 0) {
              break;
            } else if (testTrans === leftTrans || testTrans === rightTrans) {
              testTrans += testTrans < 0 ? 1 : -1;
              continue;
            }
            foundSuitable = true;
            for (let j = m1Index; j <= m2Index; j++) {
              if (_isInRange(j, testTrans) === false) {
                foundSuitable = false;
                testTrans += testTrans < 0 ? 1 : -1;
                break;
              }
            }
          }
          points[m1Index] = testTrans;
          points[m2Index] = testTrans;
          if (m1Index - leftIndex > 1) {
            this._createTransitionTranspositions(
              ngList,
              points,
              leftIndex,
              m1Index,
              minPitch,
              maxPitch,
              comfortPitch
            );
          }
          if (m2Index - m1Index > 1) {
            this._createTransitionTranspositions(
              ngList,
              points,
              m1Index,
              m2Index,
              minPitch,
              maxPitch,
              comfortPitch
            );
          }
          if (rightIndex - m2Index > 1) {
            this._createTransitionTranspositions(
              ngList,
              points,
              m2Index,
              rightIndex,
              minPitch,
              maxPitch,
              comfortPitch
            );
          }
        }
        leftIndex = i;
        leftTrans = points[i];
      }
    }
    this._scoreManager.adjustOctavesOfNotes(points, ngList);
  }
  _getOctaveShiftMarkers(ngList, minPitch, maxPitch) {
    let points = [];
    let remainingOctaveOptions = null;
    let transpositionStartIndex = 0;
    for (let i = 0; i < ngList.length; i++) {
      points.push(null);
      let ng = ngList[i];
      let note = ng.visibleNotes[0];
      let pitch = ~~(note.displayCents / 100);
      if (remainingOctaveOptions != null) {
        let octaveOptions = [];
        for (let octave of remainingOctaveOptions) {
          let testPitch = pitch + 12 * octave;
          if (testPitch >= minPitch && testPitch <= maxPitch) {
            octaveOptions.push(octave);
          }
        }
        if (octaveOptions.length === 0) {
          remainingOctaveOptions.sort((a, b) => a * a <= b * b ? -1 : 1);
          if (this._debug)
            console.log(remainingOctaveOptions);
          points[transpositionStartIndex] = remainingOctaveOptions[0];
          if (octaveOptions.length === 0) {
            remainingOctaveOptions = null;
          }
        } else {
          remainingOctaveOptions = octaveOptions;
        }
      }
      if (remainingOctaveOptions == null) {
        transpositionStartIndex = i;
        remainingOctaveOptions = [];
        let octave = 0;
        while (pitch + 12 * octave <= maxPitch) {
          if (pitch + 12 * octave >= minPitch) {
            if (remainingOctaveOptions.indexOf(octave) === -1) {
              remainingOctaveOptions.push(octave);
            }
          }
          octave++;
        }
        octave = -1;
        while (pitch + 12 * octave >= minPitch) {
          if (pitch + 12 * octave <= maxPitch) {
            if (remainingOctaveOptions.indexOf(octave) === -1) {
              remainingOctaveOptions.push(octave);
            }
          }
          octave--;
        }
      }
    }
    if (points.length > 0) {
      remainingOctaveOptions.sort((a, b) => a * a <= b * b ? -1 : 1);
      points[points.length - 1] = remainingOctaveOptions[0];
      if (points[0] == null)
        points[0] = points[points.length - 1];
      if (this._debug)
        console.log("points: ${points.toString()}");
    }
    return points;
  }
  _createTransitionTranspositions(ngList, transpositionPoints, leftIndex, rightIndex, minPitch, maxPitch, comfortPitch) {
    let leftTrans = transpositionPoints[leftIndex];
    let rightTrans = transpositionPoints[rightIndex];
    if (leftTrans === rightTrans) {
      for (let i = leftIndex + 1; i < rightIndex; i++) {
        transpositionPoints[i] = leftTrans;
      }
      return;
    }
    let bestTransitionScore = -1e4;
    let bestTransitionIndex = leftIndex;
    for (let i = leftIndex; i < rightIndex; i++) {
      let currentNG = ngList[i];
      let cNote = currentNG.visibleNotes[0];
      let cPitchLeftTrans = ~~(cNote.displayCents / 100 + 12 * leftTrans);
      let nextNG = ngList[i + 1];
      let nNote = nextNG.visibleNotes[0];
      let nPitchLeftTrans = ~~(nNote.displayCents / 100 + 12 * leftTrans);
      let nPitchRightTrans = ~~(nNote.displayCents / 100 + 12 * rightTrans);
      if (cPitchLeftTrans < minPitch || cPitchLeftTrans > maxPitch) {
        break;
      }
      if (nPitchRightTrans < minPitch || nPitchRightTrans > maxPitch) {
        bestTransitionScore = -1e4;
        continue;
      }
      let interval = Math.abs(nPitchRightTrans - cPitchLeftTrans);
      let newIntervalScore = 2 / (interval + 1);
      let alternateInterval = Math.abs(nPitchLeftTrans - cPitchLeftTrans);
      newIntervalScore = 0.4 * (alternateInterval + 24) / (interval + 24);
      let qNoteDistance = Math.abs(nextNG.qNoteTime - currentNG.qNoteTime);
      let distanceFactor = Math.pow(qNoteDistance, 0.33);
      newIntervalScore *= Math.min(distanceFactor, 2);
      let newTransitionScore = newIntervalScore;
      if (newTransitionScore >= bestTransitionScore) {
        bestTransitionIndex = i;
        bestTransitionScore = newTransitionScore;
      }
    }
    for (let i = leftIndex; i <= rightIndex; i++) {
      transpositionPoints[i] = i <= bestTransitionIndex ? leftTrans : rightTrans;
    }
  }
  get debug() {
    return this._debug;
  }
  set debug(value) {
    this._debug = value;
  }
};

// src/editing/NoteManager.ts
var NoteManager = class {
  //the Score data object
  constructor(score) {
    this._score = score;
  }
  /**
   * adds the pitch to the specified Note
   * @param note the Note to change
   * @param octaveDelta the number of octaves to shift the note - negative
   * values for transposing down.
   */
  changeNoteOctave(note, octaveDelta) {
    let noteGroup = note.noteGroup;
    let measure = noteGroup.voice.measure;
    let oldOctave = Number.parseInt(note.pitchName.substring(1));
    note.pitchName = note.pitchName[0] + (oldOctave + octaveDelta).toString();
    note.displayCents += 1200 * octaveDelta;
    note.stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(
      note.pitchName,
      measure.clefs[measure.clefs.length - 1].type
    );
    note.vPos = 1 / 2 * note.stepsFromTopStaffLine;
    note.legerLines = PitchUtils.getNumberOfLegerLines(
      note.stepsFromTopStaffLine
    );
    if (noteGroup.notes.length > 0) {
      noteGroup.sortNotesByPitch();
    }
    this.updateAccidentals(measure);
    measure.notesNeedRendering = true;
  }
  /**
   * adds the pitch to the specified Note
   * @param note the Note to change
   * @param octaveDelta the number of octaves to shift the note - negative
   * values for transposing down.
   */
  changeNoteGroupOctave(ng, octaveDelta) {
    let measure = ng.voice.measure;
    for (let note of ng.notes) {
      let oldOctave = Number.parseInt(note.pitchName.substring(1));
      note.pitchName = note.pitchName[0] + (oldOctave + octaveDelta).toString();
      note.displayCents += 1200 * octaveDelta;
      note.stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(
        note.pitchName,
        measure.clefs[measure.clefs.length - 1].type
      );
      note.vPos = 1 / 2 * note.stepsFromTopStaffLine;
      note.legerLines = PitchUtils.getNumberOfLegerLines(
        note.stepsFromTopStaffLine
      );
    }
    this.updateAccidentals(measure);
    measure.notesNeedRendering = true;
  }
  /**
   * changes the passed in NoteGroup's duration. Any notes overlapped by an 
   * increase in duration are deleted. any gaps created are filled with rests
   * @param noteGroup the NoteGroup object to change
   * @param newDuration the new Duration Unit duration (a power of 2 matching
   * a duration in the DurationType constants)
   * @param numDots the number of augmentation dots
   */
  changeDuration(noteGroup, newDuration, numDots) {
    if (noteGroup.duration === newDuration && noteGroup.numDots === numDots) {
      return;
    }
    if (newDuration !== 64 && newDuration !== 128 && newDuration !== 256 && newDuration !== 512 && newDuration !== 1024 && newDuration !== 2048 && newDuration !== 4096) {
      return;
    }
    let totalDuration = newDuration + DurationType.calculateDotDuration(newDuration, numDots);
    if (noteGroup.tuplets != null) {
      let tupletNGC = noteGroup.ngcRef.parentNGC;
      let tuplet = tupletNGC.tuplet;
      if (noteGroup.ngcRef.durUnitStartTime + totalDuration > (tupletNGC.durUnitEndTime - tupletNGC.durUnitStartTime) * (tuplet.numerator / tuplet.denominator)) {
        return;
      }
    } else {
      if (noteGroup.ngcRef.durUnitStartTime + totalDuration > (noteGroup.voice.measure.stack.endTime - noteGroup.voice.measure.stack.startTime) * 1024) {
        return;
      }
    }
    noteGroup.durationType = newDuration;
    noteGroup.duration = newDuration;
    noteGroup.numDots = numDots;
    noteGroup.ngcRef.durUnitEndTime = noteGroup.ngcRef.durUnitStartTime + totalDuration;
    noteGroup.qNoteDuration = totalDuration / 1024;
    noteGroup.stemDirection = newDuration < 4096 ? StemDirection.DOWN : StemDirection.NO_STEM;
    let parentList;
    if (noteGroup.tuplets != null) {
      let tupletRatio = 1;
      for (let tuplet of noteGroup.tuplets) {
        tupletRatio *= tuplet.denominator / tuplet.numerator * (tuplet.denominatorDuration / tuplet.numeratorDuration);
      }
      noteGroup.qNoteDuration *= tupletRatio;
      parentList = noteGroup.ngcRef.parentNGC.ngcList;
    } else {
      parentList = noteGroup.voice.noteGroupContainers;
    }
    for (let i = parentList.length - 1; i >= 0; i--) {
      let ngc = parentList[i];
      if (noteGroup.ngcRef.durUnitEndTime > ngc.durUnitStartTime && ngc.durUnitStartTime > noteGroup.ngcRef.durUnitStartTime) {
        let removedNoteGroups;
        if (ngc.noteGroup != null) {
          ngc.noteGroup.voice.removeNoteGroupContainer(ngc);
          ngc.noteGroup.voice.measure.stack.removeNoteGroup(
            ngc.noteGroup
          );
          removedNoteGroups = [ngc.noteGroup];
        } else {
          removedNoteGroups = noteGroup.voice.removeNoteGroupContainer(ngc, false);
        }
        if (noteGroup.voice.measure.staff.partRef.visible) {
          for (let ngToRemove of removedNoteGroups) {
            noteGroup.voice.measure.stack.removeNoteGroup(
              ngToRemove
            );
          }
        }
        for (let ngToRemove of removedNoteGroups) {
          if (ngToRemove.slurs != null) {
            for (let slur of ngToRemove.slurs) {
              this.removeSlur(slur);
            }
          }
        }
      }
    }
    let measure = noteGroup.voice.measure;
    this.fillMeasure(measure);
    measure.notesNeedRendering = true;
  }
  addTuplet(ng, numerator, denominator, numeratorDuration, denominatorDuration) {
    if (numeratorDuration !== 64 && numeratorDuration !== 128 && numeratorDuration !== 256 && numeratorDuration !== 512 && numeratorDuration !== 1024 && numeratorDuration !== 2048 && numeratorDuration !== 4096) {
      return;
    }
    if (denominatorDuration !== 64 && denominatorDuration !== 128 && denominatorDuration !== 256 && denominatorDuration !== 512 && denominatorDuration !== 1024 && denominatorDuration !== 2048 && denominatorDuration !== 4096) {
      return;
    }
    let parentList;
    let totalDuration = denominator * denominatorDuration;
    let tuplet;
    if (ng.tuplets != null) {
      let tupletNGC = ng.ngcRef.parentNGC;
      tuplet = tupletNGC.tuplet;
      if (ng.ngcRef.durUnitStartTime + totalDuration > (tupletNGC.durUnitEndTime - tupletNGC.durUnitStartTime) * (tuplet.numerator / tuplet.denominator)) {
        return;
      }
      parentList = ng.ngcRef.parentNGC.ngcList;
    } else {
      if (ng.ngcRef.durUnitStartTime + totalDuration > (ng.voice.measure.stack.endTime - ng.voice.measure.stack.startTime) * 1024) {
        return;
      }
      parentList = ng.voice.noteGroupContainers;
    }
    for (let i = parentList.length - 1; i >= 0; i--) {
      let ngc = parentList[i];
      if (ng.ngcRef.durUnitStartTime + totalDuration > ngc.durUnitStartTime && ngc.durUnitStartTime > ng.ngcRef.durUnitStartTime) {
        let removedNoteGroups;
        if (ngc.noteGroup != null) {
          ngc.noteGroup.voice.removeNoteGroupContainer(ngc);
          ngc.noteGroup.voice.measure.stack.removeNoteGroup(
            ngc.noteGroup
          );
          removedNoteGroups = [ngc.noteGroup];
        } else {
          removedNoteGroups = ng.voice.removeNoteGroupContainer(
            ngc,
            false
          );
        }
        if (ng.voice.measure.staff.partRef.visible) {
          for (let ngToRemove of removedNoteGroups) {
            ng.voice.measure.stack.removeNoteGroup(ngToRemove);
          }
        }
        for (let ngToRemove of removedNoteGroups) {
          if (ngToRemove.slurs != null) {
            for (let slur of ngToRemove.slurs) {
              this.removeSlur(slur);
            }
          }
        }
      }
    }
    tuplet = new Tuplet();
    tuplet.above = true;
    tuplet.denominator = denominator;
    tuplet.denominatorDuration = denominatorDuration;
    tuplet.numerator = numerator;
    tuplet.numeratorDuration = numeratorDuration;
    tuplet.firstNote = ng;
    tuplet.endNote = ng;
    if (ng.tuplets == null) {
      ng.tuplets = [];
    }
    tuplet.tupletID = ng.tuplets.length + 1;
    ng.tuplets.push(tuplet);
    let originalDurUnitDuration = ng.ngcRef.durUnitEndTime - ng.ngcRef.durUnitStartTime;
    let parentNGC = ng.ngcRef;
    parentNGC.durUnitEndTime = parentNGC.durUnitStartTime + denominator * denominatorDuration;
    let childNGC = new NoteGroupContainer();
    childNGC.durUnitStartTime = 0;
    childNGC.durUnitEndTime = originalDurUnitDuration;
    childNGC.noteGroup = ng;
    ng.ngcRef = childNGC;
    childNGC.qNoteStartTime = parentNGC.qNoteStartTime;
    childNGC.parentNGC = parentNGC;
    ng.qNoteDuration *= tuplet.denominator / tuplet.numerator;
    parentNGC.ngcList = [childNGC];
    parentNGC.noteGroup = null;
    parentNGC.tuplet = tuplet;
    let measure = ng.voice.measure;
    this.fillMeasure(measure);
    measure.notesNeedRendering = true;
  }
  /**
   * Toggles the tie state for the requested Note
   * @param note the Note to change
   */
  toggleTie(note) {
    if (note.tieState === TieState.NONE) {
      note.tieState = TieState.START;
    } else if (note.tieState === TieState.STOP) {
      note.tieState = TieState.CONTINUE;
    } else if (note.tieState === TieState.CONTINUE) {
      note.tieState = TieState.STOP;
    } else {
      note.tieState = TieState.NONE;
    }
    let measure = note.noteGroup.voice.measure;
    let measures = [measure];
    measure.notesNeedRendering = true;
    let stack = measure.stack;
    if (stack.next != null) {
      let measureIndex = stack.measures.indexOf(measure);
      let nextMeasure = stack.next.measures[measureIndex];
      measures[1] = nextMeasure;
      nextMeasure.notesNeedRendering = true;
    }
    this.updateTies(measures);
  }
  fixNotesForClefs(measure) {
    for (let i = measure.clefs.length - 1; i >= 0; i--) {
      let clef = measure.clefs[i];
      let clefType = clef.type;
      for (let voice of measure.voices) {
        let ngList = voice.noteGroups;
        let noteIdx = ngList.length - 1;
        while (noteIdx >= 0) {
          let ng = ngList[noteIdx];
          if (ng.qNoteTime < clef.qNoteTime) {
            break;
          }
          for (let note of ng.notes) {
            let steps = PitchUtils.getStepsFromTopStaffLine(
              note.pitchName,
              clefType
            );
            note.stepsFromTopStaffLine = steps;
            note.legerLines = PitchUtils.getNumberOfLegerLines(
              steps
            );
            note.vPos = 0.5 * steps;
          }
          noteIdx--;
        }
      }
    }
  }
  /**
   * adds the pitch to the specified NoteGroup
   * @param noteGroup the NoteGroup to add the Note to
   * @param pitchLetterName A, B, C, D, E, F, or G (uppercase)
   * @param octave the octave for the note - C4 is middle C, the note below it
   * is B3.
   * @param alteration a value from -2 (number flat) to 2 (number sharp). This
   * is an absolute alteration - not relative to the key signature's
   * adjustment for this pitch.
   */
  addPitch(noteGroup, pitchLetterName, octave, alteration) {
    let measure = noteGroup.voice.measure;
    let newNote = new Note();
    newNote.pitchName = pitchLetterName + octave.toString();
    newNote.displayCents = PitchUtils.pitchNameToDiatonicCents(
      newNote.pitchName
    ) + 100 * alteration;
    this._setPercussionPlaybackCents(newNote);
    newNote.stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(
      newNote.pitchName,
      measure.clefs[measure.clefs.length - 1].type
    );
    newNote.vPos = 1 / 2 * newNote.stepsFromTopStaffLine;
    newNote.alteration = alteration;
    newNote.legerLines = PitchUtils.getNumberOfLegerLines(
      newNote.stepsFromTopStaffLine
    );
    if (noteGroup.durationType === DurationType.WHOLE && noteGroup.duration < 4096) {
      let newDurationInfo = DurationType.getBaseDurationAndDots(
        noteGroup.duration
      );
      noteGroup.durationType = newDurationInfo[0];
      noteGroup.numDots = newDurationInfo[1];
    }
    noteGroup.isRest = false;
    noteGroup.stemDirection = noteGroup.duration < 4096 ? StemDirection.DOWN : StemDirection.NO_STEM;
    noteGroup.insertNote(newNote);
    this.updateAccidentals(measure);
    measure.notesNeedRendering = true;
  }
  /**changes the pitch of a Note
   * @param note the Note to repitch
   * @param pitchLetterName A, B, C, D, E, F, or G (uppercase)
   * @param octave the octave for the note - C4 is middle C, the note below it
   * is B3.
   * @param alteration a value from -2 (number flat) to 2 (number sharp). This
   * is an absolute alteration - not relative to the key signature's
   * adjustment for this pitch. */
  repitch(note, pitchLetterName, octave, alteration) {
    let ng = note.noteGroup;
    let measure = ng.voice.measure;
    note.pitchName = pitchLetterName + octave.toString();
    note.displayCents = PitchUtils.pitchNameToDiatonicCents(
      note.pitchName
    ) + 100 * alteration;
    this._setPercussionPlaybackCents(note);
    note.stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(
      note.pitchName,
      measure.clefs[measure.clefs.length - 1].type
    );
    note.vPos = 1 / 2 * note.stepsFromTopStaffLine;
    note.alteration = alteration;
    note.legerLines = PitchUtils.getNumberOfLegerLines(
      note.stepsFromTopStaffLine
    );
    ng.sortNotes();
    this.updateAccidentals(measure);
    measure.notesNeedRendering = true;
  }
  _setPercussionPlaybackCents(note) {
    if (note.noteGroup.voice.measure.staff.partRef.percussionMap != null) {
      let percussionMap = note.noteGroup.voice.measure.staff.partRef.percussionMap;
      let midiPitch = ~~(note.displayCents / 100);
      if (percussionMap[midiPitch] != null) {
        note.playbackCents = percussionMap[midiPitch] * 100;
      } else if (percussionMap[0] != null) {
        note.playbackCents = percussionMap[0] * 100;
      } else {
        note.playbackCents = 3800;
      }
    }
  }
  /**
   * removes the Note from the NoteGroup
   * @param noteGroup the NoteGroup to remove the Note from
   * @param note the Note to remove
   */
  removePitch(noteGroup, note) {
    noteGroup.removeNote(note);
    if (noteGroup.notes.length <= 0) {
      let ngcToRemove = noteGroup.ngcRef;
      while (ngcToRemove.parentNGC != null && ngcToRemove.parentNGC.tuplet.firstNote === noteGroup) {
        ngcToRemove = ngcToRemove.parentNGC;
      }
      let removedNoteGroups = noteGroup.voice.removeNoteGroupContainer(
        ngcToRemove
      );
      if (noteGroup.voice.measure.staff.partRef.visible) {
        for (let ng of removedNoteGroups) {
          noteGroup.voice.measure.stack.removeNoteGroup(ng);
        }
      }
      for (let ng of removedNoteGroups) {
        if (ng.slurs != null) {
          for (let slur of ng.slurs) {
            this.removeSlur(slur);
          }
        }
      }
      this.fillMeasure(noteGroup.voice.measure);
    }
    this.updateAccidentals(noteGroup.voice.measure);
    noteGroup.voice.measure.notesNeedRendering = true;
  }
  /**
   * updates the accidentals in the measure so that all necessary accidentals
   * are shown and unnecessary ones are hidden
   * @param measure the Measure to update
   */
  updateAccidentals(measure) {
    let pitchHistory = {};
    let voices = measure.voices;
    for (let voice of voices) {
      let noteGroups = voice.noteGroups;
      let numNoteGroups = noteGroups.length;
      for (let i = 0; i < numNoteGroups; i++) {
        if (noteGroups[i].isRest) {
          continue;
        }
        let notes = noteGroups[i].visibleNotes;
        for (let note of notes) {
          if (note.tieState === TieState.CONTINUE || note.tieState === TieState.STOP) {
            note.showAccidental = false;
            continue;
          }
          let pos = note.stepsFromTopStaffLine + 50;
          if (pitchHistory[pos] == null) {
            pitchHistory[pos] = PitchUtils.getKeySigAlterationForNote(
              note.pitchName[0],
              measure.displayKey
            );
          }
          if (pitchHistory[pos] === note.alteration) {
            note.showAccidental = false;
          } else {
            note.accidental = AccidentalType.getAccidentalType(
              note.alteration
            );
            note.showAccidental = true;
            pitchHistory[pos] = note.alteration;
          }
        }
      }
    }
  }
  /**
   * updates the ties in the measure region, getting rid of orphaned ties and
   * changing the state of ties where necessary
   * @param measure the measures to update (must be consecutive). The last
   * note in the region will not be updated in terms of how it connects to the
   * following measure.
   */
  updateTies(measures) {
    let ngLists = new Array();
    let numMeasures = measures.length;
    for (let i = 0; i < numMeasures; i++) {
      let voices = measures[i].voices;
      let numVoices = voices.length;
      for (let j = 0; j < numVoices; j++) {
        let voice = voices[j];
        while (voice.number >= ngLists.length) {
          ngLists.push(new Array());
        }
        let ngList = ngLists[voice.number];
        let nextNGIndex = ngList.length;
        let noteGroups = voices[j].noteGroups;
        let numNoteGroups = noteGroups.length;
        for (let k = 0; k < numNoteGroups; k++) {
          if (k < ngList.length) {
            ngList[nextNGIndex] = noteGroups[k];
          } else {
            ngList.push(noteGroups[k]);
          }
          nextNGIndex++;
        }
      }
    }
    let numLists = ngLists.length;
    for (let i = 0; i < numLists; i++) {
      let ngList = ngLists[i];
      let numNoteGroups = ngList.length;
      for (let j = 0; j < numNoteGroups - 1; j++) {
        let ng = ngList[j];
        let nextNG = ngList[j + 1];
        let notes = ng.notes;
        let numNotes = notes.length;
        for (let k = 0; k < numNotes; k++) {
          let note = notes[k];
          let nextNote = this._getMatchingNoteInNextNoteGroup(
            note,
            nextNG
          );
          if (note.tieState === TieState.START || note.tieState === TieState.CONTINUE) {
            if (nextNote != null) {
              if (nextNote.tieState === TieState.NONE) {
                nextNote.tieState = TieState.STOP;
              } else if (nextNote.tieState === TieState.START) {
                nextNote.tieState = TieState.CONTINUE;
              }
            } else {
              if (note.tieState === TieState.CONTINUE) {
                note.tieState = TieState.STOP;
              } else {
                note.tieState = TieState.NONE;
              }
            }
          } else {
            if (nextNote != null) {
              if (nextNote.tieState === TieState.CONTINUE) {
                nextNote.tieState = TieState.START;
              } else if (nextNote.tieState === TieState.START) {
              } else {
                nextNote.tieState = TieState.NONE;
              }
            }
          }
        }
      }
    }
  }
  _getMatchingNoteInNextNoteGroup(firstNote, nextNoteGroup) {
    let nextNotes = nextNoteGroup.notes;
    for (let nxNote of nextNotes) {
      if (nxNote.pitchName === firstNote.pitchName && nxNote.alteration === firstNote.alteration) {
        return nxNote;
      }
    }
    return null;
  }
  /**
   * For music coming from MusicXML, this creates NoteGroupContainer objects
   * for NoteGroups and Tuplets, adding relative time stamps and creating a
   * hierarchy that makes it possible to fill measures with rests
   */
  createNoteGroupContainers() {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        let measures = staff.measures;
        for (let measure of measures) {
          let voices = measure.voices;
          for (let voice of voices) {
            let topContainer = new NoteGroupContainer();
            topContainer.ngcList = [];
            this.addNotesToContainer(
              topContainer.ngcList,
              null,
              voice.noteGroups,
              0
            );
            voice.noteGroupContainers = topContainer.ngcList;
          }
        }
      }
    }
  }
  /**
   * updates the start time values for NoteGroups and their
   * NoteGroupContainers by the specified deltaTime value. Any containers with
   * an end time beyond the end of the measure are deleted
   * @param ngcList the list of NoteGroupContainers to adjust
   * @param deltaTime the amount to adjust the times by (this value is added
   * to their current qNote times)
   * @param measureEndTime the qnote end time of the measure
   * @param voiceRef the Voice object to which the NGCs and NoteGroups belong
   * @param checkEndTimes set to true if you want check the end times of
   * NoteGroupContainers and remove the ones that hang over the end
   */
  changeNoteGroupTimeStamps(ngcList, deltaTime, measureEndTime, voiceRef, checkEndTimes) {
    for (let i = ngcList.length - 1; i >= 0; i--) {
      let ngc = ngcList[i];
      ngc.qNoteStartTime += deltaTime;
      if (checkEndTimes) {
        let tupletRatio = 1;
        let parentNGC = ngc.parentNGC;
        while (parentNGC != null) {
          let tuplet = parentNGC.tuplet;
          tupletRatio *= tuplet.denominator / tuplet.numerator * (tuplet.denominatorDuration / tuplet.numeratorDuration);
          parentNGC = parentNGC.parentNGC;
        }
        let ngcEndTime = ngc.qNoteStartTime + (ngc.durUnitEndTime - ngc.durUnitStartTime) / 1024 * tupletRatio;
        if (ngcEndTime > measureEndTime) {
          let removedNoteGroups = voiceRef.removeNoteGroupContainer(ngc);
          for (let ng of removedNoteGroups) {
            voiceRef.measure.stack.removeNoteGroup(ng);
            if (ng.slurs != null) {
              for (let slur of ng.slurs) {
                this.removeSlur(slur);
              }
            }
          }
          continue;
        }
      }
      if (ngc.noteGroup != null) {
        ngc.noteGroup.qNoteTime += deltaTime;
      } else {
        this.changeNoteGroupTimeStamps(
          ngc.ngcList,
          deltaTime,
          measureEndTime,
          voiceRef,
          false
        );
      }
    }
  }
  /**
   * fills the measure's blank areas with rests
   * @param	measure the Measure object to fill
   */
  fillMeasure(measure) {
    let measureDuration = ~~(measure.numBeats * (1024 * (4 / measure.beatType)));
    for (let voice of measure.voices) {
      if (voice.noteGroupContainers == null || voice.noteGroupContainers.length === 0) {
        let noteGroup = new NoteGroup();
        noteGroup.qNoteTime = measure.stack.startTime;
        noteGroup.qNoteDuration = measureDuration / 1024;
        noteGroup.duration = measureDuration;
        noteGroup.durationType = DurationType.WHOLE;
        noteGroup.isRest = true;
        voice.addNoteGroup(noteGroup);
        if (measure.staff.partRef.visible) {
          measure.stack.addNoteGroup(noteGroup);
        }
        let ngc = new NoteGroupContainer();
        ngc.noteGroup = noteGroup;
        ngc.durUnitStartTime = 0;
        ngc.durUnitEndTime = measureDuration;
        ngc.parentNGC = null;
        ngc.qNoteStartTime = noteGroup.qNoteTime;
        noteGroup.ngcRef = ngc;
        if (voice.noteGroupContainers == null) {
          voice.noteGroupContainers = [ngc];
        } else {
          voice.noteGroupContainers.splice(
            0,
            voice.noteGroupContainers.length
          );
        }
      } else {
        let beatPositions = [];
        let beatGroups = measure.beatGroups;
        for (let i = 0; i < beatGroups.length; i++) {
          beatPositions.push(~~(beatGroups[i] * 1024));
        }
        let maxRestSize = 1024 * ~~(4 / measure.beatType);
        this.checkLevelForGaps(
          voice.noteGroupContainers,
          null,
          0,
          measureDuration,
          voice,
          beatPositions,
          maxRestSize,
          measure.stack.startTime,
          1
        );
      }
    }
    for (let voice of measure.voices) {
      let ngList = voice.noteGroups;
      if (ngList != null) {
        for (let i = 0; i < ngList.length; i++) {
          let ng = ngList[i];
          ng.clearNextPrevNGCaches();
          if (i === 0) {
            let prevNG = ng.prev;
            if (prevNG != null) {
              prevNG.clearNextPrevNGCaches();
            }
          } else if (i === ngList.length - 1) {
            let nextNG = ng.next;
            if (nextNG != null) {
              nextNG.clearNextPrevNGCaches();
            }
          }
        }
      }
    }
  }
  /**
   * adds a slur between two notes
   * @param ng1 the first NoteGroup
   * @param ng2 the second NoteGroup
   */
  addSlur(ng1, ng2) {
    if (ng1 == null || ng2 == null || ng1.isRest || ng2.isRest || ng1.voice.number !== ng2.voice.number || ng1.voice.measure.staff !== ng2.voice.measure.staff || ng1 === ng2 || ng1.qNoteTime === ng2.qNoteTime) {
      return;
    }
    let slur = new Slur();
    if (ng1.qNoteTime < ng2.qNoteTime) {
      slur.firstNote = ng1;
      slur.endNote = ng2;
    } else {
      slur.firstNote = ng2;
      slur.endNote = ng1;
    }
    if (ng1.slurs != null) {
      ng1.slurs.push(slur);
    } else {
      ng1.slurs = [slur];
    }
    if (ng2.slurs != null) {
      ng2.slurs.push(slur);
    } else {
      ng2.slurs = [slur];
    }
    ng1.voice.measure.notesNeedRendering = true;
    ng2.voice.measure.notesNeedRendering = true;
  }
  /**
   * removes a Slur from the score
   * @param slur the Slur to remove
   */
  removeSlur(slur) {
    let ng1 = slur.firstNote;
    let ng2 = slur.endNote;
    let segments = slur.segments;
    if (ng1 != null && ng1.slurs != null) {
      ng1.slurs.splice(ng1.slurs.indexOf(slur), 1);
      if (ng1.slurs.length === 0) {
        ng1.slurs = null;
      }
    }
    if (ng2 != null && ng2.slurs != null) {
      ng2.slurs.splice(ng2.slurs.indexOf(slur), 1);
      if (ng2.slurs.length === 0) {
        ng2.slurs = null;
      }
    }
    ng1.voice.measure.notesNeedRendering = true;
    ng2.voice.measure.notesNeedRendering = true;
    let segment;
    for (segment of segments) {
      segment.systemRef.slurSegments.splice(
        segment.systemRef.slurSegments.indexOf(segment),
        1
      );
    }
    if (segment.systemRef.slurSegments.length === 0) {
      segment.systemRef.slurSegments = null;
    }
  }
  /**
   * checks the contents of one NoteGroupContainer for gaps - calls itself
   * recursively for sub levels
   * @param ngcList the list of NoteGroupContainer objects for the current
   * level, including containers that hold NoteGroups and Tuplets
   * @param parentNGC the NoteGroupContainer object that stores the ngcList -
   * this will contain any tuplet information for notes in the ngcList as well
   * as the parent's start time in duration units
   * @param startTime the expected start time in duration units
   * @param endTime the expected end time in duration units
   * @param voice the voice to add any rests to
   * @param beatPositions the positions of the beat divisions, where rests
   * will need to be split - in duration units
   * @param maxRestSize the max ordinary rest size, in duration units. Note
   * that rests which occupy a full beat may exceed this (like a dotted rest
   * in compound meters)
   */
  checkLevelForGaps(ngcList, parentNGC, startTime, endTime, voice, beatPositions, maxRestSize, parentNGCQNoteTime, tupletRatio) {
    let numContainers = ngcList.length;
    for (let i = numContainers - 1; i >= 0; i--) {
      let ngc = ngcList[i];
      if (ngc.durUnitEndTime < endTime) {
        this.fillGapWithRests(
          ngc.durUnitEndTime,
          endTime,
          voice,
          ngcList,
          i + 1,
          beatPositions,
          maxRestSize,
          parentNGC,
          parentNGCQNoteTime,
          tupletRatio
        );
      }
      if (ngc.ngcList != null) {
        let tuplet = ngc.tuplet;
        let subTupletBeatPositions = [];
        for (let j = 0; j < tuplet.numerator; j++) {
          subTupletBeatPositions.push(j * tuplet.numeratorDuration);
        }
        let subTupletMaxRestSize = tuplet.numeratorDuration;
        let subTupletRatio = tupletRatio * (tuplet.denominator / tuplet.numerator * (tuplet.denominatorDuration / tuplet.numeratorDuration));
        let unadjustedEndtime = Math.trunc(
          (ngc.durUnitEndTime - ngc.durUnitStartTime) * (tuplet.numerator / tuplet.denominator) * (tuplet.numeratorDuration / tuplet.denominatorDuration)
        );
        this.checkLevelForGaps(
          ngc.ngcList,
          ngc,
          0,
          unadjustedEndtime,
          voice,
          subTupletBeatPositions,
          subTupletMaxRestSize,
          ngc.qNoteStartTime,
          subTupletRatio
        );
      }
      endTime = ngc.durUnitStartTime;
    }
    if (endTime > startTime) {
      this.fillGapWithRests(
        startTime,
        endTime,
        voice,
        ngcList,
        0,
        beatPositions,
        maxRestSize,
        parentNGC,
        parentNGCQNoteTime,
        tupletRatio
      );
    }
  }
  fillGapWithRests(startPoint, endPoint, voice, ngcList, ngcListIndex, beatPositions, maxRestSize, parentNGC, parentNGCQNoteTime, tupletRatio) {
    let currentPos = endPoint;
    while (currentPos > startPoint) {
      let beatIndex = 0;
      let beatPos = beatPositions[0];
      while (beatIndex + 1 < beatPositions.length && beatPositions[beatIndex + 1] < currentPos) {
        beatIndex++;
        beatPos = beatPositions[beatIndex];
      }
      let goalPos = beatPos > startPoint ? beatPos : startPoint;
      let goalRestSize = currentPos - goalPos;
      let beatSize;
      if (beatIndex < beatPositions.length - 1) {
        beatSize = beatPositions[beatIndex + 1] - beatPos;
      } else {
        beatSize = endPoint - beatPos;
      }
      let restSize;
      let ng;
      if (goalRestSize === beatSize) {
        restSize = beatSize;
        currentPos = goalPos;
      } else if (goalRestSize < beatSize) {
        restSize = maxRestSize;
        while (restSize > goalRestSize) {
          restSize = ~~(restSize / 2);
        }
        ng = this.getRestBySize(restSize);
        currentPos -= restSize;
      } else {
      }
      ng = this.getRestBySize(restSize);
      ng.qNoteTime = parentNGCQNoteTime + currentPos * tupletRatio / 1024;
      ng.qNoteDuration = ng.duration / 1024 * tupletRatio;
      ng.voice = voice;
      let superNGC = parentNGC;
      if (superNGC != null) {
        let tuplets = [];
        while (superNGC != null && superNGC.tuplet != null) {
          tuplets.push(superNGC.tuplet);
          superNGC = superNGC.parentNGC;
        }
        ng.tuplets = tuplets.reverse();
        for (let tuplet of tuplets) {
          if (ng.qNoteTime > tuplet.endNote.qNoteTime) {
            tuplet.endNote = ng;
          } else if (ng.qNoteTime < tuplet.firstNote.qNoteTime) {
            tuplet.firstNote = ng;
          }
        }
      }
      let ngc = new NoteGroupContainer();
      ngc.noteGroup = ng;
      ngc.durUnitStartTime = currentPos;
      ngc.durUnitEndTime = currentPos + restSize;
      ngc.qNoteStartTime = ng.qNoteTime;
      ngc.parentNGC = parentNGC;
      ng.ngcRef = ngc;
      ngcList.splice(ngcListIndex, 0, ngc);
      let noteGroups = voice.noteGroups;
      let numNotes = noteGroups.length;
      let i = 0;
      for (i = numNotes - 1; i >= 0; i--) {
        if (noteGroups[i].qNoteTime < ng.qNoteTime) {
          break;
        }
      }
      noteGroups.splice(i + 1, 0, ng);
      if (voice.measure.staff.partRef.visible) {
        voice.measure.stack.addNoteGroup(ng);
      }
    }
  }
  getRestBySize(goalSize) {
    let baseDuration = 16;
    while (baseDuration < goalSize) {
      baseDuration *= 2;
    }
    if (baseDuration > goalSize) {
      baseDuration = ~~(baseDuration / 2);
    }
    let numDots = 0;
    let dotsValue = ~~(baseDuration / 2);
    let totalDuration = baseDuration;
    while (totalDuration < goalSize) {
      numDots++;
      totalDuration += dotsValue;
      dotsValue = ~~(dotsValue / 2);
    }
    let ng = new NoteGroup();
    ng.duration = goalSize;
    ng.numDots = numDots;
    ng.isRest = true;
    ng.durationType = baseDuration;
    return ng;
  }
  addNotesToContainer(ngcList, parentNGC, notes, tupletIndex) {
    let cTime = 0;
    let subTupletNGList = [];
    for (let ng of notes) {
      if (ng.tuplets != null && ng.tuplets.length > tupletIndex) {
        subTupletNGList.push(ng);
        if (ng.tuplets[tupletIndex].endNote === ng) {
          let subTupletNGC = new NoteGroupContainer();
          subTupletNGC.tuplet = ng.tuplets[tupletIndex];
          subTupletNGC.ngcList = [];
          subTupletNGC.qNoteStartTime = subTupletNGList[0].qNoteTime;
          subTupletNGC.durUnitStartTime = cTime;
          cTime += subTupletNGC.tuplet.denominatorDuration * subTupletNGC.tuplet.denominator;
          subTupletNGC.durUnitEndTime = cTime;
          subTupletNGC.parentNGC = parentNGC;
          this.addNotesToContainer(
            subTupletNGC.ngcList,
            subTupletNGC,
            subTupletNGList,
            tupletIndex + 1
          );
          ngcList.push(subTupletNGC);
          subTupletNGList = [];
        }
      } else {
        let ngc = new NoteGroupContainer();
        ng.ngcRef = ngc;
        ngc.noteGroup = ng;
        ngc.qNoteStartTime = ng.qNoteTime;
        ngc.durUnitStartTime = cTime;
        ngc.parentNGC = parentNGC;
        cTime += DurationType.getDurationValue(
          ng.durationType,
          ng.numDots
        );
        ngc.durUnitEndTime = cTime;
        ngcList.push(ngc);
      }
    }
  }
};

// src/editing/PitchNameManager.ts
var PitchNameManager = class {
  constructor(score) {
    this._score = score;
  }
  /**
   * removes note names and restores lyrics for all staves
   */
  showLyrics() {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        if (staff.originalLyrics == null) {
          continue;
        }
        let originalLyrics = staff.originalLyrics;
        let lyricIndex = 0;
        for (let measure of staff.measures) {
          let voice = measure.voices[0];
          for (let ng of voice.noteGroups) {
            if (lyricIndex < originalLyrics.length && originalLyrics[lyricIndex].has(ng)) {
              ng.lyric = originalLyrics[lyricIndex].get(ng);
              lyricIndex++;
            } else {
              ng.lyric = null;
            }
          }
        }
        staff.originalLyrics = null;
        staff.lyricsMode = 0 /* SHOW_LYRICS */;
      }
    }
  }
  showNoteNames(staff) {
    let storeLyrics = false;
    if (staff.originalLyrics == null) {
      staff.originalLyrics = [];
      storeLyrics = true;
    }
    let originalLyrics = staff.originalLyrics;
    let vPos = -8;
    let hPos = 0.5;
    for (let measure of staff.measures) {
      let voice = measure.voices[0];
      for (let ng of voice.noteGroups) {
        if (ng.isRest || ng.isGrace) {
          continue;
        }
        let note = ng.visibleNotes[0];
        if (storeLyrics && ng.lyric != null) {
          originalLyrics.push(/* @__PURE__ */ new Map([[ng, ng.lyric]]));
        }
        if (note.tieState !== "none" && note.tieState !== "start") {
          ng.lyric = null;
          continue;
        }
        let pitchName = note.pitchName;
        pitchName = pitchName.substring(0, pitchName.length - 1);
        pitchName += this._getAccidentalText(note.alteration);
        ng.lyric = new Lyric(
          SyllablePosition.MIDDLE,
          " " + pitchName,
          1,
          hPos,
          vPos
        );
      }
    }
    staff.lyricsMode = 1 /* SHOW_NOTE_NAMES */;
  }
  updateNoteNames() {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        if (this.isStaffShowingNoteNames(staff)) {
          this.showNoteNames(staff);
        }
      }
    }
  }
  /**
   * hides lyrics for all staves
   */
  hideLyrics() {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        let storeLyrics = false;
        if (staff.originalLyrics == null) {
          staff.originalLyrics = [];
          storeLyrics = true;
        }
        let originalLyrics = staff.originalLyrics;
        for (let measure of staff.measures) {
          let voice = measure.voices[0];
          for (let ng of voice.noteGroups) {
            if (ng.isRest || ng.isGrace) {
              continue;
            }
            if (storeLyrics && ng.lyric != null) {
              originalLyrics.push(/* @__PURE__ */ new Map([[ng, ng.lyric]]));
            }
            ng.lyric = null;
          }
        }
        staff.lyricsMode = 2 /* HIDE_LYRICS */;
      }
    }
  }
  _getAccidentalText(alteration) {
    switch (alteration) {
      case -2:
        return "bb";
      case 2:
        return "x";
      case -1:
        return "b";
      case 1:
        return "#";
      default:
        return "";
    }
  }
  isStaffShowingNoteNames(staff) {
    return staff.lyricsMode === 1 /* SHOW_NOTE_NAMES */;
  }
};

// src/editing/transposition/MeasureRegion.ts
var MeasureRegion = class {
  constructor(part, startMeasure, endMeasure) {
    this.part = part;
    this.startMeasure = startMeasure;
    this.endMeasure = endMeasure;
    this.startMeasure = startMeasure;
    this.endMeasure = endMeasure;
    if (startMeasure != null) {
      this.startMeasureIndex = startMeasure.staff.measures.indexOf(
        startMeasure
      );
    }
    if (endMeasure != null) {
      this.endMeasureIndex = endMeasure.staff.measures.indexOf(
        endMeasure
      );
    }
  }
};

// src/editing/transposition/TranspositionDirection.ts
var TransposeDirection = /* @__PURE__ */ ((TransposeDirection2) => {
  TransposeDirection2[TransposeDirection2["nearest"] = 0] = "nearest";
  TransposeDirection2[TransposeDirection2["up"] = 1] = "up";
  TransposeDirection2[TransposeDirection2["down"] = 2] = "down";
  return TransposeDirection2;
})(TransposeDirection || {});

// src/editing/transposition/TranspositionRecord.ts
var TranspositionRecord = class {
};

// src/editing/transposition/TranspositionType.ts
var TranspositionType = /* @__PURE__ */ ((TranspositionType2) => {
  TranspositionType2[TranspositionType2["partOctave"] = 0] = "partOctave";
  TranspositionType2[TranspositionType2["noteOctaves"] = 1] = "noteOctaves";
  TranspositionType2[TranspositionType2["score"] = 2] = "score";
  TranspositionType2[TranspositionType2["repitches"] = 3] = "repitches";
  return TranspositionType2;
})(TranspositionType || {});

// src/editing/transposition/TranspositionManager.ts
var TranspositionManager = class {
  constructor(score, noteManager) {
    this._history = [];
    this._score = score;
    this._noteManager = noteManager;
  }
  /**/
  ////////////////MACRO LEVEL CHANGES//////////////*/
  /**prevents transposition of all parts set to midiChannel 10*/
  preventPercussionTransposition() {
    for (let part of this._score.parts) {
      if (part.midiChannel === 10) {
        part.preventTransposition = true;
      }
    }
  }
  /**repitches multiple notes and stores the action in the transposition
   * history. */
  repitchNotes(repitchDOs) {
    for (let rdo of repitchDOs) {
      rdo.storeOldSettings();
      this._noteManager.repitch(
        rdo.note,
        rdo.letter,
        rdo.octave,
        rdo.alteration
      );
    }
    let record = new TranspositionRecord();
    record.type = 3 /* repitches */;
    record.repitchDOs = repitchDOs;
    this._history.push(record);
  }
  /**
   * Changes the octave of each note in a list. This is intended to operate
   * on all of the notes in a single part, staff and voice and should not be
   * used on a sub-selection of notes or notes from multiple parts, staves, or
   * voices.
   * @param octaveShiftValues a Array<number> with each value designating the
   * number of octaves to transpose each corresponding note.
   * @param ngsNoTieIntosOrRests - a Array<NoteGroup> that contains all the
   * NoteGroups from one part/staff/voice which aren't rests and which aren't
   * the target (landing spot) of ties. This list is typically obtained via
   * calling Part.getNoteGroupsWithoutRestsOrTieIntos().
   */
  adjustOctavesOfNotes(octaveShiftValues, ngsNoTieIntosOrRests) {
    if (ngsNoTieIntosOrRests.length !== octaveShiftValues.length) {
      throw "octave shift values don't match up with NoteGroups";
    }
    let length = octaveShiftValues.length;
    for (let i = 0; i < length; i++) {
      let transposition = octaveShiftValues[i];
      if (transposition === 0)
        continue;
      let ng = ngsNoTieIntosOrRests[i];
      do {
        if (!ng.isRest)
          this._noteManager.changeNoteGroupOctave(ng, transposition);
        ng = ng.next;
      } while (i < length - 1 && ng !== ngsNoTieIntosOrRests[i + 1] || i === length - 1 && ng != null);
    }
    let record = new TranspositionRecord();
    record.type = 1 /* noteOctaves */;
    record.ngList = ngsNoTieIntosOrRests;
    record.ngOctaveDeltas = octaveShiftValues;
    this._history.push(record);
  }
  /**
   * transposes all of the notes in a given part by the specifed number
   * of octaves.
   * @octaveDelta - +1 for up one octave, -1 for down one octave, etc.
   */
  changeOctaveOfPart(part, octaveDelta) {
    if (part.preventTransposition) {
      return;
    }
    let ngList = part.noteGroupsExcludingRests;
    if (octaveDelta > 0) {
      for (let ng of ngList) {
        let notes = ng.notes;
        for (let i = notes.length - 1; i >= 0; i--) {
          this._noteManager.changeNoteOctave(notes[i], octaveDelta);
        }
      }
    } else if (octaveDelta < 0) {
      for (let ng of ngList) {
        let notes = ng.notes;
        for (let i = 0; i < notes.length; i++) {
          this._noteManager.changeNoteOctave(notes[i], octaveDelta);
        }
      }
    }
    let record = new TranspositionRecord();
    record.type = 0 /* partOctave */;
    record.part = part;
    record.octaveDelta = octaveDelta;
    this._history.push(record);
  }
  /**sets the transposition for the requested part, transposing the notes and
   * display key chromaticTrans and diatonicTrans are absolute values, not
   * relative shifts. So trumpet should always be 2 and 1 (the amount the
   * transposed pitches APPEAR above concert pitch*/
  setPartTransposition(part, chromaticTrans, diatonicTrans = null) {
    if (diatonicTrans == null) {
      diatonicTrans = PitchUtils.getCommonDiatonicTranspositionFromChromaticSteps(
        chromaticTrans
      );
    }
    let chromaticShift = chromaticTrans - part.chromaticTransposition;
    let diatonicShift = diatonicTrans - part.diatonicTransposition;
    part.setTransposition(diatonicTrans, chromaticTrans);
    let regions = this._getKeyMeasureRegions(part);
    for (let reg of regions) {
      this._updateDisplayKeyOfRegion(reg, false);
      this._transposeRegion(reg, chromaticShift, diatonicShift, true);
    }
  }
  /**creates a record of all the part transpositions in the [Score]*/
  savePartTranspositions() {
    this._partTranspositions = /* @__PURE__ */ new Map();
    for (let part of this._score.parts) {
      this._partTranspositions.set(part, new TranspositionDO(
        part.diatonicTransposition,
        part.chromaticTransposition
      ));
    }
  }
  /**restores the part transpositions to those that were saved with 
   * [savePartTranspositions] call*/
  restorePartTranspositions() {
    if (this._partTranspositions == null) {
      throw "you must call savePartTranspositions() before using restorePartTranspositions()";
    }
    for (let part of this._score.parts) {
      let tdo = this._partTranspositions.get(part);
      if (tdo != null) {
        this.setPartTransposition(part, tdo.chromatic, tdo.diatonic);
      }
    }
  }
  /**transposes the entire song by the relative amount specified
   * positive values for chromaticShift/diatonicShift transpose the song up,
   * negative down values are relative, so 2,1 always transposes the song up a
   * whole step from its current key changes concertKey, displayKey, and
   * transposes notes
   * @preventInitialKeyOptimization - if you want the first region's key to be
   * exactly what you set without looking for a better enharmonic equivalent,
   * set this to true
   * @playbackOctaveDelta - this option will adjust the
   * Part.playbackOctaveDelta property for each part by one or
   * more octaves to minimize the transposition's effect on the playback
   * pitch.*/
  transposeSong(chromaticShift, diatonicShift = null, preventInitialKeyOptimization = false, playbackOctaveDelta = 0) {
    if (diatonicShift == null) {
      diatonicShift = PitchUtils.getCommonDiatonicTranspositionFromChromaticSteps(
        chromaticShift
      );
    }
    let parts = this._score.parts;
    let regions = this._getKeyMeasureRegions(this._score.parts[0]);
    for (let reg of regions) {
      let oldKey = reg.startMeasure.concertKey;
      let isMajor = reg.startMeasure.isMajorKey;
      let keyDelta = PitchUtils.getKeySigAlterationForTransposition(
        diatonicShift,
        chromaticShift
      );
      let newKey = reg.startMeasure.concertKey + keyDelta;
      if (!preventInitialKeyOptimization || reg !== regions[0]) {
        newKey = this._optimizeKeyEnharmonically(newKey);
      }
      let transposeUp = chromaticShift > 0;
      let tdo = PitchUtils.getTranspositionForKeyChange(
        oldKey,
        newKey,
        isMajor,
        isMajor,
        transposeUp
      );
      let adjustedChromaticShift = tdo.chromatic;
      let adjustedDiatonicShift = tdo.diatonic;
      let adjustedUp = false;
      while (adjustedChromaticShift < chromaticShift) {
        adjustedChromaticShift += 12;
        adjustedDiatonicShift += 7;
        adjustedUp = true;
      }
      if (adjustedUp && adjustedChromaticShift !== chromaticShift) {
        console.log("TranspositionManager: chromaticShift should equal adjustedChromaticShift!!!");
      }
      while (adjustedChromaticShift > chromaticShift) {
        adjustedChromaticShift -= 12;
        adjustedDiatonicShift -= 7;
      }
      this._setConcertKeyForRegion(reg, newKey, isMajor, false);
      for (let part of parts) {
        if (part.preventTransposition) {
          continue;
        }
        this._transposeRegion(
          new MeasureRegion(
            part,
            reg.startMeasure,
            reg.endMeasure
          ),
          adjustedChromaticShift,
          adjustedDiatonicShift,
          true
        );
      }
    }
    for (let part of parts) {
      part.playbackOctaveDelta += playbackOctaveDelta;
    }
    let record = new TranspositionRecord();
    record.type = 2 /* score */;
    record.diatonicShift = diatonicShift;
    record.chromaticShift = chromaticShift;
    record.playbackOctaveDelta = playbackOctaveDelta;
    this._history.push(record);
  }
  /**transposes the entire song to the specified key, proportionally changing
   * key signatures that happen after the first measure
   * @newConcertKey - the new concert key (3 would be 2 sharps, -2 would be 2
   * flats)
   * @transposeDirection - matches constant from TransposeDirection class - 0
   * is NEAREST*/
  setConcertKeyOfSong(newConcertKey, transposeDirection = 0) {
    let measure = this._score.parts[0].staves[0].measures[0];
    let oldKey = measure.concertKey;
    let isMajor = measure.isMajorKey;
    let transposeUp;
    if (transposeDirection === 1 /* up */) {
      transposeUp = true;
    } else if (transposeDirection === 2 /* down */) {
      transposeUp = false;
    } else {
      transposeUp = PitchUtils.getNearestTranspositionDirection(
        oldKey,
        newConcertKey,
        isMajor,
        isMajor
      ) >= 0;
    }
    let transDO = PitchUtils.getTranspositionForKeyChange(
      oldKey,
      newConcertKey,
      isMajor,
      isMajor,
      transposeUp
    );
    this.transposeSong(transDO.chromatic, transDO.diatonic, true);
  }
  /**
   * reverts transpositions that were performed by transposeSong(),
   * setConcertKeyOfSong(), changeOctaveOfPart(), and adjustOctavesOfNotes()
   * @param steps - the number of steps in the transposition history to undo.
   * If 0 (default), all transpositions will be undone.
   */
  revertTranspositions(steps = 0) {
    if (steps <= 0 || steps > this._history.length) {
      steps = this._history.length;
    }
    let cutoffIndex = this._history.length - steps;
    for (let i = this._history.length - 1; i >= cutoffIndex; i--) {
      let record = this._history[i];
      switch (record.type) {
        case 2 /* score */: {
          this.transposeSong(
            -1 * record.chromaticShift,
            -1 * record.diatonicShift,
            false,
            -1 * record.playbackOctaveDelta
          );
          break;
        }
        case 0 /* partOctave */: {
          this.changeOctaveOfPart(
            record.part,
            -1 * record.octaveDelta
          );
          break;
        }
        case 1 /* noteOctaves */: {
          let inverse = record.ngOctaveDeltas.map((val) => val * -1);
          this.adjustOctavesOfNotes(inverse, record.ngList);
          break;
        }
        case 3 /* repitches */: {
          this._revertRepitchNotes(record.repitchDOs);
          break;
        }
      }
    }
    this._history = [];
  }
  //currently unused?
  transposeByChromaticSteps(chromaticSteps) {
    let diatonicSteps = [
      -7,
      -6,
      -6,
      -5,
      -5,
      -4,
      -4,
      -3,
      -2,
      -2,
      -1,
      -1,
      0,
      1,
      1,
      2,
      2,
      3,
      4,
      4,
      5,
      5,
      6,
      6,
      7
    ];
    this.transposeSong(diatonicSteps[chromaticSteps + 12], chromaticSteps);
  }
  /**/
  ///////////////BUILDING BLOCK METHODS//////////////////*/
  /**reverts note pitches changed via repitchNotes() */
  _revertRepitchNotes(repitchDOs) {
    for (let rdo of repitchDOs) {
      this._noteManager.repitch(
        rdo.note,
        rdo.oldLetter,
        rdo.oldOctave,
        rdo.oldAlteration
      );
    }
  }
  /**transposes notes for a single part and region
   * optionally updates which notes display accidentals (checking against key
   * and previous accidentals in measure)*/
  _transposeRegion(mr, chromaticShift, diatonicShift = null, updateDisplayedAccidentals = true) {
    if (diatonicShift == null) {
      diatonicShift = PitchUtils.getCommonDiatonicTranspositionFromChromaticSteps(
        chromaticShift
      );
    }
    let staves = mr.part.staves;
    for (let i = 0; i < staves.length; i++) {
      let staff = staves[i];
      let measures = staff.measures;
      let j = 0;
      for (j = mr.startMeasureIndex; j <= mr.endMeasureIndex; j++) {
        let cMeasure = measures[j];
        let clefList = cMeasure.clefs;
        let voices = cMeasure.voices;
        for (let voice of voices) {
          let noteGroups = voice.noteGroups;
          let numNoteGroups = noteGroups.length;
          for (let k = 0; k < numNoteGroups; k++) {
            let ng = noteGroups[k];
            let qNoteTime = ng.qNoteTime;
            if (ng.isGrace && ng.attachmentNG != null) {
              qNoteTime = ng.attachmentNG.qNoteTime;
            }
            let clefType;
            for (let clef of clefList) {
              if (qNoteTime + 1e-4 > clef.qNoteTime) {
                clefType = clef.type;
              } else {
                break;
              }
            }
            let notes = ng.notes;
            for (let note of notes) {
              note.stepsFromTopStaffLine -= diatonicShift;
              note.pitchName = PitchUtils.getPitchNameFromStaffLinePosition(
                note.stepsFromTopStaffLine,
                clefType
              );
              note.displayCents += chromaticShift * 100;
              note.alteration = PitchUtils.getAlterationForCents(
                note.displayCents,
                note.pitchName
              );
              note.accidental = AccidentalType.getAccidentalType(
                note.alteration
              );
              note.legerLines = PitchUtils.getNumberOfLegerLines(
                note.stepsFromTopStaffLine
              );
              note.vPos = 1 / 2 * note.stepsFromTopStaffLine;
            }
          }
        }
        if (updateDisplayedAccidentals) {
          this._noteManager.updateAccidentals(cMeasure);
        }
        for (let att of cMeasure.attachments) {
          if (att instanceof ChordSymbol) {
            let pitch = PitchUtils.transposePitchName(
              att.rootName,
              att.rootAlteration,
              diatonicShift,
              chromaticShift
            );
            att.rootName = pitch.newPitchName;
            att.rootAlteration = pitch.newAlteration;
            if (att.bassName != null) {
              pitch = PitchUtils.transposePitchName(
                att.bassName,
                att.bassAlteration,
                diatonicShift,
                chromaticShift
              );
              att.bassName = pitch.newPitchName;
              att.bassAlteration = pitch.newAlteration;
            }
          }
        }
        cMeasure.notesNeedRendering = true;
      }
    }
  }
  /**calculates the number of accidentals for a new key based on the original
   * key and the change. Wraps new key if it gets too many flats/sharps*/
  _optimizeKeyEnharmonically(key) {
    if (key > 7) {
      key -= 12;
    } else if (key < -7) {
      key += 12;
    }
    return key;
  }
  /**updates concertKey to new value and adjusts display key accordingly
   * this affects all parts for the specified region
   * doesn't transpose notes, but can (optionally) update which notes display
   * accidentals. the MeasureRegion.part property doesn't matter, since the
   * entire score is affected*/
  _setConcertKeyForRegion(mr, newConcertKey, isMajor, updateDisplayedAccidentals = true) {
    mr.startMeasure.stack.newKey = !(mr.startMeasureIndex > 0 && mr.startMeasure.prev.concertKey === newConcertKey);
    if (mr.endMeasure.next != null) {
      mr.endMeasure.next.stack.newKey = mr.endMeasure.next.concertKey !== newConcertKey;
      mr.endMeasure.next.notesNeedRendering = true;
    }
    if (this._stacks == null) {
      this._stacks = this._score.getMeasureStacks();
    }
    for (let i = mr.startMeasureIndex + 1; i <= mr.endMeasureIndex; i++) {
      this._stacks[i].newKey = false;
    }
    let parts = this._score.parts;
    for (let part of parts) {
      this._setKeysForPartMeasureRegion(
        part,
        mr.startMeasureIndex,
        mr.endMeasureIndex,
        newConcertKey,
        isMajor,
        updateDisplayedAccidentals
      );
    }
    this._updateKeySizeForStacks(mr);
  }
  /**updates displayKey for one measure region and part based on measure
   * transposition settings doesn't transpose notes, but can (optionally)
   * update which notes display accidentals intended to be used after changing
   * Part/Measure chromatic/diatonic transpositions*/
  _updateDisplayKeyOfRegion(mr, updateDisplayedAccidentals = true) {
    this._setKeysForPartMeasureRegion(
      mr.part,
      mr.startMeasureIndex,
      mr.endMeasureIndex,
      mr.startMeasure.concertKey,
      mr.startMeasure.isMajorKey,
      updateDisplayedAccidentals
    );
    this._updateKeySizeForStacks(mr);
  }
  /**finds and stores the largest key signature size for each stack in the
   * MeasureRegion*/
  _updateKeySizeForStacks(mr) {
    if (this._stacks == null) {
      this._stacks = this._score.getMeasureStacks();
    }
    let lastIndex = mr.endMeasureIndex < this._stacks.length - 1 ? mr.endMeasureIndex + 1 : mr.endMeasureIndex;
    for (let i = mr.startMeasureIndex; i <= lastIndex; i++) {
      let stack = this._stacks[i];
      let maxKeySize = 0;
      let measures = stack.measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        let totalKeySize = PitchUtils.getTotalKeySize(
          measures[j].displayKey,
          measures[j].outgoingKey
        );
        if (totalKeySize > maxKeySize) {
          maxKeySize = totalKeySize;
        }
      }
      stack.maxKeySize = maxKeySize;
    }
  }
  /**getKeyMeasureRegions - returns MeasureRegion objects for a Part based on
   * changes in concertKey*/
  _getKeyMeasureRegions(part) {
    let regions = [];
    let measures = part.staves[0].measures;
    let startMeasure = null;
    let measure;
    for (measure of measures) {
      if (measure.stack.newKey) {
        if (startMeasure != null) {
          regions.push(new MeasureRegion(
            part,
            startMeasure,
            measure.prev
          ));
        }
        startMeasure = measure;
      }
    }
    regions.push(new MeasureRegion(part, startMeasure, measure));
    return regions;
  }
  /**/
  ///////////////////////INTERNAL METHODS///////////////////////////*/
  /**sets the display, concert and outgoing keys for a single region of
   * measures in a single part optionally updates the displayed notes on
   * accidentals*/
  _setKeysForPartMeasureRegion(part, startMeasureIndex, endMeasureIndex, newConcertKey, isMajor, updateDisplayedAccidentals) {
    let staves = part.staves;
    for (let i = 0; i < staves.length; i++) {
      let staff = staves[i];
      let measures = staff.measures;
      let j = 0;
      let displayedKey = 0;
      for (j = startMeasureIndex; j <= endMeasureIndex; j++) {
        let cMeasure = measures[j];
        displayedKey = PitchUtils.getKeySigAlterationForTransposition(
          cMeasure.diatonicTransposition,
          cMeasure.chromaticTransposition
        ) + newConcertKey;
        cMeasure.displayKey = displayedKey;
        cMeasure.concertKey = newConcertKey;
        cMeasure.isMajorKey = isMajor;
        cMeasure.outgoingKey = j > startMeasureIndex || startMeasureIndex === 0 ? 0 : measures[j - 1].displayKey;
        if (updateDisplayedAccidentals) {
          this._noteManager.updateAccidentals(cMeasure);
          cMeasure.notesNeedRendering = true;
        }
      }
      if (j < measures.length) {
        measures[j].outgoingKey = displayedKey;
      }
    }
  }
};

// src/editing/ScoreManager.ts
var ScoreManager = class {
  ScoreManager() {
  }
  /////////////////////////////TRANSPOSITION//////////////////////////////
  /** prevents transposition of all parts set to midiChannel 10*/
  preventPercussionTransposition() {
    this._transpositionManager.preventPercussionTransposition();
  }
  /**repitches multiple notes and stores the action in the transposition
   * history. */
  repitchNotes(repitchDOs) {
    this._transpositionManager.repitchNotes(repitchDOs);
  }
  /** Changes the octave of each note in a list. This is intended to operate
   *  on all of the notes in a single part, staff and voice and should not be
   *  used on a sub-selection of notes or notes from multiple parts, staves,
   * or voices.
   *  @param octaveShiftValues a Array<number> with each value designating the
   *  number of octaves to transpose each corresponding note.
   *  @param ngsNoTieIntosOrRests - a Array<NoteGroup> that contains all the
   *  NoteGroups from one part/staff/voice which aren't rests and which aren't
   *  the target (landing spot) of ties. This list is typically obtained via
   *  calling Part.getNoteGroupsWithoutRestsOrTieIntos().*/
  adjustOctavesOfNotes(octaveShiftValues, ngsNoTieIntosOrRests) {
    this._transpositionManager.adjustOctavesOfNotes(
      octaveShiftValues,
      ngsNoTieIntosOrRests
    );
  }
  /** transposes all of the notes in a given part by the specifed number of
   *  octaves.
   *  @octaveDelta - +1 for up one octave, -1 for down one octave, etc.*/
  changeOctaveOfPart(part, octaveDelta) {
    this._transpositionManager.changeOctaveOfPart(part, octaveDelta);
  }
  /**transposes the entire song by the relative amount specified
   * positive values for chromaticShift/diatonicShift transpose the song up, 
   * negative down values are relative, so 2,1 always transposes the song up a
   * whole step from its current key changes concertKey, displayKey, and 
   * transposes notes 
   * @preventInitialKeyOptimization - if you want the first region's key to be
   * exactly what you set without looking for a better enharmonic equivalent, 
   * set this to true 
   * @playbackOctaveCorrection - this option will adjust the
   * Part.playbackOctaveDelta property for each part by one or more octaves to
   * minimize the transposition's effect on the playback pitch.*/
  transposeSong(diatonicShift, chromaticShift, preventInitialKeyOptimization = false, playbackOctaveDelta = 0) {
    this._transpositionManager.transposeSong(
      chromaticShift,
      diatonicShift,
      preventInitialKeyOptimization,
      playbackOctaveDelta
    );
    this._pitchNameManager.updateNoteNames();
  }
  /**sets the transposition for the requested part, transposing the notes and 
   * display key chromaticTrans and diatonicTrans are absolute values, not 
   * relative shifts. So trumpet should always be 2 and 1 (the amount the 
   * transposed pitches APPEAR above concert pitch*/
  setPartTransposition(part, chromaticTrans, diatonicTrans = null) {
    this._transpositionManager.setPartTransposition(
      part,
      chromaticTrans,
      diatonicTrans
    );
    this._pitchNameManager.updateNoteNames();
  }
  /**creates a record of all the part transpositions in the [Score]*/
  savePartTranspositions() {
    this._transpositionManager.savePartTranspositions();
  }
  /**restores the part transpositions to those that were saved with 
   * [savePartTranspositions] call*/
  restorePartTranspositions() {
    this._transpositionManager.restorePartTranspositions();
  }
  /**transposes the entire song to the specified key, proportionally changing 
   * key signatures that happen after the first measure
   * @newConcertKey - the new concert key (3 would be 2 sharps, -2 would be 2 
   * flats)
   * @transposeDirection - matches constant from TransposeDirection class - 0 
   * is NEAREST*/
  setConcertKeyOfSong(newConcertKey, transposeDirection = 0) {
    this._transpositionManager.setConcertKeyOfSong(
      newConcertKey,
      transposeDirection
    );
    this._pitchNameManager.updateNoteNames();
  }
  /** reverts transpositions that were performed by transposeSong(),
   *  setConcertKeyOfSong(), changeOctaveOfPart(), and adjustOctavesOfNotes()
   *  @param steps - the number of steps in the transposition history to undo.
   *  If 0 (default), all transpositions will be undone.*/
  revertTranspositions(steps = 0) {
    this._transpositionManager.revertTranspositions(steps);
  }
  /**/
  //////////////////////////NOTE NAMES/LYRICS///////////////////////////*/
  /** reverts pitch names to original lyrics (if showNoteNames() was called)*/
  showLyrics() {
    this._pitchNameManager.showLyrics();
  }
  /** replaces lyrics with pitch names for each note*/
  showNoteNames(staff) {
    this._pitchNameManager.showNoteNames(staff);
  }
  hideLyrics() {
    this._pitchNameManager.hideLyrics();
  }
  /**completely deletes the lyrics from the score*/
  removeLyrics() {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        staff.originalLyrics = null;
        for (let measure of staff.measures) {
          for (let voice of measure.voices) {
            for (let ng of voice.noteGroups) {
              ng.lyric = null;
            }
          }
        }
      }
    }
  }
  /**sets the vertical position of the lyrics in spaces from the top of the
   * staff. A negative value means lower (typical)*/
  setLyricsVerticalPosition(vPos = -8) {
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        for (let measure of staff.measures) {
          for (let voice of measure.voices) {
            for (let ng of voice.noteGroups) {
              if (ng.lyric != null) {
                ng.lyric.vPos = vPos;
              }
            }
          }
        }
      }
    }
  }
  /**/
  ///////////////////////////SCORE CREATION/////////////////////////////*/
  /** creates a new Score object, populates its scoreSettings property and 
   *  creates a single first measure
   *  @param	scoreSettings optional set of data for setting ScoreProperties 
   *  values*/
  createScore(scoreSettings = null) {
    this.score = new Score();
    if (scoreSettings != null) {
      this._score.scoreProperties = scoreSettings;
    } else {
      this._score.scoreProperties = ScoreProperties.getNewScoreProperties();
    }
    let page = new Page();
    let system = new System(this._score.scoreProperties);
    system.newPage = true;
    page.addSystem(system);
    this._score.addPage(page);
  }
  /** adds the part to the score
   *  @param	name the name of the part
   *  @param	abbr abbreviated name of the part
   *  @param	diatonicTransposition number of diatonic steps that this part 
   *  appears below concert pitch (- values for appearing above)
   *  @param	chromaticTransposition number of chromatic steps that this part 
   *  sounds above concert pitch (- values for sounding below)
   *  @return*/
  addPart(name, abbr, diatonicTransposition, chromaticTransposition) {
    let partID = "P" + (this._score.parts.length + 1).toString();
    let part = new Part(partID, name, abbr);
    part.setTransposition(
      diatonicTransposition,
      chromaticTransposition,
      false
    );
    this._score.addPart(part);
    return part;
  }
  /** adds a new staff to the part
   *  @param	part the Part to add a staff to*/
  addStaff(part) {
    let staff = new Staff();
    part.addStaff(staff);
    return staff;
  }
  /** adds the measure at the position indicated
   *  @param	positionIndex the index to insert the measure at - if greater 
   *  than the number of measures in the song, it is just added to the end
   *  @return*/
  addMeasureStack(positionIndex = 16777215) {
    if (this._score.parts.length < 1 || this._score.parts[0].staves.length < 1) {
      throw "No parts and/or staves have been added to the score - you must add a part and a staff before adding measure stacks";
    }
    let firstStaff = this._score.parts[0].staves[0];
    let numExistingMeasures = firstStaff.measures.length;
    positionIndex = numExistingMeasures;
    if (positionIndex > numExistingMeasures || positionIndex < 0) {
      positionIndex = numExistingMeasures;
    }
    let newStack = new MeasureStack();
    let prevStack;
    if (numExistingMeasures > 0) {
      prevStack = firstStaff.measures[numExistingMeasures - 1].stack;
      prevStack.systemRef.addStackAfterExistingStack(newStack, prevStack);
      newStack.startTime = prevStack.endTime;
      newStack.endTime = prevStack.endTime + prevStack.qNoteLength;
      newStack.maxKeySize = prevStack.maxKeySize;
    } else {
      newStack.startTime = 0;
      newStack.endTime = 4;
      newStack.maxKeySize = 0;
      newStack.newSystem = true;
      newStack.newClef = true;
      newStack.newKey = true;
      this._score.pages[0].systems[0].addMeasureStack(newStack);
    }
    newStack.number = positionIndex + 1;
    let staffIndex = 0;
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        let newMeasure = new Measure();
        if (prevStack != null) {
          let prevMeasure = prevStack.measures[staffIndex];
          newMeasure.beatGroups = prevMeasure.beatGroups.slice(0);
          newMeasure.beatType = prevMeasure.beatType;
          newMeasure.numBeats = prevMeasure.numBeats;
          newMeasure.displayKey = prevMeasure.displayKey;
          newMeasure.concertKey = prevMeasure.concertKey;
          newMeasure.isMajorKey = prevMeasure.isMajorKey;
          newMeasure.diatonicTransposition = prevMeasure.diatonicTransposition;
          newMeasure.chromaticTransposition = prevMeasure.chromaticTransposition;
          let clef = new Clef();
          clef.type = prevMeasure.clefs[prevMeasure.clefs.length - 1].type;
          clef.qNoteTime = newStack.startTime;
          newMeasure.clefs.push(clef);
        } else {
          newMeasure;
          newMeasure.beatGroups = [0, 1, 2, 3];
          newMeasure.beatType = 4;
          newMeasure.numBeats = 4;
          newMeasure.showTime = true;
          newMeasure.concertKey = 0;
          newMeasure.displayKey = PitchUtils.getKeySigAlterationForTransposition(
            part.diatonicTransposition,
            part.chromaticTransposition
          );
          newMeasure.diatonicTransposition = part.diatonicTransposition;
          newMeasure.chromaticTransposition = part.chromaticTransposition;
          newMeasure.isMajorKey = true;
          let keySize = Math.abs(newMeasure.displayKey);
          if (keySize > newStack.maxKeySize) {
            newStack.maxKeySize = keySize;
          }
          let clef = new Clef();
          clef.isNew = true;
          clef.show = true;
          clef.type = ClefType.TREBLE;
          clef.qNoteTime = 0;
          newMeasure.clefs.push(clef);
        }
        newMeasure.stack = newStack;
        newStack.addMeasure(newMeasure);
        staff.insertMeasure(newMeasure, positionIndex);
        newMeasure.getVoiceByNumber(1);
        this._noteManager.fillMeasure(newMeasure);
        staffIndex++;
      }
    }
    return newStack;
  }
  deleteMeasures(firstMeasure, lastMeasure) {
    let startIndex = firstMeasure.staff.measures.indexOf(
      firstMeasure
    );
    let endIndex = lastMeasure.staff.measures.indexOf(lastMeasure);
    let parts = this._score.parts;
    for (let part of parts) {
      let staves = part.staves;
      for (let staff of staves) {
        let measures = staff.measures;
        for (let i = endIndex; i >= startIndex; i--) {
          let voices = measures[i].voices;
          for (let cVoice of voices) {
            let cNoteGroups = cVoice.noteGroups;
            for (let cng of cNoteGroups) {
              if (cng.slurs != null) {
                for (let slur of cng.slurs) {
                  this.removeSlur(slur);
                }
              }
            }
          }
          staff.removeMeasureAt(i);
        }
        if (measures.length > startIndex) {
          let nextMeas = measures[startIndex];
          if (startIndex > 0) {
            let prevMeas = measures[startIndex - 1];
            if (nextMeas.beatType !== prevMeas.beatType || nextMeas.numBeats !== prevMeas.numBeats) {
              nextMeas.showTime = true;
            }
            if (nextMeas.concertKey !== prevMeas.concertKey) {
              nextMeas.showKey = true;
              nextMeas.stack.newKey = true;
            }
            if (nextMeas.clefs[0].type !== prevMeas.clefs[prevMeas.clefs.length - 1].type) {
              nextMeas.clefs[0].show = true;
              nextMeas.stack.newClef = true;
            }
          } else {
            nextMeas.showTime = true;
            nextMeas.showKey = true;
            nextMeas.stack.newKey = true;
            nextMeas.clefs[0].show = true;
            nextMeas.stack.newClef = true;
          }
        }
      }
    }
    let stacks = this._score.getMeasureStacks();
    let totalTime = 0;
    for (let i = endIndex; i >= startIndex; i--) {
      let stack = stacks[i];
      stack.systemRef.needsRendering = true;
      totalTime += stack.qNoteLength;
      stack.systemRef.removeStack(stacks[i]);
    }
    totalTime *= -1;
    stacks = this._score.getMeasureStacks();
    if (startIndex < stacks.length) {
      stacks[startIndex].needsRendering = true;
      let numMeasures = stacks.length;
      for (let i = startIndex; i < numMeasures; i++) {
        let stack = stacks[i];
        stack.startTime += totalTime;
        stack.endTime += totalTime;
      }
      for (let i = startIndex; i < numMeasures; i++) {
        for (let part of parts) {
          let staves = part.staves;
          for (let staff of staves) {
            let measure = staff.measures[i];
            for (let voice of measure.voices) {
              this._noteManager.changeNoteGroupTimeStamps(
                voice.noteGroupContainers,
                totalTime,
                measure.stack.endTime,
                voice,
                true
              );
            }
          }
        }
      }
    }
  }
  changeTime(firstMeasureStack, lastMeasureStack, numBeats, beatType, beatGroups) {
    let stacks = this._score.getMeasureStacks();
    let startMeasureIndex = firstMeasureStack != null ? stacks.indexOf(firstMeasureStack) : 0;
    let endMeasureIndex = lastMeasureStack != null ? stacks.indexOf(lastMeasureStack) : stacks.length - 1;
    if (startMeasureIndex === -1 || endMeasureIndex === -1) {
      return;
    }
    let prevStack = startMeasureIndex > 0 ? stacks[startMeasureIndex - 1] : null;
    let firstStack = stacks[startMeasureIndex];
    let showTime = !(prevStack != null && prevStack.measures[0].numBeats === numBeats && prevStack.measures[0].beatType === beatType);
    for (let measure of firstStack.measures) {
      measure.showTime = showTime;
    }
    if (endMeasureIndex < stacks.length - 1) {
      let nextStack = stacks[endMeasureIndex + 1];
      let showTime2 = nextStack.measures[0].numBeats !== numBeats || nextStack.measures[0].beatType !== beatType;
      nextStack.measures[0].notesNeedRendering = true;
      for (let measure of nextStack.measures) {
        measure.showTime = showTime2;
      }
    }
    for (let i = startMeasureIndex + 1; i <= endMeasureIndex; i++) {
      for (let measure of stacks[i].measures) {
        measure.showTime = false;
      }
    }
    let numStacks = stacks.length;
    let timeOffsets = [];
    for (let i = 0; i < numStacks - startMeasureIndex; i++) {
      timeOffsets.push(0);
    }
    let cTime = firstStack.startTime;
    for (let i = startMeasureIndex; i < numStacks; i++) {
      let stack = stacks[i];
      timeOffsets.push(cTime - stack.startTime);
      stack.startTime = cTime;
      cTime += i <= endMeasureIndex ? numBeats * (4 / beatType) : stack.measures[0].numBeats * (4 / stack.measures[0].beatType);
      stack.endTime = cTime;
    }
    let parts = this._score.parts;
    for (let part of parts) {
      let staves = part.staves;
      for (let i = 0; i < staves.length; i++) {
        let staff = staves[i];
        let measures = staff.measures;
        let numMeasures = measures.length;
        for (let j = startMeasureIndex; j < numMeasures; j++) {
          let cMeasure = measures[j];
          let voices = cMeasure.voices;
          for (let voice of voices) {
            let ngContainers = voice.noteGroupContainers;
            this._noteManager.changeNoteGroupTimeStamps(
              ngContainers,
              timeOffsets[j - startMeasureIndex],
              cMeasure.stack.endTime,
              voice,
              j <= endMeasureIndex
            );
          }
          if (j <= endMeasureIndex) {
            cMeasure.numBeats = numBeats;
            cMeasure.beatType = beatType;
            cMeasure.displayNumBeats = null;
            cMeasure.displayBeatType = null;
            cMeasure.beatGroups = beatGroups.slice(0);
            if (j > startMeasureIndex) {
              cMeasure.showTime = false;
            }
          }
          cMeasure.notesNeedRendering = true;
        }
      }
    }
    for (let part of parts) {
      let staves = part.staves;
      for (let i = 0; i < staves.length; i++) {
        let staff = staves[i];
        let measures = staff.measures;
        for (let j = startMeasureIndex; j <= endMeasureIndex; j++) {
          this._noteManager.fillMeasure(measures[j]);
        }
      }
    }
  }
  /**creates a record of all the [Clef] objects in the [Score]*/
  saveClefs() {
    this._clefsMap = /* @__PURE__ */ new Map();
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        for (let measure of staff.measures) {
          this._clefsMap.set(measure, measure.clefs);
        }
      }
    }
  }
  /**restores the clefs to those that were saved with [saveClefs] call*/
  restoreClefs() {
    if (this._clefsMap == null) {
      throw "you must call saveClefs() before using restoreClefs()";
    }
    for (let part of this._score.parts) {
      for (let staff of part.staves) {
        for (let measure of staff.measures) {
          if (measure.clefs !== this._clefsMap.get(measure)) {
            measure.clefs = this._clefsMap.get(measure);
            this._noteManager.fixNotesForClefs(measure);
          }
        }
      }
    }
  }
  /** Changes the clef for the requested region
   *  @param firstMeasure the first measure of the region
   *  @param lastMeasure the last measure of the region
   *  @param clefType a string matching a constant from the ClefType class.*/
  changeClef(firstMeasure, lastMeasure, clefType) {
    let measures = firstMeasure.staff.measures;
    let startMeasureIndex = measures.indexOf(firstMeasure);
    let endMeasureIndex = measures.indexOf(lastMeasure);
    if (endMeasureIndex === -1) {
      endMeasureIndex = measures.length - 1;
    }
    let newClef = new Clef();
    newClef.isNew = startMeasureIndex === 0 || measures[startMeasureIndex - 1].clefs[measures[startMeasureIndex - 1].clefs.length - 1].type !== clefType;
    newClef.show = newClef.isNew;
    newClef.type = clefType;
    newClef.qNoteTime = firstMeasure.stack.startTime;
    firstMeasure.clefs = [newClef];
    firstMeasure.stack.newClef = newClef.isNew;
    if (endMeasureIndex < measures.length - 1) {
      let followingMeasure = measures[endMeasureIndex + 1];
      followingMeasure.clefs[0].isNew = followingMeasure.clefs[0].type !== clefType;
      followingMeasure.clefs[0].show = followingMeasure.clefs[0].isNew;
      followingMeasure.stack.newClef = followingMeasure.clefs[0].isNew;
      followingMeasure.notesNeedRendering = true;
    }
    let spacePerStep = 1 / 2;
    for (let i = startMeasureIndex; i <= endMeasureIndex; i++) {
      let measure = measures[i];
      if (i > startMeasureIndex) {
        newClef = new Clef();
        newClef.type = clefType;
        newClef.qNoteTime = measure.stack.startTime;
        measure.clefs = [newClef];
        measure.stack.newClef = false;
      }
      let voices = measure.voices;
      for (let voice of voices) {
        let noteGroups = voice.noteGroups;
        let numNoteGroups = noteGroups.length;
        for (let j = 0; j < numNoteGroups; j++) {
          let notes = noteGroups[j].notes;
          let numNotes = notes.length;
          for (let k = 0; k < numNotes; k++) {
            let note = notes[k];
            let steps = PitchUtils.getStepsFromTopStaffLine(
              note.pitchName,
              clefType
            );
            note.stepsFromTopStaffLine = steps;
            note.legerLines = PitchUtils.getNumberOfLegerLines(
              steps
            );
            note.vPos = spacePerStep * steps;
          }
        }
      }
      measure.notesNeedRendering = true;
    }
  }
  /** changes the passed in NoteGroup's duration. Any notes overlapped by an 
   *  increase in duration are deleted. any gaps created are filled with rests
   *  @param noteGroup the NoteGroup object to change
   *  @param newDuration the new Duration Unit duration (a power of 2 matching
   *  a duration in the DurationType constants)
   *  @param numDots the number of augmentation dots*/
  changeDuration(noteGroup, newDuration, numDots) {
    this._noteManager.changeDuration(noteGroup, newDuration, numDots);
  }
  addTuplet(ng, numerator, denominator, numeratorDuration, denominatorDuration) {
    this._noteManager.addTuplet(
      ng,
      numerator,
      denominator,
      numeratorDuration,
      denominatorDuration
    );
  }
  /** Toggles the tie state for the requested Note
   *  @param note the Note to change*/
  toggleTie(note) {
    this._noteManager.toggleTie(note);
  }
  /** adds the pitch to the specified NoteGroup
   *  @param noteGroup the NoteGroup to add the Note to
   *  @param pitchLetterName A, B, C, D, E, F, or G (uppercase)
   *  @param octave the octave for the note - C4 is middle C, the note below 
   *  it is B3.
   *  @param alteration a value from -2 (number flat) to 2 (number sharp). 
   *  This is an absolute alteration - not relative to the key signature's 
   *  adjustment for this pitch.*/
  addPitch(noteGroup, pitchLetterName, octave, alteration) {
    this._noteManager.addPitch(
      noteGroup,
      pitchLetterName,
      octave,
      alteration
    );
  }
  /** removes the Note from the NoteGroup
   *  @param noteGroup the NoteGroup to remove the Note from
   *  @param note the Note to remove*/
  removePitch(noteGroup, note) {
    this._noteManager.removePitch(noteGroup, note);
  }
  /** changes the octave of the specified Note
   *  @param note the Note to change
   *  @param octaveDelta the number of octaves to shift the note - negative 
   *  values for transposing down.*/
  changeNoteOctave(note, octaveDelta) {
    this._noteManager.changeNoteOctave(note, octaveDelta);
  }
  /** changes the octave of the specified NoteGroup
   *  @param ng the NoteGroup to change
   *  @param octaveDelta the number of octaves to shift the note - negative 
   *  values for transposing down.*/
  changeNoteGroupOctave(ng, octaveDelta) {
    this._noteManager.changeNoteGroupOctave(ng, octaveDelta);
  }
  /** For music coming from MusicXML, this creates NoteGroupContainer objects 
   *  for NoteGroups and Tuplets, adding relative time stamps and creating a 
   *  hierarchy that makes it possible to fill measures with rests*/
  createNoteGroupContainers() {
    this._noteManager.createNoteGroupContainers();
  }
  /** adds a slur between two notes
   *  @param ng1 the first NoteGroup
   *  @param ng2 the second NoteGroup*/
  addSlur(ng1, ng2) {
    this._noteManager.addSlur(ng1, ng2);
  }
  /** removes a Slur from the score
   *  @param slur the Slur to remove*/
  removeSlur(slur) {
    this._noteManager.removeSlur(slur);
  }
  get score() {
    return this._score;
  }
  set score(value) {
    this._score = value;
    this._noteManager = new NoteManager(value);
    this._transpositionManager = new TranspositionManager(
      value,
      this._noteManager
    );
    this._pitchNameManager = new PitchNameManager(value);
  }
};

// src/editing/transposition/TranspositionStatsDO.ts
var TranspositionStatsDO = class _TranspositionStatsDO {
  constructor() {
  }
  static getStats(part) {
    let minPitch = 99999;
    let maxPitch = -99999;
    let concertKeys = [];
    let displayKeys = [];
    let lastConcertKey = 99999;
    let lastDisplayKey = -99999;
    for (let staff of part.staves) {
      for (let measure of staff.measures) {
        if (measure.concertKey !== lastConcertKey) {
          lastConcertKey = measure.concertKey;
          concertKeys.push(lastConcertKey);
        }
        if (measure.displayKey !== lastDisplayKey) {
          lastDisplayKey = measure.displayKey;
          displayKeys.push(lastDisplayKey);
        }
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            if (!ng.isRest) {
              let notes = ng.notes;
              if (notes[0].displayCents < minPitch) {
                minPitch = notes[0].displayCents;
              }
              if (notes[notes.length - 1].displayCents > maxPitch) {
                maxPitch = notes[notes.length - 1].displayCents;
              }
            }
          }
        }
      }
    }
    let tStatsDO = new _TranspositionStatsDO();
    tStatsDO.concertKeys = concertKeys;
    tStatsDO.displayKeys = displayKeys;
    tStatsDO.minPitch = ~~(minPitch / 100);
    tStatsDO.maxPitch = ~~(maxPitch / 100);
    return tStatsDO;
  }
};

// src/utils/PointXY.ts
var PointXY = class {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
};

// src/formatting/attachments/AttachmentFormatter.ts
var _AttachmentFormatter = class _AttachmentFormatter {
  constructor(score) {
    this._score = score;
  }
  /**
   * formats the measure attachments (and eventually note attachments)
   * @param system the System to process
   */
  formatAttachments(system) {
    this._formatMeasureAttachments(system);
    this._formatNoteAttachments(system);
  }
  /**
   * formats slurs (and eventually hairpins or other lines drawn on the 
   * systems
   */
  formatLines() {
    let systems = this._score.getSystems();
    for (let system of systems) {
      system.slurSegments = [];
    }
    let measuresPerStack = systems[0].measureStacks[0].measures.length;
    for (let system of systems) {
      let repeatEndingHeightSet = false;
      let staffSpacing = 4 + system.staffSpacing;
      let staffVPosOffsets = system.staffVPosOffsets;
      let stacks = system.measureStacks;
      let numStacks = stacks.length;
      for (let i = 0; i < numStacks; i++) {
        let stack = stacks[i];
        let vStaffPos = 0;
        let measures = stack.measures;
        for (let j = 0; j < measuresPerStack; j++) {
          let measure = measures[j];
          let voices = measure.voices;
          for (let voice of voices) {
            let noteGroups = voice.noteGroups;
            let numNoteGroups = noteGroups.length;
            for (let k = 0; k < numNoteGroups; k++) {
              let ng = noteGroups[k];
              this._processSlursForNoteGroup(
                ng,
                voice,
                k,
                vStaffPos + staffVPosOffsets[j]
              );
              this._processTupletsForNoteGroup(ng);
            }
          }
          vStaffPos += staffSpacing;
        }
        if (!repeatEndingHeightSet && stack.repeatDOs != null) {
          for (let rdo of stack.repeatDOs) {
            if (rdo.endingType != null) {
              this._setRepeatEndingHeight(stacks, i);
              repeatEndingHeightSet = true;
            }
          }
        }
      }
    }
  }
  _processSlursForNoteGroup(ng, voice, ngIndex, vStaffPos) {
    if (ng.slurs != null) {
      let slurs = ng.slurs;
      for (let x = slurs.length - 1; x >= 0; x--) {
        let slur = slurs[x];
        if (slur.firstNote === ng) {
          if (slur.endNote == null || voice.number !== slur.endNote.voice.number || ng.voice.measure.staff !== slur.endNote.voice.measure.staff || ng.isRest || slur.endNote.isRest || ng === slur.endNote) {
            ng.slurs.splice(x, 1);
            if (slur.endNote != null) {
              slur.endNote.slurs.splice(
                slur.endNote.slurs.indexOf(slur),
                1
              );
            }
          } else {
            this._buildSlurSegments(slur, ngIndex, vStaffPos);
          }
        }
      }
    }
  }
  _processTupletsForNoteGroup(ng) {
    if (ng.tuplets != null) {
      let tupletIndex = 0;
      for (let tuplet of ng.tuplets) {
        if (tuplet.firstNote === ng) {
          this._formatTuplet(
            ng,
            tuplet,
            ng.tuplets.length - 1 - tupletIndex
          );
        }
        tupletIndex++;
      }
    }
  }
  _formatMeasureAttachments(system) {
    let stacks = system.measureStacks;
    for (let i = 0; i < stacks.length; i++) {
      let stack = stacks[i];
      let measures = stack.measures;
      let numMeasures = measures.length;
      for (let j = 0; j < numMeasures; j++) {
        if (measures[j].attachments != null) {
          let measure = measures[j];
          let attachments = measure.attachments;
          let stackWidth = stack.newSystem ? stack.width - stack.indentAsSystemLeader : stack.width - stack.indent;
          for (let attachment of attachments) {
            this._processMeasureAttachment(
              attachment,
              measure,
              stack,
              stackWidth,
              i === 0
            );
          }
        }
      }
    }
  }
  _processMeasureAttachment(attachment, measure, stack, stackWidth, stackIsSystemLeader) {
    let hPos;
    let vPos;
    let voices = measure.voices;
    let isEmptyMeasure = voices.length === 0 || voices.length === 1 && voices[0].isEmptyOrSingleRest;
    if (!isEmptyMeasure) {
      let attachmentTime = attachment.qNoteTime;
      let closestPrevNG = null;
      let closestNextNG = null;
      let matchingNG = null;
      for (let voice of voices) {
        let noteGroups = voice.noteGroups;
        let numNoteGroups = noteGroups.length;
        for (let j = 0; j < numNoteGroups; j++) {
          let ngTime = noteGroups[j].qNoteTime;
          if (ngTime < attachmentTime) {
            if (closestPrevNG == null || closestPrevNG.qNoteTime < ngTime) {
              closestPrevNG = noteGroups[j];
            }
          } else if (ngTime === attachmentTime) {
            matchingNG = noteGroups[j];
            break;
          } else {
            if (closestNextNG == null || closestNextNG.qNoteTime > ngTime) {
              closestNextNG = noteGroups[j];
              break;
            }
          }
        }
      }
      let collisionNG = null;
      if (matchingNG != null) {
        hPos = matchingNG.hPos;
        collisionNG = matchingNG;
      } else if (closestNextNG != null && closestPrevNG != null) {
        let posRatio = (attachmentTime - closestPrevNG.qNoteTime) / (closestNextNG.qNoteTime - closestPrevNG.qNoteTime);
        hPos = posRatio * (closestNextNG.hPos - closestPrevNG.hPos) + closestPrevNG.hPos;
        collisionNG = hPos - closestPrevNG.hPos < closestNextNG.hPos - hPos ? closestPrevNG : closestNextNG;
      } else if (closestPrevNG != null) {
        let posRatio = (attachmentTime - closestPrevNG.qNoteTime) / (stack.endTime - closestPrevNG.qNoteTime);
        hPos = posRatio * (stack.width - closestPrevNG.hPos) + closestPrevNG.hPos;
        collisionNG = closestPrevNG;
      } else if (closestNextNG != null) {
        let posRatio = (attachmentTime - stack.startTime) / (closestNextNG.qNoteTime - stack.startTime);
        hPos = posRatio * closestNextNG.hPos;
        collisionNG = closestNextNG;
      } else {
        hPos = (attachment.qNoteTime - stack.startTime) / stack.qNoteLength * stackWidth;
      }
      if (collisionNG != null && !collisionNG.isRest) {
        let notes = collisionNG.visibleNotes;
        if (attachment.isAbove) {
          let min = collisionNG.stemDirection === StemDirection.UP ? collisionNG.stemEndPos : notes[notes.length - 1].vPos;
          vPos = min < _AttachmentFormatter.defaultAbovePos + attachment.height + 1.5 ? min - 1.5 - attachment.height : _AttachmentFormatter.defaultAbovePos - attachment.height;
        } else {
          let max = collisionNG.stemDirection === StemDirection.DOWN ? collisionNG.stemEndPos : notes[0].vPos;
          vPos = max > _AttachmentFormatter.defaultBelowPos - 1.5 ? max + 1.5 : _AttachmentFormatter.defaultBelowPos;
        }
      } else {
        vPos = attachment.isAbove ? _AttachmentFormatter.defaultAbovePos - attachment.height : _AttachmentFormatter.defaultBelowPos;
      }
    } else {
      let stackIndent = stackIsSystemLeader ? stack.indentAsSystemLeader : stack.indent;
      hPos = (attachment.qNoteTime - stack.startTime) / stack.qNoteLength * stackWidth + stackIndent;
      vPos = attachment.isAbove ? _AttachmentFormatter.defaultAbovePos - attachment.height : _AttachmentFormatter.defaultBelowPos;
    }
    if (attachment.isAbove && attachment.baseline - attachment.height < vPos) {
      vPos = attachment.baseline - attachment.height;
    } else if (!attachment.isAbove && attachment.baseline > vPos) {
      vPos = attachment.baseline;
    }
    attachment.hPos = hPos;
    attachment.vPos = vPos;
  }
  _formatNoteAttachments(system) {
    let stacks = system.measureStacks;
    for (let stack of stacks) {
      let measures = stack.measures;
      let numMeasures = measures.length;
      for (let i = 0; i < numMeasures; i++) {
        let measure = measures[i];
        let voices = measure.voices;
        for (let j = 0; j < voices.length; j++) {
          let noteGroups = voices[j].noteGroups;
          let numNoteGroups = noteGroups.length;
          for (let k = 0; k < numNoteGroups; k++) {
            let ng = noteGroups[k];
            if (ng.articulations != null) {
              for (let artic of ng.articulations) {
                this._formatArticulation(
                  artic,
                  ng,
                  voices.length,
                  j
                );
              }
            }
            if (ng.ornaments != null) {
              for (let orn of ng.ornaments) {
                this._formatOrnament(orn, ng);
              }
            }
          }
        }
      }
    }
  }
  _formatArticulation(articulation, ng, numVoices, voiceIndex) {
    let type = articulation.type;
    articulation.outsideStaff = type === ArticulationType.ACCENT || ng.isRest;
    let above;
    if (numVoices > 1) {
      above = voiceIndex % 2 === 0;
    } else {
      above = ng.stemDirection !== StemDirection.UP;
    }
    articulation.isAbove = above;
    this._formatAttachment(articulation, ng);
  }
  _formatOrnament(ornament, ng) {
    ornament.outsideStaff = true;
    this._formatAttachment(ornament, ng);
  }
  _formatAttachment(att, ng) {
    let notes = ng.visibleNotes;
    let vOffset = 0.5;
    if (att.isAbove) {
      if (ng.isRest) {
        att.hPos = (1 - att.width) / 2;
        att.vPos = -vOffset - att.height;
      } else {
        let topNote = notes[notes.length - 1];
        let highestNoteSteps = ng.stemDirection === StemDirection.UP ? Math.round(ng.stemEndPos * 2) : topNote.stepsFromTopStaffLine;
        let attSteps;
        if (att.outsideStaff || highestNoteSteps <= 1) {
          attSteps = ng.stemDirection === StemDirection.UP ? Math.min(0, highestNoteSteps) : Math.min(0, highestNoteSteps - 1);
          att.vPos = -vOffset + attSteps * 0.5 - att.height;
        } else {
          attSteps = highestNoteSteps % 2 === 0 ? highestNoteSteps - 3 : highestNoteSteps - 2;
          att.vPos = attSteps / 2 - att.height / 2;
        }
        att.hPos = topNote.hPos + (1 - att.width) / 2;
      }
    } else {
      if (ng.isRest) {
        att.hPos = (1 - att.width) / 2;
        att.vPos = vOffset;
      } else {
        let bottomNote = notes[0];
        let lowestNoteSteps = ng.stemDirection === StemDirection.DOWN ? Math.round(ng.stemEndPos * 2) : bottomNote.stepsFromTopStaffLine;
        let attSteps;
        if (att.outsideStaff || lowestNoteSteps >= 7) {
          attSteps = ng.stemDirection === StemDirection.DOWN ? Math.max(8, lowestNoteSteps) : Math.max(8, lowestNoteSteps + 1);
          att.vPos = vOffset + attSteps * 0.5;
        } else {
          attSteps = lowestNoteSteps % 2 === 0 ? lowestNoteSteps + 3 : lowestNoteSteps + 2;
          att.vPos = attSteps / 2 - att.height / 2;
        }
        att.hPos = bottomNote.hPos + (1 - att.width) / 2;
      }
    }
  }
  _formatTuplet(ng, tuplet, level) {
    let needBracket = ng.isRest || tuplet.endNote != null && tuplet.endNote.isRest;
    let nextNG = ng;
    let stemUpTally = 0;
    let stemDownTally = 0;
    let highPoint = 1e3;
    let lowPoint = -1e3;
    while (nextNG != null) {
      let nextNotes = nextNG.visibleNotes;
      if (nextNG.beamStates.length === 0) {
        needBracket = true;
      }
      if (nextNG.stemDirection === StemDirection.UP) {
        stemUpTally++;
        if (nextNotes[0].vPos > lowPoint) {
          lowPoint = nextNotes[0].vPos;
        }
        if (nextNG.stemEndPos < highPoint) {
          highPoint = nextNG.stemEndPos;
        }
      } else if (nextNG.stemDirection === StemDirection.DOWN) {
        stemDownTally++;
        let lastNote = nextNotes[nextNotes.length - 1];
        if (lastNote.vPos < highPoint) {
          highPoint = lastNote.vPos;
        }
        if (nextNG.stemEndPos > lowPoint) {
          lowPoint = nextNG.stemEndPos;
        }
      } else if (nextNG.isRest) {
        stemUpTally++;
      } else {
        stemUpTally++;
        if (nextNotes[0].vPos > lowPoint) {
          lowPoint = nextNotes[0].vPos;
        }
        let lastNote = nextNotes[nextNotes.length - 1];
        if (lastNote.vPos < highPoint) {
          highPoint = lastNote.vPos;
        }
      }
      if (nextNG === tuplet.endNote) {
        break;
      }
      nextNG = nextNG.next;
    }
    let isAbove = stemUpTally >= stemDownTally;
    tuplet.above = isAbove;
    if (highPoint > 0) {
      highPoint = 0;
    }
    if (lowPoint < 4) {
      lowPoint = 4;
    }
    if (isAbove) {
      tuplet.vPos = highPoint - (level + 1);
    } else {
      tuplet.vPos = lowPoint + (level + 1);
    }
    tuplet.showBracket = needBracket;
  }
  _buildSlurSegments(slur, ngIndex, vStaffPos) {
    let ng1 = slur.firstNote;
    let ng2 = slur.endNote;
    let voiceNumber = ng2.voice.number;
    let offset = 1 / 2;
    let measure = ng1.voice.measure;
    let cSystem = measure.stack.systemRef;
    let isAbove = ng1.stemDirection === StemDirection.DOWN;
    let multipleVoices = measure.voices.length > 1;
    let segments = [];
    let ngCoordinates = {};
    let coordPos = 0;
    let endNoteFound = false;
    let segment;
    ngIndex++;
    let breakOuterLoop = false;
    while (measure != null) {
      let measureHPos = cSystem.indent + measure.stack.startPosition;
      if (measure.stack.systemRef !== cSystem) {
        segment = new SlurSegment();
        segment.systemRef = cSystem;
        segment.slur = slur;
        segment.ngCoordinates = ngCoordinates;
        segments.push(segment);
        cSystem.slurSegments.push(segment);
        cSystem = measure.stack.systemRef;
        if (cSystem.needsRendering) {
        }
        ngCoordinates = {};
        coordPos = 0;
      }
      let voices = measure.voices;
      for (let voice of voices) {
        if (voice.number === voiceNumber) {
          let noteGroups = voice.noteGroups;
          let numNoteGroups = noteGroups.length;
          for (let i = ngIndex; i < numNoteGroups; i++) {
            let cng = noteGroups[i];
            if (cng.isRest) {
              continue;
            }
            if (cng.stemDirection === StemDirection.DOWN) {
              isAbove = true;
            }
            if (cng !== ng2) {
              let cngNotes = cng.visibleNotes;
              if (cng.stemDirection === StemDirection.DOWN) {
                let topNote = cngNotes[cngNotes.length - 1];
                ngCoordinates[coordPos] = cng.hPos + measureHPos;
                ngCoordinates[coordPos + 1] = topNote.vPos;
                coordPos += 2;
                ngCoordinates[coordPos] = cng.hPos + measureHPos;
                ngCoordinates[coordPos + 1] = cng.stemEndPos;
                coordPos += 2;
              } else {
                let bottomNote = cngNotes[0];
                ngCoordinates[coordPos] = cng.hPos + measureHPos;
                ngCoordinates[coordPos + 1] = bottomNote.vPos;
                coordPos += 2;
                ngCoordinates[coordPos] = cng.hPos + measureHPos;
                ngCoordinates[coordPos + 1] = cng.stemDirection === StemDirection.UP ? cng.stemEndPos : cngNotes[cngNotes.length - 1].vPos;
                coordPos += 2;
              }
            } else {
              segment = new SlurSegment();
              segment.ngCoordinates = ngCoordinates;
              segment.systemRef = cSystem;
              segment.slur = slur;
              segments.push(segment);
              cSystem.slurSegments.push(segment);
              endNoteFound = true;
              breakOuterLoop = true;
              break;
            }
          }
          if (breakOuterLoop) {
            break;
          }
          ngIndex = 0;
        }
      }
      if (breakOuterLoop) {
        break;
      }
      measure = measure.next;
      if (measure.voices.length > 1) {
        multipleVoices = true;
      }
    }
    if (!endNoteFound) {
      ng1.slurs.splice(ng1.slurs.indexOf(slur), 1);
      if (slur.endNote != null) {
        slur.endNote.slurs.splice(slur.endNote.slurs.indexOf(slur), 1);
      }
      return;
    }
    if (multipleVoices) {
      isAbove = ng1.voice.number === 1;
    }
    slur.segments = segments;
    let startPoint;
    let endPoint;
    let slope;
    let angle;
    let cpXOffset;
    let cp1;
    let cp2;
    let yShift;
    let yIntercept;
    let perpSlope;
    let intersectX;
    let intersectY;
    for (let i = 0; i < segments.length; i++) {
      segment = segments[i];
      let hPos;
      let vPos;
      if (i === 0) {
        if (isAbove) {
          if (ng1.stemDirection === StemDirection.UP) {
            if (ng1.beamStates.length > 0 && ng1.beamStates[0] !== BeamState.END) {
              hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + ng1.stemHPos;
              vPos = ng1.stemEndPos - offset;
            } else {
              hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + ng1.stemHPos + offset;
              vPos = ng1.stemEndPos + 2 * offset;
            }
          } else {
            hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + offset;
            vPos = ng1.visibleNotes[ng1.visibleNotes.length - 1].vPos - 2 * offset;
          }
          startPoint = new PointXY(hPos, vPos);
        } else {
          if (ng1.stemDirection === StemDirection.DOWN) {
            if (ng1.beamStates.length > 0 && ng1.beamStates[0] !== BeamState.END) {
              hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + ng1.stemHPos;
              vPos = ng1.stemEndPos + offset;
            } else {
              hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + ng1.stemHPos + offset;
              vPos = ng1.stemEndPos - 2 * offset;
            }
          } else {
            hPos = segment.systemRef.indent + ng1.voice.measure.stack.startPosition + ng1.hPos + offset;
            vPos = ng1.visibleNotes[0].vPos + 2 * offset;
          }
          startPoint = new PointXY(hPos, vPos);
        }
      } else {
        let firstStack = segment.systemRef.measureStacks[0];
        hPos = firstStack.startPosition + firstStack.indentAsSystemLeader - firstStack.indent + segment.systemRef.indent;
        if (i === segments.length - 1) {
          if (isAbove) {
            vPos = -1 * offset;
            if (ng2.stemEndPos - offset < vPos) {
              vPos = ng2.stemEndPos - offset;
            } else {
              let ng2TopNote = ng2.visibleNotes[ng2.visibleNotes.length - 1];
              if (ng2TopNote.vPos - offset < vPos) {
                vPos = ng2TopNote.vPos - offset;
              }
            }
          } else {
            vPos = 4 + offset;
            if (ng2.stemEndPos + offset > vPos) {
              vPos = ng2.stemEndPos + offset;
            } else {
              let ng2BottomNote = ng2.visibleNotes[0];
              if (ng2BottomNote.vPos + offset > vPos) {
                vPos = ng2BottomNote.vPos + offset;
              }
            }
          }
        } else {
          vPos = isAbove ? -1 * offset : 4 + offset;
        }
        startPoint = new PointXY(hPos, vPos);
      }
      segment.points[0] = startPoint;
      if (i === segments.length - 1) {
        if (isAbove) {
          if (ng2.stemDirection === StemDirection.UP) {
            if (ng2.beamStates.length > 0 && ng2.beamStates[0] !== BeamState.BEGIN) {
              hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + ng2.stemHPos;
              vPos = ng2.stemEndPos - offset;
            } else {
              hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + ng2.stemHPos - offset;
              vPos = ng2.stemEndPos + 2 * offset;
            }
          } else {
            hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + offset;
            vPos = ng2.visibleNotes[ng2.visibleNotes.length - 1].vPos - 2 * offset;
          }
          endPoint = new PointXY(hPos, vPos);
        } else {
          if (ng2.stemDirection === StemDirection.DOWN) {
            if (ng2.beamStates.length > 0 && ng2.beamStates[0] !== BeamState.BEGIN) {
              hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + ng2.stemHPos;
              vPos = ng2.stemEndPos + offset;
            } else {
              hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + ng2.stemHPos - offset;
              vPos = ng2.stemEndPos - 2 * offset;
            }
          } else {
            hPos = segment.systemRef.indent + ng2.voice.measure.stack.startPosition + ng2.hPos + offset;
            vPos = ng2.visibleNotes[0].vPos + 2 * offset;
          }
          endPoint = new PointXY(hPos, vPos);
        }
      } else {
        let lastStack = segment.systemRef.measureStacks[segment.systemRef.measureStacks.length - 1];
        hPos = lastStack.startPosition + lastStack.width + segment.systemRef.indent;
        if (i === 0) {
          if (isAbove) {
            vPos = -1 * offset;
            if (startPoint.y < vPos) {
              vPos = startPoint.y;
            }
          } else {
            vPos = 4 + offset;
            if (startPoint.y > vPos) {
              vPos = startPoint.y;
            }
          }
        } else {
          vPos = isAbove ? -1 * offset : 4 + offset;
        }
        endPoint = new PointXY(hPos, vPos);
      }
      segment.points[3] = endPoint;
      let deltaX = startPoint.x * -1;
      let deltaY = startPoint.y * -1;
      let sp = new PointXY(startPoint.x + deltaX, startPoint.y + deltaY);
      let ep = new PointXY(endPoint.x + deltaX, endPoint.y + deltaY);
      let defaultCPHeight = Math.min(
        2 * offset,
        offset * ((ep.x - sp.x) / 100)
      );
      if (isAbove) {
        slope = (ep.y - sp.y) / (ep.x - sp.x);
        angle = Math.atan2(ep.y - sp.y, ep.x - sp.x);
        cpXOffset = ep.x - sp.x > 100 ? 10 : (ep.x - sp.x) / 4;
        cp1 = new PointXY(
          cpXOffset * Math.cos(angle),
          (ep.y - sp.y) * cpXOffset / ep.x - defaultCPHeight * Math.cos(angle)
        );
        cp2 = new PointXY(
          ep.x - cpXOffset * Math.cos(angle),
          (ep.y - sp.y) * ((ep.x - cpXOffset) / ep.x) - defaultCPHeight * Math.cos(angle)
        );
        ngCoordinates = segment.ngCoordinates;
        let numValues = Object.keys(ngCoordinates).length;
        let j = 0;
        while (j < numValues) {
          ngCoordinates[j] += deltaX;
          ngCoordinates[j + 1] += deltaY;
          if (ngCoordinates[j] / ep.x < 0.25) {
            ngCoordinates[j + 1] -= offset;
          } else if (ngCoordinates[j] / ep.x > 0.75) {
            ngCoordinates[j + 1] -= offset;
          }
          if (ngCoordinates[j + 1] < ngCoordinates[j] * slope + cp1.y) {
            yShift = ngCoordinates[j + 1] - (ngCoordinates[j] * slope + cp1.y);
            cp1.y += yShift;
            cp2.y += yShift;
          }
          j += 2;
        }
        if (slope === 0) {
          slope = 1e-3;
        }
        yIntercept = cp1.y - slope * cp1.x;
        perpSlope = -1 / slope;
        intersectX = yIntercept / (perpSlope - slope);
        intersectY = perpSlope * intersectX;
        cp1.x += intersectX + 2 * offset * Math.sin(angle);
        cp1.y += intersectY - 2 * offset * Math.cos(angle);
        cp2.x += intersectX + 2 * offset * Math.sin(angle);
        cp2.y += intersectY - 2 * offset * Math.cos(angle);
      } else {
        slope = (ep.y - sp.y) / (ep.x - sp.x);
        angle = Math.atan2(ep.y - sp.y, ep.x - sp.x);
        cpXOffset = ep.x - sp.x > 100 ? 10 : (ep.x - sp.x) / 4;
        cp1 = new PointXY(
          cpXOffset * Math.cos(angle),
          (ep.y - sp.y) * cpXOffset / ep.x + defaultCPHeight * Math.cos(angle)
        );
        cp2 = new PointXY(
          ep.x - cpXOffset * Math.cos(angle),
          (ep.y - sp.y) * ((ep.x - cpXOffset) / ep.x) + defaultCPHeight * Math.cos(angle)
        );
        ngCoordinates = segment.ngCoordinates;
        let numValues = Object.keys(ngCoordinates).length;
        let j = 0;
        while (j < numValues) {
          ngCoordinates[j] += deltaX;
          ngCoordinates[j + 1] += deltaY;
          if (ngCoordinates[j] / ep.x < 0.25) {
            ngCoordinates[j + 1] += offset;
          } else if (ngCoordinates[j] / ep.x > 0.75) {
            ngCoordinates[j + 1] += offset;
          }
          if (ngCoordinates[j + 1] > ngCoordinates[j] * slope + cp1.y) {
            yShift = ngCoordinates[j + 1] - (ngCoordinates[j] * slope + cp1.y);
            cp1.y += yShift;
            cp2.y += yShift;
          }
          j += 2;
        }
        if (slope === 0) {
          slope = 1e-3;
        }
        yIntercept = cp1.y - slope * cp1.x;
        perpSlope = -1 / slope;
        intersectX = yIntercept / (perpSlope - slope);
        intersectY = perpSlope * intersectX;
        cp1.x += intersectX - 2 * offset * Math.sin(angle);
        cp1.y += intersectY + 2 * offset * Math.cos(angle);
        cp2.x += intersectX - 2 * offset * Math.sin(angle);
        cp2.y += intersectY + 2 * offset * Math.cos(angle);
      }
      segment.points[0] = sp;
      segment.points[1] = cp1;
      segment.points[2] = cp2;
      segment.points[3] = ep;
      segment.points[0].x += -deltaX;
      segment.points[0].y += vStaffPos - deltaY;
      segment.points[1].x += -deltaX;
      segment.points[1].y += vStaffPos - deltaY;
      segment.points[2].x += -deltaX;
      segment.points[2].y += vStaffPos - deltaY;
      segment.points[3].x += -deltaX;
      segment.points[3].y += vStaffPos - deltaY;
    }
  }
  _setRepeatEndingHeight(stacks, firstStackIndex) {
    let highestVPos = 2576980377;
    let underEndingBracket = true;
    let endingRDOs = [];
    for (let i = firstStackIndex; i < stacks.length; i++) {
      let stack = stacks[i];
      let endingFinished = false;
      if (stack.repeatDOs != null) {
        for (let rdo of stack.repeatDOs) {
          if (rdo.endingType === EndingType.START) {
            underEndingBracket = true;
            endingRDOs.push(rdo);
          } else if (rdo.endingType === EndingType.DISCONTINUE || rdo.endingType === EndingType.STOP) {
            endingFinished = true;
            endingRDOs.push(rdo);
          }
        }
      }
      if (underEndingBracket) {
        let measure = stack.measures[0];
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            if (ng.stemDirection === StemDirection.UP) {
              if (ng.stemEndPos < highestVPos) {
                highestVPos = ng.stemEndPos;
              }
            } else if (ng.isRest) {
              if (ng.restVPos < highestVPos) {
                highestVPos = ng.restVPos;
              }
            } else {
              let noteVPos = ng.visibleNotes[ng.visibleNotes.length - 1].vPos;
              if (noteVPos < highestVPos) {
                highestVPos = noteVPos;
              }
            }
          }
        }
      }
      if (endingFinished) {
        underEndingBracket = false;
      }
    }
    if (highestVPos > 0) {
      highestVPos = 0;
    }
    let endingVPos = highestVPos - 2;
    for (let rdo of endingRDOs) {
      rdo.endingVPos = endingVPos;
    }
  }
};
_AttachmentFormatter.defaultAbovePos = -1;
_AttachmentFormatter.defaultBelowPos = 5;
var AttachmentFormatter = _AttachmentFormatter;

// src/formatting/notes/MusicBeamer.ts
var MusicBeamer = class {
  constructor() {
    this.successCount = 0;
  }
  /**
   * sets beam state for each NoteGroup in the system - it does not set the 
   * height or direction of stems
   * @param	system the System to process
   */
  getSystemBeamGroupings(system) {
    let stacks = system.measureStacks;
    for (let stack of stacks) {
      let measures = stack.measures;
      for (let measure of measures) {
        this.getMeasureBeamGroupings(measure);
      }
    }
  }
  /**
   * sets beam state for each NoteGroup in the measure - it does not set the 
   * height or direction of stems
   * @param	measure the Measure to process
   */
  getMeasureBeamGroupings(measure) {
    for (let voice of measure.voices) {
      let noteGroups = voice.noteGroups;
      let ng1 = null;
      let ng2 = null;
      let currentBeamStartTimes = {};
      for (let cng of noteGroups) {
        cng.beamStates = new Array(cng.maxBeams);
        for (let i = 0; i < cng.beamStates.length; i++) {
          cng.beamStates[i] = BeamState.NONE;
        }
        if (cng.maxBeams <= 0) {
          ng1 = null;
          ng2 = null;
          continue;
        } else if (cng.isRest) {
          continue;
        }
        ng1 = ng2;
        ng2 = cng;
        if (ng1 == null) {
          continue;
        }
        if (ng1.isGrace && !ng2.isGrace || !ng1.isGrace && ng2.isGrace) {
          continue;
        }
        let numBeatGroups = measure.beatGroups.length;
        let ng1EndTime = ng1.qNoteTime - measure.stack.startTime + ng1.qNoteDuration - 1e-3;
        let beatGroupIndex = 0;
        while (beatGroupIndex < numBeatGroups && ng1EndTime > measure.beatGroups[beatGroupIndex]) {
          beatGroupIndex++;
        }
        if (beatGroupIndex === numBeatGroups || ng2.qNoteTime - measure.stack.startTime < measure.beatGroups[beatGroupIndex]) {
          for (let i = 0; i < ng1.maxBeams; i++) {
            if (ng1.beamStates.length <= i || ng1.beamStates[i] === BeamState.NONE) {
              if (i < ng2.maxBeams) {
                ng1.beamStates[i] = BeamState.BEGIN;
                currentBeamStartTimes[i] = ng1.qNoteTime;
              } else {
                ng1.beamStates[i] = this.getBeamletDirection(
                  currentBeamStartTimes[Object.keys(
                    currentBeamStartTimes
                  ).length - 1],
                  ng1.qNoteTime,
                  ng1.qNoteDuration,
                  ng1.beamStates[i - 1]
                );
              }
            } else {
              if (i < ng2.maxBeams) {
                if (ng1.beamStates[i] === BeamState.END) {
                  ng1.beamStates[i] = BeamState.CONTINUE;
                } else {
                  ng1.beamStates[i] = BeamState.BEGIN;
                  currentBeamStartTimes[i] = ng1.qNoteTime;
                }
              } else {
              }
            }
          }
          for (let i = 0; i < ng2.maxBeams; i++) {
            if (i < ng1.maxBeams) {
              ng2.beamStates[i] = BeamState.END;
              currentBeamStartTimes[i] = -1;
            } else {
              ng2.beamStates[i] = this.getBeamletDirection(
                currentBeamStartTimes[Object.keys(
                  currentBeamStartTimes
                ).length - 1],
                ng2.qNoteTime,
                ng2.qNoteDuration,
                ng2.beamStates[i - 1]
              );
            }
          }
        }
      }
    }
  }
  /**
   * Gets the direction for the beamlet
   * @param	beamStartTime the start time of the first note of the beam group
   * @param	noteTime the start time of the note whose beamlet direction 
   * we're setting
   * @param	noteDuration the duration of the note whose beamlet direction 
   * we're setting
   * @param	previousBeamLevelState The beam state of the immediately 
   * preceding beam level (the 8th note beam if we're considering a sixteenth 
   * note level beam, etc.)
   * @return	BeamState.BACKWARD_HOOK or BeamState.FORWARD_HOOK
   */
  getBeamletDirection(beamStartTime, noteTime, noteDuration, previousBeamLevelState) {
    if (previousBeamLevelState === BeamState.END) {
      return BeamState.BACKWARD_HOOK;
    } else if (previousBeamLevelState === BeamState.BACKWARD_HOOK || previousBeamLevelState === BeamState.FORWARD_HOOK) {
      return previousBeamLevelState;
    }
    if (noteDuration === 0) {
      noteDuration = 0.25;
    }
    let isEvenMultiple = false;
    while (noteTime >= beamStartTime - 1e-3) {
      if (noteTime - beamStartTime < 1e-3) {
        isEvenMultiple = true;
        break;
      }
      noteTime -= noteDuration;
    }
    return isEvenMultiple ? BeamState.FORWARD_HOOK : BeamState.BACKWARD_HOOK;
  }
};

// src/formatting/notes/NoteGroupFormatter.ts
var NoteGroupFormatter = class {
  constructor(scoreProperties) {
    this._scoreProps = scoreProperties;
  }
  /**
   * gets the stem directions for all of the notes in the system
   * @param	system the System to process
   */
  getSystemStemDirections(system) {
    let stacks = system.measureStacks;
    for (let stack of stacks) {
      let measures = stack.measures;
      for (let measure of measures) {
        this.getMeasureStemDirections(measure);
      }
    }
  }
  /**
   * gets the stem directions for all of the notes in the measure
   * @param	measure the Measure to process
   */
  getMeasureStemDirections(measure) {
    let numVoices = measure.voices.length;
    let numVisibleVoices = 0;
    for (let voice of measure.voices) {
      for (let ng of voice.noteGroups) {
        if (ng.visible) {
          numVisibleVoices++;
          break;
        }
      }
    }
    for (let voiceIndex = 0; voiceIndex < numVoices; voiceIndex++) {
      let noteGroups = measure.voices[voiceIndex].noteGroups;
      let currentUpScore = 0;
      let currentDownScore = 0;
      let highestUpScore = 0;
      let highestDownScore = 0;
      let numUpVotes = 0;
      let numDownVotes = 0;
      let beamGroup = [];
      for (let ng of noteGroups) {
        if (ng.isRest) {
          if (numVisibleVoices > 1) {
            ng.restVPos = voiceIndex % 2 === 0 ? -1 : 5;
          } else {
            ng.restVPos = 2;
          }
          continue;
        }
        if (ng.stemDirection === StemDirection.NO_STEM) {
          continue;
        }
        if (numVisibleVoices > 1) {
          ng.stemDirection = voiceIndex % 2 === 0 ? StemDirection.UP : StemDirection.DOWN;
          continue;
        }
        let visNotes = ng.visibleNotes;
        currentUpScore = visNotes[0].stepsFromTopStaffLine - 4;
        currentDownScore = 4 - visNotes[visNotes.length - 1].stepsFromTopStaffLine;
        if (currentUpScore > currentDownScore) {
          numUpVotes++;
        } else {
          numDownVotes++;
        }
        if (currentUpScore > highestUpScore) {
          highestUpScore = currentUpScore;
        }
        if (currentDownScore > highestDownScore) {
          highestDownScore = currentDownScore;
        }
        if (ng.beamStates.length === 0 || ng.beamStates[0] === BeamState.NONE) {
          ng.stemDirection = highestUpScore > highestDownScore ? StemDirection.UP : StemDirection.DOWN;
          highestUpScore = 0;
          highestDownScore = 0;
          numUpVotes = 0;
          numDownVotes = 0;
        } else if (ng.beamStates[0] === BeamState.END) {
          beamGroup.push(ng);
          if (highestUpScore > highestDownScore || highestUpScore === highestDownScore && numUpVotes > numDownVotes) {
            this.setStemDirectionsForBeamedNotes(
              beamGroup,
              StemDirection.UP
            );
          } else {
            this.setStemDirectionsForBeamedNotes(
              beamGroup,
              StemDirection.DOWN
            );
          }
          highestUpScore = 0;
          highestDownScore = 0;
          numUpVotes = 0;
          numDownVotes = 0;
          beamGroup = new Array();
        } else {
          beamGroup.push(ng);
        }
      }
    }
  }
  setStemDirectionsForBeamedNotes(beamedNotes, direction) {
    for (let beamedNG of beamedNotes) {
      beamedNG.stemDirection = direction;
    }
  }
  /**
   * sets the positions of noteheads within a notegroup for the passed in 
   * system
   * @param	system the system to process
   */
  getSystemNoteheadOffsets(system) {
    let stacks = system.measureStacks;
    for (let stack of stacks) {
      let measures = stack.measures;
      for (let measure of measures) {
        this.getMeasureNoteheadOffsets(measure);
      }
    }
  }
  /**
   * sets the positions of noteheads within a notegroup for the passed in 
   * Measure
   * @param	measure the Measure to process
   */
  getMeasureNoteheadOffsets(measure) {
    for (let voice of measure.voices) {
      let groups = voice.noteGroups;
      for (let ng of groups) {
        this.getNoteheadOffsets(ng);
      }
    }
  }
  /**
   * sets the positions of noteheads within a notegroup
   * @param	noteGroup the noteGroup to process
   */
  getNoteheadOffsets(noteGroup) {
    if (noteGroup.isRest) {
      return;
    }
    let placeOnLeft;
    let notes = noteGroup.visibleNotes;
    let numNotes = notes.length;
    let lastPosition = 1e3;
    let leftNotePos = 0;
    let rightNotePos = !noteGroup.isGrace ? this._scoreProps.noteheadWidth : 0.65 * this._scoreProps.noteheadWidth;
    let stemHPos = 0;
    let stemStartPos;
    let cNote;
    if (noteGroup.stemDirection === StemDirection.UP) {
      stemStartPos = notes[0].vPos;
      stemHPos = rightNotePos;
      placeOnLeft = true;
      for (let i = 0; i < numNotes; i++) {
        cNote = notes[i];
        if (lastPosition - cNote.stepsFromTopStaffLine <= 1) {
          placeOnLeft = !placeOnLeft;
          cNote.hPos = placeOnLeft ? leftNotePos : rightNotePos;
        } else {
          placeOnLeft = true;
          cNote.hPos = 0;
        }
        lastPosition = cNote.stepsFromTopStaffLine;
      }
    } else if (noteGroup.stemDirection === StemDirection.DOWN) {
      stemStartPos = notes[numNotes - 1].vPos;
      placeOnLeft = false;
      lastPosition = -1e3;
      let clusterFound = false;
      let i = numNotes - 1;
      while (i >= 0) {
        cNote = notes[i];
        if (cNote.stepsFromTopStaffLine - lastPosition <= 1) {
          placeOnLeft = !placeOnLeft;
          cNote.hPos = placeOnLeft ? -1 * rightNotePos : leftNotePos;
          stemHPos = rightNotePos;
          clusterFound = true;
        } else {
          placeOnLeft = false;
          cNote.hPos = 0;
        }
        lastPosition = cNote.stepsFromTopStaffLine;
        i--;
      }
      if (clusterFound) {
        for (let i2 = 0; i2 < numNotes; i2++) {
          notes[i2].hPos += rightNotePos;
        }
      }
    } else {
      stemStartPos = 0;
      stemHPos = rightNotePos;
      placeOnLeft = true;
      for (let i = 0; i < numNotes; i++) {
        cNote = notes[i];
        if (lastPosition - cNote.stepsFromTopStaffLine <= 1) {
          placeOnLeft = !placeOnLeft;
          cNote.hPos = placeOnLeft ? leftNotePos : rightNotePos;
        } else {
          placeOnLeft = true;
          cNote.hPos = 0;
        }
        lastPosition = cNote.stepsFromTopStaffLine;
      }
    }
    noteGroup.stemHPos = stemHPos;
    noteGroup.stemStartPos = stemStartPos;
    noteGroup.numAccidentalLevels = 0;
    if (numNotes === 1) {
      if (notes[0].showAccidental) {
        notes[0].accidentalPos = -1.1 * rightNotePos;
        noteGroup.numAccidentalLevels = 1;
      }
    } else {
      let offset = -1 * notes[numNotes - 1].stepsFromTopStaffLine;
      let listLength = notes[0].stepsFromTopStaffLine + offset + 1;
      let levelClaimsList = {};
      let numPositions = listLength;
      for (let i = 0; i < numNotes; i++) {
        cNote = i % 2 === 0 ? notes[numNotes - Math.trunc(i / 2) - 1] : notes[Math.trunc(i / 2)];
        if (cNote.showAccidental === false) {
          continue;
        }
        let noteIndex = cNote.stepsFromTopStaffLine + offset;
        let lowerBound = noteIndex - 5 >= 0 ? noteIndex - 5 : 0;
        let upperBound = noteIndex + 5 <= numPositions - 1 ? noteIndex + 5 : numPositions - 1;
        let cLevel = 1;
        let repeat = true;
        while (repeat) {
          repeat = false;
          let j = lowerBound;
          while (j <= upperBound) {
            if (levelClaimsList[j] === cLevel) {
              cLevel++;
              repeat = true;
              break;
            }
            j++;
          }
        }
        levelClaimsList[noteIndex] = cLevel;
        cNote.accidentalPos = -1.1 * rightNotePos - (cLevel - 1) * rightNotePos;
        if (cLevel > noteGroup.numAccidentalLevels) {
          noteGroup.numAccidentalLevels = cLevel;
        }
      }
    }
  }
  /**
   * gets the stem end positions for all NoteGroups in the System
   * @param	system the System to process
   */
  getSystemStemEndPositions(system) {
    let stacks = system.measureStacks;
    for (let stack of stacks) {
      let measures = stack.measures;
      for (let measure of measures) {
        this.getMeasureStemEndPositions(measure);
      }
    }
  }
  /**
   * gets the stem end positions for all NoteGroups in the Measure
   * @param	measure the Measure to process
   */
  getMeasureStemEndPositions(measure) {
    for (let voice of measure.voices) {
      let noteGroups = voice.noteGroups;
      let beamedNotes = [];
      let firstNote = null;
      let lastNote = null;
      let firstNoteRP = 0;
      let lastNoteRP = 0;
      let interiorHighestRP = 0;
      let interiorLowestRP = 0;
      let beamedNoteCount = 0;
      for (let ng of noteGroups) {
        if (ng.isRest || ng.stemDirection === StemDirection.NO_STEM) {
          continue;
        }
        let requestedStemPos = this.getRequestedStemEndPosition(ng);
        if (ng.beamStates.length === 0 || ng.beamStates[0] === BeamState.NONE) {
          ng.stemEndPos = 1 / 2 * requestedStemPos;
        } else if (ng.beamStates[0] === BeamState.BEGIN) {
          beamedNotes = [ng];
          beamedNoteCount = 1;
          firstNote = ng;
          firstNoteRP = requestedStemPos;
          interiorHighestRP = 1e3;
          interiorLowestRP = -1e3;
        } else if (ng.beamStates[0] === BeamState.CONTINUE) {
          if (requestedStemPos < interiorHighestRP) {
            interiorHighestRP = requestedStemPos;
          }
          if (requestedStemPos > interiorLowestRP) {
            interiorLowestRP = requestedStemPos;
          }
          beamedNotes.push(ng);
          beamedNoteCount++;
        } else if (ng.beamStates[0] === BeamState.END) {
          lastNoteRP = requestedStemPos;
          lastNote = ng;
          beamedNotes.push(ng);
          beamedNoteCount++;
          if (firstNote.stemDirection === StemDirection.UP) {
            if (firstNoteRP < interiorHighestRP || lastNoteRP < interiorHighestRP) {
              if (firstNoteRP <= lastNoteRP) {
                lastNoteRP = firstNoteRP + (lastNoteRP - firstNoteRP) / 4;
              } else {
                firstNoteRP = lastNoteRP + (firstNoteRP - lastNoteRP) / 4;
              }
            } else {
              firstNoteRP = interiorHighestRP;
              lastNoteRP = interiorHighestRP;
            }
          } else {
            if (firstNoteRP > interiorLowestRP || lastNoteRP > interiorLowestRP) {
              if (firstNoteRP >= lastNoteRP) {
                lastNoteRP = firstNoteRP - (firstNoteRP - lastNoteRP) / 4;
              } else {
                firstNoteRP = lastNoteRP - (lastNoteRP - firstNoteRP) / 4;
              }
            } else {
              firstNoteRP = interiorLowestRP;
              lastNoteRP = interiorLowestRP;
            }
          }
          firstNote.stemEndPos = 1 / 2 * firstNoteRP;
          lastNote.stemEndPos = 1 / 2 * lastNoteRP;
          let totalHDistance = lastNote.hPos - firstNote.hPos;
          let totalRise = lastNote.stemEndPos - firstNote.stemEndPos;
          let i = 1;
          while (i < beamedNoteCount - 1) {
            let cIntNote = beamedNotes[i];
            cIntNote.stemEndPos = firstNote.stemEndPos + totalRise * ((cIntNote.hPos - firstNote.hPos) / totalHDistance);
            i++;
          }
        }
      }
    }
  }
  getRequestedStemEndPosition(noteGroup) {
    let decidingNote;
    let bonusDurationLength;
    let penaltyLength;
    let request;
    let notes = noteGroup.visibleNotes;
    if (noteGroup.stemDirection === StemDirection.UP) {
      decidingNote = notes[notes.length - 1];
      bonusDurationLength = noteGroup.maxBeams < 2 ? 0 : noteGroup.maxBeams - 2;
      if (!noteGroup.isGrace) {
        penaltyLength = (noteGroup.maxBeams === 0 || noteGroup.beamStates.length > 0) && decidingNote.stepsFromTopStaffLine < 4 ? 1 : 0;
      } else {
        penaltyLength = 3;
      }
      request = decidingNote.stepsFromTopStaffLine - 7 - bonusDurationLength + penaltyLength;
      return request <= 4 ? request : 4;
    } else {
      decidingNote = notes[0];
      bonusDurationLength = noteGroup.maxBeams < 2 ? 0 : noteGroup.maxBeams - 2;
      if (!noteGroup.isGrace) {
        penaltyLength = (noteGroup.maxBeams === 0 || noteGroup.beamStates.length > 0) && decidingNote.stepsFromTopStaffLine > 4 ? 1 : 0;
      } else {
        penaltyLength = 3;
      }
      request = decidingNote.stepsFromTopStaffLine + 7 + bonusDurationLength - penaltyLength;
      return request >= 4 ? request : 4;
    }
  }
};

// src/formatting/spacing/MusicSpacer.ts
var _MusicSpacer = class _MusicSpacer {
  constructor(score) {
    this._score = score;
    this.init();
  }
  init() {
    this.initializeScoreSettings();
  }
  initializeScoreSettings() {
    let sProps = this._score.scoreProperties;
    this._qNoteWidth = sProps.qNoteWidth;
    this._keySigWidth = sProps.keySigWidth;
    this._timeSigWidth = sProps.timeSigWidth;
    this._clefWidth = sProps.clefWidth;
    this._clefDistanceFromBarline = sProps.clefDistanceFromBarline;
    this._measureLeadIn = sProps.measureLeadIn;
    this._minMeasureWidth = sProps.minMeasureWidth;
    this._noteheadWidth = sProps.noteheadWidth;
    this._standardSystemWidth = sProps.pageWidth - sProps.leftPageMargin - sProps.rightPageMargin - sProps.leftSystemMargin - sProps.rightSystemMargin;
  }
  /** computes the ideal hPos for every note and the ideal width of each 
   *  measure stack
   *  @param	measureStack the measureStack to compute - if null, all stacks 
   *  in the score are computed*/
  computeIdealSpacingValues(measureStack = null) {
    if (measureStack != null) {
      this.computeIdealSpacingForStack(measureStack);
    } else {
      let stacks = this._score.getMeasureStacks();
      for (let stack of stacks) {
        this.computeIdealSpacingForStack(stack);
      }
    }
  }
  /** groups the measures into systems
   *  @param	measuresPerSystem if greater than 0, this number of measures 
   *  will be enforced for all systems*/
  groupMeasuresIntoSystems(measuresPerSystem = 0) {
    let systems = this._score.getSystems();
    if (systems.length < 1) {
      return;
    }
    let originalStackGroupings = new Array();
    let firstSystem = systems[0];
    let firstSystemsStacks = systems[0].measureStacks;
    originalStackGroupings.push(firstSystemsStacks.slice());
    let cSystem = null;
    for (let i2 = 1; i2 < systems.length; i2++) {
      cSystem = systems[i2];
      let stacks = cSystem.measureStacks;
      originalStackGroupings.push(stacks.slice());
      for (let stack of stacks) {
        stack.systemRef = firstSystem;
        firstSystemsStacks.push(stack);
      }
      cSystem.clearAllStacks();
    }
    let i = 0;
    while (i < systems.length) {
      cSystem = systems[i];
      let totalWidth = 0;
      for (let j = 0; j < cSystem.measureStacks.length; j++) {
        let cStack = cSystem.measureStacks[j];
        if (j === 0) {
          cStack.newSystem = true;
          for (let measure of cStack.measures) {
            measure.showKey = true;
            measure.clefs[0].show = true;
            measure.clefs[0].smallSize = false;
          }
          if (!cStack.newKey) {
            totalWidth += cStack.maxKeySize * this._keySigWidth;
          }
          if (!cStack.newClef) {
            totalWidth += this._clefWidth;
          }
        } else {
          cStack.newSystem = false;
          for (let measure of cStack.measures) {
            measure.clefs[0].show = measure.clefs[0].isNew;
            measure.clefs[0].smallSize = true;
            measure.showKey = cStack.newKey;
          }
        }
        totalWidth += cStack.idealWidth;
        if (measuresPerSystem > 0) {
          if (j >= measuresPerSystem) {
            if (cSystem.next == null) {
              systems.push(this.addSystem(cSystem));
            }
            cSystem.next.insertMeasureStacks(cSystem.removeStacksFromEnd(
              cSystem.measureStacks.length - j
            ));
            break;
          }
        } else if (j > 0 && totalWidth > (this._standardSystemWidth - cSystem.indent) * 0.95) {
          if (cSystem.next == null) {
            systems.push(this.addSystem(cSystem));
          }
          cSystem.next.insertMeasureStacks(cSystem.removeStacksFromEnd(cSystem.measureStacks.length - j));
          break;
        }
        if (j === cSystem.measureStacks.length - 1) {
          if (cSystem.next != null) {
            cSystem.addMeasureStacks(cSystem.next.removeStacksFromBeginning(1));
          }
        }
      }
      i++;
    }
    i--;
    while (cSystem.measureStacks.length === 0) {
      let cPage = cSystem.pageRef;
      cPage.removeSystem(cSystem);
      systems.pop();
      if (cPage.systems.length === 0) {
        this._score.removePage(cPage);
      }
      cSystem = cSystem.previous;
    }
    let changeMade = measuresPerSystem === 0;
    while (changeMade) {
      changeMade = false;
      cSystem = systems[systems.length - 1];
      while (cSystem.previous != null) {
        let prevSystem = cSystem.previous;
        let qStack = prevSystem.measureStacks[prevSystem.measureStacks.length - 1];
        while (prevSystem.requestedStackWidth - qStack.idealWidth > 0.95 * cSystem.requestedStackWidth && prevSystem.measureStacks.length > 1) {
          let leadStack = cSystem.measureStacks[0];
          leadStack.newSystem = false;
          for (let measure of leadStack.measures) {
            measure.clefs[0].show = measure.clefs[0].isNew;
            measure.clefs[0].smallSize = true;
            measure.showKey = leadStack.newKey;
          }
          cSystem.insertStack(prevSystem.removeStacksFromEnd(1)[0]);
          leadStack = cSystem.measureStacks[0];
          leadStack.newSystem = true;
          for (let measure of leadStack.measures) {
            measure.clefs[0].show = true;
            measure.clefs[0].smallSize = false;
            measure.showKey = true;
          }
          changeMade = true;
        }
        cSystem = cSystem.previous;
      }
    }
    let numNewSystems = systems.length;
    let numOriginalSystems = originalStackGroupings.length;
    for (let i2 = 0; i2 < numNewSystems; i2++) {
      cSystem = systems[i2];
      if (i2 >= numOriginalSystems) {
        cSystem.needsRendering = true;
        continue;
      }
      let newSystemStacks = cSystem.measureStacks;
      let oldSystemStacks = originalStackGroupings[i2];
      if (oldSystemStacks.length === newSystemStacks.length) {
        let numStacks = oldSystemStacks.length;
        for (let j = 0; j < numStacks; j++) {
          if (oldSystemStacks[j] !== newSystemStacks[j]) {
            cSystem.needsRendering = true;
            break;
          }
        }
      } else {
        cSystem.needsRendering = true;
      }
    }
  }
  addSystem(currentSystem) {
    let newSystem = new System(this._score.scoreProperties);
    newSystem.previous = currentSystem;
    currentSystem.next = newSystem;
    currentSystem.pageRef.addSystem(newSystem);
    return newSystem;
  }
  /**
   * computes the actual position values for the measures and notes of the 
   * System in preparation for rendering
   * @param	system the system to calculate
   */
  computeActualSpacingValues(system) {
    let stacks = system.measureStacks;
    let numStacks = stacks.length;
    if (numStacks === 0) {
      return;
    }
    let sysWidth = this._standardSystemWidth - system.indent;
    let firstStack = stacks[0];
    let totalIndentSpace = firstStack.indentAsSystemLeader;
    let totalNoteIdealSpace = firstStack.idealWidth - firstStack.indent;
    for (let i = 1; i < numStacks; i++) {
      let stack = stacks[i];
      totalIndentSpace += stack.indent;
      totalNoteIdealSpace += stack.idealWidth - stack.indent;
    }
    let scale = (sysWidth - totalIndentSpace) / totalNoteIdealSpace;
    let hPos = 0;
    let stackIdealWidth;
    let stackIndent;
    for (let i = 0; i < numStacks; i++) {
      let stack = stacks[i];
      stack.startPosition = hPos;
      if (i === 0) {
        stackIdealWidth = stack.idealWidth - stack.indent + stack.indentAsSystemLeader;
        stackIndent = stack.indentAsSystemLeader;
      } else {
        stackIdealWidth = stack.idealWidth;
        stackIndent = stack.indent;
      }
      let newWidth = (stackIdealWidth - stackIndent) * scale + stackIndent;
      stack.width = newWidth;
      for (let ng of stack.noteGroups) {
        if (ng.visible === false) {
          ng.hPos = stackIndent;
          continue;
        }
        if (ng.isRest && ng.voice.noteGroups.length === 1) {
          ng.hPos = (newWidth + stackIndent - this._measureLeadIn) / 2;
        } else {
          ng.hPos = (ng.idealHPos + stackIndent - stackIndent) * scale + stackIndent;
        }
      }
      hPos += stack.width;
    }
  }
  /**
   * computes the ideal position of each note group and the ideal width of the
   * measure stack
   * @param	stack the MeasureStack to compute
   */
  computeIdealSpacingForStack(stack) {
    let cTime = stack.noteGroups.length > 0 ? stack.noteGroups[0].qNoteTime : stack.startTime;
    let cPosition = 0;
    let clefDFB = this._score.scoreProperties.clefDistanceFromBarline;
    stack.indent = this._measureLeadIn;
    if (stack.newClef) {
      stack.indent += this._clefWidth + clefDFB;
    }
    if (stack.newKey) {
      stack.indent += stack.maxKeySize * this._keySigWidth + clefDFB;
    }
    let showTime = stack.measures[0].showTime;
    if (showTime) {
      stack.indent += this._timeSigWidth + clefDFB;
    }
    stack.indentAsSystemLeader = this._measureLeadIn + this._clefWidth + clefDFB;
    if (stack.maxKeySize > 0) {
      stack.indentAsSystemLeader += stack.maxKeySize * this._keySigWidth + clefDFB;
    }
    if (showTime) {
      stack.indentAsSystemLeader += this._timeSigWidth + clefDFB;
    }
    let repeatSpaceAtEnd = 0;
    if (stack.repeatDOs != null) {
      for (let rdo of stack.repeatDOs) {
        if (rdo.repeatDirection === RepeatDirection.FORWARD) {
          stack.indent += this._measureLeadIn;
          stack.indentAsSystemLeader += this._measureLeadIn;
        } else if (rdo.repeatDirection === RepeatDirection.BACKWARD) {
          repeatSpaceAtEnd = this._measureLeadIn;
        }
      }
    }
    cPosition = 0;
    const NORMAL_MIN_WHITE_SPACE = this._noteheadWidth * 1.3;
    const LYRIC_LETTER_SPACE = 0.9 * this._noteheadWidth;
    let newTimePositionDelta = 0;
    let firstNoteTime = cTime;
    let notesAtCurrentPos = [];
    let largestLeftExtension = 0;
    let largestRightExtension = 0;
    let prevRightExtension = 0;
    let minWhiteSpace;
    let noteGroups = stack.noteGroups;
    let numNoteGroups = noteGroups.length;
    for (let i = 0; i < numNoteGroups; i++) {
      let ng = noteGroups[i];
      if (ng.visible === false) {
        continue;
      }
      let ngLeftExtension = ng.numAccidentalLevels * this._noteheadWidth;
      if (ngLeftExtension > 0 && ng.qNoteTime === stack.startTime) {
        ngLeftExtension -= this._measureLeadIn / 2;
      }
      let ngRightExtension = 0;
      if (ng.clef != null) {
        ngLeftExtension += this._clefWidth + this._clefDistanceFromBarline;
      }
      if (ng.lyric != null) {
        let halfLyricWidth = ng.lyric.text.length * LYRIC_LETTER_SPACE / 2 - this._noteheadWidth / 2;
        ngRightExtension += halfLyricWidth;
        if (ng.qNoteTime !== stack.startTime) {
          if (halfLyricWidth > ngLeftExtension) {
            ngLeftExtension = halfLyricWidth;
          }
        } else {
          if (halfLyricWidth - this._measureLeadIn > ngLeftExtension) {
            ngLeftExtension = halfLyricWidth - this._measureLeadIn;
          }
        }
      }
      if (!ng.isRest && (ng.beamStates.length === 0 || ng.beamStates[0] === BeamState.NONE) && ng.duration < 1024 && ngRightExtension < 0.5 * this._noteheadWidth && ng.stemDirection === StemDirection.UP) {
        ngRightExtension = 0.5 * this._noteheadWidth;
      }
      if (ng.qNoteTime > cTime) {
        if (notesAtCurrentPos.length > 0) {
          minWhiteSpace = cTime > firstNoteTime ? NORMAL_MIN_WHITE_SPACE : 0;
          if (newTimePositionDelta - largestLeftExtension - prevRightExtension < minWhiteSpace) {
            cPosition += minWhiteSpace - (newTimePositionDelta - largestLeftExtension - prevRightExtension);
            for (let previousNG of notesAtCurrentPos) {
              previousNG.idealHPos = cPosition;
            }
          }
          prevRightExtension = largestRightExtension;
        }
        notesAtCurrentPos = [ng];
        largestLeftExtension = ngLeftExtension;
        largestRightExtension = ngRightExtension;
        newTimePositionDelta = this._qNoteWidth * Math.pow(
          _MusicSpacer.SPACING_FACTOR,
          Math.log(ng.qNoteTime - cTime) / Math.log(2)
        );
        cPosition += newTimePositionDelta;
        cTime = ng.qNoteTime;
      } else {
        notesAtCurrentPos.push(ng);
        if (ngLeftExtension > largestLeftExtension) {
          largestLeftExtension = ngLeftExtension;
        }
        if (ngRightExtension > largestRightExtension) {
          largestRightExtension = ngRightExtension;
        }
      }
      ng.idealHPos = cPosition;
    }
    if (notesAtCurrentPos.length > 0) {
      minWhiteSpace = cTime > firstNoteTime ? NORMAL_MIN_WHITE_SPACE : 0;
      if (newTimePositionDelta - largestLeftExtension - prevRightExtension < minWhiteSpace) {
        cPosition += minWhiteSpace - (newTimePositionDelta - largestLeftExtension - prevRightExtension);
        for (let previousNG of notesAtCurrentPos) {
          previousNG.idealHPos = cPosition;
        }
      }
    }
    if (stack.endTime > cTime) {
      let endSpace = repeatSpaceAtEnd + this._qNoteWidth * Math.pow(
        _MusicSpacer.SPACING_FACTOR,
        Math.log(stack.endTime - cTime) / Math.log(2)
      );
      if (endSpace < largestRightExtension) {
        endSpace = largestRightExtension;
      }
      stack.idealWidth = stack.indent + cPosition + endSpace;
    } else {
      stack.idealWidth = cPosition + stack.indent > this._minMeasureWidth ? cPosition + stack.indent : this._minMeasureWidth;
      console.log("probably shouldn't get here... MusicSpacer.computeIdealSpacingForStack()");
    }
  }
};
/**the ratio of the horizontal spacing requirement of one note to another 
 * note of half the first note's duration (a quarter gets X times as much 
 * space as an eighth note)*/
_MusicSpacer.SPACING_FACTOR = 1.4179;
var MusicSpacer = _MusicSpacer;

// src/formatting/ScoreFormatter.ts
var ScoreFormatter = class {
  constructor(score = null) {
    this._score = score;
    if (score != null) {
      this.init();
    }
  }
  init() {
    this._musicSpacer = new MusicSpacer(this._score);
    this._beamer = new MusicBeamer();
    this._ngFormatter = new NoteGroupFormatter(this._score.scoreProperties);
    this._attachmentFormatter = new AttachmentFormatter(this._score);
    this._parts = this._score.parts;
    if (this._score.graceNotesNeedFixing) {
      this.correctGraceNoteStartTimes();
      this._score.graceNotesNeedFixing = false;
    }
    this.setPartList(null);
  }
  /** Sets the parts that will be worked with for formatting. If null, all 
   *  parts in the score will be used*/
  setPartList(parts = null) {
    this._parts = parts != null && parts.length > 0 ? parts : this._score.parts;
    this._parts.forEach((p) => p.visible = this._parts.indexOf(p) !== -1);
    this.updateMeasureStackNoteGroupsAndMeasures();
  }
  /** performs spacing and positioning algorithms on entire Score. This method
   *  is "intelligent," only updating the measures that need it.
   *  [reflowMeasuresAcrossSystems] if true, the engine will calculate how 
   *  many measures should fit on each system
   *  [measuresPerSystem] if greater than 0, this many measures will be 
   *  enforced per system (reflowMeasuresAcrossSystems must also be true)*/
  formatScore2(reflowMeasuresAcrossSystems, measuresPerSystem = 0) {
    this._musicSpacer.initializeScoreSettings();
    this._processUpdateRequests();
    if (reflowMeasuresAcrossSystems) {
      this._musicSpacer.groupMeasuresIntoSystems(measuresPerSystem);
    }
    this._getActualSpacingAndPositioning();
  }
  _processUpdateRequests() {
    let systems = this._score.getSystems();
    for (let system of systems) {
      if (system.needsRendering) {
        let stacks = system.measureStacks;
        for (let stack of stacks) {
          if (stack.needsRendering) {
            let measures = stack.measures;
            for (let measure of measures) {
              if (measure.notesNeedRendering) {
                this._beamer.getMeasureBeamGroupings(measure);
                this._ngFormatter.getMeasureStemDirections(
                  measure
                );
                this._ngFormatter.getMeasureNoteheadOffsets(
                  measure
                );
              }
            }
            this._musicSpacer.computeIdealSpacingValues(stack);
          }
        }
      }
    }
  }
  _getActualSpacingAndPositioning() {
    let systems = this._score.getSystems();
    for (let system of systems) {
      if (system.needsRendering) {
        this._musicSpacer.computeActualSpacingValues(system);
        this._ngFormatter.getSystemStemEndPositions(system);
        this._attachmentFormatter.formatAttachments(system);
      }
    }
    this._attachmentFormatter.formatLines();
  }
  /** adjusts the start times of grace notes so that they aren't identical to
   *  the notes they precede*/
  correctGraceNoteStartTimes() {
    for (let part of this._parts) {
      for (let staff of part.staves) {
        for (let measure of staff.measures) {
          for (let voice of measure.voices) {
            let noteGroups = voice.noteGroups;
            let consecutiveGraceNotes = 0;
            let i = noteGroups.length - 1;
            let attachmentNG;
            while (i >= 0) {
              let ng = noteGroups[i];
              if (ng.isGrace) {
                consecutiveGraceNotes++;
                ng.qNoteTime -= consecutiveGraceNotes * 0.1;
                ng.attachmentNG = attachmentNG;
              } else {
                consecutiveGraceNotes = 0;
                attachmentNG = ng;
              }
              i--;
            }
          }
        }
      }
    }
  }
  updateMeasureStackNoteGroupsAndMeasures() {
    let stacks = this._score.getMeasureStacks();
    let numMeasures = stacks.length;
    for (let stack2 of stacks) {
      stack2.clearNoteGroups();
      stack2.clearMeasures();
      stack2.maxKeySize = 0;
    }
    let stack;
    for (let part of this._parts) {
      for (let staff of part.staves) {
        if (!staff.visible)
          continue;
        let measures = staff.measures;
        for (let i = 0; i < numMeasures; i++) {
          stack = stacks[i];
          let measure = measures[i];
          stack.addNoteGroupsFromMeasure(measure);
          stack.addMeasure(measure);
          let totalKeySize = PitchUtils.getTotalKeySize(
            measure.displayKey,
            measure.outgoingKey
          );
          if (totalKeySize > stack.maxKeySize) {
            stack.maxKeySize = totalKeySize;
          }
        }
      }
    }
  }
  /** performs spacing and positioning algorithms on entire Score
   *  [reflowMeasuresAcrossSystems] if true, the engine will calculate how 
   *  many measures should fit on each system
   *  [measuresPerSystem] if greater than 0, this many measures will be 
   *  enforced per system (reflowMeasuresAcrossSystems must also be true)*/
  formatScore(reflowMeasuresAcrossSystems, measuresPerSystem = 0) {
    if (reflowMeasuresAcrossSystems) {
      this._musicSpacer.groupMeasuresIntoSystems(measuresPerSystem);
    }
    let systems = this._score.getSystems();
    for (let system of systems) {
      this.formatSystem(system);
    }
  }
  /** performs spacing and positioning algorithms on one System*/
  formatSystem(system) {
    this._beamer.getSystemBeamGroupings(system);
    this._ngFormatter.getSystemStemDirections(system);
    this._ngFormatter.getSystemNoteheadOffsets(system);
    this._musicSpacer.computeActualSpacingValues(system);
    this._ngFormatter.getSystemStemEndPositions(system);
  }
  /** gets and sets the Score object - running initialization tasks when set*/
  get score() {
    return this._score;
  }
  set score(value) {
    this._score = value;
    this.init();
  }
};

// src/musicXML/MusicXmlParser2.ts
var MusicXmlParser2 = class {
  constructor() {
    this._getMusicXMLObject = () => {
      try {
        let parser = new DOMParser();
        this._musicXML = parser.parseFromString(
          this._musicXMLString,
          "application/xml"
        ).querySelector("score-partwise");
        this._musicXMLString = null;
      } catch (e) {
        this._rejectCompleter(e);
        return;
      }
      setTimeout(this._parseXML);
    };
    this._parseXML = () => {
      try {
        this._lastPauseMsTime = Date.now();
        this._midiInstrumentsXMLList = null;
        this._measureStacks = null;
        this._currentTuplets = null;
        this._currentSlurs = null;
        this._currentPage = null;
        this._currentSystem = null;
        this._currentStack = null;
        this._score = new Score();
        let defaultsEl = this._musicXML.querySelector("defaults");
        this._score.scoreProperties = this.getScoreProperties(defaultsEl);
        let rightsEl = this._musicXML.querySelector(
          "identification > rights"
        );
        this._score.scoreProperties.rights = rightsEl != null ? rightsEl.textContent : null;
        if (this._overrideScoreProps) {
          this._score.scoreProperties = this._requestedScoreProps != null ? this._requestedScoreProps : ScoreProperties.getNewScoreProperties();
        }
        let softwareEl = this._musicXML.querySelector(
          "identification > encoding > software"
        );
        if (softwareEl != null) {
          this._score.scoreProperties.software = softwareEl.textContent;
        }
        this._score.graceNotesNeedFixing = true;
        this._getMusicData();
      } catch (e) {
        this._rejectCompleter(e);
      }
    };
    this._processNextMeasure = () => {
      try {
        if (this._measureIndex >= this._measureXMLList.length) {
          this._checkPartForErrors(this._cPart);
          this._processNextPart();
          return false;
        }
        let measureXML = this._measureXMLList[this._measureIndex];
        for (let staff of this._cPart.staves) {
          let prevMeasure = staff.measures.length > 0 ? staff.measures[staff.measures.length - 1] : null;
          this._measure = this._createNewMeasure(
            measureXML,
            prevMeasure,
            this._measureIndex
          );
          if (this._score.parts.length === 1) {
            this._measure.stack.startTime = this._passedTimeDPQ / this._cPartDPQ;
            this._measure.stack.endTime = this._measure.stack.startTime + this._measure.numBeats * (4 / this._measure.beatType);
          }
          staff.addMeasure(this._measure);
        }
        let cVoice = null;
        let cNoteGroup = null;
        let midMeasureClef = null;
        let dpqPositionBeforeBackup = 0;
        let staffIndex = 0;
        for (let node of measureXML.childNodes) {
          if (!(node instanceof Element)) {
            continue;
          }
          let element = node;
          let elemName = element.tagName.toString();
          let noteXML;
          if (elemName === "note") {
            noteXML = element;
          } else if (elemName === "backup") {
            dpqPositionBeforeBackup = this._passedTimeDPQ;
            this._passedTimeDPQ -= this._eint(element, "duration");
            continue;
          } else if (elemName === "forward") {
            this._passedTimeDPQ += this._eint(element, "duration");
            continue;
          } else if (this._estr(element, "clef") !== "") {
            for (let clefXML of element.querySelectorAll("clef")) {
              let clef = new Clef();
              let line = this._eint(clefXML, "line");
              clef.type = ClefType.getClefType(
                this._estr(clefXML, "sign"),
                line
              );
              clef.isNew = true;
              clef.show = true;
              staffIndex = clefXML.getAttribute("number") != null ? Number.parseInt(
                clefXML.getAttribute("number")
              ) - 1 : 0;
              let staffMsrs = this._cPart.staves[staffIndex].measures;
              let targetMeasure = staffMsrs[staffMsrs.length - 1];
              let noteHasBeenAdded = false;
              for (let voice of targetMeasure.voices) {
                if (voice.noteGroups.length > 0) {
                  noteHasBeenAdded = true;
                  break;
                }
              }
              if (noteHasBeenAdded) {
                targetMeasure.clefs.push(clef);
                midMeasureClef = clef;
                clef.qNoteTime = this._passedTimeDPQ / this._cPartDPQ;
              } else {
                clef.qNoteTime = targetMeasure.stack.startTime;
                if (targetMeasure.clefs.length === 0) {
                  targetMeasure.clefs.push(clef);
                } else {
                  targetMeasure.clefs[0] = clef;
                }
                targetMeasure.stack.newClef = true;
                if (targetMeasure.stack.newSystem) {
                  clef.show = true;
                  clef.smallSize = false;
                }
              }
            }
            continue;
          } else if (elemName === "sound") {
            if (element.getAttribute("tempo") != null) {
              let staffMsrs = this._cPart.staves[staffIndex].measures;
              let targetStack = staffMsrs[staffMsrs.length - 1].stack;
              this._createTempoMarker(
                element,
                this._passedTimeDPQ / this._cPartDPQ,
                targetStack,
                null
              );
            }
            continue;
          } else if (elemName === "direction") {
            if (element.querySelectorAll(
              "direction-type dynamics"
            ).length > 0) {
              staffIndex = this._estr(element, "staff") !== "" ? this._eint(element, "staff") - 1 : 0;
              let staffMsrs = this._cPart.staves[staffIndex].measures;
              let targetMeasure = staffMsrs[staffMsrs.length - 1];
              let dyn = new Dynamic();
              dyn.type = element.querySelector("direction-type dynamics *").tagName;
              dyn.isAbove = element.getAttribute("placement") === "above";
              dyn.baseline = dyn.isAbove ? -2 : 6;
              dyn.qNoteTime = (this._eint(element, "offset") + this._passedTimeDPQ) / this._cPartDPQ;
              dyn.volume = DynamicType.getVolumeByType(dyn.type);
              dyn.measure = targetMeasure;
              dyn.width = this._score.scoreProperties.noteheadWidth * dyn.type.length;
              dyn.height = 2;
              if (targetMeasure.attachments != null) {
                targetMeasure.attachments.push(dyn);
              } else {
                targetMeasure.attachments = [dyn];
              }
            } else if (element.querySelector("sound[tempo]") != null) {
              let staffMsrs = this._cPart.staves[staffIndex].measures;
              let targetStack = staffMsrs[staffMsrs.length - 1].stack;
              let metronomeEl = element.querySelector(
                "direction-type > metronome"
              );
              this._createTempoMarker(
                element.querySelector("sound"),
                this._passedTimeDPQ / this._cPartDPQ,
                targetStack,
                metronomeEl
              );
            } else if (element.querySelectorAll(
              "direction-type words"
            ).length > 0) {
              let words = element.querySelector(
                "direction-type words"
              ).textContent;
              if (words[0] === "{" && words[words.length - 1] === "}") {
                staffIndex = this._estr(element, "staff") !== "" ? this._eint(element, "staff") - 1 : 0;
                let staffMsrs = this._cPart.staves[staffIndex].measures;
                let targetMeasure = staffMsrs[staffMsrs.length - 1];
                let instruction = new MeasureInstruction();
                instruction.text = words;
                instruction.qNoteTime = (this._eint(element, "offset") + this._passedTimeDPQ) / this._cPartDPQ;
                if (targetMeasure.attachments != null) {
                  targetMeasure.attachments.push(instruction);
                } else {
                  targetMeasure.attachments = [instruction];
                }
              }
            }
            continue;
          } else if (elemName === "barline" && this._cPart === this._score.parts[0]) {
            let staffMsrs = this._cPart.staves[staffIndex].measures;
            let targetStack = staffMsrs[staffMsrs.length - 1].stack;
            if (targetStack.barline == null) {
              let barline = new Barline();
              barline.location = BarlineLocation.getLocation(
                element.getAttribute("location")
              );
              let styleEl = element.querySelector("bar-style");
              barline.style = styleEl != null ? BarlineStyle.getStyle(styleEl.textContent) : BarlineStyle.getStyle(null);
              targetStack.barline = barline;
            }
            let repeatEl = element.querySelector("repeat");
            let endingEl = element.querySelector("ending");
            if ((repeatEl != null || endingEl != null) && targetStack.repeatDOs == null) {
              targetStack.repeatDOs = [];
            }
            if (repeatEl != null) {
              let repeatDO = new RepeatDO();
              repeatDO.repeatDirection = RepeatDirection.getDirection(
                repeatEl.getAttribute("direction")
              );
              let repeatTimesStr = repeatEl.getAttribute("times");
              repeatDO.repeatTimes = repeatTimesStr != null ? Number.parseInt(repeatTimesStr) - 1 : 1;
              targetStack.repeatDOs.push(repeatDO);
            }
            if (endingEl != null) {
              let repeatDO = new RepeatDO();
              repeatDO.endingNumber = endingEl.getAttribute("number");
              repeatDO.endingType = EndingType.getType(endingEl.getAttribute("type"));
              targetStack.repeatDOs.push(repeatDO);
            }
            continue;
          } else if (elemName === "harmony") {
            let staffMsrs = this._cPart.staves[staffIndex].measures;
            let cMeasure2 = staffMsrs[staffMsrs.length - 1];
            this._processHarmonyEl(element, cMeasure2);
            continue;
          } else {
            continue;
          }
          let staffNum = noteXML.querySelector("staff") == null ? 1 : this._eint(noteXML, "staff");
          let cStaff = this._cPart.staves[staffNum - 1];
          let cMeasure = cStaff.measures[cStaff.measures.length - 1];
          let voiceNum = this._eint(noteXML, "voice");
          cVoice = cMeasure.getVoiceByNumber(voiceNum);
          if (noteXML.querySelector("chord") == null) {
            cNoteGroup = this._createNoteGroup(noteXML, cMeasure);
            cVoice.addNoteGroup(cNoteGroup);
            cNoteGroup.qNoteTime = this._passedTimeDPQ / this._cPartDPQ;
            cNoteGroup.qNoteDuration = cNoteGroup.duration / this._cPartDPQ;
            this._passedTimeDPQ += cNoteGroup.duration;
            cNoteGroup.duration = DurationType.getDurationValue(
              cNoteGroup.durationType,
              cNoteGroup.numDots
            );
            if (midMeasureClef != null && midMeasureClef.qNoteTime <= cNoteGroup.qNoteTime) {
              cNoteGroup.clef = midMeasureClef;
              midMeasureClef = null;
            }
          }
          if (!cNoteGroup.isRest) {
            let cNote = this._createNote(noteXML, cNoteGroup, cMeasure);
            cNoteGroup.insertNote(cNote);
            if (cNoteGroup.notes.length === 1) {
              cNoteGroup.stemStartPos = cNote.vPos;
            } else {
              if (cNote.hPos !== 0) {
                cNoteGroup.stemHPos = this._score.scoreProperties.noteheadWidth;
              }
              cNoteGroup.stemStartPos = cNoteGroup.stemDirection === StemDirection.UP ? cNoteGroup.notes[0].vPos : cNoteGroup.notes[cNoteGroup.notes.length - 1].vPos;
            }
          }
        }
        if (!this._measure.stack.isPickup) {
          this._passedTimeDPQ = Math.round(this._measure.stack.endTime * this._cPartDPQ);
        } else if (this._passedTimeDPQ < dpqPositionBeforeBackup) {
          this._passedTimeDPQ = Math.round(dpqPositionBeforeBackup);
        }
        if (this._score.parts.length === 1) {
          this._measure.stack.endTime = this._passedTimeDPQ / this._cPartDPQ;
        }
        for (let staff of this._cPart.staves) {
          this._updateMeasureBeatGroups(
            staff.measures[this._measureIndex]
          );
        }
        this._measureIndex++;
        return true;
      } catch (e) {
        console.log(`parse fail on measureIndex: ${this._measureIndex}`);
        this._rejectCompleter(e);
        return;
      }
    };
  }
  //////////////////////////XML UTILITY METHODS////////////////////////
  /**get element's text as number*/
  _enum(el, selector, zeroIfNull = true) {
    let e = el.querySelector(selector);
    if (e == null) {
      return zeroIfNull ? 0 : null;
    } else {
      return e.textContent !== "" ? Number.parseFloat(e.textContent) : 0;
    }
  }
  /**get element's text as an integer*/
  _eint(el, selector, zeroIfNull = true) {
    let e = el.querySelector(selector);
    if (e == null) {
      return zeroIfNull ? 0 : null;
    } else {
      return e.textContent !== "" ? Number.parseInt(e.textContent) : 0;
    }
  }
  /**get element's text as string*/
  _estr(el, selector, emptyStringIfNull = true) {
    let e = el.querySelector(selector);
    if (e == null) {
      return emptyStringIfNull ? "" : null;
    } else {
      return e.textContent;
    }
  }
  /**get the attribute text as an integer*/
  _aint(el, attribute, zeroIfNull = true) {
    let val = el.getAttribute(attribute);
    if (val == null) {
      return zeroIfNull ? 0 : null;
    } else {
      return val !== "" ? Number.parseInt(val) : 0;
    }
  }
  /**get the attribute text as a number*/
  _anum(el, attribute, zeroIfNull = true) {
    let val = el.getAttribute(attribute);
    if (val == null) {
      return zeroIfNull ? 0 : null;
    } else {
      return val !== "" ? Number.parseFloat(val) : 0;
    }
  }
  /**returns the numeric equivalent of a string, returning 0 if the string is 
   *null or ""*/
  _s2n(str) {
    return str !== "" && str != null ? Number.parseFloat(str) : 0;
  }
  /////////////////////////////////////////////////////////////////////
  /**
   * parses the passed in Music XML file into a nEngine.data.containers.Score 
   * object Dispatches a FINISHED_PARSING event when done - access score via 
   * score property
   * @param	musicXML an XML object in the Music XML format
   * @param 	computeMaxKeySizeForRendering if true, max key sizes will be 
   * computed for each measure stack - 
   * 		leave false if you plan on using the formatter (which does this 
   * anyway)
   * @param	overrideScoreProps if true, when a score is loaded, default 
   * score properties are set before parsing instead of the settings from the
   * XML
   */
  parseXML(musicXMLString, computeMaxKeySizeForRendering = true, overrideScoreProps = false, scoreProps = null) {
    return new Promise((resolve, reject) => {
      this._resolveCompleter = resolve;
      this._rejectCompleter = reject;
      this._musicXMLString = musicXMLString;
      this._computeMaxKeySizeForRendering = computeMaxKeySizeForRendering;
      this._overrideScoreProps = overrideScoreProps;
      this._requestedScoreProps = scoreProps;
      setTimeout(this._getMusicXMLObject);
    });
  }
  getScoreProperties(defEl) {
    if (defEl == null) {
      return ScoreProperties.getNewScoreProperties();
    }
    let sProps = ScoreProperties.getNewScoreProperties();
    let mm = this._enum(defEl, "scaling millimeters");
    let tenths = this._enum(defEl, "scaling tenths");
    const pixelsPerMillimeter = 4;
    sProps.pixelsPerSpace = mm / tenths * pixelsPerMillimeter * 10;
    sProps.pageHeight = this._enum(defEl, "page-layout page-height") / 10;
    sProps.pageWidth = this._enum(defEl, "page-layout page-width") / 10;
    sProps.leftPageMargin = this._enum(
      defEl,
      "page-layout page-margins left-margin"
    ) / 10;
    sProps.rightPageMargin = this._enum(
      defEl,
      "page-layout page-margins right-margin"
    ) / 10;
    sProps.topPageMargin = this._enum(
      defEl,
      "page-layout page-margins top-margin"
    ) / 10;
    sProps.bottomPageMargin = this._enum(
      defEl,
      "page-layout page-margins bottom-margin"
    ) / 10;
    sProps.systemSpacing = this._enum(
      defEl,
      "system-layout system-distance"
    ) / 10;
    sProps.topSystemDistance = this._enum(
      defEl,
      "system-layout top-system-distance"
    ) / 10;
    sProps.leftSystemMargin = this._enum(
      defEl,
      "system-layout system-margins left-margin"
    ) / 10;
    sProps.rightSystemMargin = this._enum(
      defEl,
      "system-layout system-margins right-margin"
    ) / 10;
    sProps.staffSpacing = this._enum(
      defEl,
      "staff-layout staff-distance"
    ) / 10;
    if (sProps.staffSpacing === 0) {
      sProps.staffSpacing = 8;
    }
    defEl.querySelectorAll("appearance line-width").forEach((el) => {
      switch (el.getAttribute("type")) {
        case "stem":
          sProps.stemWidth = this._s2n(el.textContent) / 10;
          break;
        case "beam":
          sProps.beamWidth = this._s2n(el.textContent) / 10;
          break;
        case "staff":
          sProps.staffLineWidth = this._s2n(el.textContent) / 10;
          break;
        case "light barline":
          sProps.lightBarlineWidth = this._s2n(el.textContent) / 10;
          break;
        case "heavy barline":
          sProps.heavyBarlineWidth = this._s2n(el.textContent) / 10;
          break;
        case "leger":
          sProps.legerLineWidth = this._s2n(el.textContent) / 10;
          break;
        case "ending":
          sProps.repeatEndingWidth = this._s2n(el.textContent) / 10;
          break;
        case "wedge":
          sProps.hairpinLineWidth = this._s2n(el.textContent) / 10;
          break;
        case "enclosure":
          sProps.enclosureWidth = this._s2n(el.textContent) / 10;
          break;
        case "tuplet bracket":
          sProps.tupletBracketWidth = this._s2n(el.textContent) / 10;
          break;
      }
    });
    defEl.querySelectorAll("appearance note-size").forEach((el) => {
      switch (el.getAttribute("type")) {
        case "grace":
          sProps.graceNoteSize = this._s2n(el.textContent);
          break;
        case "cue":
          sProps.cueNoteSize = this._s2n(el.textContent);
          break;
      }
    });
    return sProps;
  }
  _getMusicData() {
    this._buildInstrumentsXMLList();
    this._measureStacks = [];
    this._currentTuplets = [];
    this._currentSlurs = [];
    this._calculatePartVolumes();
    this._cPartIndex = 0;
    this._partXMLList = this._musicXML.querySelectorAll(
      "score-partwise > part"
    );
    this._processNextPart();
  }
  _buildInstrumentsXMLList() {
    this._midiInstrumentsXMLList = this._musicXML.querySelectorAll(
      "part-list score-part midi-instrument"
    );
  }
  _calculatePartVolumes() {
    this._partVol = Math.min(1 / this._musicXML.querySelectorAll(
      "score-partwise > part"
    ).length, 0.15);
  }
  _processNextPart() {
    if (this._cPartIndex >= this._partXMLList.length) {
      if (this._computeMaxKeySizeForRendering) {
        this._computeMaxKeySizes();
      }
      this._adjustMeasureLengthsToFitNotes();
      this._checkScoreForErrors();
      this._resolveCompleter(this._score);
      return;
    }
    this._partXML = this._partXMLList[this._cPartIndex];
    this._cPartIndex++;
    this._removeIncompleteSlurs();
    this._cPart = this._createPartObj();
    this._cPartDPQ = this._eint(
      this._partXML,
      "measure attributes divisions"
    );
    this._passedTimeDPQ = 0;
    if (this._createStavesForPart() === false) {
      this._processNextPart();
      return;
    }
    this._measureIndex = 0;
    this._measureXMLList = this._partXML.querySelectorAll("measure");
    this._measure = null;
    let moreMeasures = true;
    while (moreMeasures) {
      moreMeasures = this._processNextMeasure();
    }
  }
  _runTimeout() {
    return new Promise((resolve, reject) => {
      setTimeout(resolve);
    });
  }
  _removeIncompleteSlurs() {
    for (let slur of this._currentSlurs) {
      let index = slur.firstNote.slurs.indexOf(slur);
      slur.firstNote.slurs.splice(index, 1);
      if (slur.firstNote.slurs.length === 0) {
        slur.firstNote.slurs = null;
      }
    }
    this._currentSlurs = [];
  }
  _createPartObj() {
    let staffName = "";
    let abbrName = "";
    let midiPreset = 0;
    let midiChannel = 1;
    Array.from(this._musicXML.querySelectorAll("part-list score-part")).filter((el) => el.getAttribute("id") === this._partXML.getAttribute("id")).forEach((scorePartEl) => {
      staffName = this._estr(scorePartEl, "part-name");
      abbrName = this._estr(scorePartEl, "part-abbreviation");
      midiPreset = this._eint(
        scorePartEl,
        "midi-instrument midi-program"
      );
      let channel = this._eint(
        scorePartEl,
        "midi-instrument midi-channel"
      );
      if (channel > 0) {
        midiChannel = channel;
      }
    });
    let partId = `P${this._score.parts.length + 1}`;
    let cPart = new Part(partId, staffName, abbrName);
    cPart.volume = this._partVol;
    cPart.midiChannel = midiChannel;
    cPart.midiPreset = midiPreset - 1;
    if (cPart.midiPreset < 0)
      cPart.midiPreset = 0;
    this._score.addPart(cPart);
    return cPart;
  }
  _createStavesForPart() {
    let measuresXML = this._partXML.querySelectorAll("part > measure");
    if (measuresXML.length > 0) {
      let measureXML = measuresXML[0];
      let numStaves = this._eint(measureXML, "attributes staves");
      if (numStaves < 1) {
        numStaves = 1;
      }
      for (let i = 0; i < numStaves; i++) {
        this._cPart.addStaff(new Staff());
      }
      if (this._estr(measureXML, "attributes transpose") !== "") {
        let transXml = measureXML.querySelector("attributes transpose");
        let diatonicTrans = -1 * this._eint(transXml, "diatonic");
        let chromaticTrans = -1 * this._eint(transXml, "chromatic");
        if (transXml.querySelector("octave-change") != null) {
          let numOctaves = this._eint(transXml, "octave-change");
          diatonicTrans += -7 * numOctaves;
          chromaticTrans += -12 * numOctaves;
        }
        this._cPart.setTransposition(
          diatonicTrans,
          chromaticTrans,
          false
        );
      }
      return true;
    } else {
      return false;
    }
  }
  _processHarmonyEl(el, measure) {
    let rootEl = el.querySelector("root");
    if (rootEl == null) {
      return;
    }
    let pitchName = rootEl.querySelector("root-step")?.textContent;
    if (pitchName == null) {
      return;
    }
    let offset = this._eint(el, "offset");
    let symbol = new ChordSymbol();
    symbol.qNoteTime = (this._passedTimeDPQ + offset) / this._cPartDPQ;
    symbol.isAbove = true;
    symbol.rootName = pitchName;
    symbol.rootAlteration = this._eint(rootEl, "root-alter");
    let bassEl = el.querySelector("bass");
    if (bassEl != null) {
      symbol.bassName = bassEl.querySelector("bass-step")?.textContent;
      symbol.bassAlteration = this._eint(bassEl, "bass-alter");
    }
    let kindEl = el.querySelector("kind");
    if (kindEl != null) {
      symbol.type = kindEl.textContent;
      symbol.displayType = kindEl.getAttribute("text") ?? "";
    }
    let degreeEl = el.querySelector("degree");
    if (degreeEl != null) {
      let degree = new ChordSymbolDegree();
      degree.value = this._eint(degreeEl, "degree-value");
      degree.alteration = this._eint(degreeEl, "degree-alter");
      degree.type = degreeEl.querySelector("degree-type")?.textContent;
      degree.displayText = degreeEl.querySelector("degree-type")?.getAttribute("text");
      symbol.degrees.push(degree);
    }
    symbol.baseline = -2;
    symbol.calculateHeight(this._score.scoreProperties.chordSymbolFontHeight);
    measure.attachments.push(symbol);
  }
  _createNewMeasure(measureXML, previousMeasure, measureIndex) {
    let measure = new Measure();
    let reParentedMidMeasureClef = false;
    if (previousMeasure != null) {
      measure.diatonicTransposition = previousMeasure.diatonicTransposition;
      measure.chromaticTransposition = previousMeasure.chromaticTransposition;
      measure.concertKey = previousMeasure.concertKey;
      measure.displayKey = previousMeasure.displayKey;
      measure.isMajorKey = previousMeasure.isMajorKey;
      measure.numBeats = previousMeasure.numBeats;
      measure.beatType = previousMeasure.beatType;
      measure.beatGroups = previousMeasure.beatGroups.slice(0);
      measure.subBeatGroups = previousMeasure.subBeatGroups.slice(0);
      let prevClef = previousMeasure.clefs[previousMeasure.clefs.length - 1];
      if (prevClef.qNoteTime === previousMeasure.stack.endTime) {
        measure.clefs.push(previousMeasure.clefs.splice(
          previousMeasure.clefs.length - 1,
          1
        )[0]);
        prevClef.qNoteTime = previousMeasure.stack.endTime;
        prevClef.isNew = true;
        reParentedMidMeasureClef = true;
      } else {
        let clef = new Clef();
        clef.type = prevClef.type;
        clef.qNoteTime = previousMeasure.stack.endTime;
        if (measure.clefs.length === 0) {
          measure.clefs.push(clef);
        } else {
          measure.clefs[0] = clef;
        }
      }
    } else {
      let clef = new Clef();
      clef.type = ClefType.TREBLE;
      clef.qNoteTime = 0;
      clef.show = true;
      clef.smallSize = false;
      measure.clefs.push(clef);
    }
    let stack = null;
    if (this._measureStacks.length > measureIndex) {
      stack = this._measureStacks[measureIndex];
    } else {
      stack = new MeasureStack();
      try {
        stack.number = Number.parseInt(
          measureXML.getAttribute("number")
        );
      } catch (e) {
        if (this._measureStacks.length > 1 && this._measureStacks[0].isPickup || measureXML.getAttribute("implicit") === "yes") {
          stack.number = measureIndex;
        } else {
          stack.number = measureIndex + 1;
        }
      }
      if (measureXML.getAttribute("implicit") === "yes" && stack.number === 0) {
        stack.isPickup = true;
      }
      stack.startPosition = previousMeasure != null ? previousMeasure.stack.width + previousMeasure.stack.startPosition : 0;
      stack.width = measureXML.getAttribute("width") != null ? Number.parseFloat(measureXML.getAttribute("width")) : 0;
      if (this._currentStack != null) {
        this._currentStack.next = stack;
        stack.previous = this._currentStack;
      }
    }
    this._currentStack = stack;
    measure.stack = stack;
    if (reParentedMidMeasureClef) {
      stack.newClef = true;
    }
    stack.addMeasure(measure);
    if (stack.systemRef != null) {
      this._currentSystem = stack.systemRef;
    }
    let page;
    let system;
    if (measureXML.querySelector("print") != null || this._measureStacks.length === 0) {
      if (this._estr(measureXML, "print page-number") !== "" || Array.from(measureXML.querySelectorAll("print")).filter(
        (e) => e.getAttribute("new-page") === "yes"
      ).length > 0 || measureIndex === 0) {
        if (stack.systemRef == null || !stack.systemRef.newPage) {
          page = new Page();
          system = new System(this._score.scoreProperties);
          page.addSystem(system);
          this._score.addPage(page);
          if (this._currentPage != null) {
            this._currentPage.next = page;
            this._currentSystem.next = system;
            page.previous = this._currentPage;
            system.previous = this._currentSystem;
          }
          this._currentPage = page;
          this._currentSystem = system;
          system.newPage = true;
          stack.newSystem = true;
          stack.startPosition = 0;
        }
      } else if (Array.from(measureXML.querySelectorAll("print")).filter(
        (e) => e.getAttribute("new-system") === "yes"
      ).length > 0) {
        if (!stack.newSystem) {
          system = new System(this._score.scoreProperties);
          this._currentPage.addSystem(system);
          if (this._currentSystem != null) {
            this._currentSystem.next = system;
            system.previous = this._currentSystem;
          }
          this._currentSystem = system;
          stack.newSystem = true;
          stack.startPosition = 0;
        }
      }
      if (!this._overrideScoreProps) {
        let val = this._enum(
          measureXML,
          "system-layout system-margins left-margin"
        );
        if (val !== 0) {
          this._currentSystem.indent = val / 10;
        }
      }
    }
    if (this._measureStacks.length <= measureIndex) {
      this._currentSystem.addMeasureStack(stack, false);
      this._measureStacks.push(stack);
    }
    if (stack.newSystem) {
      if (measure.clefs.length > 0) {
        measure.clefs[0].show = true;
        measure.clefs[0].smallSize = false;
      }
      measure.showKey = true;
    }
    let attXML = measureXML.querySelector("attributes");
    if (attXML != null) {
      if (attXML.querySelector("transpose") != null) {
        measure.diatonicTransposition = -1 * this._eint(
          attXML,
          "transpose diatonic"
        );
        measure.chromaticTransposition = -1 * this._eint(
          attXML,
          "transpose chromatic"
        );
        if (attXML.querySelector("transpose octave-change") != null) {
          let numOctaves = this._eint(
            attXML,
            "transpose octave-change"
          );
          measure.diatonicTransposition += -7 * numOctaves;
          measure.chromaticTransposition += -12 * numOctaves;
        }
      }
      if (this._estr(attXML, "key fifths") !== "") {
        let keyXML = attXML.querySelector("key");
        measure.displayKey = this._eint(keyXML, "fifths");
        measure.concertKey = measure.displayKey - PitchUtils.getKeySigAlterationForTransposition(
          measure.diatonicTransposition,
          measure.chromaticTransposition
        );
        measure.outgoingKey = previousMeasure != null ? previousMeasure.displayKey : 0;
        if (keyXML.querySelector("mode") != null) {
          let mode = this._estr(keyXML, "mode").toLowerCase();
          measure.isMajorKey = mode === "major" || mode === "(unknown)";
        } else {
          measure.isMajorKey = true;
        }
        measure.showKey = true;
        stack.newKey = true;
      }
      if (this._estr(attXML, "time beats") !== "") {
        let timeXML = attXML.querySelector("time");
        measure.numBeats = this._eint(timeXML, "beats");
        measure.beatType = this._eint(timeXML, "beat-type");
        measure.showTime = true;
      }
    }
    return measure;
  }
  _updateMeasureBeatGroups(measure) {
    let beatGroups = [];
    let subBeatGroups = [];
    if (!measure.stack.isPickup) {
      for (let i = 0; i < measure.numBeats; i++) {
        let beatType = measure.beatType;
        if (beatType % 8 !== 0) {
          let beat = i * (4 / beatType);
          beatGroups.push(beat);
          subBeatGroups.push(beat + 2 / beatType);
        } else if (i % 3 === 0) {
          let beat = i * (4 / beatType);
          beatGroups.push(beat);
          subBeatGroups.push(beat + 4 / beatType);
          subBeatGroups.push(beat + 8 / beatType);
        }
      }
    } else {
      let stack = measure.stack;
      let stackQNoteDuration = stack.endTime - stack.startTime;
      let beatType = measure.beatType;
      let beatDur = beatType % 8 === 0 ? 3 * 4 / beatType : 4 / beatType;
      let qNotePos = stackQNoteDuration - beatDur;
      while (qNotePos > 0) {
        beatGroups.splice(0, 0, qNotePos);
        qNotePos -= beatDur;
      }
    }
    measure.beatGroups = beatGroups;
    measure.subBeatGroups = subBeatGroups;
  }
  _createNoteGroup(noteXML, cMeasure) {
    let noteGroup = new NoteGroup();
    if (noteXML.getAttribute("print-object") === "no") {
      noteGroup.visible = false;
    }
    if (noteXML.querySelector("duration") != null) {
      noteGroup.duration = this._eint(noteXML, "duration");
    } else {
      noteGroup.duration = 0;
    }
    noteGroup.durationType = noteXML.querySelector("type") != null ? DurationType.getDurationType(this._estr(noteXML, "type")) : DurationType.WHOLE;
    noteGroup.isGrace = noteXML.querySelector("grace") != null || noteGroup.duration === 0;
    noteGroup.hPos = noteXML.getAttribute("default-x") != null ? Number.parseFloat(noteXML.getAttribute("default-x")) : cMeasure.stack.width / 2;
    noteGroup.numDots = noteXML.querySelectorAll("dot").length;
    let tupletsToRemove = [];
    let notationsXML = noteXML.querySelector("notations");
    let tupletsXML = notationsXML == null ? null : notationsXML.querySelectorAll("tuplet");
    if (tupletsXML != null) {
      for (let tupletXML of tupletsXML) {
        if (tupletXML.getAttribute("type") === "start") {
          let tuplet = new Tuplet();
          tuplet.tupletID = tupletXML.getAttribute("number") != null ? Number.parseInt(tupletXML.getAttribute("number")) : 0;
          tuplet.firstNote = noteGroup;
          tuplet.above = tupletXML.getAttribute("placement") === "above";
          let numDots;
          let tupletDurType;
          if (tupletXML.querySelector("tuplet-actual") != null) {
            let tupActualXML = tupletXML.querySelector(
              "tuplet-actual"
            );
            tuplet.numerator = this._eint(
              tupActualXML,
              "tuplet-number"
            );
            numDots = tupActualXML.querySelector(
              "tuplet-dot"
            ) != null ? tupActualXML.querySelectorAll("tuplet-dot").length : 0;
            tupletDurType = DurationType.getDurationType(
              this._estr(tupActualXML, "tuplet-type")
            );
            tuplet.numeratorDuration = DurationType.getDurationValue(
              tupletDurType,
              numDots
            );
          } else {
            tuplet.numerator = this._eint(
              noteXML,
              "time-modification actual-notes"
            );
            tuplet.numeratorDuration = DurationType.getDurationValue(
              noteGroup.durationType,
              noteGroup.numDots
            );
          }
          if (tupletXML.querySelector("tuplet-normal") != null) {
            let tupNormalXML = tupletXML.querySelector(
              "tuplet-normal"
            );
            tuplet.denominator = this._eint(
              tupNormalXML,
              "tuplet-number"
            );
            numDots = tupNormalXML.querySelector(
              "tuplet-dot"
            ) != null ? tupNormalXML.querySelectorAll("tuplet-dot").length : 0;
            tupletDurType = DurationType.getDurationType(
              this._estr(tupNormalXML, "tuplet-type")
            );
            tuplet.denominatorDuration = DurationType.getDurationValue(
              tupletDurType,
              numDots
            );
          } else {
            tuplet.denominator = this._eint(
              noteXML,
              "time-modification normal-notes"
            );
            numDots = noteXML.querySelectorAll(
              "time-modification normal-dot"
            ).length;
            let durString = this._estr(
              noteXML,
              "time-modification normal-type"
            );
            tupletDurType = durString !== "" ? DurationType.getDurationType(durString) : noteGroup.durationType;
            tuplet.denominatorDuration = DurationType.getDurationValue(
              tupletDurType,
              numDots
            );
          }
          this._currentTuplets.push(tuplet);
        } else if (tupletXML.getAttribute("type") === "stop" && this._currentTuplets.length > 0) {
          let tupletID = this._aint(tupletXML, "number");
          let tuplet = this._currentTuplets[0];
          for (let tup of this._currentTuplets) {
            if (tup.tupletID === tupletID) {
              tuplet = tup;
              break;
            }
          }
          tuplet.endNote = noteGroup;
          tupletsToRemove.push(tuplet);
        }
      }
    }
    if (this._currentTuplets.length > 0) {
      noteGroup.tuplets = this._currentTuplets.slice(0);
      if (tupletsToRemove.length > 0) {
        for (let tupletToRemove of tupletsToRemove) {
          for (let i = 0; i < this._currentTuplets.length; i++) {
            if (tupletToRemove === this._currentTuplets[i]) {
              this._currentTuplets.splice(i, 1);
            }
          }
        }
      }
    }
    let slursXML = notationsXML != null ? notationsXML.querySelectorAll("slur") : null;
    if (slursXML != null) {
      for (let slurXML of slursXML) {
        let slur = null;
        if (slurXML.getAttribute("type") === TieState.START) {
          slur = new Slur();
          slur.slurID = Number.parseInt(
            slurXML.getAttribute("number")
          );
          slur.firstNote = noteGroup;
          this._currentSlurs.push(slur);
        } else if (slurXML.getAttribute("type") === TieState.STOP) {
          let slurID = Number.parseInt(
            slurXML.getAttribute("number")
          );
          let numSlurs = this._currentSlurs.length;
          let slurIndex = numSlurs - 1;
          while (slurIndex >= 0) {
            if (this._currentSlurs[slurIndex].slurID === slurID) {
              slur = this._currentSlurs[slurIndex];
              break;
            }
            slurIndex--;
          }
          if (slur != null) {
            slur.endNote = noteGroup;
            let index = this._currentSlurs.indexOf(slur);
            this._currentSlurs.splice(index, 1);
          }
        }
        if (slur != null) {
          if (noteGroup.slurs == null) {
            noteGroup.slurs = [slur];
          } else {
            noteGroup.slurs.push(slur);
          }
        }
      }
    }
    let notationsElements = noteXML.querySelectorAll("notations");
    if (notationsElements.length > 0) {
      let articulationElements = notationsElements[0].querySelectorAll("articulations");
      if (articulationElements != null && articulationElements.length > 0) {
        for (let node of articulationElements[0].childNodes) {
          if (!(node instanceof Element)) {
            continue;
          }
          let articXML = node;
          let articulation = new Articulation();
          articulation.type = ArticulationType.getTypeFromName(
            articXML.tagName
          );
          let hPos = articXML.getAttribute("default-x");
          let vPos = articXML.getAttribute("default-y");
          articulation.hPos = hPos != null ? Number.parseFloat(hPos) : 0;
          articulation.vPos = vPos != null ? -1 * Number.parseFloat(vPos) : 0;
          articulation.isAbove = articXML.getAttribute("placement") === "above";
          articulation.noteGroup = noteGroup;
          articulation.getWidthHeight();
          if (noteGroup.articulations == null) {
            noteGroup.articulations = [];
          }
          noteGroup.articulations.push(articulation);
        }
      }
    }
    let ornamentElements = noteXML.querySelectorAll("ornaments > *");
    for (let ornamentEl of ornamentElements) {
      let type = OrnamentType.getTypeFromMusicXmlName(
        ornamentEl.tagName
      );
      if (type === OrnamentType.UNKNOWN) {
        continue;
      }
      let ornament = new Ornament();
      ornament.type = type;
      ornament.hPos = 0;
      ornament.vPos = 0;
      ornament.isAbove = ornamentEl.getAttribute("placement") !== "below";
      ornament.noteGroup = noteGroup;
      ornament.getWidthHeight();
      if (noteGroup.ornaments == null) {
        noteGroup.ornaments = [];
      }
      noteGroup.ornaments.push(ornament);
    }
    if (noteXML.querySelector("lyric") != null) {
      try {
        let lyricXML = noteXML.querySelector("lyric");
        let vPos = this._anum(lyricXML, "default-y", false);
        if (vPos != null)
          vPos /= 10;
        else
          vPos = -8;
        let lyric = new Lyric(
          this._estr(lyricXML, "syllabic"),
          this._estr(lyricXML, "text"),
          this._aint(lyricXML, "number"),
          0,
          vPos
        );
        noteGroup.lyric = lyric;
      } catch (err) {
        console.log("problem with lyric from XML: ${err.toString()}");
      }
    }
    if (noteXML.querySelector("rest") != null) {
      noteGroup.isRest = true;
      if (this._estr(noteXML, "rest display-step") !== "") {
        let clefType = cMeasure.clefs.length > 0 ? cMeasure.clefs[cMeasure.clefs.length - 1].type : ClefType.TREBLE;
        let pitchName = this._estr(noteXML, "rest display-step") + this._estr(noteXML, "rest display-octave");
        let stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(pitchName, clefType);
        noteGroup.restVPos = 1 / 2 * stepsFromTopStaffLine;
      } else {
        noteGroup.restVPos = 2;
      }
      if (noteGroup.durationType === DurationType.WHOLE) {
        noteGroup.restVPos -= 0.5;
      } else if (noteGroup.durationType === DurationType.HALF) {
        noteGroup.restVPos -= 0.3;
      }
      return noteGroup;
    }
    let beamsXML = noteXML.querySelectorAll("beam");
    for (let beamXML of beamsXML) {
      noteGroup.beamStates.push(beamXML.textContent);
    }
    if (noteXML.getAttribute("default-x") != null) {
      noteGroup.hPos = Number.parseFloat(noteXML.getAttribute("default-x"));
    }
    let stemDirection;
    if (noteXML.querySelector("stem") != null) {
      stemDirection = this._estr(noteXML, "stem");
      if (noteXML.querySelector("stem[default-y]") != null) {
        noteGroup.stemEndPos = -1 * Number.parseFloat(noteXML.querySelector("stem[default-y]").getAttribute("default-y"));
      }
    } else if (noteGroup.durationType === DurationType.WHOLE) {
      stemDirection = StemDirection.NO_STEM;
    } else {
      stemDirection = StemDirection.UP;
    }
    noteGroup.stemDirection = stemDirection;
    noteGroup.stemHPos = stemDirection === StemDirection.UP ? this._score.scoreProperties.noteheadWidth : 0;
    return noteGroup;
  }
  _createNote(noteXML, noteGroup, measure) {
    let note = new Note();
    if (noteXML.getAttribute("print-object") === "no") {
    } else if (noteGroup.visible === false) {
      noteGroup.visible = true;
    }
    if (noteXML.querySelector("pitch") != null) {
      note.pitchName = this._estr(noteXML, "pitch step") + this._estr(noteXML, "pitch octave");
    } else if (noteXML.querySelector("unpitched") != null) {
      note.pitchName = this._estr(noteXML, "unpitched display-step") + this._estr(noteXML, "unpitched display-octave");
      if (noteXML.querySelector("instrument") != null && this._midiInstrumentsXMLList != null) {
        let instID = noteXML.querySelector("instrument").getAttribute("id");
        for (let miXML of this._midiInstrumentsXMLList) {
          if (miXML.getAttribute("id") === instID) {
            note.playbackCents = this._eint(
              miXML,
              "midi-unpitched"
            ) * 100 - 100;
            break;
          }
        }
      }
    } else {
      console.log("unknown note pitch format! $noteXML");
      note.pitchName = "C4";
    }
    let alteration = this._eint(noteXML, "pitch alter");
    note.alteration = alteration;
    note.displayCents = PitchUtils.pitchNameToDiatonicCents(
      note.pitchName
    ) + 100 * alteration;
    let accidental;
    if (alteration < 3 && alteration > -3) {
      accidental = noteXML.querySelector("accidental") != null ? AccidentalType.getAccidentalFromString(this._estr(
        noteXML,
        "accidental"
      )) : AccidentalType.NONE;
    } else {
      let newPitchName = PitchUtils.getCommonPitchNameFromCents(
        note.displayCents
      );
      note.alteration = PitchUtils.getAlterationForCents(
        note.displayCents,
        newPitchName
      );
      note.pitchName = newPitchName;
      accidental = AccidentalType.NONE;
    }
    if (accidental !== AccidentalType.NONE) {
      note.accidental = accidental;
      note.showAccidental = true;
    } else {
      note.accidental = AccidentalType.getAccidentalType(note.alteration);
    }
    note.accidentalPos = -1 * this._score.scoreProperties.noteheadWidth;
    let clefType = "";
    for (let clef of measure.clefs) {
      if (noteGroup.qNoteTime + 1e-4 > clef.qNoteTime) {
        clefType = clef.type;
      } else {
        break;
      }
    }
    note.stepsFromTopStaffLine = PitchUtils.getStepsFromTopStaffLine(
      note.pitchName,
      clefType
    );
    if (noteXML.getAttribute("default-x") != null) {
      note.hPos = Number.parseFloat(noteXML.getAttribute("default-x")) - noteGroup.hPos;
    }
    note.vPos = 1 / 2 * note.stepsFromTopStaffLine;
    if (note.stepsFromTopStaffLine < -1) {
      note.legerLines = Math.trunc(note.stepsFromTopStaffLine / 2);
    } else if (note.stepsFromTopStaffLine > 9) {
      note.legerLines = Math.trunc((note.stepsFromTopStaffLine - 8) / 2);
    }
    let tieXMLs = noteXML.querySelectorAll("tie");
    if (tieXMLs.length > 0) {
      note.tieState = tieXMLs.length === 1 ? tieXMLs[0].getAttribute("type") : TieState.CONTINUE;
    }
    return note;
  }
  _createTempoMarker(element, qNoteTime, targetStack, metronomeEl) {
    let tempoMarker = new TempoMarker();
    let tempoFac = 1;
    if (metronomeEl != null) {
      if (metronomeEl.querySelector("beat-unit") != null) {
        let durationType = DurationType.getDurationType(
          metronomeEl.querySelector("beat-unit").textContent
        );
        let numDots = metronomeEl.querySelector("dotted") != null ? 1 : 0;
        let duration = DurationType.getDurationValue(
          durationType,
          numDots
        );
        tempoFac = duration / 1024;
      }
    }
    tempoMarker.qNoteTime = qNoteTime;
    tempoMarker.tempo = Number.parseFloat(element.getAttribute("tempo"));
    if (targetStack.tempoMarkers == null) {
      targetStack.tempoMarkers = [tempoMarker];
    } else {
      if (targetStack.tempoMarkers[targetStack.tempoMarkers.length - 1].qNoteTime < tempoMarker.qNoteTime) {
        targetStack.tempoMarkers.push(tempoMarker);
      }
    }
  }
  _computeMaxKeySizes() {
    let stacks = this._score.getMeasureStacks();
    let numStacks = stacks.length;
    for (let i = 0; i < numStacks; i++) {
      let stack = stacks[i];
      let measures = stack.measures;
      let numMeasures = measures.length;
      let maxKeySize = 0;
      for (let j = 0; j < numMeasures; j++) {
        let measure = measures[j];
        let totalKeySize = PitchUtils.getTotalKeySize(
          measure.displayKey,
          measure.outgoingKey
        );
        if (totalKeySize > maxKeySize) {
          maxKeySize = totalKeySize;
        }
      }
      stack.maxKeySize = maxKeySize;
    }
  }
  _checkPartForErrors(part) {
    for (let staff of part.staves) {
      for (let measure of staff.measures) {
        if (measure.voices.length === 0) {
          measure.getVoiceByNumber(1);
        }
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            if (ng.isRest) {
              continue;
            }
            for (let note of ng.notes) {
              if (note.tieState === TieState.START || note.tieState === TieState.CONTINUE) {
                let nextNG = ng.next;
                if (nextNG == null || nextNG.isRest) {
                  note.tieState = TieState.NONE;
                } else {
                  let matchFound = false;
                  for (let nextNote of nextNG.notes) {
                    if ((nextNote.tieState === TieState.CONTINUE || nextNote.tieState === TieState.STOP) && nextNote.pitchName === note.pitchName && nextNote.alteration === note.alteration) {
                      matchFound = true;
                      break;
                    }
                  }
                  if (!matchFound) {
                    note.tieState = TieState.NONE;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  _checkScoreForErrors() {
    let firstStack = this._score.parts[0].staves[0].measures[0].stack;
    if (firstStack.newKey === false) {
      firstStack.newKey = true;
      for (let measure of firstStack.measures) {
        measure.showKey = true;
      }
    }
    let markers = firstStack.tempoMarkers;
    if (markers == null || markers.length === 0 || markers[0].qNoteTime > 0) {
      let tempoMarker = new TempoMarker();
      tempoMarker.tempo = ScoreProperties.DEFAULT_TEMPO;
      tempoMarker.qNoteTime = 0;
      firstStack.tempoMarkers = [tempoMarker];
    }
    let staffMsrs = this._score.parts[0].staves[0].measures;
    let lastStack = staffMsrs[staffMsrs.length - 1].stack;
    markers = lastStack.tempoMarkers;
    if (markers == null || markers.length === 0 || markers[markers.length - 1].qNoteTime < lastStack.endTime) {
      if (lastStack.tempoMarkers == null) {
        lastStack.tempoMarkers = [];
      }
      let tempoMarker = new TempoMarker();
      tempoMarker.tempo = ScoreProperties.DEFAULT_TEMPO;
      tempoMarker.qNoteTime = lastStack.endTime;
      lastStack.tempoMarkers.push(tempoMarker);
    }
  }
  _adjustMeasureLengthsToFitNotes() {
    let stacks = this._score.getMeasureStacks();
    let currentQNoteOffset = 0;
    for (let stack of stacks) {
      stack.startTime += currentQNoteOffset;
      stack.endTime += currentQNoteOffset;
      let stackStartTime = stack.startTime;
      let latestNoteEndTime = stackStartTime;
      for (let measure of stack.measures) {
        for (let voice of measure.voices) {
          for (let ng of voice.noteGroups) {
            ng.qNoteTime += currentQNoteOffset;
            if (ng.tuplets != null && ng.tuplets.length > 0) {
              let tuplet = ng.tuplets[0];
              let tupletQNoteEndTime = tuplet.firstNote.qNoteTime + tuplet.denominator * tuplet.denominatorDuration / DurationType.QUARTER;
              if (tupletQNoteEndTime > latestNoteEndTime) {
                latestNoteEndTime = tupletQNoteEndTime;
              }
            } else if (ng.qNoteTime + ng.qNoteDuration > latestNoteEndTime) {
              latestNoteEndTime = ng.qNoteTime + ng.qNoteDuration;
            }
          }
        }
        if (currentQNoteOffset !== 0) {
          for (let clef of measure.clefs) {
            clef.qNoteTime += currentQNoteOffset;
          }
          for (let attachment of measure.attachments) {
            attachment.qNoteTime += currentQNoteOffset;
          }
        }
      }
      if (currentQNoteOffset !== 0 && stack.tempoMarkers != null) {
        for (let tm of stack.tempoMarkers) {
          tm.qNoteTime += currentQNoteOffset;
        }
      }
      if (latestNoteEndTime !== stack.endTime) {
        if (latestNoteEndTime === stack.startTime) {
          continue;
        } else {
          let newNumerator;
          let newDenominator;
          let stackNewQNoteDur = latestNoteEndTime - stackStartTime;
          let qNoteRemainder = stackNewQNoteDur >= 1 ? stackNewQNoteDur % Math.floor(stackNewQNoteDur) : stackNewQNoteDur;
          if (qNoteRemainder === 0) {
            newNumerator = Math.floor(stackNewQNoteDur);
            newDenominator = 4;
          } else {
            let mult = 1 / qNoteRemainder;
            if (Math.floor(mult) === mult) {
              newNumerator = Math.floor(stackNewQNoteDur * mult);
              newDenominator = Math.floor(4 * mult);
            }
          }
          if (newNumerator != null) {
            currentQNoteOffset += latestNoteEndTime - stack.endTime;
            stack.endTime = latestNoteEndTime;
            if (stack === stacks[0]) {
              stack.isPickup = true;
            } else if (stack.repeatDOs != null && stack.repeatDOs.length > 0) {
              if (stack.repeatDOs[0].repeatDirection === RepeatDirection.FORWARD) {
                stack.isPickup = true;
              }
            }
            for (let measure of stack.measures) {
              measure.displayNumBeats = measure.numBeats;
              measure.displayBeatType = measure.beatType;
              measure.numBeats = newNumerator;
              measure.beatType = newDenominator;
              this._updateMeasureBeatGroups(measure);
            }
          }
        }
      }
    }
  }
  get score() {
    return this._score;
  }
  get musicXML() {
    return this._musicXML;
  }
};
/**
 * Dispatched when the xml file finishes parsing.
 * The MusicXMLParser.FINISHED_PARSING constant defines the value of the 
 * <code>type</code> property of the event object 
 * for a <code>finishedParsing</code> event.
 * 
 * @eventType finishedParsing
 */
MusicXmlParser2.FINISHED_PARSING = "finishedParsing";
var Dlog = class {
  static log(data) {
    let div = document.querySelector("#divLog");
    if (div == null) {
      div = document.createElement("div");
      div.id = "divLog";
      div.style.height = "300px";
      div.style.width = "300px";
      div.style.overflow = "auto";
      document.body.append(div);
    }
    if (data instanceof Array) {
      for (let item of data) {
        let p = document.createElement("p");
        p.innerHTML = item == null ? "null" : item.toString();
        div.append(p);
      }
    } else {
      let p = document.createElement("p");
      p.innerHTML = data == null ? "null" : data.toString();
      div.append(p);
    }
    div.scrollTop = 1e5;
  }
};

// src/utils/MusicXmlUtils.ts
import { CompressionUtils, ConversionUtils, HttpUtils } from "ts_utils";
var MusicXmlUtils = class _MusicXmlUtils {
  /**looks at the end of the [fileNamePath] string to determine which type
   * of MusicXml file it is. If it doesn't match one of the [MusicXmlFileType]
   * values, null is returned.*/
  static getFileType(fileNamePath) {
    if (fileNamePath.endsWith(".xml"))
      return 0 /* xml */;
    else if (fileNamePath.endsWith(".mxl"))
      return 1 /* mxl */;
    else if (fileNamePath.endsWith(".zip"))
      return 2 /* zip */;
    else
      return null;
  }
  static getMusicXmlStringFromUrl(url, type = null, userToken = null) {
    return new Promise((resolve, reject) => {
      if (type == null) {
        if (url.endsWith(".xml"))
          type = 0 /* xml */;
        else if (url.endsWith(".mxl"))
          type = 1 /* mxl */;
        else if (url.endsWith(".zip"))
          type = 2 /* zip */;
        else
          reject(MusicXmlError.INVALID_FILE_TYPE);
      }
      if (url == null)
        resolve(null);
      try {
        if (type === 0 /* xml */) {
          HttpUtils.get(url, { userToken }).then((request) => {
            let xmlString = request.responseText;
            let cleanString = _MusicXmlUtils._verifyAndCleanMusicXmlString(xmlString);
            resolve(cleanString ?? MusicXmlError.INVALID_FORMAT);
          }).catch((e) => {
            reject(e);
          });
        } else if (type === 1 /* mxl */ || type === 2 /* zip */) {
          HttpUtils.get(url, {
            userToken,
            responseType: "arraybuffer"
          }).then(async (request) => {
            let ab = request.response;
            let xml = await _MusicXmlUtils.decompressMusicXml(ab);
            let cleanXml = xml != null ? _MusicXmlUtils._verifyAndCleanMusicXmlString(xml) : null;
            resolve(cleanXml ?? MusicXmlError.INVALID_FORMAT);
          }).catch((e) => {
            reject(e);
          });
        } else {
          reject(MusicXmlError.INVALID_FILE_TYPE);
        }
      } catch (e) {
        reject(e);
      }
    });
  }
  /**reads a .xml or .mxl MusicXML file provided as a html.File object (this
   * typically comes from an HTML file selection Input control). Returns a
   * future that upon completion delivers the verified and cleaned MusicXML
   * as a String*/
  static getMusicXmlStringFromFile(file) {
    return new Promise(async (resolve, reject) => {
      try {
        let xml;
        let fname = file.name.toLowerCase();
        if (fname.length <= 4) {
          reject(MusicXmlError.INVALID_FILE_TYPE);
        }
        if (fname.lastIndexOf(".xml") === fname.length - 4) {
          xml = await ConversionUtils.getStringFromTextBlob(file);
        } else if (fname.lastIndexOf(".mxl") === fname.length - 4 || fname.lastIndexOf(".zip") === fname.length - 4) {
          let ab = await ConversionUtils.getArrayBufferFromBinaryBlob(
            file
          );
          xml = await _MusicXmlUtils.decompressMusicXml(ab);
        } else {
          reject(MusicXmlError.INVALID_FILE_TYPE);
        }
        let cleanXml = _MusicXmlUtils._verifyAndCleanMusicXmlString(xml);
        resolve(cleanXml);
      } catch (err) {
        reject(err);
      }
    });
  }
  /**decompresses MusicXML ArrayBuffer into MusicXML string*/
  static async decompressMusicXml(data) {
    let entries = await CompressionUtils.unzip(data);
    if (entries == null)
      return null;
    for (let entry of entries) {
      let fileName = entry.filename.toLowerCase();
      if (!fileName.includes("meta-inf/") && fileName.lastIndexOf(".xml") == fileName.length - 4) {
        let xml = await CompressionUtils.getStringFromEntry(entry);
        return xml;
      }
    }
    return null;
  }
  /**verifies string is valid MusicXML and cleans up unrecognized characters*/
  static _verifyAndCleanMusicXmlString(xmlString) {
    if (xmlString.indexOf("score-partwise") === -1) {
      return null;
    }
    return xmlString.replace(/[\x03\x00]/g, "");
  }
};
var MusicXmlFileType = /* @__PURE__ */ ((MusicXmlFileType2) => {
  MusicXmlFileType2[MusicXmlFileType2["xml"] = 0] = "xml";
  MusicXmlFileType2[MusicXmlFileType2["mxl"] = 1] = "mxl";
  MusicXmlFileType2[MusicXmlFileType2["zip"] = 2] = "zip";
  return MusicXmlFileType2;
})(MusicXmlFileType || {});
var MusicXmlError = class {
};
MusicXmlError.INVALID_FILE_TYPE = "invalidFileType";
MusicXmlError.INVALID_FORMAT = "invalidFormat";

// src/utils/instruments/Range.ts
var Range = class _Range {
  constructor(min, comfort, max, type) {
    this.min = min;
    this.comfort = comfort;
    this.max = max;
    this.type = type;
  }
  static getRangeString(type) {
    switch (type) {
      case 0 /* easy */:
        return "Easy";
      case 1 /* medium */:
        return "Medium";
      case 2 /* advanced */:
        return "Advanced";
      case 3 /* off */:
        return "Off";
      default:
        throw "invalid range!";
    }
  }
  clone() {
    let clone = new _Range();
    clone.min = this.min;
    clone.comfort = this.comfort;
    clone.max = this.max;
    clone.type = this.type;
    return clone;
  }
};
var RangeType = /* @__PURE__ */ ((RangeType2) => {
  RangeType2[RangeType2["easy"] = 0] = "easy";
  RangeType2[RangeType2["medium"] = 1] = "medium";
  RangeType2[RangeType2["advanced"] = 2] = "advanced";
  RangeType2[RangeType2["off"] = 3] = "off";
  RangeType2[RangeType2["custom"] = 4] = "custom";
  return RangeType2;
})(RangeType || {});

// src/utils/instruments/InstrumentDO.ts
var InstrumentDO = class {
  constructor(id, name, clef, transposition, minKey, maxKey, {
    easyRange,
    mediumRange,
    advancedRange,
    offRange,
    fingeringMode = null
  }) {
    //null means no fingerings
    ///can be used to indicate alternative fingering sets, such German. 0 by
    ///convention should be the default.
    this.fingeringSet = 0;
    this.id = id;
    this.name = name;
    this.clef = clef;
    this.transposition = transposition;
    this.minKey = minKey;
    this.maxKey = maxKey;
    this.easyRange = easyRange;
    this.mediumRange = mediumRange;
    this.advancedRange = advancedRange;
    this.offRange = offRange;
    this.fingeringMode = fingeringMode;
  }
  getRangeByType(type) {
    switch (type) {
      case 0 /* easy */:
        return this.easyRange;
      case 1 /* medium */:
        return this.mediumRange;
      case 2 /* advanced */:
        return this.advancedRange;
      case 3 /* off */:
        return this.offRange;
      default:
        throw "unsupported RangeType: " + type;
    }
  }
  get label() {
    return this.name;
  }
};

// src/utils/instruments/InstrumentUtils.ts
var _InstrumentUtils = class _InstrumentUtils {
  static getInstrumentByID(id) {
    for (let inst of _InstrumentUtils.instruments) {
      if (inst.id === id) {
        return inst;
      }
    }
    return _InstrumentUtils.instruments[0];
  }
  static isRecorder(id) {
    return id === 3 || id === 4 || id >= 300 && id <= 303;
  }
};
_InstrumentUtils.instruments = [
  new InstrumentDO(3, "Soprano Recorder", "treble", -12, -2, 2, {
    easyRange: new Range(60, 69, 74, 0 /* easy */),
    mediumRange: new Range(60, 69, 79, 1 /* medium */),
    advancedRange: new Range(60, 69, 84, 2 /* advanced */),
    offRange: new Range(60, 69, 128, 3 /* off */),
    fingeringMode: 0
  }),
  new InstrumentDO(4, "Alto Recorder Transposed", "treble", -5, -2, 2, {
    easyRange: new Range(60, 69, 74, 0 /* easy */),
    mediumRange: new Range(60, 69, 79, 1 /* medium */),
    advancedRange: new Range(60, 69, 84, 2 /* advanced */),
    offRange: new Range(60, 69, 128, 3 /* off */),
    fingeringMode: 0
  }),
  new InstrumentDO(300, "Alto Recorder", "treble", 0, -3, 1, {
    easyRange: new Range(65, 74, 79, 0 /* easy */),
    mediumRange: new Range(65, 74, 84, 1 /* medium */),
    advancedRange: new Range(65, 74, 89, 2 /* advanced */),
    offRange: new Range(65, 74, 128, 3 /* off */),
    fingeringMode: 2
  }),
  new InstrumentDO(301, "Tenor Recorder", "treble", 0, -2, 2, {
    easyRange: new Range(60, 69, 74, 0 /* easy */),
    mediumRange: new Range(60, 69, 79, 1 /* medium */),
    advancedRange: new Range(60, 69, 84, 2 /* advanced */),
    offRange: new Range(60, 69, 128, 3 /* off */),
    fingeringMode: 0
  }),
  new InstrumentDO(302, "Bass Recorder Transposed", "treble", 7, -2, 2, {
    easyRange: new Range(60, 69, 74, 0 /* easy */),
    mediumRange: new Range(60, 69, 79, 1 /* medium */),
    advancedRange: new Range(60, 69, 84, 2 /* advanced */),
    offRange: new Range(60, 69, 128, 3 /* off */),
    fingeringMode: 0
  }),
  new InstrumentDO(303, "Bass Recorder", "bass", -12, -3, 1, {
    easyRange: new Range(41, 50, 55, 0 /* easy */),
    mediumRange: new Range(41, 50, 60, 1 /* medium */),
    advancedRange: new Range(41, 50, 65, 2 /* advanced */),
    offRange: new Range(41, 50, 128, 3 /* off */),
    fingeringMode: 2
  }),
  new InstrumentDO(400, "Tin Whistle in D", "treble", -12, -1, 3, {
    easyRange: new Range(62, 68, 74, 0 /* easy */),
    mediumRange: new Range(62, 69, 79, 1 /* medium */),
    advancedRange: new Range(62, 71, 85, 2 /* advanced */),
    offRange: new Range(62, 71, 128, 3 /* off */),
    fingeringMode: 0
  }),
  new InstrumentDO(5, "Piccolo", "treble", -12, -3, 1, {
    easyRange: new Range(65, 78, 82, 0 /* easy */),
    mediumRange: new Range(65, 78, 87, 1 /* medium */),
    advancedRange: new Range(62, 78, 93, 2 /* advanced */),
    offRange: new Range(62, 78, 128, 3 /* off */)
  }),
  new InstrumentDO(6, "Flute", "treble", 0, -3, 1, {
    easyRange: new Range(65, 77, 82, 0 /* easy */),
    mediumRange: new Range(63, 77, 87, 1 /* medium */),
    advancedRange: new Range(60, 77, 93, 2 /* advanced */),
    offRange: new Range(60, 77, 128, 3 /* off */)
  }),
  new InstrumentDO(200, "Alto Flute", "treble", 5, -4, 0, {
    easyRange: new Range(65, 77, 82, 0 /* easy */),
    mediumRange: new Range(63, 77, 87, 1 /* medium */),
    advancedRange: new Range(60, 77, 93, 2 /* advanced */),
    offRange: new Range(60, 77, 128, 3 /* off */)
  }),
  new InstrumentDO(7, "Oboe", "treble", 0, -3, 1, {
    easyRange: new Range(65, 74, 79, 0 /* easy */),
    mediumRange: new Range(60, 74, 84, 1 /* medium */),
    advancedRange: new Range(58, 74, 91, 2 /* advanced */),
    offRange: new Range(58, 74, 128, 3 /* off */)
  }),
  new InstrumentDO(210, "English Horn", "treble", 7, -2, 2, {
    easyRange: new Range(65, 74, 79, 0 /* easy */),
    mediumRange: new Range(60, 74, 84, 1 /* medium */),
    advancedRange: new Range(59, 74, 88, 2 /* advanced */),
    offRange: new Range(59, 74, 128, 3 /* off */)
  }),
  new InstrumentDO(8, "Bassoon", "bass", 0, -3, 1, {
    easyRange: new Range(43, 53, 60, 0 /* easy */),
    mediumRange: new Range(43, 53, 67, 1 /* medium */),
    advancedRange: new Range(34, 53, 74, 2 /* advanced */),
    offRange: new Range(34, 53, 128, 3 /* off */)
  }),
  new InstrumentDO(9, "Clarinet", "treble", 2, -2, 2, {
    easyRange: new Range(52, 67, 77, 0 /* easy */),
    mediumRange: new Range(52, 72, 86, 1 /* medium */),
    advancedRange: new Range(52, 72, 91, 2 /* advanced */),
    offRange: new Range(52, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(10, "Bass Clarinet", "treble", 14, -2, 2, {
    easyRange: new Range(52, 67, 77, 0 /* easy */),
    mediumRange: new Range(52, 70, 86, 1 /* medium */),
    advancedRange: new Range(52, 70, 91, 2 /* advanced */),
    offRange: new Range(52, 70, 128, 3 /* off */)
  }),
  new InstrumentDO(11, "Soprano Sax", "treble", 2, -2, 2, {
    easyRange: new Range(62, 72, 79, 0 /* easy */),
    mediumRange: new Range(58, 72, 84, 1 /* medium */),
    advancedRange: new Range(58, 72, 90, 2 /* advanced */),
    offRange: new Range(58, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(12, "Alto Sax", "treble", 9, -1, 3, {
    easyRange: new Range(62, 72, 79, 0 /* easy */),
    mediumRange: new Range(58, 72, 84, 1 /* medium */),
    advancedRange: new Range(58, 72, 90, 2 /* advanced */),
    offRange: new Range(58, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(13, "Tenor Sax", "treble", 14, -2, 2, {
    easyRange: new Range(62, 72, 79, 0 /* easy */),
    mediumRange: new Range(58, 72, 84, 1 /* medium */),
    advancedRange: new Range(58, 72, 90, 2 /* advanced */),
    offRange: new Range(58, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(14, "Bari Sax", "treble", 21, -1, 3, {
    easyRange: new Range(62, 72, 79, 0 /* easy */),
    mediumRange: new Range(58, 72, 84, 1 /* medium */),
    advancedRange: new Range(58, 72, 90, 2 /* advanced */),
    offRange: new Range(58, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(15, "Trumpet", "treble", 2, -2, 2, {
    easyRange: new Range(58, 64, 74, 0 /* easy */),
    mediumRange: new Range(54, 69, 79, 1 /* medium */),
    advancedRange: new Range(54, 69, 86, 2 /* advanced */),
    offRange: new Range(54, 69, 128, 3 /* off */)
  }),
  new InstrumentDO(16, "Horn in F", "treble", 7, -2, 2, {
    easyRange: new Range(55, 64, 74, 0 /* easy */),
    mediumRange: new Range(53, 67, 79, 1 /* medium */),
    advancedRange: new Range(53, 67, 84, 2 /* advanced */),
    offRange: new Range(48, 67, 128, 3 /* off */)
  }),
  new InstrumentDO(17, "Trombone", "bass", 0, -4, 0, {
    easyRange: new Range(44, 53, 60, 0 /* easy */),
    mediumRange: new Range(40, 53, 65, 1 /* medium */),
    advancedRange: new Range(40, 53, 72, 2 /* advanced */),
    offRange: new Range(40, 53, 128, 3 /* off */)
  }),
  new InstrumentDO(18, "Euphonium TC", "treble", 14, -2, 2, {
    easyRange: new Range(58, 64, 74, 0 /* easy */),
    mediumRange: new Range(54, 69, 79, 1 /* medium */),
    advancedRange: new Range(54, 69, 86, 2 /* advanced */),
    offRange: new Range(54, 69, 128, 3 /* off */)
  }),
  new InstrumentDO(19, "Euphonium BC", "bass", 0, -4, 0, {
    easyRange: new Range(44, 50, 60, 0 /* easy */),
    mediumRange: new Range(40, 55, 65, 1 /* medium */),
    advancedRange: new Range(40, 55, 72, 2 /* advanced */),
    offRange: new Range(40, 55, 128, 3 /* off */)
  }),
  new InstrumentDO(20, "Tuba", "bass", 0, -4, 0, {
    easyRange: new Range(32, 38, 48, 0 /* easy */),
    mediumRange: new Range(28, 43, 53, 1 /* medium */),
    advancedRange: new Range(28, 43, 60, 2 /* advanced */),
    offRange: new Range(28, 43, 128, 3 /* off */)
  }),
  new InstrumentDO(21, "Violin", "treble", 0, -1, 3, {
    easyRange: new Range(62, 68, 79, 0 /* easy */),
    mediumRange: new Range(55, 72, 86, 1 /* medium */),
    advancedRange: new Range(55, 72, 92, 2 /* advanced */),
    offRange: new Range(55, 72, 128, 3 /* off */)
  }),
  new InstrumentDO(22, "Viola", "alto", 0, -1, 3, {
    easyRange: new Range(55, 61, 72, 0 /* easy */),
    mediumRange: new Range(48, 65, 79, 1 /* medium */),
    advancedRange: new Range(48, 65, 85, 2 /* advanced */),
    offRange: new Range(48, 65, 128, 3 /* off */)
  }),
  new InstrumentDO(23, "Cello", "bass", 0, -1, 3, {
    easyRange: new Range(43, 49, 60, 0 /* easy */),
    mediumRange: new Range(36, 53, 67, 1 /* medium */),
    advancedRange: new Range(36, 53, 73, 2 /* advanced */),
    offRange: new Range(36, 53, 128, 3 /* off */)
  }),
  new InstrumentDO(24, "Bass", "bass", 12, -1, 3, {
    easyRange: new Range(40, 49, 60, 0 /* easy */),
    mediumRange: new Range(40, 51, 67, 1 /* medium */),
    advancedRange: new Range(40, 53, 73, 2 /* advanced */),
    offRange: new Range(40, 53, 128, 3 /* off */)
  }),
  new InstrumentDO(25, "Piano", "treble", 0, -2, 2, {
    easyRange: new Range(1, 69, 128, 0 /* easy */),
    mediumRange: new Range(1, 69, 128, 1 /* medium */),
    advancedRange: new Range(1, 69, 128, 2 /* advanced */),
    offRange: new Range(1, 69, 128, 3 /* off */)
  }),
  new InstrumentDO(26, "Guitar", "treble", 12, -2, 2, {
    easyRange: new Range(60, 69, 79, 0 /* easy */),
    mediumRange: new Range(52, 64, 84, 1 /* medium */),
    advancedRange: new Range(52, 64, 91, 2 /* advanced */),
    offRange: new Range(52, 64, 128, 3 /* off */)
  })
];
var InstrumentUtils = _InstrumentUtils;
export {
  AccidentalType,
  Arranger,
  Articulation,
  ArticulationType,
  AttachmentFormatter,
  Barline,
  BarlineLocation,
  BarlineStyle,
  BaseDataObject,
  BeamState,
  ChordSymbol,
  ChordSymbolDegree,
  Clef,
  ClefType,
  Dlog,
  DurationType,
  Dynamic,
  DynamicType,
  DynamicVolumeMap,
  EndingType,
  InstrumentDO,
  InstrumentUtils,
  Lyric,
  LyricsMode,
  Measure,
  MeasureAttachment,
  MeasureAttachmentType,
  MeasureInstruction,
  MeasureRegion,
  MeasureStack,
  MusicAnalyzer,
  MusicBeamer,
  MusicSpacer,
  MusicXmlError,
  MusicXmlFileType,
  MusicXmlParser2,
  MusicXmlUtils,
  NotationObject,
  Note,
  NoteAttachment,
  NoteGroup,
  NoteGroupContainer,
  NoteGroupFormatter,
  NoteManager,
  Ornament,
  OrnamentType,
  Page,
  Part,
  PartData,
  PitchNameManager,
  PitchUtils,
  PlaybackRegion,
  PlaybackRegionData,
  PlaybackRegionDataType,
  PointXY,
  Range,
  RangeType,
  RepeatDO,
  RepeatDirection,
  ScaleInfo,
  Score,
  ScoreData,
  ScoreFormatter,
  ScoreIO,
  ScoreManager,
  ScorePlaybackInfo,
  ScoreProperties,
  Slur,
  SlurSegment,
  Staff,
  StaffGroupingStyle,
  Stem,
  StemDirection,
  SyllablePosition,
  System,
  TempoMarker,
  TempoTimeStampDO,
  TheoryTools,
  TieState,
  TransposeDirection,
  TranspositionDO,
  TranspositionManager,
  TranspositionRecord,
  TranspositionStatsDO,
  TranspositionType,
  Tuplet,
  Voice
};
